// ChatGPT mobile viewport fixes applied
// client/src/App.js - Apple 風格 Photo Gallery（全屏沉浸式 + 雙端獨立隊列 + 真·隨機補齊 + 明確實體保存）
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, animate } from 'framer-motion';
import EXIF from 'exif-js';
import OpenCC from 'opencc-js/t2cn';
import './App.css';

// ==========================================
// 1. 動畫變體與常數配置
// ==========================================

const springTransition = {
  type: 'spring',
  stiffness: 350,
  damping: 30
};

const modalBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } }
};

const modalContentVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0, transition: springTransition },
  exit: { opacity: 0, scale: 0.95, y: 10, transition: { duration: 0.15 } }
};

const photoViewerVariants = {
  initialFromHome: {
    opacity: 0,
    scale: 0.9,
    rotate: 0,
    x: 0,
    y: 0,
    transformOrigin: 'center center'
  },
  enterFromSide: (direction) => ({
    opacity: 0,
    scale: 0.92,
    y: 10,
    rotate: direction > 0 ? 6 : -6,
    transformOrigin: '100% 100%'
  }),
  center: {
    opacity: 1,
    scale: 1,
    x: 0,
    y: 0,
    rotate: 0,
    transformOrigin: 'center center',
    transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] }
  },
  exitRotate: (direction) => ({
    opacity: 0,
    scale: 0.85,
    rotate: direction > 0 ? -25 : 25,
    x: direction > 0 ? -80 : 80,
    y: 20,
    transformOrigin: 'center center',
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
  })
};

const TAG_LABELS = {
  street: '街頭',
  architecture: '建築',
  nature: '自然風光',
  portrait: '人像',
  film: '膠片',
  featured: '精選集錦',
  latest: '最新上傳',
  gear_cameras: '相機器材',
  gear_edc: 'EDC 隨身',
  gear_luts: '濾鏡調色',
  journal_routes: '路線與機位',
  journal_notes: '光影筆記',
  uncategorized: '未分類'
};
const DEFAULT_WORK_CATEGORIES = ['street', 'architecture', 'nature', 'portrait', 'film', 'uncategorized'];
const traditionalToSimplified = OpenCC.Converter({ from: 'tw', to: 'cn' });
const normalizeSearchValue = value => traditionalToSimplified(String(value || '').normalize('NFKC'))
  .toLowerCase()
  .replace(/[\s\p{P}\p{S}]+/gu, '');
const formatShootingDate = value => {
  if (value === undefined || value === null || value === '') return '';
  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]} / ${match[3]} / ${match[1]}`;
  const date = new Date(/^\d+$/.test(raw) ? Number(raw) : value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${month} / ${day} / ${date.getUTCFullYear()}`;
};
const parseExifShootingDate = value => {
  const match = String(value || '').trim().match(/^(\d{4}):(\d{2}):(\d{2})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/);
  if (!match) return null;
  const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4] || 0), Number(match[5] || 0), Number(match[6] || 0));
  return Number.isFinite(timestamp) ? timestamp : null;
};
const formatExifCameraName = (make, model) => {
  const combined = `${make || ''} ${model || ''}`.replace(/\s+/g, ' ').trim();
  return combined
    .replace(/^NIKON CORPORATION\s+NIKON\s+/i, 'Nikon ')
    .replace(/^NIKON CORPORATION\s*/i, 'Nikon ')
    .replace(/^HASSELBLAD\s+/i, 'Hasselblad ');
};
const getPhotoShootingInfo = photo => {
  const params = photo?.shootingParams || {};
  const structured = [params.shutterSpeed, params.aperture, params.iso].filter(Boolean);
  if (structured.length) return structured.join(' · ');
  const legacy = String(params.equipment || '');
  return [
    legacy.match(/(?:\d+\/\d+|\d+(?:\.\d+)?)s\b/i)?.[0],
    legacy.match(/f\/\d+(?:\.\d+)?\b/i)?.[0],
    legacy.match(/ISO\s*\d+\b/i)?.[0]
  ].filter(Boolean).join(' · ');
};
const getPhotoEquipmentLabel = photo => {
  const params = photo?.shootingParams || {};
  const legacy = String(params.equipment || '');
  const equipment = legacy
    .replace(/(?:\d+\/\d+|\d+(?:\.\d+)?)s\b/ig, '')
    .replace(/f\/\d+(?:\.\d+)?\b/ig, '')
    .replace(/ISO\s*\d+\b/ig, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return formatExifCameraName('', equipment) || [params.camera, params.lens].filter(Boolean).join(' + ');
};

// Classic CSS-grid masonry: every tile measures its own natural image ratio,
// then spans only the rows it needs. The grids keep normal row flow so the
// curator's sequence stays stable instead of later short photos filling gaps.
function MasonryCell({ children, rowHeight = 4, gap = 12, layoutScale = 1 }) {
  const cellRef = useRef(null);
  const [rowSpan, setRowSpan] = useState(16);

  useEffect(() => {
    const cell = cellRef.current;
    if (!cell) return undefined;
    const image = cell.querySelector('img');
    if (!image) return undefined;
    const measure = () => {
      const width = cell.getBoundingClientRect().width / layoutScale;
      const ratio = image.naturalWidth > 0 ? image.naturalHeight / image.naturalWidth : 0.8;
      const contentHeight = Math.max(40, width * ratio);
      setRowSpan(Math.max(1, Math.ceil((contentHeight + gap) / (rowHeight + gap))));
    };
    image.addEventListener('load', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(cell);
    measure();
    return () => {
      image.removeEventListener('load', measure);
      observer?.disconnect();
    };
  }, [gap, rowHeight, layoutScale]);

  return <div ref={cellRef} style={{ minWidth: 0, gridRowEnd: `span ${rowSpan}` }}>{children}</div>;
}

function FeaturedHomepagePreview({ photos, theme, isDarkMode, featuredDragPayload, onBeginDrag, onFinishDrag, onRemove, onDrop }) {
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(.3);
  const [canvasHeight, setCanvasHeight] = useState(0);
  const [dropIndex, setDropIndex] = useState(null);
  const desktopContentWidth = 1232;

  useEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return undefined;
    const measure = () => {
      setScale(Math.min(1, Math.max(.2, (viewport.clientWidth - 20) / desktopContentWidth)));
      setCanvasHeight(canvas.offsetHeight);
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(viewport);
    observer?.observe(canvas);
    measure();
    return () => observer?.disconnect();
  }, [photos.length]);

  const previewHeight = Math.max(photos.length ? 80 : 260, Math.ceil(canvasHeight * scale));
  const getDropIndex = (event, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > rect.top + rect.height / 2
      || (Math.abs(event.clientY - (rect.top + rect.height / 2)) < rect.height * .22 && event.clientX > rect.left + rect.width / 2);
    return index + (after ? 1 : 0);
  };

  return <div ref={viewportRef} onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }} onDrop={event => { setDropIndex(null); onDrop(event, photos.length); }} style={{ minHeight: '260px', maxHeight: '68vh', overflowY: 'auto', padding: '10px', borderRadius: '18px', border: `1px solid ${featuredDragPayload ? '#0071e3' : theme.cardBorder}`, background: featuredDragPayload ? (isDarkMode ? 'rgba(0,113,227,.10)' : 'rgba(0,113,227,.045)') : (isDarkMode ? 'rgba(255,255,255,.025)' : 'rgba(0,0,0,.012)'), transition: 'border-color .18s, background .18s' }}>
    {photos.length > 0 ? <div style={{ position: 'relative', minHeight: `${previewHeight}px` }}><div ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: `${desktopContentWidth}px`, transform: `scale(${scale})`, transformOrigin: 'top left' }}><div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridAutoRows: '4px', gridAutoFlow: 'row', gap: '20px', alignItems: 'start' }}>{photos.map((photo, index) => <MasonryCell key={photo._id} gap={20} layoutScale={scale}><div data-featured-index={index} draggable onDragStart={event => onBeginDrag(event, { type: 'selected', id: String(photo._id), index }, photo)} onDragEnd={() => { setDropIndex(null); onFinishDrag(); }} onDragOver={event => { event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = 'move'; setDropIndex(getDropIndex(event, index)); }} onDrop={event => { event.preventDefault(); event.stopPropagation(); const nextIndex = getDropIndex(event, index); setDropIndex(null); onDrop(event, nextIndex); }} style={{ position: 'relative', overflow: 'hidden', borderRadius: '16px', cursor: 'grab', background: theme.inputBg, outline: featuredDragPayload?.id === String(photo._id) ? '6px solid #0071e3' : 'none', boxShadow: dropIndex === index ? 'inset 0 8px 0 #0071e3' : dropIndex === index + 1 ? 'inset 0 -8px 0 #0071e3' : 'none', opacity: featuredDragPayload?.id === String(photo._id) ? .58 : 1, transition: 'opacity .18s, box-shadow .12s' }}><img src={getPhotoImagePath(photo, 'medium')} alt="精選作品預覽" draggable={false} style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} /><button type="button" onClick={() => onRemove(photo._id)} aria-label="移除精選作品" title="移除精選作品" style={{ position: 'absolute', top: '12px', right: '12px', width: '48px', height: '48px', border: '1px solid rgba(255,255,255,.32)', borderRadius: '50%', background: 'rgba(0,0,0,.68)', color: '#fff', cursor: 'pointer', lineHeight: 1, fontSize: '27px', fontWeight: 500, display: 'grid', placeItems: 'center', boxShadow: '0 3px 12px rgba(0,0,0,.28)' }}>×</button><span style={{ position: 'absolute', left: '16px', bottom: '16px', minWidth: '28px', padding: '6px 8px', borderRadius: '8px', background: 'rgba(0,0,0,.58)', color: '#fff', fontSize: '14px', textAlign: 'center' }}>{index + 1}</span></div></MasonryCell>)}</div></div></div> : <p style={{ margin: '34px 14px', textAlign: 'center', color: theme.textSecondary, fontSize: '13px' }}>把右側照片拖進這個區域，或點擊加入。</p>}
  </div>;
}

const HOME_SECTIONS = [
  { id: 'section-hero', title: '視覺展廳' },
  { id: 'section-philosophy', title: '美學宣言' },
  { id: 'section-journeys', title: '最新旅程' },
  { id: 'section-featured', title: '精選櫥窗' }
];

// Only migrate this app's original default copy. Locations, captions and other
// user-entered photo data are deliberately left untouched.
const LEGACY_SIMPLIFIED_COPY = new Map([
  ['捕捉稍纵即逝的真实。', '捕捉稍縱即逝的真實。'],
  ['每一次快门，都是与这座城市的深度对话。', '每一次快門，都是與這座城市的深度對話。'],
  ['在光影交错间，寻找被忽略的细节与温度。', '在光影交錯間，尋找被忽略的細節與溫度。'],
  ['记录真实，\n重塑秩序。', '記錄真實，\n重塑秩序。'],
  ['摄影对我而言，是一个做减法的过程。摒弃繁杂的器材依赖与过度的修饰，专注于街头的光影、建筑的几何与最真实的瞬间。以 Clean Fit 的视觉标准，褪去画面中的无序，只保留最纯粹的肌理与温度。', '攝影對我而言，是一個做減法的過程。摒棄繁雜的器材依賴與過度的修飾，專注於街頭的光影、建築的幾何與最真實的瞬間。以 Clean Fit 的視覺標準，褪去畫面中的無序，只保留最純粹的肌理與溫度。']
]);

const defaultContactSocials = () => ([
  { id: 'instagram', label: 'Instagram', url: '', kind: 'url' },
  { id: 'xiaohongshu', label: '小紅書', url: '', kind: 'url' },
  { id: 'email', label: 'Email', url: '', kind: 'email' }
]);

const normalizeContactSocials = (settings) => {
  if (Array.isArray(settings?.contactSocials) && settings.contactSocials.length) {
    return settings.contactSocials.map((item, index) => ({
      id: String(item.id || `social-${index}`),
      label: String(item.label || '').trim(),
      url: String(item.url || ''),
      kind: item.kind === 'email' ? 'email' : 'url'
    })).filter(item => item.label);
  }
  const legacy = [
    { id: 'instagram', label: 'Instagram', url: settings?.contactInstagram || '', kind: 'url' },
    { id: 'xiaohongshu', label: '小紅書', url: settings?.contactXiaohongshu || '', kind: 'url' },
    { id: 'email', label: 'Email', url: settings?.contactEmail || '', kind: 'email' }
  ];
  return legacy.some(item => item.url) ? legacy : defaultContactSocials();
};

const normalizeLegacySiteCopy = (settings) => ({
  ...settings,
  contactSocials: normalizeContactSocials(settings),
  locationCollections: normalizeLocationCollections(settings?.locationCollections),
  locationPhotoOrders: normalizeLocationPhotoOrders(settings?.locationPhotoOrders),
  locationMenuOrders: normalizeLocationMenuOrders(settings?.locationMenuOrders),
  heroLines: Array.isArray(settings?.heroLines)
    ? settings.heroLines.map((line) => LEGACY_SIMPLIFIED_COPY.get(line) || line)
    : settings?.heroLines,
  philosophyTitle: LEGACY_SIMPLIFIED_COPY.get(settings?.philosophyTitle) || settings?.philosophyTitle,
  philosophyBody: LEGACY_SIMPLIFIED_COPY.get(settings?.philosophyBody) || settings?.philosophyBody
});

const CONTACT_SETTING_KEYS = ['contactName', 'contactAbout', 'contactSocials'];

const normalizeExternalUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(candidate);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
  } catch (err) {
    return '';
  }
};

const CANDIDATE_PORTS = [5001, 5000, 5002, 8000];

// ==========================================
// 2. 輔助工具函數與獨立組件
// ==========================================

const CLOUD_ASSET_BASE = 'http://43.103.1.179';

export const cleanImagePath = (url) => {
  if (!url || typeof url !== 'string') return '';
  
  let clean = url.replace(/^https?:\/\/localhost:\d+/, '');
  if (!clean.startsWith('/') && !clean.startsWith('http')) {
    clean = '/' + clean;
  }

  const isLocal = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (isLocal && clean.startsWith('/uploads')) {
    return `${CLOUD_ASSET_BASE}${clean}`;
  }

  return clean;
};

// 新照片会带有三档服务端图片；旧照片没有该字段时会自动回退到原有路径。
// 这样前端可以逐步启用新系统，不需要一次性迁移才能继续使用。
export const getPhotoImageCandidates = (photo, size = 'medium') => {
  if (!photo) return '';
  const variants = photo.imageVariants || {};
  const candidates = {
    thumbnail: [variants.thumbnail, variants.medium, photo.previewPath, variants.hero, photo.imagePath],
    medium: [variants.medium, photo.previewPath, variants.hero, variants.thumbnail, photo.imagePath],
    hero: [variants.hero, variants.medium, photo.previewPath, variants.thumbnail, photo.imagePath],
    original: [photo.imagePath, variants.hero, variants.medium, photo.previewPath, variants.thumbnail]
  };
  return [...new Set(
    (candidates[size] || candidates.medium)
      .filter(Boolean)
      .map(cleanImagePath)
      .filter(Boolean)
  )];
};

export const getPhotoImagePath = (photo, size = 'medium') =>
  getPhotoImageCandidates(photo, size)[0] || '';

export const getPhotoImageSrcSet = photo => {
  if (!photo) return '';
  const variants = photo.imageVariants || {};
  return [
    [variants.thumbnail, 480],
    [variants.medium, 1280],
    [variants.hero, 2560]
  ]
    .filter(([path]) => Boolean(path))
    .map(([path, width]) => `${cleanImagePath(path)} ${width}w`)
    .join(', ');
};

export const getPhotoAlt = (photo, fallback = '攝影作品') => {
  const description = String(photo?.aiDescription || photo?.description || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (description) return description.slice(0, 80);
  const location = String(photo?.location || '').trim();
  if (location) return `${location}攝影作品`;
  return String(photo?.title || '').trim() || fallback;
};

const MISSING_LOCATION_KEY = '__missing_location__';
const getLocationKey = (location) => {
  const value = String(location || '').trim();
  return !value || value === '未知地点' || value === '未知地點' ? MISSING_LOCATION_KEY : value;
};
const hasPhotoEquipment = photo => Boolean(String(photo?.shootingParams?.equipment || photo?.equipment || '').trim());
const needsPhotoOrganization = photo => (
  getLocationKey(photo?.location) === MISSING_LOCATION_KEY
  || !photo?.subCategory
  || photo.subCategory === 'uncategorized'
  || !hasPhotoEquipment(photo)
);

// 地點以「省份 · 城市 · 區縣／景點」儲存。空白段不會寫入，也不會留下多餘的「·」。
const splitLocationHierarchy = (location) => {
  const value = String(location || '').trim();
  if (getLocationKey(value) === MISSING_LOCATION_KEY) return { province: '', city: '', detail: '' };
  const parts = value.split(/[·•]/).map(part => part.trim()).filter(Boolean);
  if (parts.length === 1) return { province: '', city: parts[0], detail: '' };
  const [province = '', city = '', ...details] = parts;
  return { province, city, detail: details.join('·') };
};

// 聯網核實通常回傳「城市＋景點」的連寫名稱，例如「蘇州山塘街」。帶入編輯器時
// 要先拆回三個欄位，不能把整串誤塞進城市欄。
const LOCATION_CITY_HIERARCHIES = [
  { province: '江蘇', city: '蘇州', aliases: ['蘇州市', '苏州市', '蘇州', '苏州'] },
  { province: '江蘇', city: '南京', aliases: ['南京市', '南京'] },
  { province: '廣東', city: '汕頭', aliases: ['汕頭市', '汕头市', '汕頭', '汕头'] },
  { province: '廣東', city: '潮州', aliases: ['潮州市', '潮州'] },
  { province: '廣東', city: '廣州', aliases: ['廣州市', '广州市', '廣州', '广州'] },
  { province: '廣東', city: '深圳', aliases: ['深圳市', '深圳'] },
  { province: '廣東', city: '惠州', aliases: ['惠州市', '惠州'] },
  { province: '廣東', city: '揭陽', aliases: ['揭陽市', '揭阳市', '揭陽', '揭阳'] },
  { province: '廣東', city: '普寧', aliases: ['普寧市', '普宁市', '普寧', '普宁'] },
  { province: '江西', city: '南昌', aliases: ['南昌市', '南昌'] },
  { province: '江西', city: '上饒', aliases: ['上饒市', '上饶市', '上饒', '上饶'] },
  { province: '江西', city: '婺源', aliases: ['婺源縣', '婺源县', '婺源'] },
  { province: '四川', city: '成都', aliases: ['成都市', '成都'] },
  { province: '四川', city: '九寨溝', aliases: ['九寨溝縣', '九寨沟县', '九寨溝', '九寨沟'] },
  { province: '福建', city: '南平', aliases: ['南平市', '南平'] },
  { province: '浙江', city: '杭州', aliases: ['杭州市', '杭州'] },
  { province: '浙江', city: '烏鎮', aliases: ['烏鎮', '乌镇'] },
  { province: '湖北', city: '武漢', aliases: ['武漢市', '武汉市', '武漢', '武汉'] },
  { province: '湖北', city: '十堰', aliases: ['十堰市', '十堰'] },
  { province: '湖南', city: '長沙', aliases: ['長沙市', '长沙市', '長沙', '长沙'] },
  { province: '廣西', city: '桂林', aliases: ['桂林市', '桂林'] },
  { province: '重慶', city: '重慶', aliases: ['重慶市', '重庆市', '重慶', '重庆'] },
  { province: '上海', city: '上海', aliases: ['上海市', '上海'] },
  { province: '北京', city: '北京', aliases: ['北京市', '北京'] },
  { province: '香港', city: '香港', aliases: ['香港'] }
];

const splitResearchSuggestedLocation = (location) => {
  const value = String(location || '').trim();
  if (!value) return { province: '', city: '', detail: '' };

  const parts = value.split(/[·•]/).map(part => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    const [province = '', city = '', ...details] = parts;
    return { province, city, detail: details.join('·') };
  }

  const cityMatch = LOCATION_CITY_HIERARCHIES
    .flatMap(item => item.aliases.map(alias => ({ ...item, alias })))
    .sort((a, b) => b.alias.length - a.alias.length)
    .find(item => value.includes(item.alias));

  if (cityMatch) {
    const matchAt = value.indexOf(cityMatch.alias);
    const before = value.slice(0, matchAt).replace(/[·•、,，\s]/g, '');
    const after = value.slice(matchAt + cityMatch.alias.length).replace(/^[·•、,，\s]+/, '');
    // 只有「省份 · 城市」兩段時，第二段仍是城市；城市别名後面的內容才是景點。
    const detail = parts.length === 2 && matchAt === 0 ? parts[1] : after;
    return { province: cityMatch.province, city: cityMatch.city, detail: detail || before };
  }

  return splitLocationHierarchy(value);
};

const normalizeLocationCollectionPrefix = value => String(value || '')
  .split(/[·•]/)
  .map(part => part.trim())
  .filter(Boolean)
  .join('·');

const normalizeLocationCollections = value => (Array.isArray(value) ? value : [])
  .map((item, index) => ({
    id: String(item?.id || `location-collection-${index}`).trim(),
    title: String(item?.title || '').trim().slice(0, 80),
    prefix: normalizeLocationCollectionPrefix(item?.prefix)
  }))
  .filter(item => item.id && item.title && item.prefix)
  .slice(0, 30);

// 地點圖集的順序屬於該「完整地點」；尚未手動排過的照片仍依拍攝時間補在最後。
const normalizeLocationPhotoOrders = value => (Array.isArray(value) ? value : [])
  .map(item => ({
    location: String(item?.location || '').trim().slice(0, 160),
    photoIds: [...new Set((Array.isArray(item?.photoIds) ? item.photoIds : [])
      .map(id => String(id || '').trim())
      .filter(Boolean))].slice(0, 500)
  }))
  .filter(item => item.location && item.photoIds.length)
  .slice(0, 200);

// 省份、城市與具體地點共用這個有序清單；新加入的項目仍維持原本的日期排序並補在最後。
const normalizeLocationMenuOrders = value => (Array.isArray(value) ? value : [])
  .map(item => ({
    key: String(item?.key || '').trim().slice(0, 200),
    itemIds: [...new Set((Array.isArray(item?.itemIds) ? item.itemIds : [])
      .map(id => String(id || '').trim().slice(0, 200))
      .filter(Boolean))]
  }))
  .filter(item => item.key && item.itemIds.length)
  .slice(0, 80);

const sortLocationMenuItems = (items, orderKey, locationMenuOrders, getId = item => item.id || item.key) => {
  const savedIds = normalizeLocationMenuOrders(locationMenuOrders).find(item => item.key === orderKey)?.itemIds || [];
  const positions = new Map(savedIds.map((id, index) => [id, index]));
  return [...items].sort((left, right) => {
    const leftPosition = positions.get(String(getId(left)));
    const rightPosition = positions.get(String(getId(right)));
    if (leftPosition !== undefined || rightPosition !== undefined) {
      if (leftPosition === undefined) return 1;
      if (rightPosition === undefined) return -1;
      return leftPosition - rightPosition;
    }
    return 0;
  });
};

const sortLocationPhotos = (photos, location, locationPhotoOrders) => {
  const savedOrder = normalizeLocationPhotoOrders(locationPhotoOrders)
    .find(item => item.location === location)?.photoIds || [];
  const positions = new Map(savedOrder.map((id, index) => [String(id), index]));
  return [...photos].sort((a, b) => {
    const left = positions.get(String(a._id));
    const right = positions.get(String(b._id));
    if (left !== undefined && right !== undefined) return left - right;
    if (left !== undefined) return -1;
    if (right !== undefined) return 1;
    return Number(b.shootingDate || b.createdAt || 0) - Number(a.shootingDate || a.createdAt || 0);
  });
};

const getLocationCollectionChild = (location, prefix) => {
  const locationParts = String(location || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
  const prefixParts = String(prefix || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
  if (locationParts.length <= prefixParts.length || !prefixParts.length) return '';
  const matchesPrefix = prefixParts.every((part, index) => normalizeSearchValue(locationParts[index]) === normalizeSearchValue(part));
  return matchesPrefix ? locationParts.slice(prefixParts.length).join('·') : '';
};

const matchesLocationCollection = (location, prefix) => {
  const locationParts = String(location || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
  const prefixParts = String(prefix || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
  return prefixParts.length > 0 && locationParts.length >= prefixParts.length
    && prefixParts.every((part, index) => normalizeSearchValue(locationParts[index]) === normalizeSearchValue(part));
};

const compressTo4K = (file, maxDimension = 3840, quality = 0.9) => {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);
    img.src = blobUrl;
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const newFileName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
          const compressedFile = new File([blob], newFileName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(file);
    };
  });
};

// 去重必須以使用者選取的原始檔為準，不能以 canvas 壓縮後每次可能略有不同的 JPEG 為準。
// 不支援 Web Crypto 的舊瀏覽器會回傳空值，後端仍會用畫面近似比對作為保護。
const getOriginalFileHash = async (file) => {
  try {
    if (!file?.arrayBuffer || !window.crypto?.subtle) return '';
    const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return '';
  }
};

function BeforeAfterSlider({ item, theme }) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let percentage = ((clientX - rect.left) / rect.width) * 100;
    setSliderPos(Math.max(0, Math.min(100, percentage)));
  };

  if (!item?.originalImage?.medium || !item?.gradedImage?.medium) return null;
  return (
    <div className="gear-before-after" style={{ width: '100%', maxWidth: '860px', margin: '0 auto 48px', textAlign: 'left' }}>
      {item.title && <h3 className="gear-before-after-title" style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>{item.title}</h3>}
      {item.description && <p className="gear-before-after-description" style={{ fontSize: '14px', color: theme?.textSecondary || '#86868b', marginBottom: '20px' }}>{item.description}</p>}
      <div 
        ref={containerRef} 
        onMouseMove={(e) => handleMove(e.clientX)} 
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        className="gear-before-after-stage"
        style={{ 
          position: 'relative', 
          display: 'block',
          width: '100%',
          minWidth: 0,
          aspectRatio: '16 / 10',
          minHeight: '320px',
          height: 'auto',
          borderRadius: '20px', 
          overflow: 'hidden', 
          cursor: 'ew-resize', 
          userSelect: 'none', 
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)' 
        }}
      >
        <img 
          src={item.gradedImage.medium} 
          alt="Graded" 
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} 
        />
        <span style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>GRADED · 調色版本</span>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)`, filter: 'saturate(0.7) contrast(0.9) brightness(0.95)' }}>
          <img 
            src={item.originalImage.medium} 
            alt="RAW preview" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
          <span style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>RAW · 未調色</span>
        </div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${sliderPos}%`, width: '2px', backgroundColor: '#ffffff', boxShadow: '0 0 10px rgba(0,0,0,0.5)', transform: 'translateX(-50%)' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', color: '#1d1d1f', fontSize: '14px', fontWeight: 'bold' }}>↔</div>
        </div>
      </div>
    </div>
  );
}

function GearDraftPreview({ draft, isDarkMode, theme }) {
  const image = draft?.previews?.cover || draft?.cover?.medium || '';
  const original = draft?.previews?.originalImage || draft?.originalImage?.medium || '';
  const graded = draft?.previews?.gradedImage || draft?.gradedImage?.medium || '';
  if (draft?.type === 'color') return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>{[[original, 'RAW'], [graded, 'GRADED']].map(([src, label]) => <div key={label} style={{ aspectRatio: '3 / 4', background: isDarkMode ? '#2c2c2e' : '#e5e5ea', borderRadius: '14px', overflow: 'hidden', position: 'relative' }}>{src && <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}<span style={{ position: 'absolute', left: '8px', bottom: '8px', color: '#fff', background: 'rgba(0,0,0,.55)', borderRadius: '999px', padding: '4px 7px', fontSize: '10px', fontWeight: '700' }}>{label}</span></div>)}</div>;
  return <div style={{ aspectRatio: '4 / 5', background: isDarkMode ? '#2c2c2e' : '#e5e5ea', borderRadius: '16px', overflow: 'hidden' }}>{image ? <img src={image} alt="預覽" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: theme.textSecondary, fontSize: '12px' }}>代表照片預覽</div>}</div>;
}

// 舊版單張預覽編輯器保留於此，暫不掛載；新工作區使用 GearWorkspaceEditorV2。
// eslint-disable-next-line no-unused-vars
function GearWorkspaceEditor({ draft, setDraft, onClose, onSave, isSaving, isDarkMode, theme }) {
  const coverRef = useRef(null); const originalRef = useRef(null); const gradedRef = useRef(null);
  const selectFile = (key, ref) => {
    const input = ref.current; if (!input) return;
    input.onchange = (event) => { const file = event.target.files?.[0]; if (file) setDraft(prev => ({ ...prev, files: { ...prev.files, [key]: file }, previews: { ...prev.previews, [key]: URL.createObjectURL(file) } })); };
    input.value = ''; input.click();
  };
  const update = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const isColor = draft.type === 'color';
  const fieldStyle = { width: '100%', boxSizing: 'border-box', margin: '7px 0 18px', padding: '12px 13px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit' };
  const secondaryButton = { border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', padding: '10px 15px', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontWeight: '600' };
  return <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,.58)', backdropFilter: 'blur(10px)', display: 'flex', justifyContent: 'flex-end' }}><aside style={{ width: 'min(920px, 100vw)', height: '100dvh', overflowY: 'auto', boxSizing: 'border-box', padding: '32px', background: theme.cardBg, color: theme.textPrimary, boxShadow: '-16px 0 48px rgba(0,0,0,.32)' }}><div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,.72fr) minmax(0,1.28fr)', gap: '32px' }}><div style={{ position: 'sticky', top: 0, alignSelf: 'start' }}><p style={{ margin: '0 0 12px', color: theme.textSecondary, fontSize: '12px', fontWeight: '700', letterSpacing: '.08em' }}>前台預覽</p><GearDraftPreview draft={draft} isDarkMode={isDarkMode} theme={theme} /></div><div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '26px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px', fontWeight: '700', letterSpacing: '.08em' }}>{isColor ? 'COLOR COMPARISON' : draft.type === 'camera' ? 'CAMERA & LENS' : 'CREATIVE TOOLS'}</p><h2 style={{ margin: '7px 0 0', fontSize: '26px' }}>{draft._id ? '編輯項目' : '新增項目'}</h2></div><button onClick={onClose} style={{ width: '36px', height: '36px', border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: 'transparent', color: theme.textSecondary, fontSize: '20px', cursor: 'pointer' }}>×</button></div>{!isColor && <><label>名稱</label><input value={draft.title} onChange={e => update('title', e.target.value)} placeholder={draft.type === 'camera' ? '例如：Nikon Zf' : '例如：MacBook Pro'} style={fieldStyle} /><label>器材組合 / 用途</label><input value={draft.subtitle} onChange={e => update('subtitle', e.target.value)} placeholder="例如：NIKKOR Z 40mm f/2" style={fieldStyle} /></>}{isColor && <><label>對比標題 <span style={{ color: theme.textSecondary }}>（可選）</span></label><input value={draft.title} onChange={e => update('title', e.target.value)} placeholder="例如：雨後城市，或留空" style={fieldStyle} /></>}<label>{isColor ? '調色說明' : '使用感受'}</label><textarea rows="5" value={draft.description} onChange={e => update('description', e.target.value)} placeholder={isColor ? '這組色彩想保留什麼？' : '為什麼它在你的工作流中重要？'} style={{ ...fieldStyle, resize: 'vertical' }} />{!isColor ? <><input ref={coverRef} type="file" accept="image/*" style={{ display: 'none' }} /><button onClick={() => selectFile('cover', coverRef)} style={secondaryButton}>{draft.files?.cover ? '更換代表照片' : '選擇代表照片'}</button></> : <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}><input ref={originalRef} type="file" accept="image/*" style={{ display: 'none' }} /><input ref={gradedRef} type="file" accept="image/*" style={{ display: 'none' }} /><button onClick={() => selectFile('originalImage', originalRef)} style={secondaryButton}>{draft.files?.originalImage ? '更換 RAW / 未調色' : '選擇 RAW / 未調色'}</button><button onClick={() => selectFile('gradedImage', gradedRef)} style={secondaryButton}>{draft.files?.gradedImage ? '更換 GRADED' : '選擇 GRADED'}</button></div>}<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}><button onClick={onSave} disabled={isSaving} style={{ border: 'none', borderRadius: '980px', padding: '11px 20px', background: '#0071e3', color: '#fff', fontWeight: '600', cursor: 'pointer', opacity: isSaving ? .65 : 1 }}>{isSaving ? '儲存中...' : '儲存項目'}</button></div></div></div></aside></div>;
}

const gearImagesFor = (item) => {
  const storedImages = item?.images?.length ? item.images.map(image => image.medium || image.thumbnail || image.original).filter(Boolean) : [];
  const pendingImages = item?.previews?.images?.length ? item.previews.images : [];
  if (storedImages.length || pendingImages.length) return [...storedImages, ...pendingImages].slice(0, 9);
  const legacyImage = item?.previews?.cover || item?.cover?.medium || item?.cover?.thumbnail;
  return legacyImage ? [legacyImage] : [];
};

function GearFeedPost({ item, theme, isDarkMode, preview = false, onImageClick }) {
  const images = gearImagesFor(item);
  const count = images.length;
  const gridStyle = count === 1 ? { gridTemplateColumns: '1fr' } : count === 3 ? { gridTemplateColumns: '1.35fr 1fr' } : { gridTemplateColumns: 'repeat(2, 1fr)' };
  return <article className={preview ? 'gear-feed-post gear-feed-post-preview' : 'gear-feed-post'} style={{ overflow: 'hidden', border: `1px solid ${theme.cardBorder}`, borderRadius: preview ? '16px' : '20px', background: theme.cardBg, boxShadow: preview ? 'none' : (isDarkMode ? '0 14px 30px rgba(0,0,0,.16)' : '0 10px 24px rgba(0,0,0,.025)') }}>
    <div className="gear-feed-post-copy" style={{ padding: preview ? '13px 14px 11px' : '19px 20px 15px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px' }}>
        <div style={{ minWidth: 0 }}><div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}><strong style={{ fontSize: preview ? '11px' : '14px', color: theme.textPrimary }}>黃啟銘</strong><span style={{ color: theme.textSecondary, fontSize: preview ? '9px' : '12px' }}>@M.Gallery · 發佈</span></div><h3 style={{ margin: '9px 0 0', fontSize: preview ? '16px' : '19px', letterSpacing: '-.025em' }}>{item.title || '未命名項目'}</h3></div>
        <span title="M. Gallery" style={{ flex: 'none', color: theme.textPrimary, fontFamily: 'Georgia, Times New Roman, serif', fontWeight: '700', fontSize: preview ? '17px' : '23px', lineHeight: 1 }}>M.</span>
      </div>
      {item.subtitle && <p style={{ margin: '5px 0 0', color: theme.textSecondary, fontSize: preview ? '11px' : '13px' }}>{item.subtitle}</p>}
      {item.description && <p style={{ margin: '10px 0 0', color: theme.textPrimary, lineHeight: 1.6, fontSize: preview ? '11px' : '14px', whiteSpace: 'pre-wrap' }}>{item.description}</p>}
    </div>
    {count > 0 && <div className="gear-feed-post-media" style={{ margin: preview ? 0 : '0 20px 20px', borderRadius: preview ? 0 : '14px', overflow: 'hidden' }}><div style={{ display: 'grid', gap: '2px', ...gridStyle, maxHeight: preview ? '250px' : '340px', overflow: 'hidden', background: theme.cardBorder }}>{images.slice(0, 4).map((src, index) => <button key={`${src}-${index}`} type="button" onClick={() => onImageClick?.(images, index)} className={`gear-feed-media-cell ${count === 1 ? 'gear-feed-media-single' : 'gear-feed-media-multi'}`} style={{ position: 'relative', border: 'none', padding: 0, minHeight: count === 1 ? (preview ? '190px' : '310px') : (preview ? '120px' : '168px'), cursor: onImageClick ? 'zoom-in' : 'default', background: isDarkMode ? '#1c1c1e' : '#e5e5ea', gridRow: count === 3 && index === 0 ? 'span 2' : 'auto' }}><img src={src} alt="" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />{index === 3 && count > 4 && <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.48)', color: '#fff', fontSize: '18px', fontWeight: '650' }}>+{count - 4}</span>}</button>)}</div></div>}
    {!count && <div style={{ height: preview ? '190px' : '280px', margin: preview ? 0 : '0 20px 20px', display: 'grid', placeItems: 'center', borderRadius: preview ? 0 : '14px', background: isDarkMode ? '#2c2c2e' : '#f2f2f7', color: theme.textSecondary, fontSize: '12px' }}>選擇 1–9 張圖片後，即時顯示於此</div>}
  </article>;
}

function useMAiStatus(apiBaseUrl, interval = 30000, adminToken = null) {
  const [status, setStatus] = useState({ online: false, loading: true });
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/mai/${adminToken ? 'session' : 'status'}`, {
          cache: 'no-store',
          headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : undefined
        });
        const data = await response.json();
        if (active) setStatus({ ...data, online: Boolean(response.ok && data.online), loading: false });
      } catch (_) {
        if (active) setStatus({ online: false, loading: false });
      }
    };
    check();
    const timer = window.setInterval(check, interval);
    return () => { active = false; window.clearInterval(timer); };
  }, [apiBaseUrl, interval, adminToken]);
  return status;
}

function MAiStatusBadge({ apiBaseUrl, theme, isDarkMode, adminToken }) {
  const status = useMAiStatus(apiBaseUrl, 30000, adminToken);
  return <span title={status.online ? 'Mac 上的 Ollama 已連線' : 'Mac 或 Ollama 目前未連線'} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '6px 10px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(255,255,255,.05)' : '#fbfbfd', color: theme.textSecondary, fontSize: '11px', whiteSpace: 'nowrap' }}>
    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: status.loading ? '#8e8e93' : status.online ? '#30d158' : '#ff453a', boxShadow: status.online ? '0 0 0 3px rgba(48,209,88,.13)' : 'none' }} />
    <span>{status.loading ? 'M.ai · 檢查中' : status.admin ? 'M.ai · PRIMARY' : `M.ai · ${status.online ? '在線' : '離線'}`}</span>
  </span>;
}

const formatTokenCount = value => {
  const count = Number(value) || 0;
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}K`;
  return String(count);
};

function MAiTokenUsageBadge({ apiBaseUrl, theme, isDarkMode, adminToken }) {
  const status = useMAiStatus(apiBaseUrl, 60000, adminToken);
  const [cachedUsage, setCachedUsage] = useState(() => {
    try { return JSON.parse(localStorage.getItem('m_gallery_mai_usage_cache_v1') || 'null'); }
    catch (_) { return null; }
  });
  useEffect(() => {
    if (!status.admin || !status.usage) return;
    setCachedUsage(status.usage);
    localStorage.setItem('m_gallery_mai_usage_cache_v1', JSON.stringify(status.usage));
  }, [status.admin, status.usage]);
  if (!adminToken) return null;
  const usage = status.admin && status.usage ? status.usage : cachedUsage;
  const hasUsage = Boolean(usage && Number.isFinite(Number(usage.totalTokens)));
  const usageTitle = hasUsage
    ? `輸入 ${Number(usage.promptTokens) || 0} · 輸出 ${Number(usage.completionTokens) || 0} · ${Number(usage.modelRequests) || 0} 次模型生成${status.admin ? '' : '（上次读取）'}`
    : '管理员会话尚未验证，Token 用量暂不可读';
  return <span title={usageTitle} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px', padding: '6px 10px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(255,255,255,.05)' : '#fbfbfd', color: theme.textSecondary, fontSize: '11px', whiteSpace: 'nowrap' }}>
    <span>累計 Token</span>
    <strong style={{ color: theme.textPrimary, fontWeight: '650', fontVariantNumeric: 'tabular-nums' }}>{hasUsage ? formatTokenCount(usage.totalTokens) : '—'}</strong>
  </span>;
}

function MAiAssistant({ theme, isDarkMode, apiBaseUrl, adminToken, showLauncher, onOpenPhoto }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [showAdminGreeting, setShowAdminGreeting] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [showHistoryMenu, setShowHistoryMenu] = useState(false);
  const status = useMAiStatus(apiBaseUrl, 20000, adminToken);
  const chatScrollRef = useRef(null);
  const inputRef = useRef(null);
  const inputCompositionRef = useRef(false);
  const inputCompositionEndedAtRef = useRef(0);
  const historyLoadedRef = useRef('');
  const adminGreetingShownRef = useRef(false);
  const pageScrollYRef = useRef(0);
  const suggestions = status.admin
    ? ['整理目前伺服器狀態', '查看今天的訪問量', '目前還有多少待整理照片']
    : ['推薦適合街拍的器材', '介紹 M. Gallery', '尋找九寨溝的作品'];
  const historyKey = status.admin ? 'm_gallery_mai_chat_admin_v1' : 'm_gallery_mai_chat_public_v1';
  const historyStorage = localStorage;
  const historyTtl = 7 * 24 * 60 * 60 * 1000;

  useEffect(() => {
    if (!showLauncher) return undefined;
    const timer = window.setTimeout(() => setShowHint(false), 4200);
    return () => window.clearTimeout(timer);
  }, [showLauncher]);

  useEffect(() => {
    try {
      const saved = JSON.parse(historyStorage.getItem(historyKey) || '[]');
      const isLegacyHistory = Array.isArray(saved);
      const savedAt = Number(saved?.savedAt || 0);
      const savedMessages = isLegacyHistory ? saved : saved?.messages;
      if (!isLegacyHistory && savedAt && Date.now() - savedAt > historyTtl) {
        historyStorage.removeItem(historyKey);
        setMessages([]);
      } else {
        setMessages(Array.isArray(savedMessages) ? savedMessages.slice(-30) : []);
      }
    } catch (_) {
      setMessages([]);
    }
    historyLoadedRef.current = historyKey;
  }, [historyKey, historyStorage, historyTtl]);

  useEffect(() => {
    if (historyLoadedRef.current !== historyKey) return;
    const compact = messages.slice(-30).map(message => ({
      role: message.role === 'assistant' ? 'assistant' : 'user',
      text: String(message.text || '').slice(0, 1200),
      error: Boolean(message.error),
      photos: (message.photos || []).slice(0, 4).map(photo => ({ ...photo, description: String(photo.description || '').slice(0, 260) }))
    }));
    try { historyStorage.setItem(historyKey, JSON.stringify({ savedAt: Date.now(), messages: compact })); } catch (_) {}
  }, [messages, historyKey, historyStorage]);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      const node = chatScrollRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const body = document.body;
    const root = document.documentElement;
    const scrollY = window.scrollY;
    pageScrollYRef.current = scrollY;
    const previousBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow
    };
    const previousRootOverflow = root.style.overflow;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    return () => {
      Object.assign(body.style, previousBody);
      root.style.overflow = previousRootOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      const node = chatScrollRef.current;
      if (node) node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages.length, isSending, isOpen]);

  useEffect(() => {
    if (!showLauncher) {
      setShowAdminGreeting(false);
      return undefined;
    }
    if (!status.admin || adminGreetingShownRef.current) return undefined;
    adminGreetingShownRef.current = true;
    setShowAdminGreeting(true);
    const timer = window.setTimeout(() => setShowAdminGreeting(false), 6200);
    return () => {
      window.clearTimeout(timer);
      setShowAdminGreeting(false);
    };
  }, [status.admin, showLauncher]);

  const submitMessage = async (value) => {
    const text = String(value || '').trim();
    if (!text || isSending) return;
    setShowHistoryMenu(false);
    const priorMessages = messages;
    setMessages(previous => [...previous, { role: 'user', text }]);
    setInput('');
    setIsSending(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/mai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}) },
        body: JSON.stringify({ message: text, history: priorMessages.slice(-6).map(({ role, text: previousText }) => ({ role: role === 'assistant' ? 'assistant' : 'user', text: previousText })) })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'M.ai 暫時無法回答。');
      setMessages(previous => [...previous, { role: 'assistant', text: data.reply, photos: data.photos || [] }]);
    } catch (error) {
      setMessages(previous => [...previous, { role: 'assistant', error: true, text: error.message || 'M.ai 暫時無法回答。' }]);
    } finally {
      setIsSending(false);
    }
  };

  const focusComposer = () => window.requestAnimationFrame(() => inputRef.current?.focus());

  const startNewConversation = () => {
    try { historyStorage.removeItem(historyKey); } catch (_) {}
    setMessages([]);
    setInput('');
    setShowHistoryMenu(false);
    focusComposer();
  };

  const clearAllLocalHistories = () => {
    ['m_gallery_mai_chat_admin_v1', 'm_gallery_mai_chat_public_v1'].forEach(key => {
      try { localStorage.removeItem(key); } catch (_) {}
      try { sessionStorage.removeItem(key); } catch (_) {}
    });
    setMessages([]);
    setInput('');
    setShowHistoryMenu(false);
    focusComposer();
  };

  const editUserMessage = (index) => {
    const text = String(messages[index]?.text || '');
    setMessages(previous => previous.slice(0, index));
    setInput(text);
    focusComposer();
  };

  const deleteMessageRound = (index) => {
    setMessages(previous => {
      const next = [...previous];
      const removeCount = next[index]?.role === 'user' && next[index + 1]?.role === 'assistant' ? 2 : 1;
      next.splice(index, removeCount);
      return next;
    });
  };

  if (!showLauncher && !isOpen) return null;

  return <>
    <AnimatePresence>
      {!isOpen && (showAdminGreeting || (!status.admin && showHint)) && <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: .28 }} style={{ position: 'fixed', left: '22px', bottom: '90px', zIndex: 1200, maxWidth: '280px', padding: '10px 14px', borderRadius: '13px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(28,28,30,.94)' : 'rgba(255,255,255,.94)', boxShadow: '0 12px 32px rgba(0,0,0,.1)', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5, backdropFilter: 'blur(18px)', pointerEvents: 'none' }}>{showAdminGreeting ? '歡迎回來，Miles。' : '用 M.ai 尋找作品與器材'}</motion.div>}
    </AnimatePresence>
    {!isOpen && <button type="button" onClick={() => { setShowAdminGreeting(false); setIsOpen(true); setShowHint(false); }} aria-label="開啟 M.ai" style={{ position: 'fixed', left: '22px', bottom: '22px', zIndex: 1201, width: '56px', height: '56px', borderRadius: '18px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(28,28,30,.94)' : 'rgba(255,255,255,.94)', color: theme.textPrimary, boxShadow: '0 14px 38px rgba(0,0,0,.11)', backdropFilter: 'blur(20px)', cursor: 'pointer', fontFamily: 'Georgia, Times New Roman, serif', fontSize: '17px', fontWeight: '700' }}>M.ai</button>}
    <AnimatePresence>
      {isOpen && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={() => setIsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 13000, background: 'rgba(0,0,0,.24)', backdropFilter: 'blur(4px)', overscrollBehavior: 'none' }}>
        <motion.aside initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} transition={{ type: 'spring', stiffness: 330, damping: 34 }} onMouseDown={event => event.stopPropagation()} style={{ width: 'min(460px, 100vw)', height: '100dvh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', background: isDarkMode ? '#101011' : '#fbfbfc', color: theme.textPrimary, borderRight: `1px solid ${theme.cardBorder}`, boxShadow: '20px 0 60px rgba(0,0,0,.18)', overscrollBehavior: 'contain' }}>
          <header style={{ height: '72px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', borderBottom: `1px solid ${theme.cardBorder}` }}>
            <div><strong style={{ fontFamily: 'Georgia, Times New Roman, serif', fontSize: '20px' }}>M.ai</strong><span style={{ marginLeft: '9px', color: theme.textSecondary, fontSize: '11px' }}>{status.admin ? 'MILES · PRIMARY AGENT' : 'M. GALLERY AGENT'}</span></div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button type="button" onClick={() => setShowHistoryMenu(value => !value)} aria-label="對話選項" style={{ width: '36px', height: '36px', display: 'grid', placeItems: 'center', padding: 0, borderRadius: '50%', border: `1px solid ${theme.cardBorder}`, background: showHistoryMenu ? theme.inputBg : 'transparent', color: theme.textSecondary, lineHeight: 1, cursor: 'pointer' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg></button>
              <button type="button" onClick={() => { setShowHistoryMenu(false); setIsOpen(false); }} style={{ width: '36px', height: '36px', borderRadius: '50%', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '20px', cursor: 'pointer' }}>×</button>
              {showHistoryMenu && <div style={{ position: 'absolute', top: '44px', right: '42px', zIndex: 2, width: '196px', overflow: 'hidden', padding: '6px', borderRadius: '14px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? '#242426' : '#fff', boxShadow: '0 16px 40px rgba(0,0,0,.16)' }}>
                <button type="button" onClick={startNewConversation} style={{ width: '100%', padding: '10px 11px', border: 'none', borderRadius: '9px', background: 'transparent', color: theme.textPrimary, textAlign: 'left', fontSize: '13px', cursor: 'pointer' }}>新對話</button>
                <button type="button" onClick={clearAllLocalHistories} style={{ width: '100%', padding: '10px 11px', border: 'none', borderRadius: '9px', background: 'transparent', color: '#ff453a', textAlign: 'left', fontSize: '13px', cursor: 'pointer' }}>清除所有本機記錄</button>
              </div>}
            </div>
          </header>
          <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '26px 20px 18px' }}>
            {!messages.length && <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '64px' }}><p style={{ margin: '0 0 8px', fontFamily: 'Georgia, Times New Roman, serif', fontSize: '34px', fontWeight: '700', letterSpacing: '-.04em' }}>{status.admin ? '歡迎回來，Miles。' : '你好，我是 M.ai。'}</p><p style={{ margin: '0 0 26px', color: theme.textSecondary, fontSize: '14px', lineHeight: 1.65 }}>{status.admin ? 'M. Gallery 的作品與網站狀態都在這裡。今天想先看哪一部分？' : '我會依照已入庫的作品描述、地點和器材資料，替你尋找內容。'}</p><div style={{ display: 'grid', gap: '10px' }}>{suggestions.map(suggestion => <button key={suggestion} type="button" onClick={() => submitMessage(suggestion)} style={{ padding: '14px 15px', borderRadius: '15px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(255,255,255,.045)' : '#f7f7f8', color: theme.textPrimary, textAlign: 'left', fontSize: '14px', cursor: 'pointer' }}>{suggestion}<span style={{ float: 'right', color: theme.textSecondary }}>↗</span></button>)}</div></div>}
            {!!messages.length && <div className={`mai-message-list ${isDarkMode ? 'dark' : 'light'}`}>
              {messages.map((message, index) => <div key={index} className={`mai-message ${message.role === 'user' ? 'user' : 'assistant'}`} style={{ maxWidth: message.photos?.length ? '96%' : '88%' }}>
                <div className={`mai-message-bubble ${message.error ? 'error' : ''}`}>{message.text}</div>
                {message.role === 'user' && <div className="mai-message-actions"><button type="button" onClick={() => editUserMessage(index)}>修改</button><button type="button" onClick={() => deleteMessageRound(index)}>刪除此輪</button></div>}
                {message.photos?.length > 0 && <div className="mai-photo-results">{message.photos.map(photo => <button type="button" className="mai-photo-result" key={photo.id} onClick={() => { onOpenPhoto?.(photo, pageScrollYRef.current); setIsOpen(false); }}><img src={cleanImagePath(photo.image)} alt={getPhotoAlt(photo, 'M.ai 攝影搜尋結果')} /><div><strong>{photo.location || photo.title}</strong>{photo.description && <small>{photo.description}</small>}<span>打开作品&nbsp; ↗</span></div></button>)}</div>}
              </div>)}
              {isSending && <div className="mai-thinking">M.ai 正在思考…</div>}
            </div>}
          </div>
          <form onSubmit={event => { event.preventDefault(); if (!inputCompositionRef.current && Date.now() - inputCompositionEndedAtRef.current >= 180) submitMessage(input); }} style={{ flex: 'none', padding: '14px 16px 18px', borderTop: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(16,16,17,.97)' : 'rgba(251,251,252,.97)' }}><div style={{ display: 'flex', alignItems: 'flex-end', gap: '9px', padding: '9px 9px 9px 14px', border: `1px solid ${theme.inputBorder}`, borderRadius: '22px', background: isDarkMode ? 'rgba(255,255,255,.055)' : '#f1f1f3' }}><textarea ref={inputRef} value={input} onChange={event => setInput(event.target.value)} onCompositionStart={() => { inputCompositionRef.current = true; }} onCompositionEnd={event => { inputCompositionRef.current = false; inputCompositionEndedAtRef.current = Date.now(); setInput(event.target.value); }} onKeyDown={event => { if (event.key !== 'Enter' || event.shiftKey) return; if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229 || inputCompositionRef.current || Date.now() - inputCompositionEndedAtRef.current < 180) return; event.preventDefault(); submitMessage(input); }} rows="1" placeholder="詢問 M.ai…" style={{ flex: 1, minHeight: '24px', maxHeight: '110px', resize: 'none', border: 'none', outline: 'none', background: 'transparent', color: theme.textPrimary, font: 'inherit', fontSize: '14px', lineHeight: '24px' }} /><button type="button" onClick={() => submitMessage(input)} disabled={!input.trim() || isSending} aria-label="傳送" style={{ flex: 'none', width: '34px', height: '34px', display: 'grid', placeItems: 'center', border: 'none', borderRadius: '50%', background: input.trim() && !isSending ? theme.textPrimary : theme.cardBorder, color: isDarkMode ? '#000' : '#fff', cursor: input.trim() && !isSending ? 'pointer' : 'default' }}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5" /><path d="m6.5 10.5 5.5-5.5 5.5 5.5" /></svg></button></div><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', marginTop: '9px', color: theme.textSecondary, fontSize: '10px' }}><span style={{ width: '7px', height: '7px', borderRadius: '50%', background: status.loading ? '#8e8e93' : status.online ? '#30d158' : '#ff453a', boxShadow: status.online ? '0 0 0 3px rgba(48,209,88,.12)' : 'none' }} /><span>{status.loading ? 'M.ai · 檢查中' : status.admin ? 'M.ai · PRIMARY AGENT' : `M.ai · ${status.online ? '在線' : '離線'}`}</span></div></form>
        </motion.aside>
      </motion.div>}
    </AnimatePresence>
  </>;
}

function DestinationWall({ journeys, theme, visitedRef, onBack, onSelect }) {
  const gridRef = useRef(null);
  useLayoutEffect(() => {
    if (visitedRef.current || !journeys.length) return;
    visitedRef.current = true;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (preference.matches) return;
    const cards = Array.from(gridRef.current.children).filter(card => {
      const rect = card.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    });
    const animations = cards.map((card, index) => card.animate([
      { opacity: 0, transform: 'translateY(10px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 320, delay: cards.length > 1 ? index * 160 / (cards.length - 1) : 0, easing: 'ease-out', fill: 'backwards' }));
    const cancel = () => animations.forEach(animation => animation.cancel());
    preference.addEventListener('change', cancel);
    return () => { cancel(); preference.removeEventListener('change', cancel); };
  }, [journeys, visitedRef]);

  return <main className="destination-wall" style={{ color: theme.textPrimary, '--destination-border': theme.cardBorder, '--destination-muted': theme.textSecondary, '--destination-bg': theme.cardBg }}>
    <button type="button" className="destination-entry" onClick={onBack}>← 返回首頁</button>
    <header className="destination-wall-heading">
      <h1>全部目的地 <span>{journeys.length}</span></h1>
      <p>從一個地方，走進一段光影。按拍攝日期探索目的地圖集。</p>
    </header>
    <div className="destination-grid" ref={gridRef}>
      {journeys.map(journey => <button type="button" className="destination-card" key={journey.id} onClick={() => onSelect(journey)}>
        <div className="destination-cover"><img src={getPhotoImagePath(journey.coverPhoto, 'medium')} srcSet={getPhotoImageSrcSet(journey.coverPhoto)} sizes="(max-width: 600px) 46vw, (max-width: 900px) 30vw, (max-width: 1199px) 23vw, 250px" loading="lazy" decoding="async" alt={getPhotoAlt(journey.coverPhoto, `${journey.title}攝影作品`)} /></div>
        <div className="destination-caption"><h2>{journey.title}</h2><span>{journey.count} 張作品</span></div>
      </button>)}
    </div>
    {!journeys.length && <p>暫無目的地圖集</p>}
  </main>;
}

function GearExploreRail({ latestJourneys, theme, isDarkMode, query, onQueryChange, onSelectJourney, onExploreEdc, alignOffset = 0 }) {
  const normalizedQuery = normalizeSearchValue(query);
  const visibleJourneys = latestJourneys.filter(journey => !normalizedQuery || normalizeSearchValue([journey.title, journey.desc].filter(Boolean).join(' ')).includes(normalizedQuery)).slice(0, 5);
  return <aside aria-label="Gear 探索欄" style={{ alignSelf: 'stretch', minHeight: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: onExploreEdc ? '76px' : 0 }}>
    {onExploreEdc && <section style={{ width: '100%', overflow: 'hidden', borderRadius: '18px', border: `1px solid ${theme.cardBorder}`, background: theme.cardBg, boxShadow: isDarkMode ? '0 10px 28px rgba(0,0,0,.28)' : '0 10px 28px rgba(0,0,0,.06)' }}><p style={{ margin: '15px 16px 6px', color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.08em' }}>繼續探索</p><button type="button" onClick={onExploreEdc} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 16px 16px', border: 'none', background: 'transparent', color: theme.textPrimary, textAlign: 'left', cursor: 'pointer' }}><strong style={{ fontSize: '15px', letterSpacing: '-.02em' }}>EDC 隨身裝備</strong><span style={{ color: theme.textSecondary, fontSize: '19px', lineHeight: 1 }}>›</span></button></section>}
    <div style={{ position: 'sticky', top: '94px', zIndex: 3, marginTop: onExploreEdc ? '32px' : alignOffset, display: 'flex', flexDirection: 'column', gap: '16px' }}><label style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: '9px', padding: '12px 15px', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', background: isDarkMode ? 'rgba(255,255,255,.055)' : 'rgba(255,255,255,.82)', color: theme.textSecondary }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.8-3.8" /></svg><input value={query} onChange={event => onQueryChange(event.target.value)} aria-label="搜尋 Gear 內容" placeholder="搜尋器材或地點" style={{ minWidth: 0, flex: 1, border: 'none', outline: 'none', background: 'transparent', color: theme.textPrimary, font: 'inherit', fontSize: '13px' }} /></label>
    <section style={{ padding: '18px', borderRadius: '20px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(255,255,255,.045)' : 'rgba(255,255,255,.7)', boxShadow: isDarkMode ? 'none' : '0 12px 32px rgba(0,0,0,.035)' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px', letterSpacing: '-.02em' }}>精選地點集</h3>
      {visibleJourneys.map((journey, index) => <button key={journey.id} type="button" onClick={() => onSelectJourney(journey)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: index === 0 ? '0 0 11px' : '11px 0', border: 'none', borderTop: index === 0 ? 'none' : `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, textAlign: 'left', cursor: 'pointer' }}><img src={getPhotoImagePath(journey.coverPhoto, 'thumbnail')} alt="" style={{ width: '42px', height: '42px', borderRadius: '11px', objectFit: 'cover', background: theme.inputBg }} /><span style={{ minWidth: 0, flex: 1 }}><strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>{journey.title}</strong><small style={{ color: theme.textSecondary, fontSize: '11px' }}>{journey.count} 張作品</small></span><span style={{ color: theme.textSecondary }}>›</span></button>)}
      {visibleJourneys.length === 0 && <p style={{ margin: 0, color: theme.textSecondary, fontSize: '13px' }}>{normalizedQuery ? '沒有符合的地點集。' : '尚未建立地點集。'}</p>}
    </section></div>
  </aside>;
}

function BroadcastSignalCanvas({ intensity }) {
  const canvasRef = useRef(null);
  const pulseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const width = 842;
    const height = 467;
    const openImage = new Image();
    let frameId = 0;
    let blinkTimer = 0;
    let closeTimer = 0;
    let isBlinking = false;
    let isDisposed = false;
    let frame = 0;

    canvas.width = width;
    canvas.height = height;
    openImage.src = `${process.env.PUBLIC_URL}/broadcast-portrait-open.png`;

    const scheduleBlink = () => {
      blinkTimer = window.setTimeout(() => {
        isBlinking = true;
        // 只用閉眼圖覆蓋眼睛附近，避免整張臉瞬間替換而顯得像貼圖跳幀。
        closeTimer = window.setTimeout(() => {
          isBlinking = false;
          scheduleBlink();
        }, 92);
      }, 8000 + Math.random() * 2000);
    };

    const drawSlicedImage = (image, offsetForLine) => {
      const sourceLineHeight = image.height / height;
      for (let y = 0; y < height; y += 2) {
        ctx.drawImage(image, 0, y * sourceLineHeight, image.width, sourceLineHeight * 2, offsetForLine(y), y, width, 2);
      }
    };

    const drawBlink = () => {
      // 閉眼素材和原圖五官位置不一致；直接在原圖正確眼位繪製短暫眼瞼，避免跳幀錯位。
      const eyes = [
        { x: width * 0.455, y: height * 0.445 },
        { x: width * 0.552, y: height * 0.438 },
      ];
      ctx.save();
      ctx.fillStyle = 'rgba(34, 4, 18, .92)';
      ctx.strokeStyle = 'rgba(18, 1, 9, .96)';
      ctx.lineWidth = 3;
      eyes.forEach(({ x, y }) => {
        ctx.beginPath();
        ctx.ellipse(x, y, width * 0.033, height * 0.012, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x - width * 0.024, y);
        ctx.quadraticCurveTo(x, y + height * 0.009, x + width * 0.024, y);
        ctx.stroke();
      });
      ctx.restore();
    };

    const render = () => {
      if (isDisposed) return;
      frame += 1;
      ctx.clearRect(0, 0, width, height);
      if (openImage.complete && openImage.naturalWidth) {
        if (isBlinking) {
          // 眨眼那一瞬停用橫向追蹤偏移，讓眼瞼與原始五官逐像素對齊。
          ctx.drawImage(openImage, 0, 0, width, height);
          drawBlink();
        } else {
          const rollY = (frame * (0.72 + intensity * 0.14)) % (height + 100) - 50;
          const severe = pulseRef.current > 0;
          drawSlicedImage(openImage, y => {
            const distance = Math.abs(y - rollY);
            const band = distance < 27 ? (27 - distance) / 27 : 0;
            let offset = Math.sin(y * 0.21 + frame * 0.16) * (7 + intensity * 2) * band;
            if (band > 0.55 && (y + frame) % 13 < 3) offset += (Math.random() - 0.5) * (18 + intensity * 8);
            if (severe && Math.random() < 0.34) offset += (Math.random() - 0.5) * (36 + intensity * 18);
            return offset;
          });
        }
      }
      if (pulseRef.current > 0) pulseRef.current -= 1;
      frameId = window.requestAnimationFrame(render);
    };

    scheduleBlink();
    frameId = window.requestAnimationFrame(render);
    return () => {
      isDisposed = true;
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(blinkTimer);
      window.clearTimeout(closeTimer);
    };
  }, [intensity]);

  return <canvas
    ref={canvasRef}
    className="broadcast-signal-canvas"
    aria-label="受干擾的影像訊號"
  />;
}

function BroadcastTakeover({ config = {}, preview = false, onExitPreview, password, setPassword, onRecoveryLogin }) {
  const [hasEnteredSignal, setHasEnteredSignal] = useState(false);
  const [signalStarted, setSignalStarted] = useState(false);
  const [audioActive, setAudioActive] = useState(false);
  const [showRecoveryLogin, setShowRecoveryLogin] = useState(false);
  const entryCodeClicksRef = useRef([]);
  const entryCodeTimerRef = useRef(null);
  const audioRef = useRef(null);
  const legacyTitle = !config.title || ['SIGNAL INTERRUPTED', '影像資料觀看告知'].includes(config.title);
  const legacyMessage = !config.message
    || config.message === 'ARCHIVE NODE IS NOT RESPONDING'
    || String(config.message).includes('來源不明的觀看記錄');
  const noticeTitle = legacyTitle ? '影像播放權限失效' : config.title;
  const noticeMessage = legacyMessage
    ? '此裝置正在接收一段未經審核的影像資料。\n目前的存取權限已失效，系統無法繼續播放。\n請勿關閉頁面。請聯絡網站管理員。'
    : config.message;
  const audioUrl = cleanImagePath(config.audioPath);

  const startSignal = async event => {
    event?.stopPropagation();
    setHasEnteredSignal(true);
    setSignalStarted(true);
    if (!config.audioEnabled || !audioUrl || !audioRef.current) return;
    audioRef.current.currentTime = 0;
    try {
      await audioRef.current.play();
    } catch (_) {
      setAudioActive(false);
    }
  };

  const revealRecoveryLogin = event => {
    event.stopPropagation();
    const now = Date.now();
    entryCodeClicksRef.current = [...entryCodeClicksRef.current.filter(timestamp => now - timestamp < 2600), now];
    window.clearTimeout(entryCodeTimerRef.current);
    entryCodeTimerRef.current = window.setTimeout(() => {
      const clickTimes = entryCodeClicksRef.current;
      const groups = [];
      clickTimes.forEach((timestamp, index) => {
        if (index === 0 || timestamp - clickTimes[index - 1] >= 600) groups.push(1);
        else groups[groups.length - 1] += 1;
      });
      if (groups.length === 2 && groups[0] === 2 && groups[1] === 3) setShowRecoveryLogin(true);
      entryCodeClicksRef.current = [];
    }, 1000);
  };

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
    };
  }, [audioUrl]);

  useEffect(() => () => window.clearTimeout(entryCodeTimerRef.current), []);

  const intensity = Math.max(1, Math.min(3, Number(config.intensity) || 2));
  if (!hasEnteredSignal) {
    return <main className="broadcast-entry-screen">
      {audioUrl && <audio ref={audioRef} src={audioUrl} loop preload="metadata" playsInline onPlay={() => setAudioActive(true)} onPause={() => setAudioActive(false)} onEnded={() => setAudioActive(false)} />}
      <section className="broadcast-entry-card">
        <span className="broadcast-entry-status">CONNECTION STATUS · <button type="button" className="broadcast-entry-code-button" aria-label={`狀態代碼 ${config.code || '404'}`} onClick={revealRecoveryLogin}>{config.code || '404'}</button></span>
        <h1>連線已逾時</h1>
        <p>遠端影像節點暫時沒有回應。<br />請重新建立連線。</p>
        <button type="button" className="broadcast-entry-reload" onClick={startSignal}>重新載入</button>
        {!preview && showRecoveryLogin && <form className="broadcast-entry-login" onSubmit={onRecoveryLogin} onClick={event => event.stopPropagation()}>
          <label>管理員驗證</label>
          <input type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder="輸入管理員密碼" autoFocus />
          <button type="submit">解除接管</button>
        </form>}
      </section>
      {preview && <button type="button" className="broadcast-exit" onClick={onExitPreview}>退出預覽</button>}
    </main>;
  }
  return <main className={`broadcast-takeover broadcast-portrait-takeover broadcast-intensity-${intensity} ${signalStarted ? 'signal-started' : 'signal-waiting'}`}>
    {audioUrl && <audio ref={audioRef} src={audioUrl} loop preload="metadata" playsInline onPlay={() => setAudioActive(true)} onPause={() => setAudioActive(false)} onEnded={() => setAudioActive(false)} />}
    <img className="broadcast-portrait-fallback" src={`${process.env.PUBLIC_URL}/broadcast-portrait-open.png`} alt="" aria-hidden="true" />
    <BroadcastSignalCanvas intensity={intensity} />
    <div className="broadcast-noise" aria-hidden="true" />
    <div className="broadcast-scanlines" aria-hidden="true" />
    <div className="broadcast-tracking" aria-hidden="true" />
    <div className="broadcast-moving-bars" aria-hidden="true" />

    <section className="broadcast-portrait-copy">
      <span className="broadcast-portrait-kicker">WARNING · {config.code || '404'} · ARCHIVE SIGNAL</span>
      <h1>{noticeTitle}</h1>
      <p>{noticeMessage}</p>
      <div className={`broadcast-audio-state ${audioActive ? 'active' : ''}`}>{config.audioEnabled && audioUrl ? audioActive ? 'SIGNAL AUDIO ACTIVE' : 'SIGNAL AUDIO UNAVAILABLE' : 'VISUAL SIGNAL ONLY'}</div>
    </section>

    {preview && <button type="button" className="broadcast-exit" onClick={event => { event.stopPropagation(); onExitPreview?.(); }}>退出預覽</button>}
  </main>;
}

function JourneyNotebook({ entries, photos = [], theme, isDarkMode, onBack, onOpenImages, onOpenRelatedWorks }) {
  const visible = entries.filter(entry => entry.isVisible !== false);
  const photoByImagePath = useMemo(() => new Map(photos.flatMap(photo => [photo.imagePath, photo.previewPath].filter(Boolean).map(imagePath => [imagePath, photo]))), [photos]);
  const getChapterTime = chapter => {
    if (chapter.showTime === false) return '';
    if (chapter.timeLabel) return chapter.timeLabel;
    const rawDate = photoByImagePath.get(chapter.images?.[0]?.original)?.shootingDate;
    const parsed = new Date(/^\d+$/.test(String(rawDate || '')) ? Number(rawDate) : rawDate);
    return Number.isNaN(parsed.getTime()) ? '' : `${String(parsed.getUTCHours()).padStart(2, '0')}:${String(parsed.getUTCMinutes()).padStart(2, '0')}`;
  };
  const groups = visible.reduce((result, entry) => {
    const key = Number(entry.sequenceNumber) || 1;
    if (!result.has(key)) result.set(key, []);
    result.get(key).push(entry);
    return result;
  }, new Map());
  const groupedEntries = [...groups.entries()].sort((a, b) => b[0] - a[0]);
  return <main className="journey-notebook" style={{ color: theme.textPrimary }}>
    <button type="button" onClick={onBack} className="journey-back" style={{ color: theme.textSecondary, borderColor: theme.cardBorder }}>← 返回首頁</button>
    <header className="journey-notebook-header">
      <p>JOURNEYS · 旅行與日常</p>
      <h1>作品之外，<br />那些沒有被安排的瞬間。</h1>
      <span>同行的人、路上的光，以及相機之外仍然值得留下的記錄。</span>
    </header>
    <div className="journey-group-list">
      {groupedEntries.map(([number, chapters]) => {
        return <article key={number} className="journey-group">
          <div className="journey-group-heading">
            <span>JOURNEY {String(number).padStart(2, '0')}</span>
            <small>{chapters.length} CHAPTER{chapters.length === 1 ? '' : 'S'}</small>
          </div>
          {chapters.sort((a, b) => Number(a.chapterOrder || 0) - Number(b.chapterOrder || 0)).map((chapter, chapterIndex) => {
            const imageUrls = (chapter.images || []).map(image => cleanImagePath(image.medium || image.hero || image.original)).filter(Boolean);
            const relatedCount = (chapter.relatedWorkLocations || []).length || (chapter.relatedPhotoIds || []).length;
            return <section key={chapter._id} className={`journey-chapter journey-layout-${chapterIndex % 3}`}>
              <div className="journey-copy">
                <div className="journey-sequence">{String(number).padStart(2, '0')}{chapter.sequenceSuffix ? ` · ${chapter.sequenceSuffix}` : ''}</div>
                <h2>{chapter.title}</h2>
                <div className="journey-meta">{[chapter.location, chapter.dateLabel, getChapterTime(chapter)].filter(Boolean).join(' · ')}</div>
                {chapter.body && <p>{chapter.body}</p>}
                {relatedCount > 0 && <button type="button" onClick={() => onOpenRelatedWorks?.(chapter)} style={{ marginTop: chapter.body ? '16px' : '14px', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 13px', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', background: isDarkMode ? 'rgba(10,132,255,.14)' : 'rgba(0,113,227,.08)', color: '#0071e3', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>查看關聯作品 <span aria-hidden="true">→</span><span style={{ padding: '1px 6px', borderRadius: '980px', background: 'rgba(0,113,227,.12)', fontSize: '10px' }}>{relatedCount}</span></button>}
              </div>
              {!!imageUrls.length && <div className={`journey-image-grid journey-images-${Math.min(imageUrls.length, 4)}`}>
                {imageUrls.slice(0, 4).map((url, index) => <button type="button" key={url} onClick={() => onOpenImages(imageUrls, index)}><img src={url} alt={`${chapter.location || chapter.title}旅行瞬間 ${index + 1}`} loading="lazy" />{index === 3 && imageUrls.length > 4 && <span>＋{imageUrls.length - 4}</span>}</button>)}
              </div>}
            </section>;
          })}
        </article>;
      })}
      {!groupedEntries.length && <div className="journey-empty" style={{ borderColor: theme.cardBorder, color: theme.textSecondary }}>第一段旅程正在整理中。</div>}
    </div>
  </main>;
}

function DraftWritingAssistant({ kind, imageCount = 0, onGenerate, isGenerating, theme, isDarkMode }) {
  const [note, setNote] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const startVoiceInput = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      alert('目前瀏覽器未提供語音輸入。Chrome 可直接使用；你也可以把語音轉成文字後貼到這裡。');
      return;
    }
    recognitionRef.current?.abort?.();
    const recognition = new Recognition();
    recognition.lang = 'zh-CN';
    recognition.interimResults = true;
    recognition.continuous = false;
    const initialNote = note;
    let finalText = '';
    recognition.onresult = event => {
      let interimText = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const text = event.results[index][0]?.transcript || '';
        if (event.results[index].isFinal) finalText += text;
        else interimText += text;
      }
      setNote(`${initialNote}${initialNote && (finalText || interimText) ? ' ' : ''}${finalText}${interimText}`.trim());
    };
    recognition.onerror = event => {
      if (event.error !== 'aborted') alert('語音輸入暫時無法使用，請改用文字輸入。');
    };
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    setIsListening(true);
    recognition.start();
  };
  useEffect(() => () => recognitionRef.current?.abort?.(), []);
  const title = kind === 'journey' ? 'M.ai 幫你整理這段旅程' : 'M.ai 幫你寫這則內容';
  const hint = kind === 'journey'
    ? '像聊天一樣說：今天去了哪、誰同行、發生了什麼、你想留下什麼感覺。'
    : '像聊天一樣說：這是什麼、你怎麼用、為什麼它對你有用。正文留空時會查公開資料補背景，但不會把網評當成你的心得。';
  return <section style={{ margin: '18px 0', padding: '14px', border: `1px solid ${isDarkMode ? 'rgba(10,132,255,.42)' : 'rgba(0,113,227,.28)'}`, borderRadius: '15px', background: isDarkMode ? 'rgba(10,132,255,.09)' : 'rgba(0,113,227,.045)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'baseline' }}><div><strong style={{ fontSize: '13px' }}>✦ {title}</strong><p style={{ margin: '4px 0 0', color: theme.textSecondary, fontSize: '11px', lineHeight: 1.5 }}>{hint}{imageCount ? ` 已選的 ${imageCount} 張圖片也會作為參考。` : ''}</p></div><span style={{ flex: 'none', color: theme.textSecondary, fontSize: '10px' }}>PRIMARY</span></div>
    <textarea rows="3" value={note} onChange={event => setNote(event.target.value)} placeholder="不必寫得完整，講人話就行。" style={{ width: '100%', boxSizing: 'border-box', marginTop: '11px', padding: '10px 11px', border: `1px solid ${theme.inputBorder}`, borderRadius: '10px', resize: 'vertical', background: theme.inputBg, color: theme.textPrimary, font: 'inherit', fontSize: '13px', lineHeight: 1.55, outline: 'none' }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '9px' }}><button type="button" onClick={startVoiceInput} disabled={isGenerating} style={{ border: 'none', background: 'transparent', color: isListening ? '#ff453a' : '#0071e3', padding: '5px 0', fontSize: '12px', fontWeight: '650', cursor: isGenerating ? 'not-allowed' : 'pointer' }}>{isListening ? '● 正在聽…點一下說完' : '🎙 語音輸入'}</button><button type="button" onClick={() => onGenerate(note)} disabled={isGenerating} style={{ border: 'none', borderRadius: '980px', padding: '8px 13px', background: '#0071e3', color: '#fff', fontSize: '12px', fontWeight: '650', cursor: isGenerating ? 'not-allowed' : 'pointer', opacity: isGenerating ? .65 : 1 }}>{isGenerating ? 'Qwen 整理中…' : '生成並填入草稿'}</button></div>
  </section>;
}

function JourneyCandidatePhotoPicker({ candidatePhotos, candidateIds, ordered, onToggle, publishedCandidatePhotoIds, theme }) {
  const [showPublished, setShowPublished] = useState(false);
  const publishedIds = publishedCandidatePhotoIds || new Set();
  const isSelected = photo => candidateIds.includes(String(photo._id));
  const isPublished = photo => publishedIds.has(String(photo._id));
  const unpublishedPhotos = candidatePhotos.filter(photo => !isPublished(photo) || isSelected(photo));
  const publishedPhotos = candidatePhotos.filter(photo => isPublished(photo) && !isSelected(photo));
  const renderPhoto = photo => {
    const selected = isSelected(photo);
    return <button key={photo._id} type="button" onClick={() => onToggle(photo)} disabled={!selected && ordered.length >= 9} title={photo.title || photo.location || '待選照片'} style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden', padding: 0, borderRadius: '10px', border: `2px solid ${selected ? '#0071e3' : theme.cardBorder}`, background: theme.inputBg, cursor: !selected && ordered.length >= 9 ? 'not-allowed' : 'pointer', opacity: !selected && ordered.length >= 9 ? .48 : 1 }}><img src={getPhotoImagePath(photo, 'thumbnail')} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />{selected && <span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,113,227,.38)', color: '#fff', fontSize: '18px', fontWeight: 700 }}>✓</span>}</button>;
  };

  if (!candidatePhotos.length) return null;
  return <section style={{ marginTop: '16px', paddingTop: '15px', borderTop: `1px solid ${theme.cardBorder}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '9px' }}><strong style={{ fontSize: '13px' }}>Journeys 待選照片</strong><span style={{ color: theme.textSecondary, fontSize: '11px' }}>未發佈照片優先顯示</span></div>
    {unpublishedPhotos.length > 0 ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '7px' }}>{unpublishedPhotos.map(renderPhoto)}</div> : <p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px' }}>目前沒有尚未發佈的照片。</p>}
    {publishedPhotos.length > 0 && <div style={{ marginTop: '13px', paddingTop: '12px', borderTop: `1px solid ${theme.cardBorder}` }}>
      <button type="button" onClick={() => setShowPublished(previous => !previous)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0, border: 'none', background: 'transparent', color: theme.textPrimary, cursor: 'pointer' }}><span style={{ fontSize: '12px', fontWeight: 650 }}>已發佈照片（{publishedPhotos.length}）</span><span style={{ color: '#0071e3', fontSize: '12px', fontWeight: 650 }}>{showPublished ? '收起' : '展開全部'}</span></button>
      {showPublished && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '7px', marginTop: '10px' }}>{publishedPhotos.map(renderPhoto)}</div>}
    </div>}
  </section>;
}

function JourneyRelatedWorkPicker({ workPhotos, relatedWorkLocations, onToggle, theme }) {
  const [activeProvinceKey, setActiveProvinceKey] = useState('');
  const [activeCityKey, setActiveCityKey] = useState('');
  const [activeDistrictKey, setActiveDistrictKey] = useState('');
  const selected = relatedWorkLocations || [];
  const selectedPrefixes = new Set(selected.map(item => item.prefix));
  const locationTree = useMemo(() => {
    const roots = new Map();
    const sortedEntries = map => Array.from(map.values()).sort((left, right) => right.count - left.count || left.title.localeCompare(right.title, 'zh-Hans-CN'));
    const makeNode = (key, title) => ({ key, title, count: 0, children: new Map() });

    (workPhotos || []).forEach(photo => {
      const parts = String(photo.location || '').split(/[·•]/).map(part => part.trim()).filter(Boolean).slice(0, 4);
      if (!parts.length) return;
      const [province, city, district, location] = parts;
      if (!roots.has(province)) roots.set(province, makeNode(province, province));
      const provinceNode = roots.get(province);
      provinceNode.count += 1;
      if (!city) return;
      const cityKey = `${province}·${city}`;
      if (!provinceNode.children.has(cityKey)) provinceNode.children.set(cityKey, makeNode(cityKey, city));
      const cityNode = provinceNode.children.get(cityKey);
      cityNode.count += 1;
      if (!district) return;
      const districtKey = `${cityKey}·${district}`;
      if (!cityNode.children.has(districtKey)) cityNode.children.set(districtKey, makeNode(districtKey, district));
      const districtNode = cityNode.children.get(districtKey);
      districtNode.count += 1;
      if (!location) return;
      const locationKey = `${districtKey}·${location}`;
      if (!districtNode.children.has(locationKey)) districtNode.children.set(locationKey, makeNode(locationKey, location));
      districtNode.children.get(locationKey).count += 1;
    });

    return sortedEntries(roots).map(province => ({
      ...province,
      children: sortedEntries(province.children).map(city => ({
        ...city,
        children: sortedEntries(city.children).map(district => ({ ...district, children: sortedEntries(district.children) }))
      }))
    }));
  }, [workPhotos]);
  const activeProvince = locationTree.find(item => item.key === activeProvinceKey) || locationTree[0] || null;
  const activeCity = activeProvince?.children?.find(item => item.key === activeCityKey) || activeProvince?.children?.[0] || null;
  const activeDistrict = activeCity?.children?.find(item => item.key === activeDistrictKey) || activeCity?.children?.[0] || null;
  const scopeFor = (level, item) => ({ level, prefix: item.key, label: item.key });
  const scopeButton = (scope, text) => <button type="button" onClick={() => onToggle(scope)} style={{ flex: 'none', padding: '5px 8px', border: `1px solid ${selectedPrefixes.has(scope.prefix) ? '#0071e3' : theme.cardBorder}`, borderRadius: '8px', background: selectedPrefixes.has(scope.prefix) ? 'rgba(0,113,227,.11)' : 'transparent', color: selectedPrefixes.has(scope.prefix) ? '#0071e3' : theme.textSecondary, fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>{selectedPrefixes.has(scope.prefix) ? '已關聯' : text}</button>;
  const selectProvince = province => { setActiveProvinceKey(province.key); setActiveCityKey(province.children?.[0]?.key || ''); setActiveDistrictKey(province.children?.[0]?.children?.[0]?.key || ''); };
  const selectCity = city => { setActiveCityKey(city.key); setActiveDistrictKey(city.children?.[0]?.key || ''); };

  return <details style={{ marginTop: '16px', paddingTop: '15px', borderTop: `1px solid ${theme.cardBorder}` }}>
    <summary style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', color: theme.textPrimary, fontSize: '13px', fontWeight: 650, cursor: 'pointer' }}><span>關聯作品集</span><span style={{ color: selected.length ? '#0071e3' : theme.textSecondary, fontSize: '12px' }}>{selected.length ? `已關聯 ${selected.length} 組` : '省份 → 城市 → 區域 → 景點'}</span></summary>
    <p style={{ margin: '9px 0 12px', color: theme.textSecondary, fontSize: '11px', lineHeight: 1.5 }}>關聯的是已整理好的 Work 作品集範圍，不是單張圖片。每一級右邊按「＋」即可加入；舊資料只有三級時，第三欄就是最細作品集。</p>
    {selected.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '12px' }}>{selected.map(scope => <button key={scope.prefix} type="button" onClick={() => onToggle(scope)} style={{ border: 'none', borderRadius: '980px', padding: '6px 9px', background: 'rgba(0,113,227,.11)', color: '#0071e3', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>{scope.label} ×</button>)}</div>}
    {!locationTree.length ? <p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px' }}>先在 Work 建立有地點的作品，這裡才會出現作品集。</p> : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '9px' }}>
      <section style={{ minWidth: 0, padding: '10px', borderRadius: '12px', background: theme.inputBg }}><strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', color: theme.textSecondary }}>省份</strong><div style={{ display: 'grid', gap: '6px' }}>{locationTree.map(province => <div key={province.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><button type="button" onClick={() => selectProvince(province)} style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px 5px', border: 'none', borderRadius: '7px', background: activeProvince?.key === province.key ? (theme.cardBg || '#fff') : 'transparent', color: theme.textPrimary, textAlign: 'left', fontSize: '12px', cursor: 'pointer' }}>{province.title}<small style={{ marginLeft: '4px', color: theme.textSecondary }}>{province.count}</small></button>{scopeButton(scopeFor('province', province), '＋')}</div>)}</div></section>
      <section style={{ minWidth: 0, padding: '10px', borderRadius: '12px', background: theme.inputBg }}><strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', color: theme.textSecondary }}>城市</strong>{activeProvince ? <div style={{ display: 'grid', gap: '6px' }}>{(activeProvince.children || []).map(city => <div key={city.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><button type="button" onClick={() => selectCity(city)} style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px 5px', border: 'none', borderRadius: '7px', background: activeCity?.key === city.key ? (theme.cardBg || '#fff') : 'transparent', color: theme.textPrimary, textAlign: 'left', fontSize: '12px', cursor: 'pointer' }}>{city.title}<small style={{ marginLeft: '4px', color: theme.textSecondary }}>{city.count}</small></button>{scopeButton(scopeFor('city', city), '＋')}</div>)}</div> : <p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px' }}>先選省份</p>}</section>
      <section style={{ minWidth: 0, padding: '10px', borderRadius: '12px', background: theme.inputBg }}><strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', color: theme.textSecondary }}>區縣／區域</strong>{activeCity ? <div style={{ display: 'grid', gap: '6px' }}>{(activeCity.children || []).map(district => <div key={district.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><button type="button" onClick={() => setActiveDistrictKey(district.key)} style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px 5px', border: 'none', borderRadius: '7px', background: activeDistrict?.key === district.key ? (theme.cardBg || '#fff') : 'transparent', color: theme.textPrimary, textAlign: 'left', fontSize: '12px', cursor: 'pointer' }}>{district.title}<small style={{ marginLeft: '4px', color: theme.textSecondary }}>{district.count}</small></button>{scopeButton(scopeFor('district', district), '＋')}</div>)}</div> : <p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px' }}>先選城市</p>}</section>
      <section style={{ gridColumn: '1 / -1', minWidth: 0, padding: '10px', borderRadius: '12px', background: theme.inputBg }}><strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', color: theme.textSecondary }}>具體景點（如有）</strong>{activeDistrict?.children?.length ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px' }}>{activeDistrict.children.map(location => <div key={location.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px 5px', color: theme.textPrimary, fontSize: '12px' }}>{location.title}<small style={{ marginLeft: '4px', color: theme.textSecondary }}>{location.count}</small></span>{scopeButton(scopeFor('location', location), '＋')}</div>)}</div> : <p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px' }}>目前這個區域就是最細作品集，可直接在上方點「＋」關聯。</p>}</section>
    </div>}
  </details>;
}

function PostImageOrderModal({ draft, setDraft, kind, onClose, theme, isDarkMode, candidatePhotos = [], publishedCandidatePhotoIds }) {
  const inputRef = useRef(null);
  const isGear = kind === 'gear';
  const existing = Array.isArray(draft.images) ? draft.images : [];
  const files = isGear ? (draft.files?.images || []) : (draft.files || []);
  const ids = Array.isArray(draft.pendingImageIds) && draft.pendingImageIds.length === files.length ? draft.pendingImageIds : files.map((_, index) => `legacy-${index}`);
  const candidateIds = !isGear && Array.isArray(draft.candidatePhotoIds) ? draft.candidatePhotoIds.map(String) : [];
  const candidateById = new Map(candidatePhotos.map(photo => [String(photo._id), photo]));
  const removedMarkers = draft.removedImageMarkers || [];
  const allMarkers = [...existing.map((_, index) => `old:${index}`), ...candidateIds.map(id => `candidate:${id}`), ...ids.map(id => `new:${id}`)].filter(marker => !removedMarkers.includes(marker));
  const ordered = [...new Set([...(draft.imageOrder || []), ...allMarkers])].filter(marker => allMarkers.includes(marker));
  const previews = draft.pendingImagePreviews || {};
  const dragged = useRef('');
  const write = (nextOrder, nextFiles = files, nextIds = ids, nextPreviews = previews, nextCandidateIds = candidateIds, nextPendingImageCaptureTimes = draft.pendingImageCaptureTimes || {}) => {
    const next = { ...draft, imageOrder: nextOrder, pendingImageIds: nextIds, pendingImagePreviews: nextPreviews, ...(!isGear ? { candidatePhotoIds: nextCandidateIds, pendingImageCaptureTimes: nextPendingImageCaptureTimes } : {}) };
    next.files = isGear ? { ...(draft.files || {}), images: nextFiles } : nextFiles;
    setDraft(next);
  };
  const move = (marker, delta) => {
    const from = ordered.indexOf(marker); const to = from + delta;
    if (from < 0 || to < 0 || to >= ordered.length) return;
    const next = [...ordered]; [next[from], next[to]] = [next[to], next[from]]; write(next);
  };
  const addFiles = async event => {
    const selected = Array.from(event.target.files || []).slice(0, Math.max(0, 9 - ordered.length));
    if (!selected.length) return;
    const nextIds = selected.map((_, index) => `n-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`);
    const nextPreviews = { ...previews };
    nextIds.forEach((id, index) => { nextPreviews[id] = URL.createObjectURL(selected[index]); });
    const nextPendingImageCaptureTimes = { ...(draft.pendingImageCaptureTimes || {}) };
    if (!isGear) {
      const captureTimes = await Promise.all(selected.map(file => new Promise(resolve => {
        EXIF.getData(file, function () {
          resolve(parseExifShootingDate(EXIF.getTag(this, 'DateTimeOriginal') || EXIF.getTag(this, 'DateTime')));
        });
      })));
      nextIds.forEach((id, index) => { if (captureTimes[index]) nextPendingImageCaptureTimes[id] = captureTimes[index]; });
    }
    write([...ordered, ...nextIds.map(id => `new:${id}`)], [...files, ...selected], [...ids, ...nextIds], nextPreviews, candidateIds, nextPendingImageCaptureTimes);
    event.target.value = '';
  };
  const toggleCandidate = photo => {
    const id = String(photo._id);
    const marker = `candidate:${id}`;
    if (candidateIds.includes(id)) {
      write(ordered.filter(value => value !== marker), files, ids, previews, candidateIds.filter(value => value !== id));
      return;
    }
    if (ordered.length >= 9) return;
    write([...ordered, marker], files, ids, previews, [...candidateIds, id]);
  };
  const removeImage = marker => {
    if (marker.startsWith('old:')) {
      setDraft({ ...draft, imageOrder: ordered.filter(value => value !== marker), removedImageMarkers: [...new Set([...removedMarkers, marker])] });
      return;
    }
    if (marker.startsWith('candidate:')) {
      const id = marker.slice(10);
      write(ordered.filter(value => value !== marker), files, ids, previews, candidateIds.filter(value => value !== id));
      return;
    }
    const id = marker.slice(4); const index = ids.indexOf(id); if (index < 0) return;
    const nextPreviews = { ...previews }; if (nextPreviews[id]) URL.revokeObjectURL(nextPreviews[id]); delete nextPreviews[id];
    const nextPendingImageCaptureTimes = { ...(draft.pendingImageCaptureTimes || {}) }; delete nextPendingImageCaptureTimes[id];
    write(ordered.filter(value => value !== marker), files.filter((_, itemIndex) => itemIndex !== index), ids.filter((_, itemIndex) => itemIndex !== index), nextPreviews, candidateIds, nextPendingImageCaptureTimes);
  };
  const imageSource = marker => {
    if (marker.startsWith('old:')) { const image = existing[Number(marker.slice(4))] || {}; return image.medium || image.thumbnail || image.original || ''; }
    if (marker.startsWith('candidate:')) return getPhotoImagePath(candidateById.get(marker.slice(10)), 'thumbnail');
    return previews[marker.slice(4)] || '';
  };
  return <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 22000, display: 'grid', placeItems: 'center', padding: '18px', background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(14px)' }}>
    <section onMouseDown={event => event.stopPropagation()} style={{ width: 'min(680px, 100%)', maxHeight: '86dvh', overflowY: 'auto', padding: '24px', borderRadius: '24px', background: isDarkMode ? '#1c1c1e' : '#fff', color: theme.textPrimary }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: 700, letterSpacing: '.09em' }}>POST PHOTOS</p><h3 style={{ margin: '6px 0 0', fontSize: '22px' }}>排列帖子圖片</h3><p style={{ margin: '7px 0 0', color: theme.textSecondary, fontSize: '12px' }}>可拖曳或用箭頭排序；第 1 張就是封面。</p></div><button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', color: theme.textSecondary, fontSize: '22px', cursor: 'pointer' }}>×</button></div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={addFiles} style={{ display: 'none' }} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={ordered.length >= 9} style={{ marginTop: '18px', width: '100%', padding: '11px 14px', borderRadius: '13px', border: `1px dashed ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, textAlign: 'left', cursor: ordered.length >= 9 ? 'not-allowed' : 'pointer' }}>＋ 從電腦加入照片 <span style={{ float: 'right', color: theme.textSecondary }}>{ordered.length} / 9</span></button>
      {!isGear && <JourneyCandidatePhotoPicker candidatePhotos={candidatePhotos} candidateIds={candidateIds} ordered={ordered} onToggle={toggleCandidate} publishedCandidatePhotoIds={publishedCandidatePhotoIds} theme={theme} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px', marginTop: '14px' }}>{ordered.map((marker, index) => <div key={marker} draggable onDragStart={() => { dragged.current = marker; }} onDragOver={event => event.preventDefault()} onDrop={() => { const from = ordered.indexOf(dragged.current); const to = ordered.indexOf(marker); if (from < 0 || to < 0 || from === to) return; const next = [...ordered]; next.splice(to, 0, next.splice(from, 1)[0]); write(next); }} style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden', borderRadius: '13px', border: `2px solid ${index === 0 ? '#0071e3' : theme.cardBorder}`, background: theme.inputBg, cursor: 'grab' }}>{imageSource(marker) ? <img src={imageSource(marker)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: theme.textSecondary, fontSize: '11px' }}>圖片預覽</div>}<span style={{ position: 'absolute', top: '6px', left: '6px', padding: '3px 6px', borderRadius: '8px', color: '#fff', background: index === 0 ? '#0071e3' : 'rgba(0,0,0,.58)', fontSize: '10px', fontWeight: 700 }}>{index === 0 ? '封面' : index + 1}</span><div style={{ position: 'absolute', right: '5px', bottom: '5px', display: 'flex', gap: '3px' }}><button type="button" onClick={() => move(marker, -1)} disabled={index === 0} style={{ border: 'none', borderRadius: '7px', background: 'rgba(0,0,0,.62)', color: '#fff', width: '25px', height: '25px', cursor: 'pointer' }}>←</button><button type="button" onClick={() => move(marker, 1)} disabled={index === ordered.length - 1} style={{ border: 'none', borderRadius: '7px', background: 'rgba(0,0,0,.62)', color: '#fff', width: '25px', height: '25px', cursor: 'pointer' }}>→</button><button type="button" onClick={() => removeImage(marker)} style={{ border: 'none', borderRadius: '7px', background: 'rgba(195,20,30,.86)', color: '#fff', width: '25px', height: '25px', cursor: 'pointer' }}>×</button></div></div>)}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}><button type="button" onClick={onClose} style={{ border: 'none', borderRadius: '980px', padding: '10px 17px', background: '#0071e3', color: '#fff', fontWeight: 650, cursor: 'pointer' }}>完成排序</button></div>
    </section>
  </div>;
}

// 保留舊表格式編輯器作為本機回退；目前發佈流程使用下方的朋友圈式 JourneyEditor。
// eslint-disable-next-line no-unused-vars
function JourneyEditorLegacy({ draft, setDraft, onClose, onSave, onGenerateCopy, isSaving, isGeneratingCopy, theme, isDarkMode }) {
  const photoInputRef = useRef(null);
  if (!draft) return null;
  const fieldStyle = { width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit' };
  return <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 21000, display: 'grid', placeItems: 'center', padding: '22px', background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(18px)' }}>
    <section onClick={event => event.stopPropagation()} style={{ width: 'min(680px, 94vw)', maxHeight: '90vh', overflowY: 'auto', padding: '28px', borderRadius: '26px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? '#171719' : '#fff', boxShadow: '0 30px 90px rgba(0,0,0,.4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: 700, letterSpacing: '.1em' }}>JOURNEY EDITOR</p><h2 style={{ margin: '7px 0 0', fontSize: '25px' }}>{draft._id ? '編輯旅行章節' : '新增旅行章節'}</h2></div><button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', color: theme.textSecondary, fontSize: '22px', cursor: 'pointer' }}>×</button></div>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '14px' }}>
        <label><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>JOURNEY 編號</span><input type="number" min="1" value={draft.sequenceNumber || ''} onChange={event => setDraft({ ...draft, sequenceNumber: event.target.value })} style={fieldStyle} /></label>
        <label><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>後綴（可重複編號）</span><input value={draft.sequenceSuffix || ''} onChange={event => setDraft({ ...draft, sequenceSuffix: event.target.value })} placeholder="例如：番外 / EXTRA" style={fieldStyle} /></label>
      </div>
      <label style={{ display: 'block', marginTop: '14px' }}><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>章節標題</span><input value={draft.title || ''} onChange={event => setDraft({ ...draft, title: event.target.value })} placeholder="抵達成都的晚上" style={fieldStyle} /></label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '14px' }}>
        <label><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>地點</span><input value={draft.location || ''} onChange={event => setDraft({ ...draft, location: event.target.value })} placeholder="四川 · 成都" style={fieldStyle} /></label>
        <label><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>日期文字</span><input value={draft.dateLabel || ''} onChange={event => setDraft({ ...draft, dateLabel: event.target.value })} placeholder="2026.08.18" style={fieldStyle} /></label>
      </div>
      <label style={{ display: 'block', marginTop: '14px' }}><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>旅行文字</span><textarea rows="5" value={draft.body || ''} onChange={event => setDraft({ ...draft, body: event.target.value })} placeholder="写下当时发生了什么，而不是描述照片本身。" style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.6 }} /></label>
      <DraftWritingAssistant kind="journey" imageCount={(draft.files?.length || draft.images?.length || 0)} onGenerate={onGenerateCopy} isGenerating={isGeneratingCopy} theme={theme} isDarkMode={isDarkMode} />
      <div style={{ marginTop: '14px' }}>
        <span style={{ display: 'block', marginBottom: '7px', color: theme.textSecondary, fontSize: '12px' }}>照片（最多 12 張／次）</span>
        <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={event => setDraft({ ...draft, files: [...(event.target.files || [])].slice(0, 12) })} style={{ display: 'none' }} />
        <button type="button" onClick={() => photoInputRef.current?.click()} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', padding: '12px 14px', borderRadius: '13px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, cursor: 'pointer', textAlign: 'left' }}><strong style={{ fontSize: '13px', fontWeight: 650 }}>选择照片</strong><span style={{ color: theme.textSecondary, fontSize: '12px' }}>{draft.files?.length ? `已选择 ${draft.files.length} 张` : draft.images?.length ? `现有 ${draft.images.length} 张 · 可继续添加` : '尚未选择'}</span></button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', marginTop: '22px' }}><label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.textSecondary, fontSize: '13px' }}><input type="checkbox" checked={draft.isVisible !== false} onChange={event => setDraft({ ...draft, isVisible: event.target.checked })} />前台顯示</label><div style={{ display: 'flex', gap: '10px' }}><button type="button" onClick={onClose} style={{ padding: '10px 16px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, cursor: 'pointer' }}>取消</button><button type="button" onClick={onSave} disabled={isSaving} style={{ padding: '10px 18px', borderRadius: '980px', border: 'none', background: '#0071e3', color: '#fff', fontWeight: 650, cursor: 'pointer' }}>{isSaving ? '儲存中…' : '儲存章節'}</button></div></div>
    </section>
  </div>;
}

function JourneyPhotoDateTimeField({ draft, setDraft, candidatePhotos, theme }) {
  const candidateById = useMemo(() => new Map(candidatePhotos.map(photo => [String(photo._id), photo])), [candidatePhotos]);
  const candidateByImagePath = useMemo(() => new Map(candidatePhotos.flatMap(photo => [photo.imagePath, photo.previewPath].filter(Boolean).map(imagePath => [imagePath, photo]))), [candidatePhotos]);
  const today = new Date();
  const defaultDate = `${today.getFullYear()}.${today.getMonth() + 1}.${today.getDate()}`;
  const fieldStyle = { width: '100%', boxSizing: 'border-box', padding: '12px 13px', borderRadius: '13px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit' };
  const photoCaptureOptions = useMemo(() => {
    const allMarkers = [...(draft.images || []).map((_, index) => `old:${index}`), ...(draft.candidatePhotoIds || []).map(id => `candidate:${id}`), ...(draft.pendingImageIds || []).map(id => `new:${id}`)]
      .filter(marker => !(draft.removedImageMarkers || []).includes(marker));
    const orderedMarkers = [...new Set([...(draft.imageOrder || []), ...allMarkers])].filter(marker => allMarkers.includes(marker));
    const seenPhotos = new Set();
    return orderedMarkers.map(marker => {
      const oldImage = marker.startsWith('old:') ? draft.images?.[Number(marker.slice(4))] : null;
      const sourceId = marker.startsWith('candidate:')
        ? marker.slice(10)
        : marker.startsWith('new:') ? marker.slice(4) : oldImage?.sourcePhotoId || `legacy:${oldImage?.original || ''}`;
      if (!sourceId || sourceId === 'legacy:' || seenPhotos.has(String(sourceId))) return null;
      seenPhotos.add(String(sourceId));
      const rawDate = marker.startsWith('new:')
        ? draft.pendingImageCaptureTimes?.[sourceId]
        : candidateById.get(String(sourceId))?.shootingDate || candidateByImagePath.get(oldImage?.original)?.shootingDate;
      if (rawDate === undefined || rawDate === null || rawDate === '') return null;
      const parsed = new Date(/^\d+$/.test(String(rawDate)) ? Number(rawDate) : rawDate);
      if (Number.isNaN(parsed.getTime())) return null;
      return {
        timestamp: parsed.getTime(),
        dateLabel: `${parsed.getUTCFullYear()}.${parsed.getUTCMonth() + 1}.${parsed.getUTCDate()}`,
        timeLabel: `${String(parsed.getUTCHours()).padStart(2, '0')}:${String(parsed.getUTCMinutes()).padStart(2, '0')}`
      };
    }).filter(Boolean);
  }, [candidateById, candidateByImagePath, draft.candidatePhotoIds, draft.imageOrder, draft.images, draft.pendingImageCaptureTimes, draft.pendingImageIds, draft.removedImageMarkers]);
  const journeyDateOptions = useMemo(() => [...new Map(photoCaptureOptions.map(option => [option.dateLabel, option.timestamp])).entries()]
    .sort(([, firstTimestamp], [, secondTimestamp]) => firstTimestamp - secondTimestamp)
    .map(([label]) => label), [photoCaptureOptions]);
  const journeyTimeOptions = useMemo(() => [...new Map(photoCaptureOptions.map(option => [option.timeLabel, option])).values()], [photoCaptureOptions]);
  const dateSelection = journeyDateOptions.includes(draft.dateLabel) ? draft.dateLabel : '__custom';
  const timeSelection = draft.showTime === false ? '__hidden' : (draft.timeMode === 'auto' && journeyTimeOptions.length ? '__auto' : (journeyTimeOptions.some(option => option.timeLabel === draft.timeLabel) ? draft.timeLabel : '__custom'));

  useEffect(() => {
    if (!journeyDateOptions.length) return;
    setDraft(previous => {
      if (!previous) return previous;
      const currentDate = String(previous.dateLabel || '');
      if (journeyDateOptions.includes(currentDate)) return previous;
      if (!currentDate || currentDate === defaultDate) return { ...previous, dateLabel: journeyDateOptions[0] };
      return previous;
    });
  }, [journeyDateOptions, defaultDate, setDraft]);

  useEffect(() => {
    const coverCapture = photoCaptureOptions[0];
    if (!coverCapture) return;
    setDraft(previous => {
      if (!previous || previous.showTime === false || (previous.timeMode !== 'auto' && previous.timeLabel)) return previous;
      return { ...previous, showTime: true, timeLabel: coverCapture.timeLabel, timeMode: 'auto' };
    });
  }, [photoCaptureOptions, setDraft]);

  return <div style={{ display: 'grid', gap: '12px' }}>
    <label><span style={{ display: 'block', marginBottom: '7px', color: theme.textSecondary, fontSize: '12px' }}>{journeyDateOptions.length ? `日期 · 照片中找到 ${journeyDateOptions.length} 個` : '日期'}</span>{journeyDateOptions.length > 0 && <select value={dateSelection} onChange={event => setDraft({ ...draft, dateLabel: event.target.value === '__custom' ? '' : event.target.value })} style={{ ...fieldStyle, cursor: 'pointer' }}>{journeyDateOptions.map(date => <option key={date} value={date}>{date}</option>)}<option value="__custom">手動輸入日期…</option></select>}{(!journeyDateOptions.length || dateSelection === '__custom') && <input value={draft.dateLabel || ''} onChange={event => setDraft({ ...draft, dateLabel: event.target.value })} placeholder={defaultDate} style={{ ...fieldStyle, marginTop: journeyDateOptions.length ? '8px' : 0 }} />}</label>
    <label><span style={{ display: 'block', marginBottom: '7px', color: theme.textSecondary, fontSize: '12px' }}>{journeyTimeOptions.length ? `時間 · 封面預設 ${journeyTimeOptions[0].timeLabel}` : '時間 · 未讀取到照片拍攝時間'}</span>{journeyTimeOptions.length > 0 && <select value={timeSelection} onChange={event => { const nextValue = event.target.value; if (nextValue === '__auto') return setDraft({ ...draft, showTime: true, timeLabel: journeyTimeOptions[0].timeLabel, timeMode: 'auto' }); setDraft({ ...draft, showTime: nextValue !== '__hidden', timeLabel: nextValue === '__hidden' || nextValue === '__custom' ? '' : nextValue, timeMode: nextValue === '__hidden' ? 'hidden' : nextValue === '__custom' ? 'manual' : 'photo' }); }} style={{ ...fieldStyle, cursor: 'pointer' }}><option value="__auto">自動 · 封面 {journeyTimeOptions[0].timeLabel}</option>{journeyTimeOptions.slice(1).map(option => <option key={option.timeLabel} value={option.timeLabel}>{option.timeLabel} · 其他照片</option>)}<option value="__hidden">不顯示時間</option><option value="__custom">手動輸入時間…</option></select>}{(!journeyTimeOptions.length || timeSelection === '__custom') && <input type="time" value={draft.timeLabel || ''} onChange={event => setDraft({ ...draft, showTime: true, timeLabel: event.target.value, timeMode: 'manual' })} style={{ ...fieldStyle, marginTop: journeyTimeOptions.length ? '8px' : 0 }} />}{journeyTimeOptions.length > 1 && <small style={{ display: 'block', marginTop: '6px', color: theme.textSecondary, fontSize: '10px', lineHeight: 1.4 }}>每次打開會先回到封面自動時間；也可改選其他照片或不顯示。</small>}</label>
  </div>;
}

function JourneyPhotoLibraryGrid({ photos, theme, isDarkMode, onMoveToWork, onDelete }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(138px, 1fr))', gap: '11px' }}>
    {photos.map(photo => <article key={photo._id} style={{ overflow: 'hidden', borderRadius: '12px', border: `1px solid ${theme.cardBorder}`, background: theme.cardBg }}>
      <img src={getPhotoImagePath(photo, 'thumbnail')} alt={getPhotoAlt(photo)} style={{ display: 'block', width: '100%', aspectRatio: '1.2', objectFit: 'cover', background: theme.cardBorder }} />
      <div style={{ padding: '9px' }}>
        <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }}>📍 {photo.location || '未填地點'}</strong>
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          <button type="button" onClick={() => onMoveToWork(photo)} style={{ flex: 1, padding: '6px 4px', border: 'none', borderRadius: '7px', background: isDarkMode ? 'rgba(255,255,255,.09)' : 'rgba(0,0,0,.05)', color: theme.textPrimary, fontSize: '10px', cursor: 'pointer' }}>手動移回 Work</button>
          <button type="button" onClick={() => onDelete(photo)} title="永久刪除原始照片" style={{ padding: '6px 7px', border: 'none', borderRadius: '7px', background: 'rgba(255,59,48,.12)', color: '#ff3b30', fontSize: '11px', cursor: 'pointer' }}>🗑</button>
        </div>
      </div>
    </article>)}
  </div>;
}

function JourneyEditor({ draft, setDraft, onClose, onSave, onGenerateCopy, isSaving, isGeneratingCopy, theme, isDarkMode, candidatePhotos = [], workPhotos = [], publishedCandidatePhotoIds }) {
  if (!draft) return null;
  const fieldStyle = { width: '100%', boxSizing: 'border-box', padding: '12px 13px', borderRadius: '13px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit' };
  const existingCount = draft.images?.length || 0;
  const pendingCount = draft.files?.length || 0;
  const candidateIds = draft.candidatePhotoIds || [];
  const candidateById = new Map(candidatePhotos.map(photo => [String(photo._id), photo]));
  const imageCount = existingCount + pendingCount + candidateIds.length;
  const today = new Date();
  const defaultDate = `${today.getFullYear()}.${today.getMonth() + 1}.${today.getDate()}`;
  const setValue = (key, value) => setDraft({ ...draft, [key]: value });
  const toggleRelatedWork = scope => {
    const currentScopes = draft.relatedWorkLocations || [];
    setValue('relatedWorkLocations', currentScopes.some(item => item.prefix === scope.prefix)
      ? currentScopes.filter(item => item.prefix !== scope.prefix)
      : [...currentScopes, scope].slice(0, 30));
  };
  const pendingIds = draft.pendingImageIds || [];
  const allMarkers = [...(draft.images || []).map((_, index) => `old:${index}`), ...candidateIds.map(id => `candidate:${id}`), ...pendingIds.map(id => `new:${id}`)].filter(marker => !(draft.removedImageMarkers || []).includes(marker));
  const orderedMarkers = [...new Set([...(draft.imageOrder || []), ...allMarkers])].filter(marker => allMarkers.includes(marker));
  const imageSources = orderedMarkers.map(marker => marker.startsWith('old:') ? (() => { const image = draft.images?.[Number(marker.slice(4))] || {}; return image.medium || image.thumbnail || image.original || ''; })() : marker.startsWith('candidate:') ? getPhotoImagePath(candidateById.get(marker.slice(10)), 'medium') : draft.pendingImagePreviews?.[marker.slice(4)] || '').filter(Boolean);
  return <div onMouseDown={onClose} style={{ position: 'fixed', inset: '44px 0 0', zIndex: 10002, display: 'flex', justifyContent: 'flex-end', background: 'rgba(0,0,0,.58)', backdropFilter: 'blur(10px)' }}>
    <section onMouseDown={event => event.stopPropagation()} style={{ width: 'min(1040px, 100vw)', height: 'calc(100dvh - 44px)', overflowY: 'auto', boxSizing: 'border-box', padding: '30px 32px 42px', border: 'none', borderRadius: 0, background: theme.cardBg, color: theme.textPrimary, boxShadow: '-16px 0 48px rgba(0,0,0,.32)' }}>
      <div className="journey-editor-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, .78fr) minmax(0, 1.22fr)', gap: '34px', alignItems: 'start' }}><aside style={{ position: 'sticky', top: 0 }}><p style={{ margin: '0 0 10px', color: theme.textSecondary, fontSize: '11px', fontWeight: 700, letterSpacing: '.09em' }}>發佈位置預覽</p><article style={{ overflow: 'hidden', border: `1px solid ${theme.cardBorder}`, borderRadius: '18px', background: theme.cardBg }}><div style={{ padding: '16px' }}><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: theme.textSecondary }}><span>黃啟銘 @M.Gallery · 發佈</span><strong style={{ color: theme.textPrimary }}>M.</strong></div><h3 style={{ margin: '10px 0 4px', fontSize: '19px' }}>{draft.title || '未命名旅程'}</h3><p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px' }}>{[draft.location, draft.dateLabel || defaultDate, draft.showTime !== false && draft.timeLabel].filter(Boolean).join(' · ')}</p>{draft.body && <p style={{ margin: '12px 0 0', fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{draft.body}</p>}</div>{imageSources.length > 0 ? <div style={{ display: 'grid', gridTemplateColumns: imageSources.length === 1 ? '1fr' : imageSources.length === 3 ? '1.35fr 1fr' : 'repeat(2, 1fr)', gap: '2px', background: theme.cardBorder }}>{imageSources.slice(0, 4).map((src, index) => <div key={`${src}-${index}`} style={{ aspectRatio: imageSources.length === 1 ? '4 / 5' : '1', gridRow: imageSources.length === 3 && index === 0 ? 'span 2' : 'auto', overflow: 'hidden' }}><img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>)}</div> : <div style={{ height: '230px', display: 'grid', placeItems: 'center', color: theme.textSecondary, background: theme.inputBg, fontSize: '12px' }}>選擇照片後，這裡會即時預覽</div>}</article></aside><div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: 700, letterSpacing: '.1em' }}>JOURNEY · NEW POST</p><h2 style={{ margin: '7px 0 0', fontSize: '25px' }}>{draft._id ? '編輯這段旅程' : '今天，想留下什麼？'}</h2><p style={{ margin: '7px 0 0', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.55 }}>先排照片，再用一句話或語音記下當時的事；其他資料需要時再展開。</p></div><button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', color: theme.textSecondary, fontSize: '22px', cursor: 'pointer' }}>×</button></div>
      <button type="button" onClick={() => setDraft({ ...draft, showImageSorter: true })} style={{ width: '100%', marginTop: '20px', padding: '15px', borderRadius: '16px', border: `1px solid ${imageCount ? '#0071e3' : theme.inputBorder}`, background: imageCount ? (isDarkMode ? 'rgba(0,113,227,.12)' : 'rgba(0,113,227,.045)') : theme.inputBg, color: theme.textPrimary, cursor: 'pointer', textAlign: 'left' }}><strong style={{ fontSize: '14px' }}>{imageCount ? `已選 ${imageCount} 張照片` : '＋ 選擇 1–9 張照片'}</strong><span style={{ float: 'right', color: imageCount ? '#0071e3' : theme.textSecondary, fontSize: '12px' }}>{imageCount ? '拖曳排序 · 第 1 張是封面' : '先從照片開始'}</span></button>
      <label style={{ display: 'block', marginTop: '18px' }}><span style={{ display: 'block', marginBottom: '7px', color: theme.textSecondary, fontSize: '12px' }}>標題</span><input value={draft.title || ''} onChange={event => setValue('title', event.target.value)} placeholder="例如：雨天野餐（可讓 AI 幫你補）" style={fieldStyle} /></label><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}><label><span style={{ display: 'block', marginBottom: '7px', color: theme.textSecondary, fontSize: '12px' }}>地點</span><input value={draft.location || ''} onChange={event => setValue('location', event.target.value)} placeholder="廣東 · 汕頭" style={fieldStyle} /></label><JourneyPhotoDateTimeField draft={draft} setDraft={setDraft} candidatePhotos={candidatePhotos} theme={theme} /></div>
      <label style={{ display: 'block', marginTop: '18px' }}><span style={{ display: 'block', marginBottom: '7px', color: theme.textSecondary, fontSize: '12px' }}>這一刻想記下什麼？</span><textarea rows="5" value={draft.body || ''} onChange={event => setValue('body', event.target.value)} placeholder="不用寫成遊記。例如：下午臨時下雨，還是去了海邊；人不多，坐了一會再走。" style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.65 }} /></label>
      <DraftWritingAssistant kind="journey" imageCount={imageCount} onGenerate={note => onGenerateCopy(note, draft, candidatePhotos)} isGenerating={isGeneratingCopy} theme={theme} isDarkMode={isDarkMode} />
      <JourneyRelatedWorkPicker workPhotos={workPhotos} relatedWorkLocations={draft.relatedWorkLocations || []} onToggle={toggleRelatedWork} theme={theme} />
      <details style={{ marginTop: '16px', paddingTop: '14px', borderTop: `1px solid ${theme.cardBorder}` }}><summary style={{ color: theme.textSecondary, fontSize: '13px', cursor: 'pointer' }}>進階設定：JOURNEY 編號與後綴</summary><p style={{ margin: '11px 0 0', color: theme.textSecondary, fontSize: '11px', lineHeight: 1.5 }}>預設會在發佈時依日期由早到晚自動編號；這裡留空即可。只有手動填入編號，才會改成指定位置並自動補齊其他序號。</p><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}><label><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>JOURNEY 編號（手動覆蓋）</span><input type="number" min="1" value={draft.sequenceNumber || ''} onChange={event => setDraft({ ...draft, sequenceNumber: event.target.value, sequenceManuallyChanged: true })} placeholder="依日期自動編號" style={fieldStyle} /></label><label><span style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '12px' }}>後綴（可選）</span><input value={draft.sequenceSuffix || ''} onChange={event => setValue('sequenceSuffix', event.target.value)} placeholder="例如：番外 / EXTRA" style={fieldStyle} /></label></div></details>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', marginTop: '22px' }}><label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.textSecondary, fontSize: '13px' }}><input type="checkbox" checked={draft.isVisible !== false} onChange={event => setValue('isVisible', event.target.checked)} />前台顯示</label><div style={{ display: 'flex', gap: '10px' }}><button type="button" onClick={onClose} style={{ padding: '10px 16px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, cursor: 'pointer' }}>取消</button><button type="button" onClick={onSave} disabled={isSaving} style={{ padding: '10px 18px', borderRadius: '980px', border: 'none', background: '#0071e3', color: '#fff', fontWeight: 650, cursor: 'pointer' }}>{isSaving ? '發佈中…' : '發佈旅程'}</button></div></div>
      </div></div>
    </section>
    {draft.showImageSorter && <PostImageOrderModal draft={draft} setDraft={setDraft} kind="journey" candidatePhotos={candidatePhotos} publishedCandidatePhotoIds={publishedCandidatePhotoIds} theme={theme} isDarkMode={isDarkMode} onClose={() => setDraft(previous => previous ? { ...previous, showImageSorter: false } : previous)} />}
  </div>;
}

function BroadcastControlPage({ draft, setDraft, savedMode, onPreview, onSave, onEmergencyClose, onUploadAudio, isSaving, isUploadingAudio }) {
  const audioInputRef = useRef(null);
  const enabled = draft.mode !== 'off';
  const includesAdmin = draft.mode === 'all';
  const setEnabled = nextEnabled => setDraft(current => ({ ...current, mode: nextEnabled ? 'visitors' : 'off' }));
  const setIncludesAdmin = nextValue => setDraft(current => ({ ...current, mode: nextValue ? 'all' : 'visitors' }));
  return <section className="broadcast-admin-console">
      <div className="broadcast-glass-control">
        <div className="broadcast-console-heading"><span className="broadcast-skull-mark">☠︎</span><div><p>SIGNAL OVERRIDE</p><h2>Broadcast 接管</h2></div></div>
        <span className="broadcast-restricted-label">RESTRICTED CONTROL</span>
      </div>
      <div className="broadcast-live-state"><span>当前线上状态</span><strong className={savedMode === 'off' ? 'safe' : 'danger'}>{savedMode === 'off' ? '正常开放' : savedMode === 'visitors' ? '访客接管中' : '全站接管中'}</strong></div>

      <div className="broadcast-switch-panel">
        <div className="broadcast-switch-row">
          <div><strong>启用 Broadcast</strong><span>开启后，访客会进入信号中断页面。</span></div>
          <button type="button" role="switch" aria-checked={enabled} aria-label="启用 Broadcast" className={`broadcast-ios-switch ${enabled ? 'active' : ''}`} onClick={() => setEnabled(!enabled)}><span /></button>
        </div>
        <div className={`broadcast-switch-row ${enabled ? '' : 'disabled'}`}>
          <div><strong>同时接管管理员</strong><span>关闭时管理员仍可正常进入后台；开启后管理员也会看到接管页面。</span></div>
          <button type="button" role="switch" aria-checked={includesAdmin} aria-label="同时接管管理员" disabled={!enabled} className={`broadcast-ios-switch ${includesAdmin ? 'active' : ''}`} onClick={() => setIncludesAdmin(!includesAdmin)}><span /></button>
        </div>
      </div>
      <p className="broadcast-draft-note">开关只编辑草稿；点击“储存并应用”后才会改变线上网站。</p>

      <div className="broadcast-fields-grid"><label><span>警报代码</span><input value={draft.code || ''} onChange={event => setDraft({ ...draft, code: event.target.value })} /></label><label><span>主标题</span><input value={draft.title || ''} onChange={event => setDraft({ ...draft, title: event.target.value })} /></label></div>
      <label className="broadcast-field-block"><span>警报内容</span><textarea rows="3" value={draft.message || ''} onChange={event => setDraft({ ...draft, message: event.target.value })} /></label>
      <div className="broadcast-audio-upload">
        <div><strong>循环播报音频</strong><span>{draft.audioName || (draft.audioPath ? '已上传 Broadcast 音频' : '尚未上传音频')}</span></div>
        <input ref={audioInputRef} type="file" accept="audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/webm,.mp3,.wav,.m4a,.aac,.ogg,.webm" onChange={event => { const file = event.target.files?.[0]; if (file) onUploadAudio?.(file); event.target.value = ''; }} />
        <button type="button" onClick={() => audioInputRef.current?.click()} disabled={isUploadingAudio}>{isUploadingAudio ? '上传中…' : draft.audioPath ? '更换音频' : '选择音频'}</button>
      </div>
      <div className="broadcast-options"><label><input type="checkbox" checked={draft.audioEnabled !== false} onChange={event => setDraft({ ...draft, audioEnabled: event.target.checked })} />进入警告页后循环播放</label><label>故障强度<input type="range" min="1" max="3" value={draft.intensity || 2} onChange={event => setDraft({ ...draft, intensity: Number(event.target.value) })} /></label></div>
      <div className="broadcast-actions"><button type="button" className="secondary" onClick={onPreview}>预览效果</button><div>{savedMode !== 'off' && <button type="button" className="emergency" onClick={onEmergencyClose}>紧急关闭</button>}<button type="button" className="apply" onClick={onSave} disabled={isSaving}>{isSaving ? '储存中…' : '储存并应用'}</button></div></div>
  </section>;
}

// eslint-disable-next-line no-unused-vars
function GearWorkspaceEditorV2Legacy({ draft, setDraft, onClose, onSave, onGenerateCopy, isSaving, isGeneratingCopy, isDarkMode, theme, publishedItems }) {
  const imageRef = useRef(null); const rawRef = useRef(null); const gradedRef = useRef(null);
  const isColor = draft.type === 'color';
  const choose = (key, ref, multiple = false) => {
    if (key === 'images' && ((draft.images || []).length || (draft.files?.images || []).length)) {
      setDraft(prev => ({ ...prev, showImageSorter: true }));
      return;
    }
    const input = ref.current; if (!input) return;
    input.onchange = event => {
      const existingStoredCount = key === 'images' ? (draft.images || []).length : 0;
      const existingPending = multiple ? (draft.files?.[key] || []) : [];
      const remaining = Math.max(0, 9 - existingStoredCount - existingPending.length);
      const selected = multiple ? Array.from(event.target.files || []).slice(0, remaining) : event.target.files?.[0];
      if (!selected || (Array.isArray(selected) && !selected.length)) return;
      const preview = multiple ? selected.map(file => URL.createObjectURL(file)) : URL.createObjectURL(selected);
      setDraft(prev => {
        if (key !== 'images') return { ...prev, files: { ...prev.files, [key]: multiple ? [...(prev.files?.[key] || []), ...selected].slice(0, 9 - existingStoredCount) : selected }, previews: { ...prev.previews, [key]: multiple ? [...(prev.previews?.[key] || []), ...preview].slice(0, 9 - existingStoredCount) : preview } };
        const nextFiles = [...(prev.files?.images || []), ...selected].slice(0, 9 - existingStoredCount);
        const nextIds = [...(prev.pendingImageIds || []), ...selected.map((_, index) => `n-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`)].slice(0, nextFiles.length);
        const nextPreviews = { ...(prev.pendingImagePreviews || {}) };
        nextIds.slice(-selected.length).forEach((id, index) => { nextPreviews[id] = preview[index]; });
        const oldMarkers = (prev.images || []).map((_, index) => `old:${index}`);
        return { ...prev, files: { ...prev.files, images: nextFiles }, previews: { ...prev.previews, images: [...(prev.previews?.images || []), ...preview] }, pendingImageIds: nextIds, pendingImagePreviews: nextPreviews, imageOrder: [...new Set([...(prev.imageOrder || []), ...oldMarkers, ...nextIds.map(id => `new:${id}`)])], showImageSorter: true };
      });
    };
    input.value = ''; input.click();
  };
  const update = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const referencePost = publishedItems.find(item => item._id !== draft._id && item.type === draft.type) || publishedItems.find(item => item._id !== draft._id);
  const fieldStyle = { width: '100%', boxSizing: 'border-box', margin: '7px 0 18px', padding: '12px 13px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit' };
  const secondaryButton = { border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', padding: '10px 15px', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontWeight: '600' };
  const livePost = isColor ? <GearDraftPreview draft={draft} isDarkMode={isDarkMode} theme={theme} /> : <GearFeedPost item={draft} preview theme={theme} isDarkMode={isDarkMode} />;
  const publishedPost = referencePost?.type === 'color' ? <GearDraftPreview draft={referencePost} isDarkMode={isDarkMode} theme={theme} /> : referencePost ? <GearFeedPost item={referencePost} preview theme={theme} isDarkMode={isDarkMode} /> : <div style={{ height: '118px', border: `1px dashed ${theme.cardBorder}`, borderRadius: '16px', display: 'grid', placeItems: 'center', color: theme.textSecondary, fontSize: '12px' }}>第一則內容將從這裡開始</div>;
  const draftImageCount = (draft.files?.images?.length || draft.images?.length || (draft.files?.originalImage ? 1 : 0) + (draft.files?.gradedImage ? 1 : 0));
  return <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 10002, background: 'rgba(0,0,0,.58)', backdropFilter: 'blur(10px)', display: 'flex', justifyContent: 'flex-end' }}><aside onMouseDown={event => event.stopPropagation()} style={{ width: 'min(1060px, 100vw)', height: '100dvh', overflowY: 'auto', boxSizing: 'border-box', padding: '30px 32px 42px', background: theme.cardBg, color: theme.textPrimary, boxShadow: '-16px 0 48px rgba(0,0,0,.32)' }}><div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,.85fr) minmax(0,1.15fr)', gap: '38px', alignItems: 'start' }}><div style={{ position: 'sticky', top: 0 }}><p style={{ margin: '0 0 12px', color: theme.textSecondary, fontSize: '12px', fontWeight: '700', letterSpacing: '.08em' }}>發佈位置預覽</p>{livePost}<p style={{ margin: '18px 0 10px', color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.08em' }}>下方已發佈帖子</p>{publishedPost}</div><div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '26px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px', fontWeight: '700', letterSpacing: '.08em' }}>{isColor ? 'COLOR COMPARISON' : draft.type === 'camera' ? 'CAMERA & LENS' : 'CREATIVE TOOLS'}</p><h2 style={{ margin: '7px 0 0', fontSize: '26px' }}>{draft._id ? '編輯項目' : '新增項目'}</h2></div><button onClick={onClose} style={{ width: '36px', height: '36px', border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: 'transparent', color: theme.textSecondary, fontSize: '20px', cursor: 'pointer' }}>×</button></div>{!isColor && <><label>名稱</label><input value={draft.title} onChange={event => update('title', event.target.value)} placeholder={draft.type === 'camera' ? '例如：Nikon Zf' : '例如：MacBook Pro'} style={fieldStyle} /><label>器材組合 / 用途</label><input value={draft.subtitle} onChange={event => update('subtitle', event.target.value)} placeholder={draft.type === 'camera' ? '例如：NIKKOR Z 40mm f/2' : '例如：MacBook、相機包與儲存設備'} style={fieldStyle} /></>}{isColor && <><label>對比標題 <span style={{ color: theme.textSecondary }}>（可選）</span></label><input value={draft.title} onChange={event => update('title', event.target.value)} placeholder="例如：雨後城市，或留空" style={fieldStyle} /></>}<label>{isColor ? '調色說明' : '使用感受'}</label><textarea rows="5" value={draft.description} onChange={event => update('description', event.target.value)} placeholder={isColor ? '這組色彩想保留什麼？' : '為什麼它在你的工作流中重要？'} style={{ ...fieldStyle, resize: 'vertical' }} /><DraftWritingAssistant kind="gear" imageCount={draftImageCount} onGenerate={onGenerateCopy} isGenerating={isGeneratingCopy} theme={theme} isDarkMode={isDarkMode} />{!isColor ? <><input ref={imageRef} type="file" accept="image/*" multiple style={{ display: 'none' }} /><button onClick={() => choose('images', imageRef, true)} style={secondaryButton}>{draft.files?.images?.length ? `已選 ${draft.files.images.length} 張，重新選擇` : '選擇帖子圖片（最多 9 張）'}</button><p style={{ margin: '9px 0 0', fontSize: '12px', color: theme.textSecondary }}>圖片將先壓縮，再產生不同尺寸的展示版本。</p></> : <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}><input ref={rawRef} type="file" accept="image/*" style={{ display: 'none' }} /><input ref={gradedRef} type="file" accept="image/*" style={secondaryButton} /><button onClick={() => choose('originalImage', rawRef)} style={secondaryButton}>{draft.files?.originalImage ? '更換 RAW / 未調色' : '選擇 RAW / 未調色'}</button><button onClick={() => choose('gradedImage', gradedRef)} style={secondaryButton}>{draft.files?.gradedImage ? '更換 GRADED' : '選擇 GRADED'}</button></div>}<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}><button onClick={onSave} disabled={isSaving} style={{ border: 'none', borderRadius: '980px', padding: '11px 20px', background: '#0071e3', color: '#fff', fontWeight: '600', cursor: 'pointer', opacity: isSaving ? .65 : 1 }}>{isSaving ? '儲存中...' : '儲存項目'}</button></div></div></div></aside></div>;
}

function GearWorkspaceEditorV2({ draft, setDraft, onClose, onSave, onGenerateCopy, isSaving, isGeneratingCopy, isDarkMode, theme }) {
  const rawRef = useRef(null);
  const gradedRef = useRef(null);
  const isColor = draft.type === 'color';
  const fieldStyle = { width: '100%', boxSizing: 'border-box', marginTop: '7px', padding: '12px 13px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit' };
  const secondaryButton = { border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', padding: '10px 15px', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontWeight: '600' };
  const setValue = (key, value) => setDraft(previous => ({ ...previous, [key]: value }));
  const pendingIds = draft.pendingImageIds || [];
  const allMarkers = [...(draft.images || []).map((_, index) => `old:${index}`), ...pendingIds.map(id => `new:${id}`)].filter(marker => !(draft.removedImageMarkers || []).includes(marker));
  const orderedMarkers = [...new Set([...(draft.imageOrder || []), ...allMarkers])].filter(marker => allMarkers.includes(marker));
  const imageSources = orderedMarkers.map(marker => marker.startsWith('old:') ? (() => { const image = draft.images?.[Number(marker.slice(4))] || {}; return image.medium || image.thumbnail || image.original || ''; })() : draft.pendingImagePreviews?.[marker.slice(4)] || '').filter(Boolean);
  const previewItem = { ...draft, images: [], previews: { ...(draft.previews || {}), images: imageSources } };
  const chooseColorFile = (key, ref) => {
    ref.current.onchange = event => { const file = event.target.files?.[0]; if (file) setDraft(previous => ({ ...previous, files: { ...(previous.files || {}), [key]: file }, previews: { ...(previous.previews || {}), [key]: URL.createObjectURL(file) } })); event.target.value = ''; };
    ref.current.click();
  };
  return <div onMouseDown={onClose} style={{ position: 'fixed', inset: '44px 0 0', zIndex: 10002, display: 'flex', justifyContent: 'flex-end', background: 'rgba(0,0,0,.58)', backdropFilter: 'blur(10px)' }}><aside onMouseDown={event => event.stopPropagation()} style={{ width: 'min(1060px, 100vw)', height: 'calc(100dvh - 44px)', overflowY: 'auto', boxSizing: 'border-box', padding: '30px 32px 42px', background: theme.cardBg, color: theme.textPrimary, boxShadow: '-16px 0 48px rgba(0,0,0,.32)' }}><div className="journey-editor-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,.85fr) minmax(0,1.15fr)', gap: '38px', alignItems: 'start' }}><div style={{ position: 'sticky', top: 0 }}><p style={{ margin: '0 0 12px', color: theme.textSecondary, fontSize: '12px', fontWeight: 700, letterSpacing: '.08em' }}>發佈位置預覽</p>{isColor ? <GearDraftPreview draft={draft} isDarkMode={isDarkMode} theme={theme} /> : <GearFeedPost item={previewItem} preview theme={theme} isDarkMode={isDarkMode} />}</div><div><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '22px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px', fontWeight: 700, letterSpacing: '.08em' }}>{isColor ? 'COLOR COMPARISON' : draft.type === 'camera' ? 'CAMERA & LENS' : 'CREATIVE TOOLS'}</p><h2 style={{ margin: '7px 0 0', fontSize: '26px' }}>{draft._id ? '編輯項目' : '新增項目'}</h2></div><button type="button" onClick={onClose} style={{ width: '36px', height: '36px', border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: 'transparent', color: theme.textSecondary, fontSize: '20px', cursor: 'pointer' }}>×</button></div>{!isColor && <button type="button" onClick={() => setDraft(previous => ({ ...previous, showImageSorter: true }))} style={{ width: '100%', padding: '15px', borderRadius: '16px', border: `1px solid ${imageSources.length ? '#0071e3' : theme.inputBorder}`, background: imageSources.length ? (isDarkMode ? 'rgba(0,113,227,.12)' : 'rgba(0,113,227,.045)') : theme.inputBg, color: theme.textPrimary, cursor: 'pointer', textAlign: 'left' }}><strong style={{ fontSize: '14px' }}>{imageSources.length ? `已選 ${imageSources.length} 張照片` : '＋ 選擇 1–9 張照片'}</strong><span style={{ float: 'right', color: imageSources.length ? '#0071e3' : theme.textSecondary, fontSize: '12px' }}>{imageSources.length ? '拖曳排序 · 第 1 張是封面' : '先從照片開始'}</span></button>}{!isColor && <><label style={{ display: 'block', marginTop: '18px' }}>名稱<input value={draft.title || ''} onChange={event => setValue('title', event.target.value)} placeholder={draft.type === 'camera' ? '例如：Nikon Zf' : '例如：MacBook Pro'} style={fieldStyle} /></label><label style={{ display: 'block', marginTop: '14px' }}>器材組合 / 用途<input value={draft.subtitle || ''} onChange={event => setValue('subtitle', event.target.value)} placeholder="例如：MacBook、相機包與儲存設備" style={fieldStyle} /></label></>}{isColor && <label style={{ display: 'block' }}>對比標題（可選）<input value={draft.title || ''} onChange={event => setValue('title', event.target.value)} placeholder="例如：雨後城市，或留空" style={fieldStyle} /></label>}<label style={{ display: 'block', marginTop: '16px' }}>{isColor ? '調色說明' : '使用感受'}<textarea rows="5" value={draft.description || ''} onChange={event => setValue('description', event.target.value)} placeholder={isColor ? '這組色彩想保留什麼？' : '為什麼它在你的工作流中重要？'} style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.6 }} /></label><DraftWritingAssistant kind="gear" imageCount={isColor ? Number(Boolean(draft.files?.originalImage || draft.originalImage?.medium)) + Number(Boolean(draft.files?.gradedImage || draft.gradedImage?.medium)) : imageSources.length} onGenerate={onGenerateCopy} isGenerating={isGeneratingCopy} theme={theme} isDarkMode={isDarkMode} />{isColor && <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}><input ref={rawRef} type="file" accept="image/*" style={{ display: 'none' }} /><input ref={gradedRef} type="file" accept="image/*" style={{ display: 'none' }} /><button type="button" onClick={() => chooseColorFile('originalImage', rawRef)} style={secondaryButton}>選擇 RAW / 未調色</button><button type="button" onClick={() => chooseColorFile('gradedImage', gradedRef)} style={secondaryButton}>選擇 GRADED</button></div>}<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}><button type="button" onClick={onSave} disabled={isSaving} style={{ border: 'none', borderRadius: '980px', padding: '11px 20px', background: '#0071e3', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: isSaving ? .65 : 1 }}>{isSaving ? '儲存中…' : '儲存項目'}</button></div></div></div></aside>{draft.showImageSorter && <PostImageOrderModal draft={draft} setDraft={setDraft} kind="gear" theme={theme} isDarkMode={isDarkMode} onClose={() => setDraft(previous => previous ? { ...previous, showImageSorter: false } : previous)} />}</div>;
}

// ==========================================
// 3. Apple 側邊章節進度條
// ==========================================

function ScrollNavigation({ sections, isDarkMode, theme }) {
  const [activeId, setActiveId] = useState('section-hero');
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const threshold = window.innerHeight * 0.5;

      for (let i = 0; i < sections.length; i++) {
        const el = document.getElementById(sections[i].id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= threshold && rect.bottom >= threshold) {
            setActiveId(sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <aside 
      className="apple-scroll-nav"
      aria-label="頁面導覽"
      style={{
        position: 'fixed',
        right: 'max(20px, env(safe-area-inset-right, 20px))',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        alignItems: 'center',
        pointerEvents: 'auto'
      }}
    >
      {sections.map((sec) => {
        const isActive = activeId === sec.id;
        const isHovered = hoveredId === sec.id;
        return (
          <div
            key={sec.id}
            onMouseEnter={() => setHoveredId(sec.id)}
            onMouseLeave={() => setHoveredId(null)}
            onClick={() => scrollTo(sec.id)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <AnimatePresence>
              {isHovered && (
                <motion.span
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: 'absolute',
                    right: '22px',
                    whiteSpace: 'nowrap',
                    fontSize: '12px',
                    fontWeight: '500',
                    padding: '5px 12px',
                    borderRadius: '10px',
                    background: isDarkMode ? 'rgba(28, 28, 30, 0.88)' : 'rgba(255, 255, 255, 0.88)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    color: theme.textPrimary,
                    border: `1px solid ${theme.cardBorder}`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    pointerEvents: 'none'
                  }}
                >
                  {sec.title}
                </motion.span>
              )}
            </AnimatePresence>

            <motion.div
              animate={{
                scale: isActive ? 1.25 : (isHovered ? 1.15 : 1),
                height: isActive ? '20px' : '6px',
                width: '6px',
                borderRadius: '999px',
                backgroundColor: isActive
                  ? (isDarkMode ? '#ffffff' : '#1d1d1f')
                  : (isDarkMode ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.22)')
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              style={{
                boxShadow: isActive ? (isDarkMode ? '0 0 12px rgba(255,255,255,0.7)' : '0 0 8px rgba(0,0,0,0.3)') : 'none'
              }}
            />
          </div>
        );
      })}
    </aside>
  );
}

// ==========================================
// 4. 獨立 HeroSection 組件（純粹居中沉浸式）
// ==========================================

const HeroSection = React.memo(function HeroSection({
  theme,
  isDarkMode,
  isMobileScreen,
  isAndroid,
  isLoading,
  currentHeroPhoto,
  heroIndex,
  heroImage,
  isHeroPaused,
  setIsHeroPaused,
  heroPhotos,
  heroTextColor,
  heroSubColor,
  heroTextShadow,
  heroLines
}) {
  const heroTrackRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ 
    target: heroTrackRef, 
    offset: ["start start", "end end"] 
  });

  const heroBgY = useTransform(heroScroll, [0, 1], ['0%', '-5%']);

  const titleOpacity = useTransform(heroScroll, [0, 0.1], [1, 0]);
  const titleY = useTransform(heroScroll, [0, 0.1], [0, -30]);
  const titleDisplay = useTransform(heroScroll, (v) => (v >= 0.1 ? 'none' : 'block'));

  const textGroupOpacity = useTransform(heroScroll, [0.08, 0.15], [0, 1]);
  const textGroupY = useTransform(heroScroll, [0.65, 1], [0, 45]);

  const line1Opacity = useTransform(heroScroll, [0, 0.12, 0.22, 0.32, 1], [0, 0, 1, 0.45, 0.45]);
  const line1Y = useTransform(heroScroll, [0.12, 0.22], [20, 0]);

  const line2Opacity = useTransform(heroScroll, [0, 0.26, 0.36, 0.46, 1], [0, 0, 1, 0.45, 0.45]);
  const line2Y = useTransform(heroScroll, [0.26, 0.36], [20, 0]);

  const line3Opacity = useTransform(heroScroll, [0, 0.40, 0.50, 0.60, 1], [0, 0, 1, 0.45, 0.45]);
  const line3Y = useTransform(heroScroll, [0.40, 0.50], [20, 0]);

  // Keep the full desktop-style exit on mobile. The previous 0.96 scale and
  // zero Y travel made the motion effectively invisible. Stable `vh` geometry
  // and the themed stage behind the card prevent the old address-bar flash.
  const heroExitRange = isMobileScreen ? [0.55, 0.92] : [0.65, 1];
  const heroScale = useTransform(heroScroll, heroExitRange, [1, 0.88]);
  const heroRadius = useTransform(heroScroll, heroExitRange, [0, isMobileScreen ? 32 : 36]);
  const heroWrapY = useTransform(heroScroll, heroExitRange, ['0%', isMobileScreen ? '-7%' : '-10%']);
  const heroTitleLetterSpacing = isMobileScreen && isAndroid ? '-0.7px' : '-2px';
  const heroLineLetterSpacing = isMobileScreen && isAndroid ? '-0.6px' : '-1.5px';

  // Keep the rendered photo and its metadata on the same loaded image state.
  // While the next slide is loading, the previous complete pair stays visible.
  const displayedHeroPhoto = heroImage?.photo || currentHeroPhoto;
  const displayedPhotoId = String(displayedHeroPhoto?._id || '');
  const heroRequestSize = isMobileScreen ? 'medium' : 'hero';
  const displayImage = heroImage?.photoId === displayedPhotoId
    ? cleanImagePath(heroImage.src)
    : getPhotoImagePath(displayedHeroPhoto, heroRequestSize);
  const displayImageCandidates = getPhotoImageCandidates(displayedHeroPhoto, heroRequestSize);
  const initialFallbackIndex = Math.max(0, displayImageCandidates.indexOf(displayImage));

  const lines = heroLines && heroLines.length >= 3 ? heroLines : [
    '捕捉稍縱即逝的真實。',
    '每一次快門，都是與這座城市的深度對話。',
    '在光影交錯間，尋找被忽略的細節與溫度。'
  ];

  return (
    <div id="section-hero" ref={heroTrackRef} style={{ height: '300vh', position: 'relative', backgroundColor: theme.bg }}>
      <div 
        style={{ 
          position: 'sticky', 
          top: 0, 
          // Use the same stable viewport unit as the 300vh scroll track. Using
          // 100dvh here made Android browsers recalculate the sticky geometry
          // whenever their address bar expanded or collapsed.
          height: '100vh', 
          width: '100%',
          overflow: 'hidden', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: theme.bg
        }}
      >
        <motion.div 
          style={{ 
            width: '100%', 
            height: '100%', 
            scale: heroScale, 
            borderRadius: heroRadius, 
            y: heroWrapY,
            overflow: 'hidden', 
            transformOrigin: 'center center', 
            willChange: 'transform',
            transform: 'translate3d(0,0,0)',
            WebkitTransform: 'translate3d(0,0,0)',
            backfaceVisibility: 'hidden',
            position: 'relative'
          }}
        >
          <AnimatePresence mode="popLayout">
            {isLoading ? (
              <motion.div 
                key="loading" 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  color: '#86868b', 
                  fontSize: '16px', 
                  backgroundColor: isDarkMode ? '#000' : '#1d1d1f' 
                }}
              >
                正在載入精選照片... ✨
              </motion.div>
            ) : displayedHeroPhoto ? (
              <motion.div 
                key={displayedHeroPhoto._id || heroIndex} 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                transition={{ duration: 1.2, ease: 'easeInOut' }} 
                style={{ 
                  position: 'absolute', 
                  top: '-10%', 
                  left: '-5%', 
                  width: '110%', 
                  height: '120%', 
                  y: heroBgY,
                  willChange: 'transform' 
                }}
              >
                <img 
                  src={displayImage} 
                  alt={getPhotoAlt(displayedHeroPhoto, 'M.Gallery 精選攝影作品')} 
                  decoding="async" 
                  fetchPriority="high"
                  data-fallback-index={initialFallbackIndex}
                  onError={(e) => {
                    const nextIndex = Number(e.currentTarget.dataset.fallbackIndex || 0) + 1;
                    const fallback = displayImageCandidates[nextIndex];
                    if (fallback) {
                      e.currentTarget.dataset.fallbackIndex = String(nextIndex);
                      e.currentTarget.src = fallback;
                    }
                  }}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover', 
                    objectPosition: 'center center',
                    display: 'block', 
                    userSelect: 'none', 
                    WebkitUserDrag: 'none', 
                    animation: 'kenBurnsEffect 6s linear forwards',
                    animationPlayState: isHeroPaused ? 'paused' : 'running'
                  }} 
                />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '65%', background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 45%, transparent 100%)', pointerEvents: 'none' }} />
              </motion.div>
            ) : (
              <div style={{ width: '100%', height: '100%', backgroundColor: isDarkMode ? '#000' : '#1d1d1f' }} />
            )}
          </AnimatePresence>

          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 20 }}>
            
            <motion.div
              style={{
                position: 'absolute',
                textAlign: 'center',
                width: '100%',
                maxWidth: '800px',
                padding: '0 24px',
                opacity: titleOpacity,
                y: titleY,
                display: titleDisplay
              }}
            >
              <h1
                style={{
                  fontSize: 'clamp(34px, 5.5vw, 72px)',
                  fontWeight: '700',
                  letterSpacing: heroTitleLetterSpacing,
                  color: heroTextColor,
                  textShadow: heroTextShadow,
                  margin: '0 0 12px 0'
                }}
              >
                Photo Gallery.
              </h1>

              <p
                style={{
                  fontSize: 'clamp(14px, 2vw, 24px)',
                  fontWeight: '400',
                  color: heroSubColor,
                  textShadow: heroTextShadow,
                  margin: 0
                }}
              >
                光影與細節的城市體驗
              </p>
            </motion.div>

            <motion.div 
              style={{ 
                position: 'absolute', 
                textAlign: 'center', 
                width: '100%', 
                maxWidth: '1080px', 
                padding: '0 24px',
                opacity: textGroupOpacity,
                y: textGroupY
              }}
            >
              <motion.h2 style={{ fontSize: 'clamp(20px, 4vw, 56px)', fontWeight: '700', letterSpacing: heroLineLetterSpacing, color: heroTextColor, textShadow: heroTextShadow, margin: '0 0 12px 0', opacity: line1Opacity, y: line1Y }}>
                {lines[0]}
              </motion.h2>
              
              <motion.h2 style={{ fontSize: 'clamp(20px, 4vw, 56px)', fontWeight: '700', letterSpacing: heroLineLetterSpacing, color: heroTextColor, textShadow: heroTextShadow, margin: '0 0 12px 0', opacity: line2Opacity, y: line2Y }}>
                {lines[1]}
              </motion.h2>
              
              <motion.h2 style={{ fontSize: 'clamp(20px, 4vw, 56px)', fontWeight: '700', letterSpacing: heroLineLetterSpacing, color: heroTextColor, textShadow: heroTextShadow, margin: 0, opacity: line3Opacity, y: line3Y }}>
                {lines[2]}
              </motion.h2>
            </motion.div>

          </div>

          {displayedHeroPhoto && (
            <motion.div 
              style={{ 
                position: 'absolute', 
                left: 'max(20px, env(safe-area-inset-left, 20px))', 
                bottom: 'max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px))', 
                color: '#fff', 
                zIndex: 10, 
                maxWidth: 'calc(100% - 90px)', 
                opacity: 1
              }}
            >
              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '980px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', fontSize: '11px', fontWeight: '500', marginBottom: '8px', letterSpacing: '0.5px' }}>
                Featured Shot
              </span>
              <h2 style={{ margin: '0 0 4px 0', fontSize: 'clamp(17px, 3.8vw, 26px)', fontWeight: '600', letterSpacing: '-0.5px', textShadow: '0 2px 10px rgba(0,0,0,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                📍 {displayedHeroPhoto.location || '光影瞬間'}
              </h2>
              <p style={{ margin: 0, fontSize: 'clamp(12px, 2.5vw, 15px)', color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 6px rgba(0,0,0,0.6)', fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getPhotoEquipmentLabel(displayedHeroPhoto) || '記錄生活中的真實與感動'}
              </p>
            </motion.div>
          )}

          {heroPhotos.length > 1 && (
            <motion.button 
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              onClick={() => setIsHeroPaused(!isHeroPaused)} 
              aria-label={isHeroPaused ? "Play slideshow" : "Pause slideshow"}
              style={{ 
                position: 'absolute', 
                right: 'max(20px, env(safe-area-inset-right, 20px))', 
                bottom: 'max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px))', 
                zIndex: 30, 
                width: '42px', 
                height: '42px', 
                borderRadius: '50%', 
                background: 'rgba(0, 0, 0, 0.45)', 
                backdropFilter: 'blur(15px)', 
                WebkitBackdropFilter: 'blur(15px)', 
                border: '1px solid rgba(255, 255, 255, 0.25)', 
                color: '#fff', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                boxShadow: '0 4px 15px rgba(0,0,0,0.35)', 
                outline: 'none',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              {isHeroPaused ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="#ffffff" style={{ transform: 'translateX(1px)' }}>
                  <path d="M5 3.868v16.264c0 .825.908 1.328 1.606.892l13.118-8.132a1.042 1.042 0 000-1.784L6.606 2.976C5.908 2.54 5 3.043 5 3.868z" />
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="#ffffff">
                  <rect x="5.5" y="4" width="4" height="16" rx="1.5" />
                  <rect x="14.5" y="4" width="4" height="16" rx="1.5" />
                </svg>
              )}
            </motion.button>
          )}
        </motion.div>
      </div>
    </div>
  );
});

// ==========================================
// 5. 主應用組件 App
// ==========================================

function App() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const photoLibraryModeRef = useRef('none');
  const fullPhotoRequestRef = useRef(null);

  const isLocalEnvironment = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const [apiBaseUrl, setApiBaseUrl] = useState(isLocalEnvironment ? 'http://localhost:5001' : '');

  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const [isMobileScreen, setIsMobileScreen] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const isWindows = typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);
  const useInterInterface = isAndroid || isWindows;

  const [currentSubPage, setCurrentSubPage] = useState(null);
  const destinationsVisitedRef = useRef(false);
  const [maiPhotoTargetId, setMaiPhotoTargetId] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token') || null);
  const [adminSessionVerified, setAdminSessionVerified] = useState(false);
  const [adminVerificationResolved, setAdminVerificationResolved] = useState(!localStorage.getItem('admin_token'));
  const [broadcastResolved, setBroadcastResolved] = useState(false);
  const [broadcastPreview, setBroadcastPreview] = useState(false);
  const [broadcastRecoveryBypassed, setBroadcastRecoveryBypassed] = useState(
    () => sessionStorage.getItem('broadcast_recovery_bypass') === '1'
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [isAdminDashboard, setIsAdminDashboard] = useState(false);
  const [showAdminQuickMenu, setShowAdminQuickMenu] = useState(false);
  const [showMAiLauncher, setShowMAiLauncher] = useState(false);
  
  // 後台模組：Work | Gear | Journeys | Contact
  const [adminModule, setAdminModule] = useState('Work');
  
  // 🍎 【Work】模組內部視圖：'hero' | 'library' | 'inbox'
  const [workTab, setWorkTab] = useState('library');
  const [libraryFilterTag, setLibraryFilterTag] = useState('all');
  const [librarySearchQuery, setLibrarySearchQuery] = useState('');
  const [librarySortMode, setLibrarySortMode] = useState('shootingDate');
  const [activeLocationGroupKey, setActiveLocationGroupKey] = useState(null);
  const [locationAdminPath, setLocationAdminPath] = useState([]);
  const [contactTab, setContactTab] = useState('about');
  const [isUploadingContactPortrait, setIsUploadingContactPortrait] = useState(false);

  // 🍎 Hero 門面配置狀態（雙端切換：desktop | mobile）
  const [heroMode, setHeroMode] = useState('desktop'); 
  const [isHeroEditing, setIsHeroEditing] = useState(false); 
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  const [siteSettings, setSiteSettings] = useState({
    heroPhotoIds: [],
    heroPhotoMobileIds: [],
    equipmentPresets: [],
    locationCollections: [],
    locationPhotoOrders: [],
    locationMenuOrders: [],
    featuredPhotoIds: [],
    featuredTargetCount: 9,
    heroLines: [
      '捕捉稍縱即逝的真實。',
      '每一次快門，都是與這座城市的深度對話。',
      '在光影交錯間，尋找被忽略的細節與溫度。'
    ],
    philosophyTitle: '記錄真實，\n重塑秩序。',
    philosophyBody: '攝影對我而言，是一個做減法的過程。摒棄繁雜的器材依賴與過度的修飾，專注於街頭的光影、建築的幾何與最真實的瞬間。以 Clean Fit 的視覺標準，褪去畫面中的無序，只保留最純粹的肌理與溫度。',
    contactName: 'Miles',
    contactRole: '街頭與城市攝影',
    contactAbout: '熱愛光影與街頭攝影，記錄生活中的真實與細節。',
    contactEmail: '',
    contactInstagram: '',
    contactXiaohongshu: '',
    contactSocials: defaultContactSocials(),
    contactPortrait: { original: '', hero: '', medium: '', thumbnail: '', positionX: 50, positionY: 50, scale: 100 },
    broadcast: { mode: 'off', code: '404', title: 'SIGNAL INTERRUPTED', message: 'ARCHIVE NODE IS NOT RESPONDING', audioEnabled: true, intensity: 2 }
  });

  const [editHeroLines, setEditHeroLines] = useState(['', '', '']);
  const [editPhilTitle, setEditPhilTitle] = useState('');
  const [editPhilBody, setEditPhilBody] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [featuredDragPayload, setFeaturedDragPayload] = useState(null);

  const [isBatchAiRunning, setIsBatchAiRunning] = useState(false);
  const [aiProcessingId, setAiProcessingId] = useState(null);
  const [batchAiProgress, setBatchAiProgress] = useState(null);
  const [batchAiMode, setBatchAiMode] = useState(null);

  const [activeMenu, setActiveMenu] = useState(null);
  const [hoveredLocationMenuGroup, setHoveredLocationMenuGroup] = useState(null);
  const [hoveredLocationMenuCity, setHoveredLocationMenuCity] = useState(null);
  const [hasOpenedLocationCityColumn, setHasOpenedLocationCityColumn] = useState(false);
  const [hasOpenedLocationDetailColumn, setHasOpenedLocationDetailColumn] = useState(false);
  const [locationMenuColumnAnchors, setLocationMenuColumnAnchors] = useState({ cities: 0, details: 0 });

  const [heroIndex, setHeroIndex] = useState(0);
  const [isHeroPaused, setIsHeroPaused] = useState(false);

  const [heroTextColor, setHeroTextColor] = useState('#ffffff');
  const [heroSubColor, setHeroSubColor] = useState('rgba(255, 255, 255, 0.75)');
  const [heroTextShadow, setHeroTextShadow] = useState('0 4px 40px rgba(0,0,0,0.6)');

  const [heroImage, setHeroImage] = useState(null);

  const [lightboxIndex, setLightboxIndex] = useState(-1);
  // 預覽器必須記住「從哪一組照片開啟」；不能再把地點頁的排序下標拿去全媒體庫使用。
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxDirection, setLightboxDirection] = useState(1);
  const [isFirstOpen, setIsFirstOpen] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isLightboxTransitioning, setIsLightboxTransitioning] = useState(false);
  const lightboxPanX = useMotionValue(0);
  const lightboxPanY = useMotionValue(0);
  const [isSettled, setIsSettled] = useState(true);

  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isManagingSocialPlatforms, setIsManagingSocialPlatforms] = useState(false);
  const [gearItems, setGearItems] = useState([]);
  const [gearTab, setGearTab] = useState('camera');
  const [gearDraft, setGearDraft] = useState(null);
  const [isSavingGear, setIsSavingGear] = useState(false);
  const [gearViewer, setGearViewer] = useState(null);
  const [gearViewerDirection, setGearViewerDirection] = useState(1);
  const [isGearViewerFirstOpen, setIsGearViewerFirstOpen] = useState(true);
  const [isGearViewerZoomed, setIsGearViewerZoomed] = useState(false);
  const [isGearViewerTransitioning, setIsGearViewerTransitioning] = useState(false);
  const gearViewerPanX = useMotionValue(0);
  const gearViewerPanY = useMotionValue(0);
  const [journeyEntries, setJourneyEntries] = useState([]);
  const [journeyDraft, setJourneyDraft] = useState(null);
  const [isSavingJourney, setIsSavingJourney] = useState(false);
  const [showPublishedJourneyPhotos, setShowPublishedJourneyPhotos] = useState(false);
  const [journeyActionHoverId, setJourneyActionHoverId] = useState(null);
  const [aiDraftKind, setAiDraftKind] = useState(null);
  const [isSavingBroadcast, setIsSavingBroadcast] = useState(false);
  const [isUploadingBroadcastAudio, setIsUploadingBroadcastAudio] = useState(false);
  const [broadcastDraft, setBroadcastDraft] = useState({ mode: 'off', code: '404', title: '影像播放權限失效', message: '此裝置正在接收一段未經審核的影像資料。\n目前的存取權限已失效，系統無法繼續播放。\n請勿關閉頁面。請聯絡網站管理員。', audioEnabled: true, audioPath: '', audioName: '', intensity: 2 });
  const gearViewerWheelIntentRef = useRef({
    lastEventAt: 0,
    lastDirection: 0,
    lastSwitchAt: 0,
    accumulatedX: 0,
    acceptingImpulse: true,
    peakMagnitude: 0,
    tailFloorMagnitude: Infinity,
    hasDecelerated: false
  });
  const gearViewerAnimatingRef = useRef(false);
  const gearViewerClickTimerRef = useRef(null);
  const gearViewerDragRef = useRef(false);
  const gearViewerDragReleaseTimerRef = useRef(null);
  const gearViewerImageRef = useRef(null);
  const gearViewerShellRef = useRef(null);
  const gearViewerPanAnimationRef = useRef([]);
  const gearViewerPanPointerRef = useRef(null);
  const gearViewerZoomTransitionTimerRef = useRef(null);
  const [gearSearchQuery, setGearSearchQuery] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);
  const [storageOverview, setStorageOverview] = useState(null);
  const [showStorageOverview, setShowStorageOverview] = useState(false);
  const [isLoadingStorageOverview, setIsLoadingStorageOverview] = useState(false);

  const setGearViewerPan = (nextPan, { animate: shouldAnimate = false, useRenderedBounds } = {}) => {
    const bounded = clampGearViewerPan(nextPan, 2.8, useRenderedBounds);
    gearViewerPanAnimationRef.current.forEach(control => control.stop());
    gearViewerPanAnimationRef.current = [];
    if (shouldAnimate) {
      const transition = { duration: 0.32, ease: [0.16, 1, 0.3, 1] };
      gearViewerPanAnimationRef.current = [
        animate(gearViewerPanX, bounded.x, transition),
        animate(gearViewerPanY, bounded.y, transition)
      ];
      return;
    }
    gearViewerPanX.set(bounded.x);
    gearViewerPanY.set(bounded.y);
  };

  const finishGearViewerZoomTransition = (willBeZoomed) => {
    gearViewerZoomTransitionTimerRef.current = null;
    setIsGearViewerTransitioning(false);
    if (willBeZoomed) {
      setGearViewerPan({ x: gearViewerPanX.get(), y: gearViewerPanY.get() }, { useRenderedBounds: true });
    }
    const pointer = gearViewerPanPointerRef.current;
    if (pointer?.deferred) {
      pointer.deferred = false;
      pointer.x = gearViewerPanX.get();
      pointer.y = gearViewerPanY.get();
      pointer.clientX = pointer.latestClientX;
      pointer.clientY = pointer.latestClientY;
    }
  };

  const startGearViewerZoomTransition = (willBeZoomed) => {
    if (gearViewerZoomTransitionTimerRef.current) clearTimeout(gearViewerZoomTransitionTimerRef.current);
    setIsGearViewerTransitioning(true);
    gearViewerZoomTransitionTimerRef.current = window.setTimeout(() => finishGearViewerZoomTransition(willBeZoomed), 340);
  };

  const openGearViewer = (images, index) => {
    if (gearViewerZoomTransitionTimerRef.current) clearTimeout(gearViewerZoomTransitionTimerRef.current);
    gearViewerWheelIntentRef.current = {
      lastEventAt: 0,
      lastDirection: 0,
      lastSwitchAt: 0,
      accumulatedX: 0,
      acceptingImpulse: true,
      peakMagnitude: 0,
      tailFloorMagnitude: Infinity,
      hasDecelerated: false
    };
    setIsGearViewerFirstOpen(true);
    setIsGearViewerTransitioning(false);
    setIsGearViewerZoomed(false);
    gearViewerPanPointerRef.current = null;
    gearViewerPanX.set(0);
    gearViewerPanY.set(0);
    setGearViewer({ images, index });
  };

  const closeGearViewer = () => {
    if (gearViewerClickTimerRef.current) clearTimeout(gearViewerClickTimerRef.current);
    if (gearViewerDragReleaseTimerRef.current) clearTimeout(gearViewerDragReleaseTimerRef.current);
    if (gearViewerZoomTransitionTimerRef.current) clearTimeout(gearViewerZoomTransitionTimerRef.current);
    gearViewerZoomTransitionTimerRef.current = null;
    gearViewerClickTimerRef.current = null;
    gearViewerDragRef.current = false;
    gearViewerPanPointerRef.current = null;
    setIsGearViewerTransitioning(false);
    setIsGearViewerZoomed(false);
    gearViewerPanX.set(0);
    gearViewerPanY.set(0);
    setGearViewer(null);
  };

  const resetGearViewerZoom = (withTransition = false) => {
    if (withTransition) startGearViewerZoomTransition(false);
    else {
      if (gearViewerZoomTransitionTimerRef.current) clearTimeout(gearViewerZoomTransitionTimerRef.current);
      gearViewerZoomTransitionTimerRef.current = null;
      setIsGearViewerTransitioning(false);
    }
    setIsGearViewerZoomed(false);
    setGearViewerPan({ x: 0, y: 0 }, { animate: withTransition });
  };

  const moveGearViewer = direction => setGearViewer(current => {
    if (!current) return current;
    const nextIndex = Math.max(0, Math.min(current.images.length - 1, current.index + direction));
    return nextIndex === current.index ? current : { ...current, index: nextIndex };
  });

  const navigateGearViewer = direction => {
    if (gearViewerAnimatingRef.current) return;
    gearViewerAnimatingRef.current = true;
    setIsGearViewerFirstOpen(false);
    setGearViewerDirection(direction);
    moveGearViewer(direction);
    resetGearViewerZoom();
    window.setTimeout(() => { gearViewerAnimatingRef.current = false; }, 300);
  };

  const handleGearViewerWheel = event => {
    if (isGearViewerTransitioning) return;
    if (isGearViewerZoomed) {
      event.stopPropagation();
      gearViewerWheelIntentRef.current = {
        lastEventAt: 0,
        lastDirection: 0,
        lastSwitchAt: 0,
        accumulatedX: 0,
        acceptingImpulse: true,
        peakMagnitude: 0,
        tailFloorMagnitude: Infinity,
        hasDecelerated: false
      };
      setGearViewerPan({
        x: gearViewerPanX.get() - event.deltaX * 1.2,
        y: gearViewerPanY.get() - event.deltaY * 1.2
      });
      return;
    }
    const deltaX = event.deltaX;
    const magnitude = Math.abs(deltaX);
    if (magnitude < 4) return;

    const now = performance.now();
    const direction = deltaX > 0 ? 1 : -1;
    const wheelState = gearViewerWheelIntentRef.current;
    const eventGap = wheelState.lastEventAt > 0
      ? now - wheelState.lastEventAt
      : Infinity;
    const observedPeakMagnitude = Math.max(wheelState.peakMagnitude, magnitude);
    const sameDirectionGuardMs = observedPeakMagnitude >= 60
      ? 700
      : observedPeakMagnitude >= 40
        ? 500
        : 220;
    const directionChanged = wheelState.lastDirection !== 0
      && direction !== wheelState.lastDirection
      && magnitude >= 16;
    const sameDirectionRestart = !wheelState.acceptingImpulse
      && wheelState.hasDecelerated
      && now - wheelState.lastSwitchAt >= sameDirectionGuardMs
      && magnitude >= 28
      && magnitude >= wheelState.tailFloorMagnitude * 1.8
      && magnitude - wheelState.tailFloorMagnitude >= 10;
    const pausedLongEnough = eventGap > 90
      && (wheelState.lastSwitchAt === 0
        || now - wheelState.lastSwitchAt >= sameDirectionGuardMs);
    const isFreshImpulse = pausedLongEnough || directionChanged || sameDirectionRestart;

    if (isFreshImpulse) {
      wheelState.acceptingImpulse = true;
      wheelState.accumulatedX = 0;
      wheelState.peakMagnitude = magnitude;
      wheelState.tailFloorMagnitude = magnitude;
      wheelState.hasDecelerated = false;
    }

    wheelState.lastEventAt = now;
    if (wheelState.lastDirection === 0 || magnitude >= 16) {
      wheelState.lastDirection = direction;
    }
    wheelState.peakMagnitude = observedPeakMagnitude;

    if (!wheelState.acceptingImpulse) {
      wheelState.tailFloorMagnitude = Math.min(wheelState.tailFloorMagnitude, magnitude);
      if (magnitude <= wheelState.peakMagnitude * 0.55) {
        wheelState.hasDecelerated = true;
      }
      return;
    }

    wheelState.accumulatedX += deltaX;
    if (gearViewerAnimatingRef.current || Math.abs(wheelState.accumulatedX) < 28) return;

    const navigationDirection = wheelState.accumulatedX > 0 ? 1 : -1;
    wheelState.acceptingImpulse = false;
    wheelState.accumulatedX = 0;
    wheelState.lastSwitchAt = now;
    wheelState.peakMagnitude = magnitude;
    wheelState.tailFloorMagnitude = magnitude;
    wheelState.hasDecelerated = false;

    if (navigationDirection > 0 && gearViewer && gearViewer.index < gearViewer.images.length - 1) {
      navigateGearViewer(1);
    } else if (navigationDirection < 0 && gearViewer && gearViewer.index > 0) {
      navigateGearViewer(-1);
    }
  };

  const beginGearViewerDrag = () => {
    if (gearViewerClickTimerRef.current) {
      clearTimeout(gearViewerClickTimerRef.current);
      gearViewerClickTimerRef.current = null;
    }
    if (gearViewerDragReleaseTimerRef.current) clearTimeout(gearViewerDragReleaseTimerRef.current);
    gearViewerDragRef.current = true;
  };

  const finishGearViewerDrag = () => {
    if (gearViewerDragReleaseTimerRef.current) clearTimeout(gearViewerDragReleaseTimerRef.current);
    gearViewerDragReleaseTimerRef.current = setTimeout(() => { gearViewerDragRef.current = false; }, 0);
  };

  const beginGearViewerPan = event => {
    if (!isGearViewerZoomed || event.button > 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gearViewerPanPointerRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: gearViewerPanX.get(),
      y: gearViewerPanY.get(),
      latestClientX: event.clientX,
      latestClientY: event.clientY,
      moved: false,
      deferred: isGearViewerTransitioning
    };
  };

  const moveGearViewerPan = event => {
    const pointer = gearViewerPanPointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;
    pointer.latestClientX = event.clientX;
    pointer.latestClientY = event.clientY;
    if (pointer.deferred) return;
    const deltaX = event.clientX - pointer.clientX;
    const deltaY = event.clientY - pointer.clientY;
    if (!pointer.moved && Math.hypot(deltaX, deltaY) < 3) return;
    if (!pointer.moved) {
      pointer.moved = true;
      beginGearViewerDrag();
    }
    event.preventDefault();
    setGearViewerPan({ x: pointer.x + deltaX, y: pointer.y + deltaY });
  };

  const endGearViewerPan = event => {
    const pointer = gearViewerPanPointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;
    gearViewerPanPointerRef.current = null;
    if (pointer.moved) finishGearViewerDrag();
  };

  const clampGearViewerPan = (nextPan, scale = 2.8, useRenderedBounds = isGearViewerZoomed && !isGearViewerTransitioning) => {
    const image = gearViewerImageRef.current;
    if (!image || typeof window === 'undefined') return nextPan;
    const viewport = gearViewerShellRef.current?.getBoundingClientRect();
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportHeight = viewport?.height || window.innerHeight;

    if (useRenderedBounds && viewport) {
      const imageRect = image.getBoundingClientRect();
      const currentX = gearViewerPanX.get();
      const currentY = gearViewerPanY.get();
      const clampRenderedAxis = (target, current, imageStart, imageEnd, viewportStart, viewportEnd) => {
        if (imageEnd - imageStart <= viewportEnd - viewportStart) return 0;
        const requestedDelta = target - current;
        const minDelta = viewportEnd - imageEnd;
        const maxDelta = viewportStart - imageStart;
        return current + Math.max(minDelta, Math.min(maxDelta, requestedDelta));
      };
      return {
        x: clampRenderedAxis(nextPan.x, currentX, imageRect.left, imageRect.right, viewport.left, viewport.right),
        y: clampRenderedAxis(nextPan.y, currentY, imageRect.top, imageRect.bottom, viewport.top, viewport.bottom)
      };
    }

    const maxX = Math.max(0, (image.offsetWidth * scale - viewportWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * scale - viewportHeight) / 2);
    return { x: Math.max(-maxX, Math.min(maxX, nextPan.x)), y: Math.max(-maxY, Math.min(maxY, nextPan.y)) };
  };

  const handleGearViewerDragEnd = (event, { offset, velocity }) => {
    finishGearViewerDrag();
    if (isGearViewerZoomed) return;
    if (gearViewerAnimatingRef.current || !gearViewer) return;
    const swipeThreshold = 35;
    const velocityThreshold = 250;
    const isSwipeLeft = offset.x < -swipeThreshold || velocity.x < -velocityThreshold;
    const isSwipeRight = offset.x > swipeThreshold || velocity.x > velocityThreshold;
    if (isSwipeLeft && gearViewer.index < gearViewer.images.length - 1) navigateGearViewer(1);
    if (isSwipeRight && gearViewer.index > 0) navigateGearViewer(-1);
  };

  const handleGearViewerClick = event => {
    if (gearViewerDragRef.current || event.detail > 1) return;
    if (gearViewerClickTimerRef.current) clearTimeout(gearViewerClickTimerRef.current);
    gearViewerClickTimerRef.current = setTimeout(() => {
      if (!isGearViewerZoomed && !gearViewerDragRef.current) closeGearViewer();
      gearViewerClickTimerRef.current = null;
    }, 220);
  };

  const handleGearViewerDoubleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    if (gearViewerDragRef.current || isGearViewerTransitioning) return;
    if (gearViewerClickTimerRef.current) clearTimeout(gearViewerClickTimerRef.current);
    gearViewerClickTimerRef.current = null;
    if (isGearViewerZoomed) {
      resetGearViewerZoom(true);
      return;
    }
    const rect = gearViewerImageRef.current?.getBoundingClientRect() || event.currentTarget.getBoundingClientRect();
    const zoomScaleDelta = 2.8 - 1;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    setGearViewerPan({
      x: (centerX - event.clientX) * zoomScaleDelta,
      y: (centerY - event.clientY) * zoomScaleDelta
    }, { animate: true });
    startGearViewerZoomTransition(true);
    setIsGearViewerZoomed(true);
  };

  useEffect(() => {
    const updateScrollTopVisibility = () => {
      const threshold = currentSubPage
        ? Math.max(760, window.innerHeight * 0.95)
        : window.innerHeight * 2.72;
      setShowScrollTop(window.scrollY >= threshold);
    };
    window.addEventListener('scroll', updateScrollTopVisibility, { passive: true });
    window.addEventListener('resize', updateScrollTopVisibility);
    updateScrollTopVisibility();
    return () => {
      window.removeEventListener('scroll', updateScrollTopVisibility);
      window.removeEventListener('resize', updateScrollTopVisibility);
    };
  }, [currentSubPage]);

  const formatStorageBytes = (bytes = 0) => {
    const value = Number(bytes) || 0;
    if (value >= 1024 ** 3) return `${(value / (1024 ** 3)).toFixed(value >= 10 * (1024 ** 3) ? 0 : 1)} GB`;
    if (value >= 1024 ** 2) return `${(value / (1024 ** 2)).toFixed(1)} MB`;
    return `${Math.max(0, Math.round(value / 1024))} KB`;
  };

  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editLocation, setEditLocation] = useState('');
  const [editLocationParts, setEditLocationParts] = useState({ province: '', city: '', detail: '' });
  const [editEquipment, setEditEquipment] = useState('');
  const [editShootingDate, setEditShootingDate] = useState('');
  const [isManagingEquipmentPresets, setIsManagingEquipmentPresets] = useState(false);
  const [newEquipmentPreset, setNewEquipmentPreset] = useState('');
  const [isSavingLocationPhotoOrder, setIsSavingLocationPhotoOrder] = useState(false);
  const [locationResearch, setLocationResearch] = useState(null);
  const [isLocationResearching, setIsLocationResearching] = useState(false);
  const [locationResearchError, setLocationResearchError] = useState('');
  const [locationResearchOverlay, setLocationResearchOverlay] = useState(null);
  const [locationResearchElapsed, setLocationResearchElapsed] = useState(0);
  const [locationResearchByPhotoId, setLocationResearchByPhotoId] = useState({});
  const [photoEditDrafts, setPhotoEditDrafts] = useState({});
  const locationResearchStartedAtRef = useRef(null);
  const editingPhotoRef = useRef(null);
  const locationResearchAudioContextRef = useRef(null);

  const [availableCategories, setAvailableCategories] = useState(DEFAULT_WORK_CATEGORIES);
  
  const [activeCategory, setActiveCategory] = useState('uncategorized');
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [aiDescription, setAiDescription] = useState(''); 
  const [batchSyncSource, setBatchSyncSource] = useState(null);
  const [selectedBatchPhotoIds, setSelectedBatchPhotoIds] = useState([]);
  const [batchSyncFields, setBatchSyncFields] = useState({ location: true, equipment: true, shootingDate: true });
  const [isBatchSyncing, setIsBatchSyncing] = useState(false);
  const [showExistingBatchSyncPhotos, setShowExistingBatchSyncPhotos] = useState(false);
  const [locationBatchTarget, setLocationBatchTarget] = useState(null);
  const [selectedLocationPhotoIds, setSelectedLocationPhotoIds] = useState([]);
  const [isLocationBatchSaving, setIsLocationBatchSaving] = useState(false);
  const [showExistingLocationBatchPhotos, setShowExistingLocationBatchPhotos] = useState(false);
  const [locationPhotoLayout, setLocationPhotoLayout] = useState('double');
  const [visibleSubpagePhotoCount, setVisibleSubpagePhotoCount] = useState(24);
  const [isManagingLocationCollections, setIsManagingLocationCollections] = useState(false);
  const [newLocationCollectionTitle, setNewLocationCollectionTitle] = useState('');
  const [newLocationCollectionPrefix, setNewLocationCollectionPrefix] = useState('');
  const [isSavingLocationCollections, setIsSavingLocationCollections] = useState(false);
  const [isSavingLocationMenuOrder, setIsSavingLocationMenuOrder] = useState(false);

  const fileInputRef = useRef(null);
  const contactPortraitInputRef = useRef(null);
  const containerRef = useRef(null);
  const equipmentPresetCompositionRef = useRef(false);
  const equipmentPresetCompositionEndedAtRef = useRef(0);
  const tagCompositionRef = useRef(false);
  const tagCompositionEndedAtRef = useRef(0);
  
  const settleTimerRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const lightboxWheelIntentRef = useRef({
    lastEventAt: 0,
    lastDirection: 0,
    lastSwitchAt: 0,
    accumulatedX: 0,
    acceptingImpulse: true,
    peakMagnitude: 0,
    tailFloorMagnitude: Infinity,
    hasDecelerated: false
  });
  const clickTimerRef = useRef(null);
  const lightboxDragRef = useRef(false);
  const lightboxDragReleaseTimerRef = useRef(null);
  const lightboxImageRef = useRef(null);
  const lightboxShellRef = useRef(null);
  const lightboxPanAnimationRef = useRef([]);
  const lightboxPanPointerRef = useRef(null);
  const lightboxZoomTransitionTimerRef = useRef(null);
  
  const menuLeaveTimer = useRef(null);
  const workMenuPanelRef = useRef(null);
  const locationMenuCityColumnRef = useRef(null);
  const locationMenuDetailColumnRef = useRef(null);
  const heroTimerRef = useRef(null);
  const clickTimesRef = useRef([]); 
  const analyzePatternTimer = useRef(null);

  const authHeaders = {
    'Authorization': adminToken ? `Bearer ${adminToken}` : '',
    'Content-Type': 'application/json'
  };

  // ----------------------------------------------------
  // 核心事件處理函數 
  // ----------------------------------------------------

  const handleGoHome = () => { 
    setActiveMenu(null); 
    setIsAdminDashboard(false); 
    setCurrentSubPage(null); 
    setMaiPhotoTargetId(null);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleSmartLogoClick = () => {
    handleGoHome();
    const now = Date.now();
    clickTimesRef.current.push(now);
    if (analyzePatternTimer.current) clearTimeout(analyzePatternTimer.current);

    analyzePatternTimer.current = setTimeout(() => {
      const times = clickTimesRef.current;
      if (times.length > 0) {
        const groups = []; 
        let currentGroupCount = 1;
        for (let i = 1; i < times.length; i++) {
          if (times[i] - times[i - 1] < 600) {
            currentGroupCount++;
          } else {
            groups.push(currentGroupCount);
            currentGroupCount = 1;
          }
        }
        groups.push(currentGroupCount);
        if (groups.length === 2 && groups[0] === 2 && groups[1] === 3) {
          if (adminToken) {
            setIsAdminDashboard(true);
            setCurrentSubPage(null);
          } else {
            setShowLoginModal(true);
          }
        }
      }
      clickTimesRef.current = []; 
    }, 1000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword })
      });
      const data = await res.json();
      if (data.success) {
        setAdminToken(data.token);
        setAdminSessionVerified(true);
        setAdminVerificationResolved(true);
        localStorage.setItem('admin_token', data.token);
        sessionStorage.setItem('broadcast_admin_bypass', '1');
        setShowLoginModal(false);
        setLoginPassword('');
        setIsAdminDashboard(true);
        setCurrentSubPage(null);
      } else {
        alert(data.error || `登入失敗（HTTP ${res.status}）`);
      }
    } catch (err) {
      alert('無法連線到伺服器進行驗證。');
    }
  };

  const handleBroadcastRecoveryLogin = async event => {
    event.preventDefault();
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword })
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.error || `登入失敗（HTTP ${res.status}）`);
        return;
      }
      setAdminToken(data.token);
      setAdminSessionVerified(true);
      setAdminVerificationResolved(true);
      localStorage.setItem('admin_token', data.token);
      sessionStorage.setItem('broadcast_recovery_bypass', '1');
      setBroadcastRecoveryBypassed(true);
      setLoginPassword('');
    } catch (err) {
      alert('無法連線到伺服器進行驗證。');
    }
  };

  const handleLogout = () => {
    setAdminToken(null);
    setAdminSessionVerified(false);
    setAdminVerificationResolved(true);
    localStorage.removeItem('admin_token');
    sessionStorage.removeItem('broadcast_admin_bypass');
    sessionStorage.removeItem('broadcast_recovery_bypass');
    setBroadcastRecoveryBypassed(false);
    sessionStorage.removeItem('m_gallery_mai_chat_admin_v1');
    sessionStorage.removeItem('m_gallery_mai_admin_greeted');
    const logoutUrl = new URL(window.location.href);
    logoutUrl.searchParams.delete('admin');
    logoutUrl.searchParams.delete('admind');
    window.history.replaceState({}, '', `${logoutUrl.pathname}${logoutUrl.search}${logoutUrl.hash}`);
    setIsAdminDashboard(false);
    handleGoHome();
  };

  const handleMenuToggle = (menuKey, e) => {
    if (e) e.stopPropagation();
    if (menuLeaveTimer.current) clearTimeout(menuLeaveTimer.current);
    if (menuKey !== 'Work') {
      setHoveredLocationMenuGroup(null);
      setHoveredLocationMenuCity(null);
    }
    setActiveMenu(prev => prev === menuKey ? null : menuKey);
  };

  const handleMenuEnter = (menuKey) => {
    if (window.innerWidth <= 768) return; 
    if (menuLeaveTimer.current) clearTimeout(menuLeaveTimer.current);
    if (menuKey !== 'Work') {
      setHoveredLocationMenuGroup(null);
      setHoveredLocationMenuCity(null);
    }
    setActiveMenu(menuKey);
  };

  const handleMenuLeave = () => {
    if (window.innerWidth <= 768) return;
    menuLeaveTimer.current = setTimeout(() => {
      setActiveMenu(null);
      setHoveredLocationMenuGroup(null);
      setHoveredLocationMenuCity(null);
    }, 180);
  };

  // On desktop, a child column begins beside the item that opened it. Long
  // columns keep their own downward scroll rather than moving the whole menu.
  const updateLocationMenuColumnAnchor = (column, sourceNode) => {
    if (isMobileScreen || !sourceNode || !workMenuPanelRef.current) return;
    window.requestAnimationFrame(() => {
      const panel = workMenuPanelRef.current;
      if (!panel || !sourceNode.isConnected) return;
      const panelRect = panel.getBoundingClientRect();
      const sourceRect = sourceNode.getBoundingClientRect();
      const panelPaddingTop = Number.parseFloat(window.getComputedStyle(panel).paddingTop) || 0;
      const minTop = 0;
      const minVisibleColumnHeight = 138;
      const maxTop = Math.max(minTop, panel.clientHeight - minVisibleColumnHeight);
      const nextTop = Math.max(minTop, Math.min(sourceRect.top - panelRect.top - panelPaddingTop, maxTop));
      setLocationMenuColumnAnchors(previous => (
        previous[column] === nextTop ? previous : { ...previous, [column]: nextTop }
      ));
    });
  };

  useEffect(() => {
    if (activeMenu !== 'Work') {
      setHasOpenedLocationCityColumn(false);
      setHasOpenedLocationDetailColumn(false);
      setLocationMenuColumnAnchors({ cities: 0, details: 0 });
    }
  }, [activeMenu]);

  // 切換省份／城市時回到各自列表的起點；上方空白是可滾動緩衝，
  // 不再把子欄本身鎖死在觸發項的水平線以下。
  useEffect(() => {
    if (locationMenuCityColumnRef.current) locationMenuCityColumnRef.current.scrollTop = 0;
  }, [hoveredLocationMenuGroup]);

  useEffect(() => {
    if (locationMenuDetailColumnRef.current) locationMenuDetailColumnRef.current.scrollTop = 0;
  }, [hoveredLocationMenuGroup, hoveredLocationMenuCity]);
  
  const handleSelectSubPage = (item) => { 
    setActiveMenu(null); 
    setHoveredLocationMenuGroup(null);
    setHoveredLocationMenuCity(null);
    setIsAdminDashboard(false); 
    const homeReturn = !currentSubPage && !isAdminDashboard
      ? { id: '__home__', title: '首頁', scrollY: window.scrollY }
      : null;
    setCurrentSubPage(item.returnTo ? item : { ...item, ...(homeReturn ? { returnTo: homeReturn } : {}) }); 
    setMaiPhotoTargetId(null);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleSubPageBack = () => {
    if (currentSubPage?.returnTo?.id === '__home__') {
      const scrollY = Number(currentSubPage.returnTo.scrollY) || 0;
      setActiveMenu(null);
      setIsAdminDashboard(false);
      setCurrentSubPage(null);
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'auto' })));
      return;
    }
    if (currentSubPage?.returnTo) {
      const savedScrollY = Number(currentSubPage.returnTo.scrollY);
      const shouldRestoreScroll = Number.isFinite(savedScrollY);
      setActiveMenu(null);
      setIsAdminDashboard(false);
      setCurrentSubPage(currentSubPage.returnTo);
      setMaiPhotoTargetId(null);
      if (shouldRestoreScroll) {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.scrollTo({ top: savedScrollY, behavior: 'auto' })));
      } else {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
      return;
    }
    handleGoHome();
  };

  // ----------------------------------------------------
  // 門面配置讀寫與純隨機補齊 (Random Fill & Explicit Save)
  // ----------------------------------------------------

  const fetchSettings = () => {
    setBroadcastResolved(false);
    fetch(`${apiBaseUrl}/api/photos/settings`)
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) {
          const normalizedSettings = normalizeLegacySiteCopy(data);
          setSiteSettings(normalizedSettings);
          const savedCategories = Array.isArray(data.workCategories) ? data.workCategories.filter(Boolean) : DEFAULT_WORK_CATEGORIES;
          setAvailableCategories(savedCategories.includes('uncategorized') ? savedCategories : [...savedCategories, 'uncategorized']);
          setEditHeroLines(normalizedSettings.heroLines || ['', '', '']);
          setEditPhilTitle(normalizedSettings.philosophyTitle || '');
          setEditPhilBody(normalizedSettings.philosophyBody || '');
        }
      })
      .catch(err => console.error('取得設定失敗:', err))
      .finally(() => setBroadcastResolved(true));
  };

  const handleSaveSiteSettings = async () => {
    setIsSavingSettings(true);
    try {
      const payload = {
        heroPhotoIds: sanitizeHeroPhotoIds(siteSettings.heroPhotoIds),
        heroPhotoMobileIds: sanitizeHeroPhotoIds(siteSettings.heroPhotoMobileIds),
        heroLines: editHeroLines,
        philosophyTitle: editPhilTitle,
        philosophyBody: editPhilBody
      };
      const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSiteSettings(normalizeLegacySiteCopy(data.setting));
        alert('門面與文案設定儲存成功！✨');
      } else {
        alert(data.error || '儲存失敗');
      }
    } catch (err) {
      alert('無法連線到伺服器');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveContact = async (silent = false) => {
    setIsSavingContact(true);
    try {
      const normalizedSocials = (siteSettings.contactSocials || []).map((platform, index) => ({
        id: String(platform.id || `social-${index}`),
        label: String(platform.label || '').trim(),
        kind: platform.kind === 'email' ? 'email' : 'url',
        url: platform.kind === 'email' ? String(platform.url || '').trim() : normalizeExternalUrl(platform.url)
      })).filter(platform => platform.label);
      if (normalizedSocials.some(platform => platform.kind === 'url' && siteSettings.contactSocials?.find(item => item.id === platform.id)?.url && !platform.url)) {
        throw new Error('社交連結格式不正確，請輸入網域或完整 https:// 連結。');
      }
      const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          contactName: siteSettings.contactName || 'Miles',
          contactRole: siteSettings.contactRole || '',
          contactAbout: siteSettings.contactAbout || '',
          contactEmail: siteSettings.contactEmail || '',
          contactInstagram: normalizedSocials.find(item => item.id === 'instagram')?.url || '',
          contactXiaohongshu: normalizedSocials.find(item => item.id === 'xiaohongshu')?.url || '',
          contactSocials: normalizedSocials,
          contactPortrait: { positionX: 50, positionY: 50, scale: 100, ...(siteSettings.contactPortrait || {}) }
        })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || '儲存失敗');
      const hasPersistedContactFields = CONTACT_SETTING_KEYS.every(key => Object.prototype.hasOwnProperty.call(data.setting || {}, key));
      if (!hasPersistedContactFields) {
        throw new Error('目前運行中的後端尚未更新 Contact 設定，資料沒有儲存。');
      }
      setSiteSettings(normalizeLegacySiteCopy(data.setting));
      if (!silent) alert('Contact 設定已儲存，前台會同步更新。');
      return true;
    } catch (err) {
      alert(err.message || '無法連線到伺服器');
      return false;
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleContactPortraitUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingContactPortrait(true);
    try {
      const preparedFile = await compressTo4K(file, 2200, 0.9);
      const formData = new FormData();
      formData.append('image', preparedFile);
      const res = await fetch(`${apiBaseUrl}/api/photos/settings/contact-portrait`, {
        method: 'POST',
        headers: { Authorization: authHeaders.Authorization },
        body: formData
      });
      const data = await res.json();
      if (!data.success || !data.setting?.contactPortrait?.medium) throw new Error(data.error || '上傳失敗');
      setSiteSettings(normalizeLegacySiteCopy(data.setting));
    } catch (err) {
      alert(err.message || '作者頁照片上傳失敗');
    } finally {
      event.target.value = '';
      setIsUploadingContactPortrait(false);
    }
  };

  const validHeroPhotoIdSet = new Set(
    photos
      .filter(photo => photo && photo._id && (photo.imagePath || photo.previewPath))
      .map(photo => String(photo._id))
  );

  const sanitizeHeroPhotoIds = (ids) => {
    const normalizedIds = Array.from(new Set((Array.isArray(ids) ? ids : []).map(id => String(id))));
    if (isLoading) return normalizedIds.slice(0, 5);
    return normalizedIds.filter(id => validHeroPhotoIdSet.has(id)).slice(0, 5);
  };

  const desktopHeroIds = sanitizeHeroPhotoIds(siteSettings.heroPhotoIds);
  const mobileHeroIds = sanitizeHeroPhotoIds(siteSettings.heroPhotoMobileIds);
  const currentHeroIds = heroMode === 'desktop' ? desktopHeroIds : mobileHeroIds;

  const updateHeroIds = (newIds) => {
    const key = heroMode === 'desktop' ? 'heroPhotoIds' : 'heroPhotoMobileIds';
    setSiteSettings(prev => ({ ...prev, [key]: sanitizeHeroPhotoIds(newIds) }));
  };

  const handleRemoveHeroSlot = (photoId) => {
    const next = currentHeroIds.filter(id => id !== photoId);
    updateHeroIds(next);
  };

  const handleMoveHeroSlot = (photoId, direction) => {
    const list = [...currentHeroIds];
    const index = list.indexOf(photoId);
    if (index < 0) return;
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const temp = list[index];
    list[index] = list[target];
    list[target] = temp;
    updateHeroIds(list);
  };

  const handleAddHeroPhoto = (photoId) => {
    if (currentHeroIds.includes(photoId)) return;
    if (currentHeroIds.length >= 5) {
      alert('Hero 輪播上限為 5 張，請先移除一張後再新增 ✨');
      return;
    }
    const next = [...currentHeroIds, photoId];
    updateHeroIds(next);
    setShowPhotoPicker(false);
  };

  // 🍎 真·隨機補齊演算法（保證每次點擊都不重復）
  const handleSmartAiFillHero = () => {
    const maxLimit = 5;
    const existing = [...currentHeroIds];
    const needed = maxLimit - existing.length;

    if (needed <= 0) {
      alert('Hero 隊列已滿 5 張！如需更換，請點擊「編輯」移除部分照片後再補齊 ✨');
      return;
    }

    let availablePool = workLibraryPhotos.filter(p => !existing.includes(p._id));
    if (availablePool.length === 0) {
      alert('媒體庫中暫無更多可選照片');
      return;
    }

    // 真正隨機打亂陣列
    const shuffled = [...availablePool].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, needed);

    const finalIds = [...existing, ...picked.map(p => p._id)];
    updateHeroIds(finalIds);
  };

  // ----------------------------------------------------
  // 計算屬性 (useMemo)
  // ----------------------------------------------------
  
  const validPhotos = useMemo(
    () => photos.filter(p => p && (p.imagePath || p.previewPath)),
    [photos]
  );

  // 移進 Journeys 的照片仍可用於前台與旅程編輯器，但不再佔用 Work 媒體庫。
  const workLibraryPhotos = useMemo(
    () => validPhotos.filter(photo => !photo.journeyCandidate),
    [validPhotos]
  );

  const journeyCandidatePhotos = useMemo(
    () => validPhotos.filter(photo => photo.journeyCandidate),
    [validPhotos]
  );

  const publishedJourneyPhotoIds = useMemo(
    () => new Set(journeyEntries.flatMap(entry => (entry.images || [])
      .map(image => String(image?.sourcePhotoId || ''))
      .filter(Boolean))),
    [journeyEntries]
  );

  const unpublishedJourneyCandidatePhotos = useMemo(
    () => journeyCandidatePhotos.filter(photo => !publishedJourneyPhotoIds.has(String(photo._id))),
    [journeyCandidatePhotos, publishedJourneyPhotoIds]
  );

  const publishedJourneyCandidatePhotos = useMemo(
    () => journeyCandidatePhotos.filter(photo => publishedJourneyPhotoIds.has(String(photo._id))),
    [journeyCandidatePhotos, publishedJourneyPhotoIds]
  );

  const dynamicAdminTabs = useMemo(() => {
    const tabs = [
      { id: 'all', label: '全部作品', count: workLibraryPhotos.length },
      { id: 'uncategorized', label: '⚠️ 未分類', count: workLibraryPhotos.filter(needsPhotoOrganization).length }
    ];

    availableCategories.forEach(cat => {
      if (cat !== 'uncategorized' && cat !== 'all') {
        const count = workLibraryPhotos.filter(p => p.subCategory === cat).length;
        tabs.push({
          id: cat,
          label: TAG_LABELS[cat] || cat,
          count
        });
      }
    });

    return tabs;
  }, [workLibraryPhotos, availableCategories]);

  const locationGroups = useMemo(() => {
    const groups = new Map();
    workLibraryPhotos.forEach(photo => {
      const key = getLocationKey(photo.location);
      if (!groups.has(key)) groups.set(key, { key, label: key === MISSING_LOCATION_KEY ? '待填地點' : key, photos: [] });
      groups.get(key).photos.push(photo);
    });
    return Array.from(groups.values()).map(group => {
      const photos = sortLocationPhotos(group.photos, group.key, siteSettings.locationPhotoOrders);
      const latestDate = Math.max(0, ...group.photos.map(photo => Number(photo.shootingDate || photo.createdAt || 0)));
      return { ...group, photos, latestDate };
    }).sort((a, b) => Number(b.latestDate || 0) - Number(a.latestDate || 0));
  }, [workLibraryPhotos, siteSettings.locationPhotoOrders]);

  // 後台地點管理採用同一套省份／城市／景點階層；保留原始地點群組，避免批量編輯時誤傷相近地點。
  const locationAdminTree = useMemo(() => {
    const roots = new Map();
    const latestPhoto = (groups) => groups.flatMap(group => group.photos).sort((a, b) => Number(b.shootingDate || 0) - Number(a.shootingDate || 0))[0];

    const ensureRoot = (key, title) => {
      if (!roots.has(key)) roots.set(key, { kind: 'root', key, title, directGroups: [], cities: new Map() });
      return roots.get(key);
    };

    locationGroups.forEach(group => {
      if (group.key === MISSING_LOCATION_KEY) {
        ensureRoot(MISSING_LOCATION_KEY, '待填地點').directGroups.push(group);
        return;
      }

      const { province, city, detail } = splitLocationHierarchy(group.label);
      const rootTitle = province || city || group.label;
      const root = ensureRoot(rootTitle, rootTitle);

      if (!province || !city) {
        root.directGroups.push(group);
        return;
      }

      const cityKey = `${province}·${city}`;
      if (!root.cities.has(cityKey)) root.cities.set(cityKey, { kind: 'city', key: cityKey, title: city, directGroups: [], details: new Map() });
      const cityNode = root.cities.get(cityKey);
      if (!detail) {
        cityNode.directGroups.push(group);
        return;
      }
      if (!cityNode.details.has(group.key)) cityNode.details.set(group.key, { kind: 'group', key: group.key, title: detail, group, groups: [group], photo: group.photos[0], count: group.photos.length, latestDate: group.latestDate });
    });

    const withSummary = (node) => {
      const children = node.kind === 'root'
        ? Array.from(node.cities.values()).map(withSummary)
        : node.kind === 'city'
          ? Array.from(node.details.values())
          : [];
      const groups = [
        ...(node.directGroups || []),
        ...children.flatMap(child => child.groups || [child.group]).filter(Boolean)
      ];
      return {
        ...node,
        children: sortLocationMenuItems(
          children.sort((a, b) => Number(b.latestDate || 0) - Number(a.latestDate || 0)),
          node.kind === 'root' ? `cities:${node.key}` : `details:${node.key}`,
          siteSettings.locationMenuOrders,
          child => child.key
        ),
        groups,
        count: groups.reduce((sum, group) => sum + group.photos.length, 0),
        photo: latestPhoto(groups),
        latestDate: Math.max(0, ...groups.map(group => Number(group.latestDate || 0)))
      };
    };

    return sortLocationMenuItems(
      Array.from(roots.values()).map(withSummary).sort((a, b) => Number(b.latestDate || 0) - Number(a.latestDate || 0)),
      'roots',
      siteSettings.locationMenuOrders,
      root => root.key
    );
  }, [locationGroups, siteSettings.locationMenuOrders]);

  const locationBatchCandidates = useMemo(() => {
    if (!locationBatchTarget) return { missing: [], existing: [] };
    const candidates = workLibraryPhotos.filter(photo => getLocationKey(photo.location) !== locationBatchTarget.key);
    return {
      missing: candidates.filter(photo => getLocationKey(photo.location) === MISSING_LOCATION_KEY),
      existing: candidates.filter(photo => getLocationKey(photo.location) !== MISSING_LOCATION_KEY)
    };
  }, [workLibraryPhotos, locationBatchTarget]);

  const batchSyncCandidates = useMemo(() => {
    if (!batchSyncSource) return { missing: [], existing: [] };
    const candidates = workLibraryPhotos
      .filter(photo => photo._id !== batchSyncSource._id)
      .sort((a, b) => Number(b.shootingDate || 0) - Number(a.shootingDate || 0));
    return {
      missing: candidates.filter(photo => getLocationKey(photo.location) === MISSING_LOCATION_KEY),
      existing: candidates.filter(photo => getLocationKey(photo.location) !== MISSING_LOCATION_KEY)
    };
  }, [workLibraryPhotos, batchSyncSource]);

  const locationCollectionPrefixOptions = useMemo(() => {
    const prefixes = new Set();
    workLibraryPhotos.forEach(photo => {
      const parts = String(photo.location || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
      for (let index = 1; index < parts.length; index += 1) prefixes.add(parts.slice(0, index).join('·'));
    });
    return Array.from(prefixes).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
  }, [workLibraryPhotos]);

  // 🍎 手機端與桌面端各自精確讀取對應 Hero 圖片陣列
  const heroPhotos = useMemo(() => {
    const activeIds = isMobileScreen
      ? (mobileHeroIds.length > 0 ? mobileHeroIds : desktopHeroIds)
      : desktopHeroIds;

    if (activeIds && activeIds.length > 0) {
      const photoMap = new Map(workLibraryPhotos.map(p => [p._id, p]));
      const ordered = activeIds.map(id => photoMap.get(id)).filter(Boolean);
      if (ordered.length > 0) return ordered;
    }
    return workLibraryPhotos.slice(0, 4);
  }, [workLibraryPhotos, desktopHeroIds, mobileHeroIds, isMobileScreen]);

  const currentHeroPhoto = heroPhotos.length > 0 ? heroPhotos[heroIndex % heroPhotos.length] : null;

  const dynamicNavMenus = useMemo(() => {
    const locationStats = new Map();
    workLibraryPhotos.forEach(photo => {
      const location = String(photo.location || '').trim();
      if (getLocationKey(location) === MISSING_LOCATION_KEY) return;
      const current = locationStats.get(location) || { location, count: 0, latestDate: 0 };
      current.count += 1;
      current.latestDate = Math.max(current.latestDate, Number(photo.shootingDate || photo.createdAt || 0));
      locationStats.set(location, current);
    });

    const locations = Array.from(locationStats.values()).sort((a, b) => b.latestDate - a.latestDate);
    const manualCollections = normalizeLocationCollections(siteSettings.locationCollections);
    const inferredPrefixes = new Map();
    locations.forEach(stat => {
      const parts = String(stat.location || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
      if (parts.length < 2) return;
      const prefix = parts[0];
      const current = inferredPrefixes.get(prefix) || { prefix, locations: new Set() };
      current.locations.add(stat.location);
      inferredPrefixes.set(prefix, current);
    });
    const automaticCollections = Array.from(inferredPrefixes.values())
      .filter(item => !manualCollections.some(collection => normalizeSearchValue(collection.prefix) === normalizeSearchValue(item.prefix)))
      .map(item => ({ id: `auto-location-collection-${normalizeSearchValue(item.prefix)}`, title: item.prefix, prefix: item.prefix, automatic: true }));
    const collections = [...manualCollections, ...automaticCollections];
    const collectionStats = new Map(collections.map(collection => [collection.id, { ...collection, children: [], total: 0, latestDate: 0 }]));
    const emittedCollections = new Set();
    const locationItems = [];
    locations.forEach(stat => {
      const collection = [...collectionStats.values()]
        .filter(item => matchesLocationCollection(stat.location, item.prefix))
        .sort((a, b) => b.prefix.length - a.prefix.length)[0];
      if (collection) {
        collection.total += stat.count;
        collection.latestDate = Math.max(collection.latestDate, stat.latestDate);
        const detail = getLocationCollectionChild(stat.location, collection.prefix);
        if (detail) collection.children.push({ ...stat, detail });
        if (emittedCollections.has(collection.id)) return;
        emittedCollections.add(collection.id);
        locationItems.push({
          id: collection.id,
          type: 'location-collection',
          title: collection.title,
          desc: collection.automatic ? '自動折疊地點' : '已設定折疊地點',
          collectionPrefix: collection.prefix
        });
        return;
      }
      locationItems.push({
        id: stat.location,
        type: 'location',
        title: stat.location,
        desc: `${stat.count} 張作品 · 按地點探索圖集`
      });
    });

    const collectionItems = locationItems.map(item => {
      if (item.type !== 'location-collection') return item;
      const collection = collectionStats.get(item.id);
      const cityStats = new Map();
      collection.children.forEach(child => {
        const city = String(child.detail || '').split(/[·•]/).map(part => part.trim()).filter(Boolean)[0];
        if (!city) return;
        const cityPrefix = [collection.prefix, city].filter(Boolean).join('·');
        const current = cityStats.get(cityPrefix) || { id: cityPrefix, type: 'location-collection', collectionPrefix: cityPrefix, title: city, count: 0, latestDate: 0 };
        current.count += child.count;
        current.latestDate = Math.max(current.latestDate, child.latestDate);
        cityStats.set(cityPrefix, current);
      });
      collection.children.forEach(child => {
        const parts = String(child.detail || '').split(/[·•]/).map(part => part.trim()).filter(Boolean);
        const city = parts[0];
        if (!city) return;
        const cityPrefix = [collection.prefix, city].filter(Boolean).join('·');
        const cityItem = cityStats.get(cityPrefix);
        if (!cityItem) return;
        const detail = parts.slice(1).join('·') || `${city} 全部`;
        cityItem.children = cityItem.children || [];
        cityItem.children.push({
          id: child.location,
          type: 'location',
          title: detail,
          desc: `${child.count} 張作品 · ${city}`,
          latestDate: child.latestDate
        });
      });
      const children = Array.from(cityStats.values()).sort((a, b) => b.latestDate - a.latestDate).map(child => ({
        ...child,
        desc: `${child.count} 張作品 · ${collection.title}`,
        children: sortLocationMenuItems(
          (child.children || []).sort((a, b) => b.latestDate - a.latestDate),
          `details:${child.collectionPrefix}`,
          siteSettings.locationMenuOrders,
          detail => detail.id
        )
      }));
      return {
        ...item,
        desc: `${children.length} 個具體地點 · ${collection.total} 張作品`,
        children: sortLocationMenuItems(children, `cities:${collection.collectionPrefix}`, siteSettings.locationMenuOrders, city => city.collectionPrefix)
      };
    });

    return {
      Work: [
        {
          category: '攝影題材',
          items: availableCategories
            .filter(category => category !== 'uncategorized')
            .map(category => ({ id: category, title: TAG_LABELS[category] || category, desc: '按題材探索作品' }))
        },
        {
          category: '目的地圖集',
          items: collectionItems.length > 0
            ? sortLocationMenuItems(collectionItems, 'roots', siteSettings.locationMenuOrders, item => item.collectionPrefix || item.id)
            : [{ id: 'none', title: '暫無地點', desc: '上傳照片即可自動生成' }]
        }
      ],
      Gear: [
        { category: '器材配置', items: [{ id: 'gear_cameras', title: '機身與鏡頭', desc: '常用的攝影器材配置' }, { id: 'gear_edc', title: 'EDC 隨身裝備', desc: '相機包與隨身好物' }] },
        { category: '後期調色', items: [{ id: 'gear_luts', title: '色彩 Preset / LUTs', desc: 'Clean Fit 調色與前後對比' }] }
      ],
      Journeys: [
        { category: '旅行與日常', items: [{ id: 'journeys_index', title: '旅行手帳', desc: '同行的人、路上的光與隨手記錄' }] }
      ],
      Contact: [
        { category: '關於與聯絡', items: [{ id: 'contact_about', title: '個人簡介', desc: 'Miles 的攝影理念與審美追求' }, { id: 'contact_socials', title: '社交動態', desc: 'Instagram、小紅書與聯絡方式' }] }
      ]
    };
  }, [workLibraryPhotos, availableCategories, siteSettings.locationCollections, siteSettings.locationMenuOrders]);

  const activeLocationMenuGroup = useMemo(() => {
    if (activeMenu !== 'Work' || !hoveredLocationMenuGroup) return null;
    const destinationColumn = dynamicNavMenus.Work?.find(column => column.category === '目的地圖集');
    return destinationColumn?.items.find(item => item.id === hoveredLocationMenuGroup && item.type === 'location-collection') || null;
  }, [activeMenu, hoveredLocationMenuGroup, dynamicNavMenus]);

  const activeLocationMenuCity = useMemo(() => {
    if (!activeLocationMenuGroup || !hoveredLocationMenuCity) return null;
    return activeLocationMenuGroup.children?.find(item => item.id === hoveredLocationMenuCity) || null;
  }, [activeLocationMenuGroup, hoveredLocationMenuCity]);

  const workMenuColumnCount = hasOpenedLocationDetailColumn ? 4 : hasOpenedLocationCityColumn ? 3 : 2;
  const workMenuMaxWidth = workMenuColumnCount === 4 ? '1240px' : workMenuColumnCount === 3 ? '980px' : '760px';

  const updateEditLocationParts = (province, city, detail) => {
    const nextParts = {
      province: String(province || ''),
      city: String(city || ''),
      detail: String(detail || '')
    };
    const normalizedProvince = nextParts.province.trim();
    const normalizedCity = nextParts.city.trim();
    const normalizedDetail = nextParts.detail.trim();
    setEditLocationParts(nextParts);
    setEditLocation([normalizedProvince, normalizedCity, normalizedDetail].filter(Boolean).join('·'));
  };

  const applyResearchSuggestedLocation = location => {
    const { province, city, detail } = splitResearchSuggestedLocation(location);
    updateEditLocationParts(province, city, detail);
  };

  const closePhotoEditor = () => {
    if (editingPhoto?._id && locationResearchByPhotoId[String(editingPhoto._id)]) {
      const photoId = String(editingPhoto._id);
      setPhotoEditDrafts(previous => ({
        ...previous,
        [photoId]: {
          location: editLocation,
          locationParts: editLocationParts,
          equipment: editEquipment
        }
      }));
    }
    setEditingPhoto(null);
  };

  // 二級頁歸屬哪個一級導航；首頁沒有歸屬，因此不顯示藍線。
  const currentPrimaryMenu = useMemo(() => {
    if (!currentSubPage) return null;
    if (String(currentSubPage.returnTo?.id || '').startsWith('gear_')) return 'Gear';
    return Object.entries(dynamicNavMenus).find(([, columns]) => columns.some(column =>
      column.items.some(item => item.id === currentSubPage.id && (!item.type || item.type === currentSubPage.type))
    ))?.[0] || null;
  }, [currentSubPage, dynamicNavMenus]);

  // 首頁 Hero 是完整的沉浸區域；M.ai 只在 Hero 結束後或其他頁面出現。
  useEffect(() => {
    const updateVisibility = () => {
      const hasLeftHero = window.scrollY >= window.innerHeight * 2.72;
      setShowMAiLauncher(Boolean(isAdminDashboard || currentSubPage || hasLeftHero));
    };
    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [isAdminDashboard, currentSubPage]);

  // 輕量匿名統計：只記隨機裝置／分頁識別碼，不上傳原始 IP 或個人資料。
  useEffect(() => {
    const getIdentifier = (storage, key) => {
      let value = storage.getItem(key);
      if (!value) {
        value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        storage.setItem(key, value);
      }
      return value;
    };
    const visitorId = getIdentifier(localStorage, 'm_gallery_visitor_id_v1');
    const sessionId = getIdentifier(sessionStorage, 'm_gallery_session_id_v1');
    const pagePath = isAdminDashboard
      ? `/admin/${String(adminModule || 'work').toLowerCase()}`
      : currentSubPage
        ? `/view/${encodeURIComponent(currentSubPage.id || currentSubPage.title || 'album')}`
        : '/';
    const width = window.innerWidth;
    const deviceType = width <= 768 ? 'mobile' : width <= 1100 ? 'tablet' : 'desktop';
    fetch(`${apiBaseUrl}/api/analytics/visit`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {})
      },
      body: JSON.stringify({ visitorId, sessionId, path: pagePath, referrer: document.referrer, deviceType })
    }).catch(() => {});
  }, [apiBaseUrl, adminToken, isAdminDashboard, adminModule, currentSubPage]);

  const displayPhotos = useMemo(() => {
    if (isAdminDashboard) {
      const adminPhotos = adminModule === 'Work' ? workLibraryPhotos : validPhotos;
      if (workTab === 'inbox') {
        return adminPhotos.filter(p => needsPhotoOrganization(p) || !p.aiDescription);
      }
      let filtered = adminPhotos;
      if (libraryFilterTag === 'uncategorized') filtered = filtered.filter(needsPhotoOrganization);
      else if (libraryFilterTag !== 'all') filtered = filtered.filter(p => p.subCategory === libraryFilterTag);
      const normalizedLibraryQuery = normalizeSearchValue(librarySearchQuery);
      if (normalizedLibraryQuery) {
        filtered = filtered.filter(photo => normalizeSearchValue([
          photo.title,
          photo.location,
          photo.shootingParams?.equipment,
          photo.shootingParams?.camera,
          photo.shootingParams?.lens,
          photo.subCategory,
          photo.aiDescription
        ].filter(Boolean).join(' ')).includes(normalizedLibraryQuery));
      }
      if (librarySortMode === 'needsAttention') {
        return [...filtered].sort((a, b) => {
          const priorityDifference = Number(needsPhotoOrganization(b)) - Number(needsPhotoOrganization(a));
          if (priorityDifference) return priorityDifference;
          return Number(b.shootingDate || b.createdAt || 0) - Number(a.shootingDate || a.createdAt || 0);
        });
      }
      const sortField = librarySortMode === 'createdAt' ? 'createdAt' : 'shootingDate';
      return [...filtered].sort((a, b) => Number(b[sortField] || 0) - Number(a[sortField] || 0));
    }
    
    if (!currentSubPage) return workLibraryPhotos;

    if (currentSubPage.type === 'journey-related') {
      const locationScopes = (currentSubPage.locationScopes || []).filter(scope => scope?.prefix);
      if (locationScopes.length) {
        return workLibraryPhotos
          .filter(photo => locationScopes.some(scope => matchesLocationCollection(photo.location, scope.prefix)))
          .sort((left, right) => Number(right.shootingDate || 0) - Number(left.shootingDate || 0));
      }
      const relatedIds = (currentSubPage.photoIds || []).map(String);
      const order = new Map(relatedIds.map((id, index) => [id, index]));
      return workLibraryPhotos
        .filter(photo => order.has(String(photo._id)))
        .sort((left, right) => order.get(String(left._id)) - order.get(String(right._id)));
    }

    if (currentSubPage.type === 'location') {
      return sortLocationPhotos(
        workLibraryPhotos.filter(p => p.location === currentSubPage.id),
        currentSubPage.id,
        siteSettings.locationPhotoOrders
      );
    }

    if (currentSubPage.type === 'location-collection') {
      return workLibraryPhotos
        .filter(photo => matchesLocationCollection(photo.location, currentSubPage.collectionPrefix))
        .sort((a, b) => Number(b.shootingDate || 0) - Number(a.shootingDate || 0));
    }

    const subId = currentSubPage.id;
    return workLibraryPhotos.filter(p => p.subCategory === subId || (p.tags && p.tags.includes(subId)));
  }, [currentSubPage, validPhotos, workLibraryPhotos, siteSettings.locationPhotoOrders, isAdminDashboard, adminModule, workTab, libraryFilterTag, librarySearchQuery, librarySortMode]);

  const activeLightboxPhotos = lightboxPhotos.length ? lightboxPhotos : displayPhotos;
  const isPhotoLayoutAvailable = Boolean(
    currentSubPage
    && displayPhotos.length > 0
    && !['gear_luts', 'gear_cameras', 'gear_edc'].includes(currentSubPage.id)
  );
  // Narrow screens always use one full-width card. The desktop layout choice
  // remains available, but it must never force desktop metadata into a phone.
  const isDoublePhotoLayout = isPhotoLayoutAvailable && !isMobileScreen && locationPhotoLayout === 'double';

  useEffect(() => {
    setVisibleSubpagePhotoCount(24);
  }, [currentSubPage?.id]);

  const latestJourneys = useMemo(() => {
    const locationMap = new Map();
    workLibraryPhotos.forEach(photo => {
      const loc = (photo.location || '').trim();
      if (!loc || loc === '未知地點') return;
      if (!locationMap.has(loc)) {
        locationMap.set(loc, []);
      }
      locationMap.get(loc).push(photo);
    });

    const albums = Array.from(locationMap.entries()).map(([locName, photoList]) => {
      const sorted = [...photoList].sort((a, b) => Number(b.shootingDate || 0) - Number(a.shootingDate || 0));
      const coverPhoto = sorted.find(p => p.subCategory === 'featured' || (p.tags && p.tags.includes('featured'))) || sorted[0];
      const latestDate = sorted[0]?.shootingDate || 0;

      return {
        id: locName,
        type: 'location',
        title: locName,
        desc: '📍 按地點探索圖集',
        count: photoList.length,
        coverPhoto,
        latestDate
      };
    });

    return albums.sort((a, b) => Number(b.latestDate || 0) - Number(a.latestDate || 0));
  }, [workLibraryPhotos]);

  const featuredPhotos = useMemo(() => {
    const photoMap = new Map(workLibraryPhotos.map(photo => [String(photo._id), photo]));
    const targetCount = Math.max(1, Math.min(36, Number(siteSettings.featuredTargetCount) || 9));
    return (siteSettings.featuredPhotoIds || []).map(id => photoMap.get(String(id))).filter(Boolean).slice(0, targetCount);
  }, [workLibraryPhotos, siteSettings.featuredPhotoIds, siteSettings.featuredTargetCount]);

  const filteredPhotos = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(searchQuery);
    if (!normalizedQuery) return [];
    return workLibraryPhotos.filter(photo => normalizeSearchValue([
      photo.title,
      photo.location,
      photo.shootingParams?.equipment,
      photo.shootingParams?.camera,
      photo.shootingParams?.lens,
      photo.subCategory,
      ...(Array.isArray(photo.tags) ? photo.tags : []),
      photo.aiDescription
    ].filter(Boolean).join(' ')).includes(normalizedQuery)).slice(0, 8);
  }, [searchQuery, workLibraryPhotos]);

  // ----------------------------------------------------
  // 生命週期與副作用 (useEffect)
  // ----------------------------------------------------

  useEffect(() => {
    const handleResize = () => {
      setIsMobileScreen(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (lightboxIndex >= 0) {
      const preventTrackpadNav = (e) => {
        if (Math.abs(e.deltaX) > 0) {
          if (e.cancelable) e.preventDefault();
        }
      };
      window.addEventListener('wheel', preventTrackpadNav, { passive: false, capture: true });
      document.addEventListener('wheel', preventTrackpadNav, { passive: false, capture: true });
      return () => {
        window.removeEventListener('wheel', preventTrackpadNav, { capture: true });
        document.removeEventListener('wheel', preventTrackpadNav, { capture: true });
      };
    }
  }, [lightboxIndex]);

  useEffect(() => {
    if (!gearViewer) return undefined;
    const preventTrackpadNav = event => {
      if (Math.abs(event.deltaX) > 0 && event.cancelable) event.preventDefault();
    };
    window.addEventListener('wheel', preventTrackpadNav, { passive: false, capture: true });
    document.addEventListener('wheel', preventTrackpadNav, { passive: false, capture: true });
    return () => {
      window.removeEventListener('wheel', preventTrackpadNav, { capture: true });
      document.removeEventListener('wheel', preventTrackpadNav, { capture: true });
    };
  }, [gearViewer]);

  useEffect(() => {
    if (lightboxIndex >= 0) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    if (lightboxDragReleaseTimerRef.current) {
      clearTimeout(lightboxDragReleaseTimerRef.current);
      lightboxDragReleaseTimerRef.current = null;
    }
    if (lightboxZoomTransitionTimerRef.current) {
      clearTimeout(lightboxZoomTransitionTimerRef.current);
      lightboxZoomTransitionTimerRef.current = null;
    }
    lightboxDragRef.current = false;
    lightboxPanPointerRef.current = null;
    setIsLightboxTransitioning(false);
  }, [lightboxIndex]);

  useEffect(() => {
    if (gearViewer) return;
    if (gearViewerClickTimerRef.current) {
      clearTimeout(gearViewerClickTimerRef.current);
      gearViewerClickTimerRef.current = null;
    }
    if (gearViewerDragReleaseTimerRef.current) {
      clearTimeout(gearViewerDragReleaseTimerRef.current);
      gearViewerDragReleaseTimerRef.current = null;
    }
    gearViewerDragRef.current = false;
  }, [gearViewer]);

  useEffect(() => {
    const isModalOpen = editingPhoto !== null
      || lightboxIndex >= 0
      || showSearchModal
      || photoToDelete !== null
      || showLoginModal
      || showPhotoPicker
      || batchSyncSource !== null
      || gearViewer !== null;

    if (!isModalOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehavior;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
    };
  }, [editingPhoto, lightboxIndex, showSearchModal, photoToDelete, showLoginModal, showPhotoPicker, batchSyncSource, gearViewer]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  const fetchPhotos = ({ full = true, blockHero = true, force = true } = {}) => {
    if (full && !force && photoLibraryModeRef.current === 'full') return Promise.resolve();
    if (full && !force && fullPhotoRequestRef.current) return fullPhotoRequestRef.current;
    if (blockHero) setIsLoading(true);

    const endpoint = full ? '/api/photos' : '/api/photos/home';
    const request = fetch(`${apiBaseUrl}${endpoint}`)
      .then(async res => {
        const data = await res.json();
        if (!res.ok || !Array.isArray(data)) throw new Error(data?.error || '照片資料格式錯誤');
        // 若完整媒體庫先回來，不允許較慢的首頁索引覆蓋它。
        if (full || photoLibraryModeRef.current !== 'full') {
          setPhotos(data);
          photoLibraryModeRef.current = full ? 'full' : 'home';
        }
      })
      .catch(err => {
        console.error(full ? '取得完整照片失敗:' : '取得首頁照片失敗:', err);
        throw err;
      })
      .finally(() => {
        if (blockHero) setIsLoading(false);
        if (full && fullPhotoRequestRef.current === request) fullPhotoRequestRef.current = null;
      });

    if (full) fullPhotoRequestRef.current = request;
    return request;
  };

  const fetchGearItems = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/gear`);
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setGearItems([...data].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch (err) { console.error('取得 Gear 內容失敗:', err); }
  };

  const fetchJourneyEntries = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/journeys`, { headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {} });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) setJourneyEntries(data);
    } catch (err) { console.error('取得 Journeys 失敗:', err); }
  };

  const generateDraftCopy = async (kind, note, draft, selectedCandidatePhotos = []) => {
    const pendingValues = kind === 'gear'
      ? Object.values(draft.files || {}).flatMap(value => Array.isArray(value) ? value : [value])
      : (draft.files || []);
    const pendingFiles = pendingValues.filter(value => typeof File !== 'undefined' && value instanceof File).slice(0, 9);
    const candidatePaths = kind === 'journey'
      ? (draft.candidatePhotoIds || []).map(id => {
        const photo = selectedCandidatePhotos.find(item => String(item._id) === String(id));
        return photo?.imagePath || photo?.imageVariants?.medium || photo?.previewPath || '';
      })
      : [];
    const storedPaths = kind === 'gear'
      ? [draft.cover?.medium, ...(draft.images || []).map(image => image.medium), draft.originalImage?.medium, draft.gradedImage?.medium]
      : [...(draft.images || []).map(image => image.medium || image.hero || image.original), ...candidatePaths];
    if (!String(note || '').trim() && !pendingFiles.length && !storedPaths.filter(Boolean).length) {
      alert('先說說這次想記下什麼，或選擇至少一張圖片。');
      return;
    }
    setAiDraftKind(kind);
    try {
      const inlineImages = await Promise.all(pendingFiles.map(async file => {
        const compressed = await compressTo4K(file, 960, 0.74);
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('圖片讀取失敗'));
          reader.readAsDataURL(compressed);
        });
      }));
      const res = await fetch(`${apiBaseUrl}/api/mai/draft`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          kind,
          note: String(note || '').trim(),
          fields: {
            type: draft.type,
            title: draft.title,
            subtitle: draft.subtitle,
            location: draft.location,
            dateLabel: draft.dateLabel
          },
          imagePaths: storedPaths.filter(Boolean).slice(0, 9),
          inlineImages,
          // Gear 正文留空時才查公開資料；它僅用於核實背景，不會取代 Miles 的欄位或口述。
          useWebReference: kind === 'gear' && !String(note || '').trim()
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Qwen 暫時無法生成草稿');
      // 模型永遠只能補空欄或更新正文；Miles 已手動輸入的名稱、用途與標題不可被覆蓋。
      if (kind === 'gear') setGearDraft(previous => previous ? {
        ...previous,
        title: previous.title?.trim() || data.draft.title || '',
        subtitle: previous.subtitle?.trim() || data.draft.subtitle || '',
        description: data.draft.description || previous.description || ''
      } : previous);
      else setJourneyDraft(previous => previous ? {
        ...previous,
        title: previous.title?.trim() || data.draft.title || '',
        body: data.draft.body || previous.body || ''
      } : previous);
    } catch (error) {
      alert(error.message || '生成草稿失敗，請稍後再試。');
    } finally {
      setAiDraftKind(null);
    }
  };

  const openJourneyDraft = async (entry = null) => {
    if (entry) {
      setJourneyDraft({ ...entry, relatedPhotoIds: (entry.relatedPhotoIds || []).map(String), relatedWorkLocations: (entry.relatedWorkLocations || []).filter(item => item?.prefix), files: [], imageOrder: (entry.images || []).map((_, index) => `old:${index}`), removedImageMarkers: [], pendingImageIds: [], pendingImagePreviews: {}, pendingImageCaptureTimes: {}, candidatePhotoIds: [], sequenceManuallyChanged: false, timeMode: 'auto' });
      return;
    }
    const today = new Date();
    setJourneyDraft({ sequenceNumber: '', sequenceSuffix: '', chapterOrder: '', title: '', location: '', dateLabel: `${today.getFullYear()}.${today.getMonth() + 1}.${today.getDate()}`, timeLabel: '', showTime: true, timeMode: 'auto', body: '', isVisible: true, files: [], imageOrder: [], removedImageMarkers: [], pendingImageIds: [], pendingImagePreviews: {}, pendingImageCaptureTimes: {}, candidatePhotoIds: [], relatedPhotoIds: [], relatedWorkLocations: [], sequenceManuallyChanged: false });
  };

  const saveJourneyDraft = async () => {
    if (!journeyDraft?.images?.length && !journeyDraft?.files?.length && !journeyDraft?.candidatePhotoIds?.length) return alert('先選擇至少一張照片。');
    setIsSavingJourney(true);
    try {
      const body = new FormData();
      const fallbackTitle = [journeyDraft.location, journeyDraft.dateLabel].filter(Boolean).join(' · ') || `JOURNEY ${String(journeyDraft.sequenceNumber || '').padStart(2, '0')}`;
      ['sequenceNumber', 'sequenceSuffix', 'chapterOrder', 'location', 'dateLabel', 'timeLabel', 'showTime', 'body', 'isVisible'].forEach(key => body.append(key, String(journeyDraft[key] ?? '')));
      body.append('title', String(journeyDraft.title || fallbackTitle));
      if (journeyDraft._id && (journeyDraft.files?.length || journeyDraft.candidatePhotoIds?.length)) body.append('appendImages', 'true');
      body.append('imageOrder', JSON.stringify(journeyDraft.imageOrder || []));
      body.append('removedImageMarkers', JSON.stringify(journeyDraft.removedImageMarkers || []));
      body.append('newImageIds', JSON.stringify(journeyDraft.pendingImageIds || []));
      body.append('pendingImageCaptureTimes', JSON.stringify(journeyDraft.pendingImageCaptureTimes || {}));
      body.append('candidatePhotoIds', JSON.stringify(journeyDraft.candidatePhotoIds || []));
      body.append('relatedPhotoIds', JSON.stringify(journeyDraft.relatedPhotoIds || []));
      body.append('relatedWorkLocations', JSON.stringify(journeyDraft.relatedWorkLocations || []));
      body.append('sequenceMode', journeyDraft.sequenceManuallyChanged ? 'manual' : 'date');
      for (const file of journeyDraft.files || []) body.append('images', await compressTo4K(file, 2200, 0.9));
      const res = await fetch(`${apiBaseUrl}/api/journeys${journeyDraft._id ? `/${journeyDraft._id}` : ''}`, {
        method: journeyDraft._id ? 'PUT' : 'POST',
        headers: { Authorization: authHeaders.Authorization },
        body
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '儲存失敗');
      await fetchJourneyEntries();
      fetchPhotos();
      setJourneyDraft(null);
    } catch (err) { alert(err.message || 'Journey 儲存失敗'); }
    finally { setIsSavingJourney(false); }
  };

  const deleteJourneyEntry = async entry => {
    if (!window.confirm(`刪除 JOURNEY ${String(entry.sequenceNumber).padStart(2, '0')}「${entry.title}」？`)) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/journeys/${entry._id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '刪除失敗');
      await fetchJourneyEntries();
      fetchPhotos();
    } catch (err) { alert(err.message || 'Journey 刪除失敗'); }
  };

  const resequenceJourneysByDate = async () => {
    if (!window.confirm('會依日期由早到晚重新排列所有 Journeys，確定要整理嗎？')) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/journeys/resequence-by-date`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '按日期整理失敗');
      await fetchJourneyEntries();
    } catch (error) {
      alert(error.message || '按日期整理失敗');
    }
  };

  const saveBroadcast = async modeOverride => {
    setIsSavingBroadcast(true);
    try {
      const broadcast = { ...broadcastDraft, ...(modeOverride ? { mode: modeOverride } : {}) };
      const res = await fetch(`${apiBaseUrl}/api/photos/settings`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ broadcast }) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '儲存失敗');
      setSiteSettings(normalizeLegacySiteCopy(data.setting));
      setBroadcastDraft(data.setting.broadcast || broadcast);
      setAdminSessionVerified(true);
      setAdminVerificationResolved(true);
      if (broadcast.mode !== 'off') sessionStorage.setItem('broadcast_admin_bypass', '1');
      else sessionStorage.removeItem('broadcast_admin_bypass');
    } catch (err) { alert(err.message || 'Broadcast 儲存失敗'); }
    finally { setIsSavingBroadcast(false); }
  };

  const uploadBroadcastAudio = async file => {
    if (!file) return;
    setIsUploadingBroadcastAudio(true);
    try {
      const body = new FormData();
      body.append('audio', file);
      const res = await fetch(`${apiBaseUrl}/api/photos/settings/broadcast-audio`, {
        method: 'POST',
        headers: { Authorization: authHeaders.Authorization },
        body
      });
      const responseText = await res.text();
      let data = null;
      try { data = responseText ? JSON.parse(responseText) : null; } catch (_) {}
      if (!res.ok || !data?.success) {
        const fallbackMessage = res.status === 413
          ? '音訊檔超過伺服器目前允許的上傳大小；請改用 AAC / M4A，或先縮短音檔。'
          : responseText.trimStart().startsWith('<')
            ? `音訊上傳接口回傳了網頁內容（HTTP ${res.status}），請確認後端接口與反向代理。`
            : `音訊上傳失敗（HTTP ${res.status}）`;
        throw new Error(data?.error || fallbackMessage);
      }
      setSiteSettings(normalizeLegacySiteCopy(data.setting));
      setBroadcastDraft(current => ({
        ...current,
        audioPath: data.setting.broadcast?.audioPath || current.audioPath,
        audioName: data.setting.broadcast?.audioName || file.name,
        audioEnabled: true
      }));
    } catch (err) {
      alert(err.message || 'Broadcast 音訊上傳失敗');
    } finally {
      setIsUploadingBroadcastAudio(false);
    }
  };

  const openBroadcastControl = () => {
    const current = siteSettings.broadcast || {};
    setBroadcastDraft({
      mode: current.mode || 'off',
      code: current.code || '404',
      title: !current.title || current.title === 'SIGNAL INTERRUPTED' || current.title === '影像資料觀看告知' ? '影像播放權限失效' : current.title,
      message: !current.message || current.message === 'ARCHIVE NODE IS NOT RESPONDING' || String(current.message).includes('來源不明的觀看記錄') ? '此裝置正在接收一段未經審核的影像資料。\n目前的存取權限已失效，系統無法繼續播放。\n請勿關閉頁面。請聯絡網站管理員。' : current.message,
      audioEnabled: current.audioEnabled !== false,
      audioPath: current.audioPath || '',
      audioName: current.audioName || '',
      intensity: current.intensity || 2
    });
    setAdminModule('Broadcast');
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const fetchStorageInfo = async () => {
    if (!adminToken) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/system/storage`, { headers: authHeaders });
      const data = await res.json();
      if (res.ok && Number.isFinite(data.totalBytes)) setStorageInfo(data);
    } catch (err) { console.error('讀取儲存空間失敗:', err); }
  };

  const openStorageOverview = async () => {
    if (!adminToken) return;
    setShowStorageOverview(true);
    setStorageOverview(null);
    setIsLoadingStorageOverview(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/system/storage/overview`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '讀取儲存空間失敗');
      setStorageOverview(data);
      setStorageInfo(data);
    } catch (err) {
      console.error('分析儲存空間失敗:', err);
      setStorageOverview({ error: err.message || '無法分析儲存空間' });
    } finally {
      setIsLoadingStorageOverview(false);
    }
  };

  const openGearDraft = (type, item = null) => {
    setGearDraft(item ? { ...item, files: {}, previews: {}, imageOrder: (item.images || []).map((_, index) => `old:${index}`), removedImageMarkers: [], pendingImageIds: [], pendingImagePreviews: {} } : { type, title: '', subtitle: '', description: '', sortOrder: gearItems.filter(entry => entry.type === type).length, isVisible: true, files: {}, previews: {}, imageOrder: [], removedImageMarkers: [], pendingImageIds: [], pendingImagePreviews: {} });
  };

  const saveGearDraft = async () => {
    if (gearDraft.type !== 'color' && !gearDraft?.title?.trim()) return alert('請填寫名稱。');
    if (gearDraft.type === 'color' && (!gearDraft._id && (!gearDraft.files?.originalImage || !gearDraft.files?.gradedImage))) return alert('色彩對比需要同時選擇未調色與調色版本。');
    setIsSavingGear(true);
    try {
      const body = new FormData();
      ['type', 'title', 'subtitle', 'description', 'sortOrder', 'isVisible'].forEach(key => body.append(key, String(gearDraft[key] ?? '')));
      if (gearDraft._id && Array.isArray(gearDraft.files?.images) && gearDraft.files.images.length) body.append('appendImages', 'true');
      body.append('imageOrder', JSON.stringify(gearDraft.imageOrder || []));
      body.append('newImageIds', JSON.stringify(gearDraft.pendingImageIds || []));
      for (const [key, file] of Object.entries(gearDraft.files || {})) {
        if (Array.isArray(file)) {
          for (const image of file) body.append(key, await compressTo4K(image, 2200, 0.9));
        } else if (file) {
          body.append(key, await compressTo4K(file, 2200, 0.9));
        }
      }
      const res = await fetch(`${apiBaseUrl}/api/gear${gearDraft._id ? `/${gearDraft._id}` : ''}`, { method: gearDraft._id ? 'PUT' : 'POST', headers: { Authorization: authHeaders.Authorization }, body });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '儲存失敗');
      await fetchGearItems();
      setGearDraft(null);
    } catch (err) { alert(err.message || 'Gear 儲存失敗'); }
    finally { setIsSavingGear(false); }
  };

  const deleteGearItem = async (item) => {
    if (!window.confirm(`刪除「${item.title}」？`)) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/gear/${item._id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '刪除失敗');
      await fetchGearItems();
    } catch (err) { alert(err.message || 'Gear 刪除失敗'); }
  };

  useEffect(() => {
    // 訪客首頁先取輕量索引；已有管理員登入狀態時直接取完整資料。
    fetchPhotos({ full: Boolean(adminToken), blockHero: true, force: true }).catch(() => {});
    fetchGearItems();
    fetchJourneyEntries();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!adminToken) {
      setAdminSessionVerified(false);
      setAdminVerificationResolved(true);
      return;
    }
    setAdminVerificationResolved(false);
    fetch(`${apiBaseUrl}/api/photos/session`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(res => {
        setAdminSessionVerified(res.ok);
        if (res.status === 401) {
          setAdminToken(null);
          localStorage.removeItem('admin_token');
          setIsAdminDashboard(false);
        }
      })
      .catch(() => setAdminSessionVerified(false))
      .finally(() => setAdminVerificationResolved(true));
  }, [apiBaseUrl, adminToken]);

  useEffect(() => {
    // 初次匿名載入已在上方完成；只有權限狀態真的改變時才重取管理員資料。
    if (adminToken) fetchJourneyEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  useEffect(() => {
    const needsFullLibrary = activeMenu === 'Work'
      || showSearchModal
      || isAdminDashboard
      || Boolean(currentSubPage);
    if (needsFullLibrary) {
      fetchPhotos({ full: true, blockHero: false, force: false }).catch(() => {});
    }
    // fetchPhotos 依賴目前的 apiBaseUrl，並以 ref 去除重複請求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu, showSearchModal, isAdminDashboard, currentSubPage, apiBaseUrl]);

  // 已打开的访客页也能在后台切换后进入或退出 Broadcast；只读取轻量设置，不重载照片。
  useEffect(() => {
    if (isAdminDashboard || broadcastPreview) return undefined;
    const timer = window.setInterval(() => {
      fetch(`${apiBaseUrl}/api/photos/settings`)
        .then(res => res.json())
        .then(data => {
          if (data?.broadcast) setSiteSettings(previous => ({ ...previous, broadcast: data.broadcast }));
        })
        .catch(() => {});
    }, 15000);
    return () => window.clearInterval(timer);
  }, [apiBaseUrl, isAdminDashboard, broadcastPreview]);

  useEffect(() => {
    fetchStorageInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, adminToken]);

  useEffect(() => {
    if (isAdminDashboard) fetchStorageInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminDashboard]);

  useEffect(() => {
    if (isHeroPaused || currentSubPage || isAdminDashboard) {
      if (heroTimerRef.current) clearInterval(heroTimerRef.current);
      return;
    }
    heroTimerRef.current = setInterval(() => setHeroIndex(prev => prev + 1), 4000);
    return () => {
      if (heroTimerRef.current) clearInterval(heroTimerRef.current);
    };
  }, [isHeroPaused, currentSubPage, isAdminDashboard]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const bg = isDarkMode ? '#000000' : '#f5f5f7';
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflowX = 'hidden';

    const existingStyle = document.getElementById('custom-gallery-styles');
    if (existingStyle) existingStyle.remove();

    const customStyle = document.createElement("style");
    customStyle.id = 'custom-gallery-styles';
    customStyle.textContent = `
      ::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
      html, body {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        overscroll-behavior-x: none !important;
      }

      #section-philosophy, #section-journeys, #section-featured {
        scroll-margin-top: 68px;
      }

      .lightbox-nav-btn {
        opacity: 0 !important;
        transition: opacity 0.25s ease, transform 0.2s ease !important;
      }
      .lightbox-nav-btn:hover {
        opacity: 0.85 !important;
        transform: translateY(-50%) scale(1.08) !important;
      }
      img {
        user-select: none !important;
        -webkit-user-drag: none !important;
      }
      @keyframes kenBurnsEffect {
        0% { transform: scale(1); }
        100% { transform: scale(1.06); }
      }
      .grid-image-fade {
        animation: fadeInImage 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }
      @keyframes fadeInImage {
        from { opacity: 0; filter: blur(4px); }
        to { opacity: 1; filter: blur(0); }
      }
      .admin-tabs-container::-webkit-scrollbar, .pill-container::-webkit-scrollbar {
        display: none;
      }
      .typing-effect::after {
        content: '|';
        animation: blink 1s step-end infinite;
      }
      @keyframes blink {
        50% { opacity: 0; }
      }
      .ai-progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 3px;
        background: #0071e3;
        z-index: 10;
        box-shadow: 0 0 8px rgba(0,113,227,0.8);
      }
      input[type="date"]::-webkit-calendar-picker-indicator {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
        background: transparent;
        color: transparent;
      }
      input[type="date"]::-webkit-inner-spin-button, input[type="date"]::-webkit-clear-button {
        display: none;
      }
      body {
        overscroll-behavior-x: none !important;
      }
      .horizontal-scroll-container::-webkit-scrollbar {
        display: none;
      }

      .nav-menu-item {
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
      }
      .mobile-dropdown-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 48px;
      }
      .admin-module-label-short { display: none; }

      @media (max-width: 768px) {
        .apple-scroll-nav {
          display: none !important;
        }
        .lightbox-nav-btn {
          display: none !important;
        }
        .nav-links-wrap {
          gap: 14px !important;
        }
        .site-header-admin .site-header-inner {
          height: 96px !important;
          padding: 0 14px !important;
          display: grid !important;
          grid-template-columns: auto 1fr auto;
          grid-template-rows: 48px 48px;
          column-gap: 10px;
        }
        .site-header-admin .site-header-brand { grid-column: 1; grid-row: 1; }
        .site-header-admin .admin-status-actions { grid-column: 3; grid-row: 1; }
        .site-header-admin .admin-status-actions > :not(.admin-quick-button) { display: none !important; }
        .site-header-admin .admin-quick-button { display: grid !important; }
        .site-header-admin .broadcast-skull-button { width: 28px; height: 34px; }
        .site-header-admin .admin-module-nav {
          grid-column: 1 / -1;
          grid-row: 2;
          width: 100%;
          min-width: 0;
          justify-content: center;
          gap: clamp(12px, 4vw, 20px) !important;
          margin-left: 0 !important;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .site-header-admin .admin-module-nav::-webkit-scrollbar { display: none; }
        .site-header-admin .admin-module-nav .nav-menu-item { flex: none; padding: 15px 0 14px !important; font-size: 12px !important; }
        .admin-module-label-short { display: inline; }
        .admin-module-label-full { display: none; }
        .admin-quick-menu {
          position: fixed;
          top: 56px;
          right: 14px;
          z-index: 1001;
          display: grid !important;
          gap: 10px;
          width: min(248px, calc(100vw - 28px));
          padding: 12px;
          border: 1px solid ${theme.cardBorder};
          border-radius: 16px;
          background: ${isDarkMode ? 'rgba(28,28,30,.98)' : 'rgba(255,255,255,.98)'};
          box-shadow: 0 16px 42px rgba(0,0,0,.18);
          backdrop-filter: blur(18px);
        }
        .admin-quick-menu-status { display: grid; gap: 8px; }
        .admin-quick-menu-status > span { width: 100%; justify-content: space-between; }
        .admin-quick-menu > button { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 11px 12px; border: 1px solid ${theme.cardBorder}; border-radius: 11px; background: ${isDarkMode ? 'rgba(255,255,255,.06)' : '#f7f7f8'}; color: ${theme.textPrimary}; font: inherit; font-size: 13px; cursor: pointer; }
        .admin-quick-menu-logout { color: #d70015 !important; }
        .admin-main { padding: 132px 16px 72px !important; }
        .gear-viewer-nav { display: none !important; }
        .site-header:not(.site-header-admin) .site-header-inner {
          height: 56px !important;
          padding: 0 16px !important;
          display: grid !important;
          grid-template-columns: 48px minmax(0, 1fr) 32px;
          column-gap: 8px;
        }
        .site-header:not(.site-header-admin) .site-header-brand { grid-column: 1; justify-self: start; }
        .site-header:not(.site-header-admin) .site-header-brand .nav-menu-item { padding: 4px 0 4px 2px !important; font-size: 28px !important; }
        .site-header:not(.site-header-admin) .nav-links-wrap {
          grid-column: 2;
          min-width: 0;
          width: 100%;
          justify-content: space-between;
          gap: 0 !important;
        }
        .site-header:not(.site-header-admin) .nav-menu-item { flex: none; padding-left: 0 !important; padding-right: 0 !important; font-size: clamp(11px, 3.1vw, 13px) !important; }
        .site-header:not(.site-header-admin) .site-header-actions { grid-column: 3; justify-self: end; }
        .gear-page-shell {
          max-width: none !important;
          padding: 82px 16px 60px !important;
        }
        .gear-content-grid {
          gap: 0 !important;
        }
        .gear-feed-column {
          padding: 0 !important;
        }
        .gear-page-back {
          margin-bottom: 30px !important;
          padding: 7px 16px !important;
        }
        .gear-page-header {
          padding: 0 4px 18px !important;
          margin-bottom: 8px !important;
        }
        .gear-page-header h1 {
          margin-top: 8px !important;
          font-size: clamp(32px, 10vw, 42px) !important;
          line-height: 1.04 !important;
          overflow-wrap: anywhere;
        }
        .gear-page-header p:last-child {
          margin-top: 10px !important;
          font-size: 15px !important;
          line-height: 1.55;
        }
        .gear-mobile-edc-link {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin: 0 0 18px;
          padding: 15px 17px;
          border: 1px solid ${theme.cardBorder};
          border-radius: 18px;
          background: ${isDarkMode ? 'rgba(255,255,255,.045)' : 'rgba(255,255,255,.74)'};
          color: ${theme.textPrimary};
          text-align: left;
          box-shadow: ${isDarkMode ? '0 10px 24px rgba(0,0,0,.16)' : '0 8px 22px rgba(0,0,0,.035)'};
        }
        .gear-mobile-edc-link span {
          display: grid;
          gap: 4px;
        }
        .gear-mobile-edc-link small {
          color: ${theme.textSecondary};
          font-size: 10px;
          font-weight: 700;
          letter-spacing: .1em;
        }
        .gear-mobile-edc-link strong {
          font-size: 16px;
          letter-spacing: -.02em;
        }
        .gear-mobile-edc-link b {
          color: ${theme.textSecondary};
          font-size: 25px;
          font-weight: 400;
          line-height: 1;
        }
        .gear-feed-stack {
          padding: 0 !important;
          border: none !important;
        }
        .gear-feed-post {
          border: none !important;
          border-radius: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        .gear-feed-post-copy {
          padding: 16px 16px 12px !important;
        }
        .gear-feed-post-copy h3 {
          margin-top: 7px !important;
          font-size: 18px !important;
        }
        .gear-feed-post-media {
          margin: 0 0 18px !important;
          border-radius: 13px !important;
        }
        .gear-feed-media-single {
          min-height: min(66vw, 258px) !important;
        }
        .gear-feed-media-multi {
          min-height: 128px !important;
        }
        .gear-luts-shell {
          padding-top: 74px !important;
        }
        .gear-luts-surface {
          padding-top: 22px !important;
          padding-bottom: 28px !important;
        }
        .gear-luts-heading {
          margin-bottom: 12px !important;
          font-size: clamp(30px, 8.8vw, 38px) !important;
          line-height: 1.08 !important;
          letter-spacing: -.045em !important;
          overflow-wrap: anywhere;
        }
        .gear-luts-description {
          margin-bottom: 28px !important;
          font-size: 16px !important;
          line-height: 1.5;
        }
        .gear-luts-list {
          gap: 30px !important;
        }
        .gear-before-after {
          margin-bottom: 28px !important;
        }
        .gear-before-after-title {
          margin: 0 20px 9px !important;
          font-size: 18px !important;
        }
        .gear-before-after-description {
          margin: 0 20px 14px !important;
          font-size: 13px !important;
          line-height: 1.55;
        }
        .gear-before-after-stage {
          min-height: 0 !important;
          aspect-ratio: 16 / 9 !important;
          border-radius: 16px !important;
          box-shadow: 0 14px 34px rgba(0,0,0,.18) !important;
        }
        .mobile-dropdown-grid {
          grid-template-columns: 1fr !important;
          gap: 24px !important;
          max-height: 68vh !important;
          overflow-y: auto !important;
          padding-bottom: 20px !important;
        }
        .subpage-shell {
          max-width: none !important;
          padding: 84px 0 72px !important;
        }
        .subpage-surface {
          padding: 36px 0 !important;
          border: none !important;
          border-radius: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        .subpage-heading,
        .subpage-description,
        .subpage-back {
          margin-left: 24px !important;
          margin-right: 24px !important;
        }
        .subpage-photo-stack {
          grid-template-columns: 1fr !important;
          gap: 40px !important;
          margin-top: 36px !important;
        }
        .subpage-photo-card {
          border: none !important;
          border-radius: 0 !important;
          background: transparent !important;
        }
        .subpage-photo-meta {
          padding: 18px 24px 0 !important;
          flex-direction: column !important;
          gap: 12px !important;
        }
        .subpage-photo-meta-main {
          min-width: 0 !important;
          width: 100% !important;
        }
        .subpage-photo-meta-main h3,
        .subpage-photo-meta-main p {
          overflow-wrap: anywhere;
        }
        .subpage-photo-meta-aside {
          width: 100% !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: space-between !important;
        }
        .contact-about-shell {
          padding: 90px 16px 64px !important;
        }
        .contact-about-surface {
          border-radius: 24px !important;
        }
        .contact-about-grid {
          grid-template-columns: 1fr !important;
        }
        .contact-about-portrait {
          min-height: 230px !important;
        }
        .contact-about-portrait.contact-about-has-photo {
          min-height: min(126vw, 560px) !important;
        }
        .contact-about-copy {
          padding: 34px 26px 38px !important;
        }
        .contact-about-mark {
          min-height: 230px !important;
        }
        .contact-about-mark .mark-main,
        .contact-about-mark .mark-echo { font-size: 142px !important; }
      }
      .contact-about-mark { position: relative; width: 100%; height: 100%; min-height: 620px; overflow: hidden; isolation: isolate; }
      .contact-about-mark span { position: absolute; top: 50%; transform: translateY(-50%); font-family: Georgia, 'Times New Roman', serif; font-weight: 600; letter-spacing: -0.13em; line-height: .8; user-select: none; pointer-events: none; }
      .contact-about-mark .mark-main { left: 13%; font-size: 230px; z-index: 2; }
      .contact-about-mark .mark-echo { font-size: 230px; z-index: 1; }
      .contact-about-mark .mark-echo-1 { left: 30%; opacity: .26; }
      .contact-about-mark .mark-echo-2 { left: 45%; opacity: .14; }
      .contact-about-mark .mark-echo-3 { left: 59%; opacity: .07; }
      .contact-about-mark .mark-echo-4 { left: 71%; opacity: .025; }
    `;
    document.head.appendChild(customStyle);
  }, [isDarkMode]);

  useEffect(() => {
    let isMounted = true;
    const probeBackendPort = async () => {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocal) {
        if (isMounted) setApiBaseUrl('');
        return;
      }
      for (const port of CANDIDATE_PORTS) {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 800);
          const res = await fetch(`http://localhost:${port}/api/photos`, { method: 'GET', signal: controller.signal });
          clearTimeout(timer);
          if (res.ok) {
            if (isMounted) setApiBaseUrl(`http://localhost:${port}`);
            break;
          }
        } catch (err) {}
      }
    };
    probeBackendPort();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!currentHeroPhoto || currentSubPage || isAdminDashboard) {
      setHeroImage(null);
      return;
    }

    let cancelled = false;
    let preloadImg = null;
    const photoId = String(currentHeroPhoto._id || '');
    // 行動裝置不再預載 2560px Hero；1280px 已足以覆蓋高密度手機螢幕。
    const candidates = getPhotoImageCandidates(currentHeroPhoto, isMobileScreen ? 'medium' : 'hero');

    const applyTextColors = (colorImg) => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = 50;
        canvas.height = 50;

        const sWidth = colorImg.width * 0.3;
        const sHeight = colorImg.height * 0.3;
        const sx = (colorImg.width - sWidth) / 2;
        const sy = (colorImg.height - sHeight) / 2;

        ctx.drawImage(colorImg, sx, sy, sWidth, sHeight, 0, 0, 50, 50);

        const imageData = ctx.getImageData(0, 0, 50, 50).data;
        let r = 0, g = 0, b = 0, count = 0;

        for (let i = 0; i < imageData.length; i += 16) {
          r += imageData[i];
          g += imageData[i + 1];
          b += imageData[i + 2];
          count++;
        }

        r /= count;
        g /= count;
        b /= count;

        if ((0.2126 * r + 0.7152 * g + 0.0722 * b) > 125) {
          setHeroTextColor('#1d1d1f');
          setHeroSubColor('rgba(29, 29, 31, 0.75)');
          setHeroTextShadow('0 4px 40px rgba(255,255,255,0.8)');
        } else {
          setHeroTextColor('#ffffff');
          setHeroSubColor('rgba(255, 255, 255, 0.75)');
          setHeroTextShadow('0 4px 40px rgba(0,0,0,0.6)');
        }
      } catch (e) {
        setHeroTextColor('#ffffff');
        setHeroSubColor('rgba(255, 255, 255, 0.75)');
        setHeroTextShadow('0 4px 40px rgba(0,0,0,0.6)');
      }
    };

    const loadCandidate = (candidateIndex) => {
      if (cancelled || candidateIndex >= candidates.length) return;

      const src = candidates[candidateIndex];
      preloadImg = new Image();
      preloadImg.decoding = 'async';
      preloadImg.fetchPriority = 'high';

      preloadImg.onload = async () => {
        const loadedImg = preloadImg;
        try {
          if (loadedImg.decode) await loadedImg.decode();
        } catch (e) {}
        if (cancelled) return;

        setHeroImage({ photoId, src, photo: currentHeroPhoto });
        applyTextColors(loadedImg);
      };

      preloadImg.onerror = () => loadCandidate(candidateIndex + 1);
      preloadImg.src = src;
    };

    loadCandidate(0);

    return () => {
      cancelled = true;
      if (preloadImg) {
        preloadImg.onload = null;
        preloadImg.onerror = null;
      }
    };
  }, [currentHeroPhoto, currentSubPage, isAdminDashboard, isMobileScreen]);

  useEffect(() => {
    if (displayPhotos.length === 0) return;
    const preloadImage = (src) => {
      const cleanSrc = cleanImagePath(src);
      if (!cleanSrc) return;
      const img = new Image();
      img.src = cleanSrc;
      if (img.decode) img.decode().catch(() => {});
    };
    if (lightboxIndex < 0) {
      displayPhotos.slice(0, isMobileScreen ? 1 : 4).forEach(item => preloadImage(getPhotoImagePath(item, 'medium')));
      return;
    }
    [lightboxIndex - 2, lightboxIndex - 1, lightboxIndex + 1, lightboxIndex + 2].forEach(idx => {
      if (idx >= 0 && idx < activeLightboxPhotos.length) {
        preloadImage(getPhotoImagePath(activeLightboxPhotos[idx], 'medium'));
      }
    });
  }, [lightboxIndex, displayPhotos, activeLightboxPhotos, isMobileScreen]);

  useEffect(() => {
    if (lightboxIndex < 0) return;
    setIsSettled(false);
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      setIsSettled(true);
    }, 450);
    return () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, [lightboxIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex < 0 || isAnimatingRef.current) return;
      const resetLightboxForKeyboard = () => {
        if (lightboxZoomTransitionTimerRef.current) clearTimeout(lightboxZoomTransitionTimerRef.current);
        lightboxZoomTransitionTimerRef.current = null;
        setIsLightboxTransitioning(false);
        setIsZoomed(false);
        lightboxPanX.set(0);
        lightboxPanY.set(0);
      };
      if (e.key === 'Escape') {
        setLightboxIndex(-1);
        resetLightboxForKeyboard();
      }
      const currentListLength = activeLightboxPhotos.length;
      if (e.key === 'ArrowRight' && lightboxIndex < currentListLength - 1 && !isZoomed) {
        isAnimatingRef.current = true;
        setIsFirstOpen(false);
        setLightboxDirection(1);
        setLightboxIndex(prev => prev + 1);
        resetLightboxForKeyboard();
        setTimeout(() => {
          isAnimatingRef.current = false;
        }, 300);
      }
      if (e.key === 'ArrowLeft' && lightboxIndex > 0 && !isZoomed) {
        isAnimatingRef.current = true;
        setIsFirstOpen(false);
        setLightboxDirection(-1);
        setLightboxIndex(prev => prev - 1);
        resetLightboxForKeyboard();
        setTimeout(() => {
          isAnimatingRef.current = false;
        }, 300);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, activeLightboxPhotos, isZoomed, lightboxPanX, lightboxPanY]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (!gearViewer || gearViewerAnimatingRef.current) return;
      if (event.key === 'Escape') {
        setIsGearViewerZoomed(false);
        gearViewerPanX.set(0);
        gearViewerPanY.set(0);
        setGearViewer(null);
        return;
      }
      if (event.key === 'ArrowRight' && gearViewer.index < gearViewer.images.length - 1 && !isGearViewerZoomed) {
        gearViewerAnimatingRef.current = true;
        setIsGearViewerFirstOpen(false);
        setGearViewerDirection(1);
        setGearViewer(current => current ? { ...current, index: current.index + 1 } : current);
        setIsGearViewerZoomed(false);
        gearViewerPanX.set(0);
        gearViewerPanY.set(0);
        setTimeout(() => { gearViewerAnimatingRef.current = false; }, 300);
      }
      if (event.key === 'ArrowLeft' && gearViewer.index > 0 && !isGearViewerZoomed) {
        gearViewerAnimatingRef.current = true;
        setIsGearViewerFirstOpen(false);
        setGearViewerDirection(-1);
        setGearViewer(current => current ? { ...current, index: current.index - 1 } : current);
        setIsGearViewerZoomed(false);
        gearViewerPanX.set(0);
        gearViewerPanY.set(0);
        setTimeout(() => { gearViewerAnimatingRef.current = false; }, 300);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gearViewer, isGearViewerZoomed, gearViewerPanX, gearViewerPanY]);

  // ----------------------------------------------------
  // 交互處理函數
  // ----------------------------------------------------

  // 這是 Gear／Journeys 同一套實時平移邏輯：縮放時以 MotionValue 直接更新，
  // 避免 Framer drag 在手勢結束後再回寫一次舊位置而造成跳回中央。
  const clampLightboxPan = (nextPan, scale = 2.8, useRenderedBounds = isZoomed && !isLightboxTransitioning) => {
    const image = lightboxImageRef.current;
    if (!image || typeof window === 'undefined') return nextPan;
    const viewport = lightboxShellRef.current?.getBoundingClientRect();
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportHeight = viewport?.height || window.innerHeight;

    if (useRenderedBounds && viewport) {
      const imageRect = image.getBoundingClientRect();
      const currentX = lightboxPanX.get();
      const currentY = lightboxPanY.get();
      const clampRenderedAxis = (target, current, imageStart, imageEnd, viewportStart, viewportEnd) => {
        if (imageEnd - imageStart <= viewportEnd - viewportStart) return 0;
        const requestedDelta = target - current;
        const minDelta = viewportEnd - imageEnd;
        const maxDelta = viewportStart - imageStart;
        return current + Math.max(minDelta, Math.min(maxDelta, requestedDelta));
      };
      return {
        x: clampRenderedAxis(nextPan.x, currentX, imageRect.left, imageRect.right, viewport.left, viewport.right),
        y: clampRenderedAxis(nextPan.y, currentY, imageRect.top, imageRect.bottom, viewport.top, viewport.bottom)
      };
    }

    const maxX = Math.max(0, (image.offsetWidth * scale - viewportWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * scale - viewportHeight) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, nextPan.x)),
      y: Math.max(-maxY, Math.min(maxY, nextPan.y))
    };
  };

  const setLightboxPan = (nextPan, { animate: shouldAnimate = false, useRenderedBounds } = {}) => {
    const bounded = clampLightboxPan(nextPan, 2.8, useRenderedBounds);
    lightboxPanAnimationRef.current.forEach(control => control.stop());
    lightboxPanAnimationRef.current = [];
    if (shouldAnimate) {
      const transition = { duration: 0.32, ease: [0.16, 1, 0.3, 1] };
      lightboxPanAnimationRef.current = [
        animate(lightboxPanX, bounded.x, transition),
        animate(lightboxPanY, bounded.y, transition)
      ];
      return;
    }
    lightboxPanX.set(bounded.x);
    lightboxPanY.set(bounded.y);
  };

  const finishLightboxZoomTransition = willBeZoomed => {
    lightboxZoomTransitionTimerRef.current = null;
    setIsLightboxTransitioning(false);
    if (willBeZoomed) {
      setLightboxPan({ x: lightboxPanX.get(), y: lightboxPanY.get() }, { useRenderedBounds: true });
    }
    const pointer = lightboxPanPointerRef.current;
    if (pointer?.deferred) {
      pointer.deferred = false;
      pointer.x = lightboxPanX.get();
      pointer.y = lightboxPanY.get();
      pointer.clientX = pointer.latestClientX;
      pointer.clientY = pointer.latestClientY;
    }
  };

  const startLightboxZoomTransition = willBeZoomed => {
    if (lightboxZoomTransitionTimerRef.current) clearTimeout(lightboxZoomTransitionTimerRef.current);
    setIsLightboxTransitioning(true);
    lightboxZoomTransitionTimerRef.current = window.setTimeout(() => finishLightboxZoomTransition(willBeZoomed), 340);
  };

  const resetZoomAndPan = (withTransition = false) => {
    if (withTransition) startLightboxZoomTransition(false);
    else {
      if (lightboxZoomTransitionTimerRef.current) clearTimeout(lightboxZoomTransitionTimerRef.current);
      lightboxZoomTransitionTimerRef.current = null;
      setIsLightboxTransitioning(false);
    }
    setIsZoomed(false);
    setLightboxPan({ x: 0, y: 0 }, { animate: withTransition });
  };

  const beginLightboxDrag = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    if (lightboxDragReleaseTimerRef.current) {
      clearTimeout(lightboxDragReleaseTimerRef.current);
    }
    lightboxDragRef.current = true;
  };

  const finishLightboxDrag = () => {
    if (lightboxDragReleaseTimerRef.current) {
      clearTimeout(lightboxDragReleaseTimerRef.current);
    }
    lightboxDragReleaseTimerRef.current = setTimeout(() => {
      lightboxDragRef.current = false;
    }, 0);
  };

  const beginLightboxPan = event => {
    if (!isZoomed || event.button > 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    lightboxPanPointerRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: lightboxPanX.get(),
      y: lightboxPanY.get(),
      latestClientX: event.clientX,
      latestClientY: event.clientY,
      moved: false,
      deferred: isLightboxTransitioning
    };
  };

  const moveLightboxPan = event => {
    const pointer = lightboxPanPointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;
    pointer.latestClientX = event.clientX;
    pointer.latestClientY = event.clientY;
    if (pointer.deferred) return;
    const deltaX = event.clientX - pointer.clientX;
    const deltaY = event.clientY - pointer.clientY;
    if (!pointer.moved && Math.hypot(deltaX, deltaY) < 3) return;
    if (!pointer.moved) {
      pointer.moved = true;
      beginLightboxDrag();
    }
    event.preventDefault();
    setLightboxPan({ x: pointer.x + deltaX, y: pointer.y + deltaY });
  };

  const endLightboxPan = event => {
    const pointer = lightboxPanPointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;
    lightboxPanPointerRef.current = null;
    if (pointer.moved) finishLightboxDrag();
  };

  const openLightbox = (index, photoList = displayPhotos) => {
    if (lightboxZoomTransitionTimerRef.current) clearTimeout(lightboxZoomTransitionTimerRef.current);
    lightboxWheelIntentRef.current = {
      lastEventAt: 0,
      lastDirection: 0,
      lastSwitchAt: 0,
      accumulatedX: 0,
      acceptingImpulse: true,
      peakMagnitude: 0,
      tailFloorMagnitude: Infinity,
      hasDecelerated: false
    };
    setIsFirstOpen(true);
    setIsLightboxTransitioning(false);
    setIsZoomed(false);
    lightboxPanPointerRef.current = null;
    lightboxPanX.set(0);
    lightboxPanY.set(0);
    const nextPhotos = Array.isArray(photoList) ? photoList.filter(Boolean) : [];
    if (!nextPhotos.length) return;
    setLightboxPhotos(nextPhotos);
    setLightboxIndex(Math.max(0, Math.min(Number(index) || 0, nextPhotos.length - 1)));
    setIsSettled(true);
  };

  const handleWheel = (e) => {
    if (lightboxIndex < 0 || isLightboxTransitioning) return;

    if (isZoomed) {
      e.stopPropagation();
      lightboxWheelIntentRef.current = {
        lastEventAt: 0,
        lastDirection: 0,
        lastSwitchAt: 0,
        accumulatedX: 0,
        acceptingImpulse: true,
        peakMagnitude: 0,
        tailFloorMagnitude: Infinity,
        hasDecelerated: false
      };
      setLightboxPan({
        x: lightboxPanX.get() - e.deltaX * 1.2,
        y: lightboxPanY.get() - e.deltaY * 1.2
      });
    } else {
      const deltaX = e.deltaX;
      const magnitude = Math.abs(deltaX);
      if (magnitude < 4) return;

      const now = performance.now();
      const direction = deltaX > 0 ? 1 : -1;
      const wheelState = lightboxWheelIntentRef.current;
      const eventGap = wheelState.lastEventAt > 0
        ? now - wheelState.lastEventAt
        : Infinity;
      const observedPeakMagnitude = Math.max(wheelState.peakMagnitude, magnitude);
      const sameDirectionGuardMs = observedPeakMagnitude >= 60
        ? 700
        : observedPeakMagnitude >= 40
          ? 500
          : 220;
      const directionChanged = wheelState.lastDirection !== 0
        && direction !== wheelState.lastDirection
        && magnitude >= 16;
      const sameDirectionRestart = !wheelState.acceptingImpulse
        && wheelState.hasDecelerated
        && now - wheelState.lastSwitchAt >= sameDirectionGuardMs
        && magnitude >= 28
        && magnitude >= wheelState.tailFloorMagnitude * 1.8
        && magnitude - wheelState.tailFloorMagnitude >= 10;
      const pausedLongEnough = eventGap > 90
        && (wheelState.lastSwitchAt === 0
          || now - wheelState.lastSwitchAt >= sameDirectionGuardMs);
      const isFreshImpulse = pausedLongEnough || directionChanged || sameDirectionRestart;

      if (isFreshImpulse) {
        wheelState.acceptingImpulse = true;
        wheelState.accumulatedX = 0;
        wheelState.peakMagnitude = magnitude;
        wheelState.tailFloorMagnitude = magnitude;
        wheelState.hasDecelerated = false;
      }

      wheelState.lastEventAt = now;
      // 忽略大滑動尾端很小的反向抖動，避免它被誤認為新的反向手勢。
      if (wheelState.lastDirection === 0 || magnitude >= 16) {
        wheelState.lastDirection = direction;
      }
      wheelState.peakMagnitude = observedPeakMagnitude;

      if (!wheelState.acceptingImpulse) {
        wheelState.tailFloorMagnitude = Math.min(wheelState.tailFloorMagnitude, magnitude);
        if (magnitude <= wheelState.peakMagnitude * 0.55) {
          wheelState.hasDecelerated = true;
        }
        return;
      }

      wheelState.accumulatedX += deltaX;
      if (isAnimatingRef.current || Math.abs(wheelState.accumulatedX) < 28) return;

      const navigationDirection = wheelState.accumulatedX > 0 ? 1 : -1;
      const currentListLength = activeLightboxPhotos.length;

      // 一次物理滑動只消耗一次；後續持續衰減的慣性不再重複切圖。
      wheelState.acceptingImpulse = false;
      wheelState.accumulatedX = 0;
      wheelState.lastSwitchAt = now;
      wheelState.peakMagnitude = magnitude;
      wheelState.tailFloorMagnitude = magnitude;
      wheelState.hasDecelerated = false;

      if (navigationDirection > 0 && lightboxIndex < currentListLength - 1) {
        isAnimatingRef.current = true;
        setIsFirstOpen(false);
        setLightboxDirection(1);
        setLightboxIndex(prev => prev + 1);
        resetZoomAndPan();
        setTimeout(() => {
          isAnimatingRef.current = false;
        }, 300);
      } else if (navigationDirection < 0 && lightboxIndex > 0) {
        isAnimatingRef.current = true;
        setIsFirstOpen(false);
        setLightboxDirection(-1);
        setLightboxIndex(prev => prev - 1);
        resetZoomAndPan();
        setTimeout(() => {
          isAnimatingRef.current = false;
        }, 300);
      }
    }
  };

  const handleSmartPhotoClick = (e) => {
    e.stopPropagation();
    if (lightboxDragRef.current || e.detail > 1) return;

    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      if (!isZoomed && !lightboxDragRef.current) {
        setLightboxIndex(-1);
        resetZoomAndPan();
      }
      clickTimerRef.current = null;
    }, 220);
  };

  const handlePhotoDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (lightboxDragRef.current || isLightboxTransitioning) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    if (isZoomed) {
      resetZoomAndPan(true);
      return;
    }

    const rect = lightboxImageRef.current?.getBoundingClientRect() || e.currentTarget.getBoundingClientRect();
    const zoomScaleDelta = 2.8 - 1;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    setLightboxPan({
      x: (centerX - e.clientX) * zoomScaleDelta,
      y: (centerY - e.clientY) * zoomScaleDelta
    }, { animate: true });
    startLightboxZoomTransition(true);
    setIsZoomed(true);
  };

  const runBatchAiClassification = async ({ descriptionOnly = false } = {}) => {
    const targetPhotos = workLibraryPhotos.filter(photo => (
      descriptionOnly
        ? !String(photo.aiDescription || '').trim()
        : !photo.subCategory || photo.subCategory === 'uncategorized' || !String(photo.aiDescription || '').trim()
    ));
    if (targetPhotos.length === 0) {
      alert(descriptionOnly ? '目前沒有缺少 AI 描述的照片。' : '所有照片均已完成 AI 分類與描述！✨');
      return;
    }
    setIsBatchAiRunning(true);
    setBatchAiMode(descriptionOnly ? 'description' : 'full');
    setBatchAiProgress({ total: targetPhotos.length, processed: 0, failed: 0 });
    let failed = 0;
    for (let i = 0; i < targetPhotos.length; i++) {
      const photo = targetPhotos[i];
      setAiProcessingId(photo._id);
      try {
        const res = await fetch(`${apiBaseUrl}/api/photos/${photo._id}/ai-tag`, {
          method: 'POST',
          headers: authHeaders,
          body: descriptionOnly ? JSON.stringify({ descriptionOnly: true }) : undefined
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.error || 'AI 處理失敗');
        // 每一張完成就直接更新本地資料：待整理篩選會立即把它移出收件箱。
        setPhotos(prevPhotos => prevPhotos.map(item => item._id === photo._id
          ? (descriptionOnly
            ? { ...item, aiDescription: data.aiDescription || item.aiDescription }
            : { ...item, subCategory: data.subCategory || item.subCategory, aiDescription: data.aiDescription || item.aiDescription })
          : item));
      } catch (err) {
        failed += 1;
      } finally {
        setBatchAiProgress({ total: targetPhotos.length, processed: i + 1, failed });
      }
    }
    setAiProcessingId(null);
    setIsBatchAiRunning(false);
    setBatchAiMode(null);
    fetchPhotos();
  };

  const triggerSingleAiReclassify = async (photoId) => {
    setAiProcessingId(photoId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/${photoId}/ai-tag`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ preferredSubCategory: activeCategory })
      });
      const data = await res.json();
      setAiProcessingId(null);
      if (data.success) {
        setActiveCategory(data.subCategory);
        setAiDescription(data.aiDescription);
        fetchPhotos();
      } else {
        alert(data.error || '權限不足，或請確認終端已啟動 Ollama');
      }
    } catch (err) {
      setAiProcessingId(null);
      alert('無法連線到伺服器。');
    }
  };

  const researchPhotoLocation = async () => {
    if (!editingPhoto?._id || isLocationResearching) return;
    const targetPhotoId = String(editingPhoto._id);
    const targetLocationHint = editLocation.trim();
    setIsLocationResearching(true);
    setLocationResearch(null);
    setLocationResearchError('');
    locationResearchStartedAtRef.current = Date.now();
    setLocationResearchElapsed(0);
    setLocationResearchOverlay('loading');
    setLocationResearchByPhotoId(prev => ({ ...prev, [targetPhotoId]: { status: 'loading', startedAt: Date.now() } }));
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const context = locationResearchAudioContextRef.current || new AudioContextClass();
        locationResearchAudioContextRef.current = context;
        context.resume?.().catch(() => {});
      }
    } catch (_) {
      // 音效被系統限制時不影響核實本身。
    }
    try {
      const res = await fetch(`${apiBaseUrl}/api/mai/photos/${targetPhotoId}/location-research`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ locationHint: targetLocationHint })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || '聯網核實失敗');
      const completedAt = new Date().toISOString();
      setLocationResearchByPhotoId(prev => ({ ...prev, [targetPhotoId]: { status: 'success', data, completedAt } }));
      if (String(editingPhotoRef.current?._id || '') === targetPhotoId) setLocationResearch(data);
      setLocationResearchOverlay('success');
      const audioContext = locationResearchAudioContextRef.current;
      if (audioContext?.state === 'running') {
        const playTone = (frequency, startsAt, duration) => {
          const oscillator = audioContext.createOscillator();
          const gain = audioContext.createGain();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(frequency, startsAt);
          gain.gain.setValueAtTime(0.0001, startsAt);
          gain.gain.exponentialRampToValueAtTime(0.32, startsAt + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);
          oscillator.connect(gain).connect(audioContext.destination);
          oscillator.start(startsAt);
          oscillator.stop(startsAt + duration + 0.02);
        };
        const startsAt = audioContext.currentTime + 0.03;
        playTone(659.25, startsAt, 0.12);
        playTone(987.77, startsAt + 0.13, 0.22);
      }
      await new Promise(resolve => window.setTimeout(resolve, 1050));
    } catch (error) {
      const message = error.message || '聯網核實失敗，請稍後再試。';
      setLocationResearchByPhotoId(prev => ({ ...prev, [targetPhotoId]: { status: 'error', error: message } }));
      if (String(editingPhotoRef.current?._id || '') === targetPhotoId) setLocationResearchError(message);
      setLocationResearchOverlay('error');
      await new Promise(resolve => window.setTimeout(resolve, 1650));
    } finally {
      setIsLocationResearching(false);
      setLocationResearchOverlay(null);
      setLocationResearchElapsed(0);
      locationResearchStartedAtRef.current = null;
    }
  };

  useEffect(() => {
    if (!isLocationResearching || locationResearchOverlay !== 'loading') return undefined;
    const updateElapsed = () => {
      const startedAt = locationResearchStartedAtRef.current || Date.now();
      setLocationResearchElapsed((Date.now() - startedAt) / 1000);
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 250);
    return () => window.clearInterval(timer);
  }, [isLocationResearching, locationResearchOverlay]);

  useEffect(() => {
    editingPhotoRef.current = editingPhoto;
  }, [editingPhoto]);

  const confirmDeletePhoto = () => {
    if (!photoToDelete) return;
    const targetId = photoToDelete._id;
    const targetPath = photoToDelete.imagePath;
    setPhotos(prevPhotos => prevPhotos.filter(p => (targetId && p._id) ? p._id !== targetId : p.imagePath !== targetPath));
    if (targetId) {
      const normalizedTargetId = String(targetId);
      setSiteSettings(prev => ({
        ...prev,
        heroPhotoIds: (prev.heroPhotoIds || []).filter(id => String(id) !== normalizedTargetId),
        heroPhotoMobileIds: (prev.heroPhotoMobileIds || []).filter(id => String(id) !== normalizedTargetId)
      }));
    }
    setPhotoToDelete(null);
    if (targetId) {
      fetch(`${apiBaseUrl}/api/photos/${targetId}`, {
        method: 'DELETE',
        headers: authHeaders
      }).then(res => {
        if (res.status === 401 || res.status === 403) {
          alert('您沒有刪除權限，請先登入後台。');
          fetchPhotos();
          fetchSettings();
          return;
        }
        if (!res.ok) {
          alert('刪除失敗，已重新載入伺服器資料。');
          fetchPhotos();
          fetchSettings();
        }
      }).catch(() => {
        alert('刪除請求未送達伺服器，已重新載入資料。');
        fetchPhotos();
        fetchSettings();
      });
    }
  };

  const handleSaveParameters = (e) => {
    if (e) e.preventDefault();
    if (!editingPhoto) return;

    const targetId = editingPhoto._id;
    const parsedTimestamp = editShootingDate ? new Date(editShootingDate).getTime() : editingPhoto.shootingDate;

    const payload = {
      location: editLocation,
      equipment: editEquipment,
      shootingDate: parsedTimestamp,
      subCategory: activeCategory,
      aiDescription: aiDescription
    };

    setPhotos(prevPhotos => prevPhotos.map(p => {
      if (p._id === targetId) {
        return {
          ...p,
          ...payload,
          location: editLocation,
          shootingParams: { ...p.shootingParams, equipment: editEquipment },
          shootingDate: parsedTimestamp
        };
      }
      return p;
    }));

    setPhotoEditDrafts(previous => {
      const next = { ...previous };
      delete next[String(targetId)];
      return next;
    });
    setEditingPhoto(null);

    fetch(`${apiBaseUrl}/api/photos/${targetId}`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            alert('您沒有修改權限。');
          }
          fetchPhotos();
        }
      })
      .catch(err => {
        console.error('修改照片資訊提交失敗:', err);
      });
  };

  const handleSaveAndSelectBatchTargets = async () => {
    if (!editingPhoto) return;

    const sourcePhoto = editingPhoto;
    const parsedTimestamp = editShootingDate ? new Date(editShootingDate).getTime() : sourcePhoto.shootingDate;
    const payload = {
      location: editLocation,
      equipment: editEquipment,
      shootingDate: parsedTimestamp,
      subCategory: activeCategory,
      aiDescription
    };

    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/${sourcePhoto._id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('儲存失敗');

      const updatedSource = {
        ...sourcePhoto,
        ...payload,
        shootingParams: { ...sourcePhoto.shootingParams, equipment: editEquipment }
      };
      setPhotos(prev => prev.map(photo => photo._id === sourcePhoto._id ? updatedSource : photo));
      setPhotoEditDrafts(previous => {
        const next = { ...previous };
        delete next[String(sourcePhoto._id)];
        return next;
      });
      setEditingPhoto(updatedSource);
      setBatchSyncSource(updatedSource);
      setSelectedBatchPhotoIds([]);
      setBatchSyncFields({ location: true, equipment: true, shootingDate: true });
      setShowExistingBatchSyncPhotos(false);
    } catch (err) {
      alert('儲存失敗，無法開始批量同步。請確認您已登入後台。');
    }
  };

  const toggleBatchPhotoSelection = (photoId) => {
    setSelectedBatchPhotoIds(prev => prev.includes(photoId)
      ? prev.filter(id => id !== photoId)
      : [...prev, photoId]);
  };

  const closeBatchSyncPicker = () => {
    setBatchSyncSource(null);
    setSelectedBatchPhotoIds([]);
    setBatchSyncFields({ location: true, equipment: true, shootingDate: true });
    setShowExistingBatchSyncPhotos(false);
  };

  const toggleBatchSyncField = field => {
    setBatchSyncFields(previous => ({ ...previous, [field]: !previous[field] }));
  };

  const handleApplyBatchSync = async () => {
    if (!batchSyncSource || selectedBatchPhotoIds.length === 0 || !Object.values(batchSyncFields).some(Boolean)) return;
    setIsBatchSyncing(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/batch/metadata`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ sourcePhotoId: batchSyncSource._id, targetPhotoIds: selectedBatchPhotoIds, syncFields: batchSyncFields })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '同步失敗');

      setPhotos(prev => prev.map(photo => {
        if (!selectedBatchPhotoIds.includes(photo._id)) return photo;
        const updatedPhoto = { ...photo };
        if (batchSyncFields.location) updatedPhoto.location = batchSyncSource.location;
        if (batchSyncFields.shootingDate) updatedPhoto.shootingDate = batchSyncSource.shootingDate;
        if (batchSyncFields.equipment) {
          updatedPhoto.shootingParams = {
            ...photo.shootingParams,
            ...batchSyncSource.shootingParams
          };
        }
        return updatedPhoto;
      }));
      closeBatchSyncPicker();
      setEditingPhoto(null);
      const fieldLabels = [batchSyncFields.location && '地點', batchSyncFields.equipment && '器材', batchSyncFields.shootingDate && '拍攝日期'].filter(Boolean);
      alert(`已同步 ${data.updatedCount} 張照片的${fieldLabels.join('、')}。`);
    } catch (err) {
      alert(err.message || '批量同步失敗，請重試。');
    } finally {
      setIsBatchSyncing(false);
    }
  };

  const openLocationBatchPicker = group => {
    if (!group?.label || group.key === MISSING_LOCATION_KEY) return;
    setLocationBatchTarget({ key: group.key, label: group.label });
    setSelectedLocationPhotoIds([]);
    setShowExistingLocationBatchPhotos(false);
  };

  const toggleLocationPhotoSelection = photoId => {
    setSelectedLocationPhotoIds(previous => previous.includes(photoId)
      ? previous.filter(id => id !== photoId)
      : [...previous, photoId]);
  };

  const handleApplyLocationBatch = async () => {
    if (!locationBatchTarget || selectedLocationPhotoIds.length === 0) return;
    setIsLocationBatchSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/batch/location`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ location: locationBatchTarget.label, targetPhotoIds: selectedLocationPhotoIds })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '加入地點失敗');
      setPhotos(previous => previous.map(photo => selectedLocationPhotoIds.includes(photo._id)
        ? { ...photo, location: locationBatchTarget.label }
        : photo));
      setLocationBatchTarget(null);
      setSelectedLocationPhotoIds([]);
      setShowExistingLocationBatchPhotos(false);
      alert(`已將 ${data.updatedCount} 張照片加入「${data.location}」。`);
    } catch (err) {
      alert(err.message || '批量加入地點失敗，請重試。');
    } finally {
      setIsLocationBatchSaving(false);
    }
  };

  const triggerSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    setUploading(true);

    try {
      let uploadedCount = 0;
      let skippedDuplicates = 0;
      const failedFiles = [];

      // 一次選多張仍逐張處理：每完成一張便立即插入畫面，不必等待整批結束。
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const originalFile = selectedFiles[index];
        setUploadProgress({ total: selectedFiles.length, completed: index, uploaded: uploadedCount, currentName: originalFile.name });
        try {
          const item = await new Promise(resolve => {
            const autoTitle = originalFile.name.replace(/\.[^/.]+$/, '');
            EXIF.getData(originalFile, async function () {
              const make = EXIF.getTag(this, 'Make') || '';
              const model = EXIF.getTag(this, 'Model') || '';
              const fNumber = EXIF.getTag(this, 'FNumber');
              const exposureTime = EXIF.getTag(this, 'ExposureTime');
              const iso = EXIF.getTag(this, 'ISOSpeedRatings');
              const dateTime = EXIF.getTag(this, 'DateTimeOriginal') || EXIF.getTag(this, 'DateTime');
              const lens = EXIF.getTag(this, 'LensModel') || EXIF.getTag(this, 'Lens') || '';
              const cameraInfo = formatExifCameraName(make, model);
              const shutterSpeed = exposureTime ? `1/${Math.round(1 / exposureTime)}s` : '';
              const aperture = fNumber ? `f/${fNumber}` : '';
              const equipmentInfo = [cameraInfo, String(lens || '').trim()].filter(Boolean).join(' + ');
              const [compressed4KFile, sourceHash] = await Promise.all([
                compressTo4K(originalFile, 3840, 0.9),
                getOriginalFileHash(originalFile)
              ]);
              resolve({
                file: compressed4KFile,
                title: autoTitle,
                sourceHash,
                shootingDate: parseExifShootingDate(dateTime),
                shootingParams: {
                  equipment: equipmentInfo,
                  camera: cameraInfo,
                  lens: String(lens || '').trim(),
                  aperture,
                  shutterSpeed,
                  iso: iso ? `ISO ${iso}` : ''
                }
              });
            });
          });
        const formData = new FormData();
        formData.append('images', item.file);
        // canvas 壓縮後的 JPG 不再帶 EXIF；把從原始檔讀到的資料跟這一張一起送到後端。
        formData.append('metadata', JSON.stringify([{
          sourceHash: item.sourceHash,
          shootingDate: item.shootingDate,
          shootingParams: item.shootingParams
        }]));
        const res = await fetch(`${apiBaseUrl}/api/photos`, {
          method: 'POST',
          headers: { 'Authorization': authHeaders.Authorization },
          body: formData
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 401 || res.status === 403) throw new Error('您沒有上傳權限，請先登入後台！');
        if (!res.ok) throw new Error(data.error || '上傳失敗');
        const skipped = Number(res.headers.get('X-Skipped-Duplicates') || 0);
        skippedDuplicates += skipped;
        const savedPhotos = Array.isArray(data) ? data : (data?._id ? [data] : []);
        uploadedCount += savedPhotos.length;
        if (savedPhotos.length) {
          setPhotos(previous => [
            ...savedPhotos,
            ...previous.filter(existing => !savedPhotos.some(saved => String(saved._id) === String(existing._id)))
          ]);
        }
        } catch (error) {
          if (/沒有上傳權限/.test(error.message || '')) throw error;
          failedFiles.push(originalFile.name);
        }
        setUploadProgress({ total: selectedFiles.length, completed: index + 1, uploaded: uploadedCount, currentName: originalFile.name });
      }

      e.target.value = '';
      fetchPhotos();
      const messages = [`已上傳 ${uploadedCount} 張作品。`];
      if (skippedDuplicates > 0) messages.push(`自動略過 ${skippedDuplicates} 張重複照片。`);
      if (failedFiles.length > 0) messages.push(`${failedFiles.length} 張上傳失敗，可再試一次。`);
      alert(messages.join(''));
    } catch (err) {
      alert(err.message || '批量上傳失敗');
      fetchPhotos();
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const openLocationGroup = (locationKey) => {
    setActiveLocationGroupKey(locationKey);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const handleRenameLocation = async (group) => {
    const currentLocation = group.key === MISSING_LOCATION_KEY ? '' : group.label;
    const nextLocation = window.prompt(
      group.key === MISSING_LOCATION_KEY ? '為未填地點的照片設定地點名稱' : `將「${group.label}」整組改名為：`,
      group.key === MISSING_LOCATION_KEY ? '' : group.label
    );
    if (nextLocation === null) return;
    const normalizedNextLocation = nextLocation.trim();
    if (!normalizedNextLocation) {
      alert('地點名稱不能留空。');
      return;
    }
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/locations/rename`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ currentLocation, nextLocation: normalizedNextLocation })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '修改地點失敗');
      setPhotos(previous => previous.map(photo => getLocationKey(photo.location) === group.key
        ? { ...photo, location: data.nextLocation }
        : photo));
      if (activeLocationGroupKey === group.key) setActiveLocationGroupKey(data.nextLocation);
      alert(`已更新 ${data.updatedCount} 張照片的地點。`);
    } catch (err) {
      alert(err.message || '修改地點失敗，請重試。');
    }
  };

  const openEditModal = (photo) => {
    const draft = photoEditDrafts[String(photo._id)];
    setEditingPhoto(photo);
    setEditLocation(draft?.location ?? photo.location ?? '');
    setEditLocationParts(draft?.locationParts ?? splitLocationHierarchy(photo.location));
    setEditEquipment(draft?.equipment ?? photo.shootingParams?.equipment ?? '');
    const dateStr = photo.shootingDate ? new Date(photo.shootingDate).toISOString().split('T')[0] : '';
    setEditShootingDate(dateStr);
    setActiveCategory(photo.subCategory || 'uncategorized');
    setAiDescription(photo.aiDescription || '');
    setIsEditingTags(false);
    setIsManagingEquipmentPresets(false);
    setNewEquipmentPreset('');
    const savedResearch = locationResearchByPhotoId[String(photo._id)];
    setLocationResearch(savedResearch?.status === 'success' ? savedResearch.data : null);
    setLocationResearchError(savedResearch?.status === 'error' ? savedResearch.error : '');
  };

  const toggleJourneyCandidate = async photo => {
    const nextValue = !photo.journeyCandidate;
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/${photo._id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ journeyCandidate: nextValue })
      });
      const data = await res.json();
      if (!res.ok || !data?._id) throw new Error(data.error || '更新 Journeys 待選照片失敗');
      setPhotos(previous => previous.map(item => String(item._id) === String(photo._id) ? data : item));
      return data;
    } catch (error) {
      alert(error.message || '更新 Journeys 待選照片失敗');
      return null;
    }
  };

  const moveEditingPhotoToJourney = async () => {
    if (!editingPhoto) return;
    const updatedPhoto = await toggleJourneyCandidate(editingPhoto);
    if (updatedPhoto?.journeyCandidate) setEditingPhoto(null);
  };

  const duplicatePhotoToJourney = async photo => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/${photo._id}/duplicate-to-journey`, {
        method: 'POST',
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok || !data?._id) throw new Error(data.error || '另存 Journey 照片失敗');
      setPhotos(previous => [data, ...previous]);
      setJourneyActionHoverId(null);
      alert('已另存一份到 Journey 照片庫；原 Work 作品保留不變。');
    } catch (error) {
      alert(error.message || '另存 Journey 照片失敗');
    }
  };

  const persistWorkCategories = async (nextCategories) => {
    const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ workCategories: nextCategories })
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || '儲存分類失敗');
    const saved = Array.isArray(data.setting?.workCategories) ? data.setting.workCategories : nextCategories;
    setAvailableCategories(saved.includes('uncategorized') ? saved : [...saved, 'uncategorized']);
    setSiteSettings(normalizeLegacySiteCopy(data.setting));
  };

  const moveWorkCategory = async (category, direction) => {
    const currentIndex = availableCategories.indexOf(category);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= availableCategories.length) return;
    const nextCategories = [...availableCategories];
    [nextCategories[currentIndex], nextCategories[nextIndex]] = [nextCategories[nextIndex], nextCategories[currentIndex]];
    try {
      await persistWorkCategories(nextCategories);
    } catch (err) {
      alert(err.message || '調整分類順序失敗');
    }
  };

  const persistEquipmentPresets = async (nextPresets) => {
    const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ equipmentPresets: nextPresets })
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || '儲存常用設備失敗');
    const saved = Array.isArray(data.setting?.equipmentPresets) ? data.setting.equipmentPresets : nextPresets;
    setSiteSettings(normalizeLegacySiteCopy(data.setting));
    return saved;
  };

  const addEquipmentPreset = async () => {
    const preset = newEquipmentPreset.trim();
    const presets = Array.isArray(siteSettings.equipmentPresets) ? siteSettings.equipmentPresets : [];
    if (!preset || presets.some(item => item.toLowerCase() === preset.toLowerCase())) return;
    try {
      await persistEquipmentPresets([...presets, preset]);
      setEditEquipment(preset);
      setNewEquipmentPreset('');
    } catch (err) {
      alert(err.message || '新增常用設備失敗');
    }
  };

  const removeEquipmentPreset = async preset => {
    const presets = Array.isArray(siteSettings.equipmentPresets) ? siteSettings.equipmentPresets : [];
    try {
      await persistEquipmentPresets(presets.filter(item => item !== preset));
    } catch (err) {
      alert(err.message || '刪除常用設備失敗');
    }
  };

  const persistLocationCollections = async nextCollections => {
    const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ locationCollections: nextCollections })
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || '儲存折疊地點失敗');
    const saved = normalizeLocationCollections(data.setting?.locationCollections);
    setSiteSettings(normalizeLegacySiteCopy(data.setting));
    return saved;
  };

  const persistLocationPhotoOrders = async nextOrders => {
    const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ locationPhotoOrders: nextOrders })
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || '儲存圖集排序失敗');
    setSiteSettings(normalizeLegacySiteCopy(data.setting));
  };

  const persistLocationMenuOrders = async nextOrders => {
    const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({ locationMenuOrders: nextOrders })
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || '儲存地點選單排序失敗');
    setSiteSettings(normalizeLegacySiteCopy(data.setting));
  };

  const moveLocationMenuItem = async (orderKey, items, index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length || isSavingLocationMenuOrder) return;
    const itemIds = items.map(item => String(item.collectionPrefix || item.id || item.key || '')).filter(Boolean);
    [itemIds[index], itemIds[targetIndex]] = [itemIds[targetIndex], itemIds[index]];
    const nextOrders = [
      ...normalizeLocationMenuOrders(siteSettings.locationMenuOrders).filter(item => item.key !== orderKey),
      { key: orderKey, itemIds }
    ];
    setIsSavingLocationMenuOrder(true);
    try {
      await persistLocationMenuOrders(nextOrders);
    } catch (err) {
      alert(err.message || '調整地點選單順序失敗');
    } finally {
      setIsSavingLocationMenuOrder(false);
    }
  };

  const moveLocationPhoto = async (group, index, direction) => {
    const targetIndex = index + direction;
    if (!group || targetIndex < 0 || targetIndex >= group.photos.length || isSavingLocationPhotoOrder) return;
    const nextPhotoIds = group.photos.map(photo => String(photo._id));
    [nextPhotoIds[index], nextPhotoIds[targetIndex]] = [nextPhotoIds[targetIndex], nextPhotoIds[index]];
    const currentOrders = normalizeLocationPhotoOrders(siteSettings.locationPhotoOrders);
    const nextOrders = [
      ...currentOrders.filter(item => item.location !== group.key),
      { location: group.key, photoIds: nextPhotoIds }
    ];
    setIsSavingLocationPhotoOrder(true);
    try {
      await persistLocationPhotoOrders(nextOrders);
    } catch (err) {
      alert(err.message || '調整圖集排序失敗');
    } finally {
      setIsSavingLocationPhotoOrder(false);
    }
  };

  const addLocationCollection = async () => {
    const title = newLocationCollectionTitle.trim();
    const prefix = normalizeLocationCollectionPrefix(newLocationCollectionPrefix);
    const collections = normalizeLocationCollections(siteSettings.locationCollections);
    if (!title || !prefix) {
      alert('請填寫前台顯示名稱與地點前綴。');
      return;
    }
    if (collections.some(item => normalizeSearchValue(item.prefix) === normalizeSearchValue(prefix))) {
      alert('這個地點前綴已經設定過折疊。');
      return;
    }
    setIsSavingLocationCollections(true);
    try {
      await persistLocationCollections([...collections, { id: `location-collection-${Date.now()}`, title, prefix }]);
      setNewLocationCollectionTitle('');
      setNewLocationCollectionPrefix('');
    } catch (err) {
      alert(err.message || '新增折疊地點失敗');
    } finally {
      setIsSavingLocationCollections(false);
    }
  };

  const removeLocationCollection = async collectionId => {
    setIsSavingLocationCollections(true);
    try {
      await persistLocationCollections(normalizeLocationCollections(siteSettings.locationCollections).filter(item => item.id !== collectionId));
    } catch (err) {
      alert(err.message || '移除折疊地點失敗');
    } finally {
      setIsSavingLocationCollections(false);
    }
  };

  const saveFeaturedSettings = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/photos/settings`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          featuredPhotoIds: (siteSettings.featuredPhotoIds || []).slice(0, 36),
          featuredTargetCount: Math.max(1, Math.min(36, Number(siteSettings.featuredTargetCount) || 9))
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || '儲存精選作品失敗');
      setSiteSettings(normalizeLegacySiteCopy(data.setting));
      alert('精選作品已儲存，首頁會依照目前順序展示。');
    } catch (err) {
      alert(err.message || '儲存精選作品失敗');
    }
  };

  const handleAddNewTag = async () => {
    const t = newTagInput.trim().toLowerCase();
    if (t && !availableCategories.includes(t)) {
      try {
        await persistWorkCategories([...availableCategories, t]);
        setActiveCategory(t);
      } catch (err) {
        alert(err.message || '新增分類失敗');
      }
    }
    setNewTagInput('');
  };

  const handleRemoveTag = async (tagToRemove) => {
    if (tagToRemove === 'uncategorized') return;
    const affectedPhotos = photos.filter(p => p.subCategory === tagToRemove);
    if (affectedPhotos.length > 0 && !window.confirm(`「${TAG_LABELS[tagToRemove] || tagToRemove}」下仍有 ${affectedPhotos.length} 張照片。刪除後會改為未分類，是否繼續？`)) return;
    try {
      if (affectedPhotos.length > 0) {
        const updates = await Promise.all(affectedPhotos.map(photo => fetch(`${apiBaseUrl}/api/photos/${photo._id}`, {
          method: 'PUT', headers: authHeaders, body: JSON.stringify({ subCategory: 'uncategorized' })
        })));
        if (updates.some(response => !response.ok)) throw new Error('部分作品無法改為未分類，分類尚未刪除');
        setPhotos(prev => prev.map(photo => photo.subCategory === tagToRemove ? { ...photo, subCategory: 'uncategorized' } : photo));
      }
      await persistWorkCategories(availableCategories.filter(category => category !== tagToRemove));
      if (activeCategory === tagToRemove) setActiveCategory('uncategorized');
    } catch (err) {
      alert(err.message || '刪除分類失敗');
    }
  };

  const theme = useMemo(() => ({
    bg: isDarkMode ? '#000000' : '#f5f5f7',
    textPrimary: isDarkMode ? '#f5f5f7' : '#1d1d1f',
    textSecondary: isDarkMode ? '#86868b' : '#6e6e73',
    headerBg: isDarkMode ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.85)',
    headerBorder: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
    cardBg: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.9)',
    cardBorder: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
    cardShadow: isDarkMode ? '0 20px 40px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.04)',
    modalBg: isDarkMode ? '#1c1c1e' : '#ffffff',
    inputBg: isDarkMode ? '#2c2c2e' : '#f2f2f7',
    inputBorder: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'
  }), [isDarkMode]);

  const currentPhotoData = activeLightboxPhotos.length > 0 ? activeLightboxPhotos[lightboxIndex] : null;

  const handleMAiOpenPhoto = (photo, sourceScrollY = window.scrollY) => {
    const location = String(photo?.location || '').trim();
    if (!location) return;
    // M.ai 是全站入口；每次都回到首頁，不能把上一個 M.ai 圖集當成返回來源。
    setCurrentSubPage({
      id: location,
      type: 'location',
      title: location,
      desc: '📍 按地點探索圖集',
      returnTo: { id: '__home__', title: '首頁', scrollY: sourceScrollY }
    });
    setMaiPhotoTargetId(String(photo?.id || ''));
    setLightboxIndex(-1);
  };

  const openJourneyRelatedWorks = entry => {
    const locationScopes = (entry?.relatedWorkLocations || []).filter(scope => scope?.prefix);
    const photoIds = [...new Set((entry?.relatedPhotoIds || []).map(String).filter(Boolean))];
    if (!locationScopes.length && !photoIds.length) return;
    handleSelectSubPage({
      id: `journey-related-${entry._id}`,
      type: 'journey-related',
      title: `JOURNEY ${String(entry.sequenceNumber || '').padStart(2, '0')} · 關聯作品`,
      desc: locationScopes.length ? locationScopes.map(scope => scope.label || scope.prefix).join('、') : `${entry.title || '這段旅程'}當時拍下的作品`,
      locationScopes,
      photoIds,
      // 從長時間線進來時，返回原本那一篇 Journey 的閱讀位置，而不是整頁頂端。
      returnTo: { id: 'journeys_index', title: 'Journeys', scrollY: window.scrollY }
    });
  };

  // 從 M.ai 點選某張卡片後，進入同地點圖集並定位到那一張，而不是只跳到圖集頂端。
  useEffect(() => {
    if (!maiPhotoTargetId || currentSubPage?.type !== 'location') return undefined;
    let frame;
    let settleTimer;
    let clearTimer;
    let attempts = 0;
    const locateTargetPhoto = () => {
      const target = document.getElementById(`work-photo-${maiPhotoTargetId}`);
      if (!target && attempts < 16) {
        attempts += 1;
        frame = window.requestAnimationFrame(locateTargetPhoto);
        return;
      }
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 懶載入圖片會改變前方卡片高度；版面穩定後再校正一次，避免落在圖集頂部或錯誤卡片。
      settleTimer = window.setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        clearTimer = window.setTimeout(() => setMaiPhotoTargetId(null), 420);
      }, 520);
    };
    frame = window.requestAnimationFrame(locateTargetPhoto);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      window.clearTimeout(clearTimer);
    };
  }, [maiPhotoTargetId, currentSubPage?.id, currentSubPage?.type, displayPhotos.length]);

  const broadcastMode = siteSettings.broadcast?.mode || 'off';
  const editingPhotoResearch = editingPhoto ? locationResearchByPhotoId[String(editingPhoto._id)] : null;
  const explicitBroadcastBypass = sessionStorage.getItem('broadcast_admin_bypass') === '1';
  const broadcastBypassed = broadcastRecoveryBypassed || (broadcastMode === 'visitors'
    ? adminSessionVerified
    : broadcastMode === 'all' && adminSessionVerified && explicitBroadcastBypass);
  const shouldShowBroadcast = broadcastResolved && broadcastMode !== 'off' && !broadcastBypassed;

  if (broadcastPreview) {
    return <BroadcastTakeover config={broadcastDraft} preview onExitPreview={() => setBroadcastPreview(false)} />;
  }

  if (!broadcastResolved || (adminToken && !adminVerificationResolved)) {
    return <div style={{ minHeight: '100vh', background: '#000' }} aria-label="正在读取网站状态" />;
  }

  if (shouldShowBroadcast) {
    return <BroadcastTakeover config={siteSettings.broadcast} password={loginPassword} setPassword={setLoginPassword} onRecoveryLogin={handleBroadcastRecoveryLogin} />;
  }

  return (
    <div 
      ref={containerRef} 
      onClick={() => { if (activeMenu) setActiveMenu(null); }}
      style={{ 
        position: 'relative', 
        minHeight: '100vh', 
        width: '100vw', 
        maxWidth: '100%', 
        backgroundColor: theme.bg, 
        color: theme.textPrimary, 
        fontFamily: useInterInterface
          ? '"Inter Interface", "Noto Sans SC", "Microsoft YaHei UI", sans-serif'
          : '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif', 
        transition: 'background-color 0.4s ease, color 0.4s ease', 
        boxSizing: 'border-box' 
      }}
    >
      <style>{`
        @font-face {
          font-family: 'Inter Interface';
          src: url('${process.env.PUBLIC_URL}/fonts/Inter-Variable.woff2') format('woff2');
          font-weight: 100 900;
          font-style: normal;
          font-display: swap;
        }
        .gear-viewer-nav {
          opacity: 0;
          transition: opacity .2s ease, background .2s ease;
        }
        .gear-viewer-nav:hover,
        .gear-viewer-nav:focus-visible {
          opacity: 1;
        }
        .gear-viewer-nav span {
          width: 44px;
          height: 58px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          background: rgba(20,20,22,.62);
          border: 1px solid rgba(255,255,255,.22);
          box-shadow: 0 8px 28px rgba(0,0,0,.28);
          backdrop-filter: blur(12px);
        }
      `}</style>

      <MAiAssistant
        apiBaseUrl={apiBaseUrl}
        theme={theme}
        isDarkMode={isDarkMode}
        adminToken={adminToken}
        showLauncher={showMAiLauncher}
        onOpenPhoto={handleMAiOpenPhoto}
      />
      
      {/* 🧭 Apple 側邊進度條 */}
      {!isAdminDashboard && !currentSubPage && lightboxIndex < 0 && (
        <ScrollNavigation sections={HOME_SECTIONS} isDarkMode={isDarkMode} theme={theme} />
      )}

      {/* 1. Header */}
      <motion.header
        className={`site-header${currentPrimaryMenu === 'Gear' ? ' site-header-gear' : ''}${isAdminDashboard ? ' site-header-admin' : ''}`}
        layoutRoot
        onMouseLeave={handleMenuLeave}
        onClick={(e) => e.stopPropagation()}
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          zIndex: 1000, 
          backdropFilter: 'blur(20px)', 
          WebkitBackdropFilter: 'blur(20px)', 
          backgroundColor: theme.headerBg, 
          borderBottom: `1px solid ${theme.headerBorder}`, 
          transition: 'opacity 0.35s ease, background-color 0.3s ease', 
          boxSizing: 'border-box', 
          opacity: lightboxIndex >= 0 ? 0 : 1, 
          pointerEvents: lightboxIndex >= 0 ? 'none' : 'auto' 
        }}
      >
        <div 
          className="site-header-inner"
          style={{ 
            maxWidth: isAdminDashboard ? '1440px' : '1080px', 
            margin: '0 auto', 
            height: '52px', 
            padding: isAdminDashboard ? '0 28px' : '0 20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}
        >
          <div className="site-header-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span 
              onClick={handleSmartLogoClick} 
              title="M." 
              className="nav-menu-item"
              style={{ 
                fontSize: '22px', 
                fontWeight: '800', 
                fontFamily: 'serif', 
                letterSpacing: '-1px', 
                color: theme.textPrimary, 
                cursor: 'pointer', 
                padding: '4px 8px 4px 0'
              }}
            >
              M.
            </span>
            {isAdminDashboard && <>
              <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px', background: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)', color: theme.textSecondary, letterSpacing: '0.5px' }}>ADMIN</span>
              <button type="button" onClick={openBroadcastControl} aria-label="打开 Broadcast 控制台" title="Broadcast 接管" className={`broadcast-skull-button ${adminModule === 'Broadcast' ? 'active' : ''}`}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.4a8.1 8.1 0 0 0-8.1 8.1c0 3.25 1.58 5.78 4.1 7.13V21h2.5v-2.1h3V21H16v-3.37c2.52-1.35 4.1-3.88 4.1-7.13A8.1 8.1 0 0 0 12 2.4Z"/><circle cx="9" cy="10.7" r="1.35"/><circle cx="15" cy="10.7" r="1.35"/><path d="m10.2 15.1 1.8-1.5 1.8 1.5"/></svg>
              </button>
            </>}
          </div>
          
          {/* 導航分區 */}
          {isAdminDashboard ? (
            <nav className="nav-links-wrap admin-module-nav" style={{ display: 'flex', gap: '28px', marginLeft: '-18px' }}>
              {['Work', 'Gear', 'Journeys', 'Contact'].map((mod) => (
                <span 
                  key={mod} 
                  onClick={() => {
                    setAdminModule(mod);
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                  }}
                  className="nav-menu-item"
                  style={{ 
                    fontSize: '13px', 
                    fontWeight: adminModule === mod ? '600' : '500', 
                    color: adminModule === mod ? (isDarkMode ? '#fff' : '#000') : theme.textSecondary, 
                    cursor: 'pointer', 
                    transition: 'color 0.2s ease', 
                    padding: '16px 4px',
                    position: 'relative'
                  }}
                >
                  {mod === 'Work' && <><span className="admin-module-label-full">Work 作品</span><span className="admin-module-label-short">Work</span></>}
                  {mod === 'Gear' && <><span className="admin-module-label-full">Gear 器材</span><span className="admin-module-label-short">Gear</span></>}
                  {mod === 'Journeys' && <><span className="admin-module-label-full">Journeys 旅程</span><span className="admin-module-label-short">Journeys</span></>}
                  {mod === 'Contact' && <><span className="admin-module-label-full">Contact 聯絡</span><span className="admin-module-label-short">Contact</span></>}
                  {adminModule === mod && (
                    <motion.div 
                      layoutId="adminActiveIndicator"
                      style={{ position: 'absolute', bottom: '0px', left: 0, right: 0, height: '2px', backgroundColor: '#0071e3', borderRadius: '2px' }}
                    />
                  )}
                </span>
              ))}
            </nav>
          ) : (
            <nav className="nav-links-wrap" style={{ display: 'flex', gap: '32px' }}>
              {Object.keys(dynamicNavMenus).map((menuKey) => (
                <span 
                  key={menuKey} 
                  className="nav-menu-item"
                  onMouseEnter={() => handleMenuEnter(menuKey)} 
                  onClick={(e) => handleMenuToggle(menuKey, e)}
                  style={{ 
                    fontSize: '13px', 
                    fontWeight: activeMenu === menuKey || currentPrimaryMenu === menuKey ? '600' : '500', 
                    color: activeMenu === menuKey || currentPrimaryMenu === menuKey ? (isDarkMode ? '#fff' : '#000') : theme.textSecondary, 
                    cursor: 'pointer', 
                    transition: 'color 0.2s ease', 
                    padding: '16px 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    position: 'relative'
                  }}
                >
                  {menuKey}
                  {activeMenu === menuKey && (
                    <span style={{ fontSize: '8px', opacity: 0.6, transform: 'translateY(1px)' }}>▲</span>
                  )}
                  {currentPrimaryMenu === menuKey && (
                    <motion.div layoutId="publicActiveIndicator" transition={{ type: 'spring', stiffness: 460, damping: 34 }} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '2px', borderRadius: '2px', background: '#0071e3' }} />
                  )}
                </span>
              ))}
            </nav>
          )}
          
          <div className={isAdminDashboard ? 'site-header-actions admin-status-actions' : 'site-header-actions'} style={{ display: 'flex', alignItems: 'center', gap: isAdminDashboard ? '10px' : '14px' }}>
            {isAdminDashboard ? (
              <>
                <button type="button" className="admin-quick-button" onClick={() => setShowAdminQuickMenu(value => !value)} aria-label="開啟後台狀態與操作" aria-expanded={showAdminQuickMenu} style={{ display: 'none', width: '32px', height: '32px', placeItems: 'center', padding: 0, border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: showAdminQuickMenu ? theme.inputBg : 'transparent', color: theme.textPrimary, fontSize: '18px', lineHeight: 1, cursor: 'pointer' }}>•••</button>
                <MAiStatusBadge apiBaseUrl={apiBaseUrl} theme={theme} isDarkMode={isDarkMode} adminToken={adminToken} />
                <MAiTokenUsageBadge apiBaseUrl={apiBaseUrl} theme={theme} isDarkMode={isDarkMode} adminToken={adminToken} />
                {storageInfo && (
                  <button
                    type="button"
                    onClick={openStorageOverview}
                    title="查看伺服器儲存空間分析"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 10px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(255,255,255,.05)' : '#fbfbfd', color: theme.textSecondary, fontSize: '11px', lineHeight: 1, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <span>可用空間</span>
                    <span style={{ width: '36px', height: '4px', overflow: 'hidden', borderRadius: '99px', background: isDarkMode ? 'rgba(255,255,255,.18)' : '#d2d2d7' }}>
                      <span style={{ display: 'block', width: `${Math.min(100, Math.max(3, (storageInfo.usedBytes / storageInfo.totalBytes) * 100))}%`, height: '100%', borderRadius: 'inherit', background: '#0071e3' }} />
                    </span>
                    <strong style={{ color: theme.textPrimary, fontWeight: '650' }}>{(storageInfo.freeBytes / (1024 ** 3)).toFixed(1)} GB</strong>
                  </button>
                )}
                <motion.button 
                  whileHover={{ scale: 1.05 }} 
                  whileTap={{ scale: 0.95 }} 
                  onClick={handleLogout} 
                  style={{ background: 'transparent', border: `1px solid ${theme.cardBorder}`, color: theme.textSecondary, borderRadius: '980px', padding: '5px 14px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}
                >
                  退出後台
                </motion.button>
              </>
            ) : (
              <motion.button 
                whileHover={{ opacity: 1 }} 
                whileTap={{ scale: 0.9 }} 
                onClick={() => setShowSearchModal(true)} 
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: theme.textPrimary, 
                  cursor: 'pointer', 
                  opacity: 0.8, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  padding: '6px', 
                  transition: 'opacity 0.2s ease' 
                }} 
                title="搜尋作品"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </motion.button>
            )}
          </div>
        </div>

        {isAdminDashboard && showAdminQuickMenu && <div className="admin-quick-menu" style={{ display: 'none' }}>
          <div className="admin-quick-menu-status"><MAiStatusBadge apiBaseUrl={apiBaseUrl} theme={theme} isDarkMode={isDarkMode} adminToken={adminToken} /><MAiTokenUsageBadge apiBaseUrl={apiBaseUrl} theme={theme} isDarkMode={isDarkMode} adminToken={adminToken} /></div>
          {storageInfo && <button type="button" onClick={openStorageOverview}><span>可用空間</span><strong>{(storageInfo.freeBytes / (1024 ** 3)).toFixed(1)} GB</strong></button>}
          <button type="button" className="admin-quick-menu-logout" onClick={handleLogout}>退出後台</button>
        </div>}

        {/* 前台下拉選單 */}
        <AnimatePresence>
          {!isAdminDashboard && activeMenu && (
            <motion.div 
              key="dropdown-container" 
              initial={{ height: 0, opacity: 0 }} 
              animate={{ height: 'auto', opacity: 1 }} 
              exit={{ opacity: 0, height: 0 }} 
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }} 
              style={{ 
                maxHeight: 'min(580px, calc(100dvh - 52px))',
                overflow: 'hidden', 
                backgroundColor: isDarkMode ? 'rgba(18, 18, 20, 0.97)' : 'rgba(255, 255, 255, 0.97)', 
                borderTop: `1px solid ${theme.cardBorder}`, 
                boxShadow: '0 30px 60px rgba(0,0,0,0.25)' 
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeMenu} 
                  initial={{ opacity: 0, y: 6 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: -6 }} 
                  transition={{ duration: 0.15, ease: 'easeOut' }} 
                  className="mobile-dropdown-grid"
                  ref={activeMenu === 'Work' ? workMenuPanelRef : undefined}
                  style={{ 
                    width: '100%',
                    maxWidth: activeMenu === 'Work' ? workMenuMaxWidth : '1080px', 
                    margin: '0 auto', 
                    padding: '32px 24px 44px 24px', 
                    alignItems: 'start',
                    position: 'relative',
                    gridTemplateColumns: activeMenu === 'Work' ? `repeat(${workMenuColumnCount}, minmax(0, 1fr))` : undefined,
                    height: activeMenu === 'Work' && !isMobileScreen ? 'min(580px, calc(100dvh - 52px))' : undefined,
                    maxHeight: 'min(580px, calc(100dvh - 52px))',
                    overflowY: activeMenu === 'Work' && !isMobileScreen ? 'hidden' : (activeMenu === 'Work' ? 'auto' : 'visible'),
                    overscrollBehavior: 'contain',
                    boxSizing: 'border-box',
                    transition: 'max-width .2s ease'
                  }}
                >
                  {(!isMobileScreen || activeMenu !== 'Work' || (!activeLocationMenuGroup && !activeLocationMenuCity)) && dynamicNavMenus[activeMenu].map((column, colIdx) => (
                    <div key={colIdx} style={{ display: 'flex', flexDirection: 'column', gap: '14px', minHeight: 0, height: activeMenu === 'Work' && !isMobileScreen ? '100%' : undefined, overflowY: activeMenu === 'Work' && !isMobileScreen ? 'auto' : undefined, overscrollBehavior: 'contain', paddingRight: activeMenu === 'Work' && !isMobileScreen ? '10px' : undefined }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#86868b', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '2px' }}>
                        {column.category}
                      </span>
                      {column.items.map((subItem, itemIdx) => (
                        <motion.div 
                          key={subItem.id || itemIdx} 
                          whileHover={{ x: 4 }} 
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.15 }} 
                          onMouseEnter={(event) => {
                            if (isMobileScreen) return;
                            if (subItem.type === 'location-collection') {
                              setHasOpenedLocationCityColumn(true);
                              setHoveredLocationMenuGroup(subItem.id);
                              setHoveredLocationMenuCity(null);
                              updateLocationMenuColumnAnchor('cities', event.currentTarget);
                            } else if (column.category === '目的地圖集') {
                              setHasOpenedLocationCityColumn(true);
                              setHoveredLocationMenuGroup(null);
                              setHoveredLocationMenuCity(null);
                            }
                          }}
                          onClick={() => {
                            if (isMobileScreen && subItem.type === 'location-collection') {
                              setHasOpenedLocationCityColumn(true);
                              setHasOpenedLocationDetailColumn(false);
                              setHoveredLocationMenuGroup(subItem.id);
                              setHoveredLocationMenuCity(null);
                              return;
                            }
                            handleSelectSubPage(subItem);
                          }} 
                          style={{ 
                            cursor: 'pointer', 
                            // 地點項目只讓標題與說明文字本身觸發第三欄，保留右側空白作為
                            // 從省份移動到具體地點時的安全通道，避免斜向移動時誤切換到別組。
                            width: column.category === '目的地圖集' ? 'fit-content' : undefined,
                            maxWidth: '100%',
                            alignSelf: column.category === '目的地圖集' ? 'flex-start' : undefined,
                            padding: column.category === '目的地圖集' ? '6px 10px 6px 0' : '6px 0',
                            borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.03)' 
                          }}
                        >
                          <h4 style={{ margin: '0 0 3px 0', fontSize: '15px', fontWeight: '600', color: theme.textPrimary, letterSpacing: '-0.2px', whiteSpace: column.category === '目的地圖集' ? 'nowrap' : undefined }}>
                            {subItem.title}
                          </h4>
                          <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary, lineHeight: '1.4', whiteSpace: column.category === '目的地圖集' ? 'nowrap' : undefined }}>
                            {subItem.desc}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  ))}
                  {activeMenu === 'Work' && (activeLocationMenuGroup || hasOpenedLocationCityColumn) && (!isMobileScreen || !activeLocationMenuCity) && (
                    <motion.div
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 8 }}
                      transition={{ duration: 0.14 }}
                      onMouseEnter={() => activeLocationMenuGroup && setHoveredLocationMenuGroup(activeLocationMenuGroup.id)}
                      ref={locationMenuCityColumnRef}
                      style={{
                        minWidth: 0,
                        minHeight: 0,
                        height: !isMobileScreen ? '100%' : undefined,
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        scrollbarGutter: 'stable',
                        padding: '0 20px 20px 24px',
                        boxSizing: 'border-box',
                        position: 'relative',
                        borderLeft: `1px solid ${theme.cardBorder}`
                      }}
                    >
                      <div style={{ minHeight: '100%', boxSizing: 'border-box', paddingTop: !isMobileScreen ? `${locationMenuColumnAnchors.cities}px` : undefined, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {isMobileScreen && <button type="button" onClick={() => { setHoveredLocationMenuGroup(null); setHoveredLocationMenuCity(null); setHasOpenedLocationCityColumn(false); setHasOpenedLocationDetailColumn(false); }} style={{ alignSelf: 'flex-start', padding: '7px 12px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '12px', cursor: 'pointer' }}>← 返回目的地</button>}
                      <span style={{ height: !isMobileScreen ? '20px' : undefined, marginTop: !isMobileScreen ? '-20px' : undefined, marginBottom: !isMobileScreen ? '-14px' : undefined, fontSize: '11px', fontWeight: '700', color: '#86868b', letterSpacing: '0.6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{activeLocationMenuGroup ? `${activeLocationMenuGroup.title} · 城市` : '城市'}</span>
                      {activeLocationMenuGroup ? activeLocationMenuGroup.children.map(subItem => (
                        <motion.div
                          key={subItem.id}
                          whileHover={{ x: 4 }}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: '0.15' }}
                          onMouseEnter={(event) => {
                            if (isMobileScreen) return;
                            setHoveredLocationMenuCity(subItem.id);
                            setHasOpenedLocationDetailColumn(true);
                            updateLocationMenuColumnAnchor('details', event.currentTarget);
                          }}
                          onClick={() => {
                            if (isMobileScreen && Array.isArray(subItem.children) && subItem.children.length > 0) {
                              setHoveredLocationMenuCity(subItem.id);
                              setHasOpenedLocationDetailColumn(true);
                              return;
                            }
                            handleSelectSubPage(subItem);
                          }}
                          style={{ cursor: 'pointer', padding: '6px 0', borderBottom: isDarkMode ? '1px solid rgba(255,255,255,.04)' : '1px solid rgba(0,0,0,.03)' }}
                        >
                          <h4 style={{ margin: '0 0 3px 0', fontSize: '15px', fontWeight: '600', color: theme.textPrimary, letterSpacing: '-0.2px' }}>{subItem.title}</h4>
                          <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary, lineHeight: '1.4' }}>{subItem.desc}</p>
                        </motion.div>
                      )) : <p style={{ margin: '8px 0 0', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>這個目的地沒有下一級城市，點擊名稱即可查看作品。</p>}
                      </div>
                    </motion.div>
                  )}
                  {activeMenu === 'Work' && hasOpenedLocationDetailColumn && (
                    <motion.div
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 8 }}
                      transition={{ duration: 0.14 }}
                      onMouseEnter={() => {
                        if (activeLocationMenuCity) {
                          setHoveredLocationMenuGroup(activeLocationMenuGroup?.id || null);
                          setHoveredLocationMenuCity(activeLocationMenuCity.id);
                        }
                      }}
                      ref={locationMenuDetailColumnRef}
                      style={{
                        minWidth: 0,
                        minHeight: 0,
                        height: !isMobileScreen ? '100%' : undefined,
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        scrollbarGutter: 'stable',
                        padding: '0 0 20px 24px',
                        boxSizing: 'border-box',
                        position: 'relative',
                        borderLeft: `1px solid ${theme.cardBorder}`
                      }}
                    >
                      <div style={{ minHeight: '100%', boxSizing: 'border-box', paddingTop: !isMobileScreen ? `${locationMenuColumnAnchors.details}px` : undefined, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {isMobileScreen && <button type="button" onClick={() => { setHoveredLocationMenuCity(null); setHasOpenedLocationDetailColumn(false); }} style={{ alignSelf: 'flex-start', padding: '7px 12px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '12px', cursor: 'pointer' }}>← 返回{activeLocationMenuGroup?.title || '城市'}</button>}
                      <span style={{ height: !isMobileScreen ? '20px' : undefined, marginTop: !isMobileScreen ? '-20px' : undefined, marginBottom: !isMobileScreen ? '-14px' : undefined, fontSize: '11px', fontWeight: '700', color: '#86868b', letterSpacing: '0.6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{activeLocationMenuCity ? `${activeLocationMenuCity.title} · 具體地點` : '具體地點'}</span>
                      {activeLocationMenuCity ? activeLocationMenuCity.children?.map(subItem => (
                        <motion.div key={subItem.id} whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.15 }} onClick={() => handleSelectSubPage(subItem)} style={{ cursor: 'pointer', padding: '6px 0', borderBottom: isDarkMode ? '1px solid rgba(255,255,255,.04)' : '1px solid rgba(0,0,0,.03)' }}>
                          <h4 style={{ margin: '0 0 3px 0', fontSize: '15px', fontWeight: '600', color: theme.textPrimary, letterSpacing: '-0.2px' }}>{subItem.title}</h4>
                          <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary, lineHeight: '1.4' }}>{subItem.desc}</p>
                        </motion.div>
                      )) : <p style={{ margin: '8px 0 0', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>移到城市名稱，即可查看具體地點。</p>}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* 🔐 Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100dvh', 
              backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.6)', 
              backdropFilter: 'blur(20px)', 
              WebkitBackdropFilter: 'blur(20px)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 20000 
            }}
          >
            <motion.div 
              variants={modalContentVariants} 
              initial="hidden" 
              animate="visible" 
              exit="exit" 
              style={{ 
                background: theme.modalBg, 
                border: `1px solid ${theme.cardBorder}`, 
                padding: '40px', 
                borderRadius: '24px', 
                width: '360px', 
                boxShadow: '0 40px 100px rgba(0, 0, 0, 0.5)' 
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '18px', background: isDarkMode ? '#2c2c2e' : '#f2f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }}>
                  🛡️
                </div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', letterSpacing: '-0.5px' }}>Terminal Access</h2>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: theme.textSecondary }}>請輸入管理員密碼以進入控制中心</p>
              </div>

              <form onSubmit={handleLogin}>
                <input 
                  type="password" 
                  autoFocus 
                  placeholder="Enter Password" 
                  value={loginPassword} 
                  onChange={e => setLoginPassword(e.target.value)} 
                  style={{ 
                    width: '100%', 
                    padding: '16px 20px', 
                    borderRadius: '14px', 
                    border: `1px solid ${theme.inputBorder}`, 
                    backgroundColor: theme.inputBg, 
                    color: theme.textPrimary, 
                    fontSize: '16px', 
                    letterSpacing: '4px', 
                    outline: 'none', 
                    boxSizing: 'border-box', 
                    marginBottom: '24px', 
                    textAlign: 'center' 
                  }} 
                />
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <motion.button 
                    type="button" 
                    whileHover={{ scale: 1.02 }} 
                    whileTap={{ scale: 0.98 }} 
                    onClick={() => setShowLoginModal(false)} 
                    style={{ 
                      flex: 1, 
                      padding: '14px', 
                      background: 'transparent', 
                      border: `1px solid ${theme.cardBorder}`, 
                      borderRadius: '14px', 
                      color: theme.textPrimary, 
                      fontSize: '15px', 
                      fontWeight: '600', 
                      cursor: 'pointer' 
                    }}
                  >
                    取消
                  </motion.button>
                  <motion.button 
                    type="submit" 
                    whileHover={{ scale: 1.02 }} 
                    whileTap={{ scale: 0.98 }} 
                    style={{ 
                      flex: 1, 
                      padding: '14px', 
                      background: '#0071e3', 
                      border: 'none', 
                      borderRadius: '14px', 
                      color: '#fff', 
                      fontSize: '15px', 
                      fontWeight: '600', 
                      cursor: 'pointer' 
                    }}
                  >
                    解鎖
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 搜尋 Modal */}
      <AnimatePresence>
        {showSearchModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            transition={{ duration: 0.2 }} 
            onClick={() => setShowSearchModal(false)} 
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100dvh', 
              backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.45)', 
              backdropFilter: 'blur(12px)', 
              WebkitBackdropFilter: 'blur(12px)', 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'center', 
              paddingTop: '100px', 
              zIndex: 20000, 
              willChange: 'opacity' 
            }}
          >
            <motion.div 
              variants={modalContentVariants} 
              initial="hidden" 
              animate="visible" 
              exit="exit" 
              onClick={e => e.stopPropagation()} 
              style={{ 
                background: theme.modalBg, 
                border: `1px solid ${theme.cardBorder}`, 
                padding: '24px', 
                borderRadius: '22px', 
                width: '540px', 
                maxWidth: '90vw', 
                boxShadow: '0 30px 70px rgba(0, 0, 0, 0.5)', 
                willChange: 'transform, opacity' 
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderBottom: `1px solid ${theme.inputBorder}`, paddingBottom: '14px', marginBottom: '16px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>

                <input 
                  type="text" 
                  autoFocus 
                  placeholder="搜尋地點、相機、鏡頭..." 
                  value={searchQuery} 
                  onChange={e => setSearchQuery(e.target.value)} 
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: '16px', color: theme.textPrimary, fontWeight: '500' }} 
                />

                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: '13px', padding: '2px 6px' }}>
                    ✕
                  </button>
                )}

                <button onClick={() => { setShowSearchModal(false); setSearchQuery(''); }} style={{ background: 'transparent', border: 'none', color: '#0071e3', fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}>
                  取消
                </button>
              </div>

              <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                {searchQuery.trim() ? (
                  filteredPhotos.length > 0 ? (
                    filteredPhotos.map((p, idx) => (
                      <div 
                        key={p._id || idx} 
                        onClick={() => { setShowSearchModal(false); setSearchQuery(''); openLightbox(idx, filteredPhotos); }} 
                        style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 12px', borderRadius: '12px', cursor: 'pointer', transition: 'background 0.15s ease' }} 
                        onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'} 
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <img 
                          src={getPhotoImagePath(p, 'thumbnail')} 
                          alt="preview" 
                          loading="lazy" 
                          decoding="async"
                          style={{ width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', display: 'block', backgroundColor: isDarkMode ? '#2c2c2e' : '#e5e5ea' }} 
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h4 style={{ margin: '0 0 3px 0', fontSize: '14px', fontWeight: '600', color: theme.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            📍 {p.location || '未知地點'}
                          </h4>
                          <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            📷 {getPhotoEquipmentLabel(p) || '未填寫器材'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '36px 0', color: theme.textSecondary, fontSize: '14px' }}>未找到相關作品，嘗試搜尋其他關鍵詞 ✨</div>
                  )
                ) : (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: theme.textSecondary, fontSize: '13px' }}>輸入地點或器材關鍵詞開始搜尋...</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🖼️ Hero 照片勾選添加彈窗 */}
      <AnimatePresence>
        {showPhotoPicker && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={() => setShowPhotoPicker(false)} 
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100dvh', 
              backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)', 
              backdropFilter: 'blur(20px)', 
              WebkitBackdropFilter: 'blur(20px)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 20000 
            }}
          >
            <motion.div 
              variants={modalContentVariants} 
              initial="hidden" 
              animate="visible" 
              exit="exit" 
              onClick={e => e.stopPropagation()} 
              style={{ 
                background: theme.modalBg, 
                border: `1px solid ${theme.cardBorder}`, 
                padding: '28px', 
                borderRadius: '24px', 
                width: '780px', 
                maxWidth: '92vw', 
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 40px 100px rgba(0, 0, 0, 0.5)' 
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700' }}>
                    選擇要加入 {heroMode === 'desktop' ? '💻 桌面端' : '📱 行動端'} Hero 輪播的照片
                  </h3>
                  <p style={{ margin: 0, fontSize: '13px', color: theme.textSecondary }}>點擊照片即可加入輪播佇列（目前已選 {currentHeroIds.length}/5，新增後請點擊儲存）</p>
                </div>
                <button onClick={() => setShowPhotoPicker(false)} style={{ background: 'transparent', border: 'none', color: '#0071e3', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>完成</button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '14px', paddingRight: '4px' }}>
                {workLibraryPhotos.map((photo) => {
                  const isSelected = currentHeroIds.includes(photo._id);
                  return (
                    <motion.div
                      key={photo._id}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => !isSelected && handleAddHeroPhoto(photo._id)}
                      style={{
                        position: 'relative',
                        height: '110px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: isSelected ? 'not-allowed' : 'pointer',
                        border: isSelected ? '2px solid #0071e3' : `1px solid ${theme.cardBorder}`,
                        opacity: isSelected ? 0.45 : 1
                      }}
                    >
                      <img src={getPhotoImagePath(photo, 'thumbnail')} alt="pick" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {isSelected && (
                        <span style={{ position: 'absolute', top: '6px', right: '6px', background: '#0071e3', color: '#fff', padding: '2px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: '700' }}>
                          已在佇列
                        </span>
                      )}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', color: '#fff', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        📍 {photo.location || '未知地點'}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 🖥️ 核心視窗渲染：控制中心 (Work/Gear/Journeys/Profile) */}
      {/* ======================================================== */}
      
      {isAdminDashboard ? (
        <main className="admin-main" style={{ position: 'relative', zIndex: 1, maxWidth: '1120px', margin: '0 auto', padding: '90px 24px 100px 24px' }}>
          
          {/* 【Work】作品與門面模組 */}
          {adminModule === 'Work' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px', flexWrap: 'wrap', gap: '16px' }}>
                
                <div style={{ display: 'inline-flex', padding: '4px', borderRadius: '12px', background: isDarkMode ? '#1c1c1e' : '#e5e5ea', border: `1px solid ${theme.cardBorder}` }}>
                  {[
                    { id: 'hero', label: 'Hero 門面排程' },
                    { id: 'library', label: `媒體庫 (${workLibraryPhotos.length})` },
                    { id: 'locations', label: `地點管理 (${locationGroups.length})` },
                    { id: 'featured', label: `精選作品 (${featuredPhotos.length})` },
                    { id: 'inbox', label: `待整理 (${workLibraryPhotos.filter(p => needsPhotoOrganization(p) || !p.aiDescription).length})` }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setWorkTab(tab.id)}
                      style={{
                        padding: '7px 18px',
                        borderRadius: '9px',
                        border: 'none',
                        background: workTab === tab.id ? (isDarkMode ? '#2c2c2e' : '#ffffff') : 'transparent',
                        color: workTab === tab.id ? theme.textPrimary : theme.textSecondary,
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: workTab === tab.id ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
                  <motion.button 
                    whileHover={{ scale: 1.03 }} 
                    whileTap={{ scale: 0.97 }} 
                    onClick={triggerSelectFile} 
                    disabled={uploading} 
                    style={{ padding: '8px 20px', borderRadius: '980px', border: 'none', background: isDarkMode ? '#ffffff' : '#1d1d1f', color: isDarkMode ? '#000000' : '#ffffff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    {uploading ? `上傳 ${uploadProgress?.completed || 0}/${uploadProgress?.total || 0}` : '上傳作品'}
                  </motion.button>
                  {uploading && <span style={{ color: theme.textSecondary, fontSize: '11px', whiteSpace: 'nowrap' }}>已顯示 {uploadProgress?.uploaded || 0} 張</span>}
                </div>
              </div>

              {/* 子視圖 1: 🌟 Hero 門面排程 */}
              {workTab === 'hero' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'inline-flex', padding: '3px', borderRadius: '10px', background: isDarkMode ? '#1c1c1e' : '#e5e5ea' }}>
                      <button
                        onClick={() => setHeroMode('desktop')}
                        style={{
                          padding: '5px 14px',
                          borderRadius: '8px',
                          border: 'none',
                          background: heroMode === 'desktop' ? (isDarkMode ? '#2c2c2e' : '#ffffff') : 'transparent',
                          color: heroMode === 'desktop' ? theme.textPrimary : theme.textSecondary,
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        💻 桌面端橫屏 ({desktopHeroIds.length}/5)
                      </button>
                      <button
                        onClick={() => setHeroMode('mobile')}
                        style={{
                          padding: '5px 14px',
                          borderRadius: '8px',
                          border: 'none',
                          background: heroMode === 'mobile' ? (isDarkMode ? '#2c2c2e' : '#ffffff') : 'transparent',
                          color: heroMode === 'mobile' ? theme.textPrimary : theme.textSecondary,
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        📱 行動端豎屏 ({mobileHeroIds.length}/5)
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={handleSmartAiFillHero}
                        style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {currentHeroIds.length >= 5 ? '🎲 隨機重新補齊' : `🎲 隨機補齊（剩 ${5 - currentHeroIds.length} 張）`}
                      </button>
                      
                      <button
                        onClick={() => setIsHeroEditing(!isHeroEditing)}
                        style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: isHeroEditing ? '#ff3b30' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'), color: isHeroEditing ? '#ffffff' : theme.textPrimary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {isHeroEditing ? '完成' : '編輯 / 排序'}
                      </button>

                      <button
                        onClick={handleSaveSiteSettings}
                        disabled={isSavingSettings}
                        style={{ padding: '6px 18px', borderRadius: '8px', border: 'none', background: '#0071e3', color: '#ffffff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,113,227,0.4)' }}
                      >
                        {isSavingSettings ? '儲存中...' : '✓ 儲存目前排程'}
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '40px' }}>
                    {Array.from({ length: 5 }).map((_, index) => {
                      const photoId = currentHeroIds[index];
                      const photo = workLibraryPhotos.find(p => p._id === photoId);

                      if (photo) {
                        return (
                          <div 
                            key={photo._id} 
                            style={{ 
                              position: 'relative', 
                              height: '240px', 
                              borderRadius: '18px', 
                              overflow: 'hidden', 
                              border: `1px solid ${theme.cardBorder}`, 
                              background: isDarkMode ? '#1c1c1e' : '#e5e5ea',
                              boxShadow: theme.cardShadow
                            }}
                          >
                            <img src={getPhotoImagePath(photo, 'thumbnail')} alt="hero slot" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            
                            <span style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', color: '#fff', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700' }}>
                              #{index + 1}
                            </span>

                            {isHeroEditing && (
                              <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '4px', zIndex: 30 }}>
                                <button type="button" title="向左移動" aria-label="向左移動" onClick={() => handleMoveHeroSlot(photo._id, -1)} disabled={index === 0} style={{ padding: '3px 7px', border: 'none', borderRadius: '6px', background: 'rgba(0,0,0,0.72)', color: '#fff', cursor: index === 0 ? 'not-allowed' : 'pointer', fontSize: '11px', opacity: index === 0 ? 0.45 : 1 }}>◀</button>
                                <button type="button" title="向右移動" aria-label="向右移動" onClick={() => handleMoveHeroSlot(photo._id, 1)} disabled={index === currentHeroIds.length - 1} style={{ padding: '3px 7px', border: 'none', borderRadius: '6px', background: 'rgba(0,0,0,0.72)', color: '#fff', cursor: index === currentHeroIds.length - 1 ? 'not-allowed' : 'pointer', fontSize: '11px', opacity: index === currentHeroIds.length - 1 ? 0.45 : 1 }}>▶</button>
                                <button type="button" title="移除此欄位" aria-label="移除此欄位" onClick={() => handleRemoveHeroSlot(photo._id)} style={{ padding: '3px 7px', border: 'none', borderRadius: '6px', background: '#ff3b30', color: '#fff', cursor: 'pointer', fontSize: '11px' }}>🗑</button>
                              </div>
                            )}

                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px', background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)', color: '#fff' }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {photo.location || '未知地點'}</div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <motion.div
                          key={`empty-${index}`}
                          whileHover={{ scale: 1.02, borderColor: '#0071e3' }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setShowPhotoPicker(true)}
                          style={{
                            height: '240px',
                            borderRadius: '18px',
                            border: `2px dashed ${isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)'}`,
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            transition: 'border-color 0.2s ease'
                          }}
                        >
                          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: theme.textSecondary }}>
                            ＋
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary }}>
                            新增第 #{index + 1} 張照片
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* 門面文案配置卡片 */}
                  <div style={{ padding: '24px', borderRadius: '20px', background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>✍️ 門面文案與美學宣言設定</h4>
                      <button
                        onClick={handleSaveSiteSettings}
                        disabled={isSavingSettings}
                        style={{ padding: '8px 18px', borderRadius: '980px', border: 'none', background: '#0071e3', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {isSavingSettings ? '儲存中...' : '儲存文案設定'}
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                      {[0, 1, 2].map((idx) => (
                        <div key={idx}>
                          <label style={{ display: 'block', fontSize: '11px', color: theme.textSecondary, marginBottom: '5px' }}>高光文案 #{idx + 1}</label>
                          <input type="text" value={editHeroLines[idx]} onChange={e => { const arr = [...editHeroLines]; arr[idx] = e.target.value; setEditHeroLines(arr); }} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', color: theme.textSecondary, marginBottom: '5px' }}>美學宣言大標題</label>
                        <textarea rows="3" value={editPhilTitle} onChange={e => setEditPhilTitle(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', fontSize: '13px', resize: 'none', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', color: theme.textSecondary, marginBottom: '5px' }}>美學宣言正文段落</label>
                        <textarea rows="3" value={editPhilBody} onChange={e => setEditPhilBody(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', fontSize: '13px', resize: 'none', boxSizing: 'border-box' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 子視圖 3: 📍 地點管理 */}
              {workTab === 'locations' && (() => {
                const activeGroup = locationGroups.find(group => group.key === activeLocationGroupKey);
                const activeRoot = locationAdminTree.find(root => root.key === locationAdminPath[0]);
                const activeCity = activeRoot?.children?.find(city => city.key === locationAdminPath[1]);
                const leafFromGroup = (group) => ({ kind: 'group', key: group.key, title: group.label, group, photo: group.photos[0], count: group.photos.length, latestDate: group.latestDate });
                const rawLocationItems = activeCity
                  ? [...activeCity.children, ...activeCity.directGroups.map(leafFromGroup)]
                  : activeRoot
                    ? [...activeRoot.children, ...activeRoot.directGroups.map(leafFromGroup)]
                    : locationAdminTree;
                const locationMenuOrderKey = activeCity ? `details:${activeCity.key}` : activeRoot ? `cities:${activeRoot.key}` : 'roots';
                const locationItems = sortLocationMenuItems(rawLocationItems, locationMenuOrderKey, siteSettings.locationMenuOrders, item => item.collectionPrefix || item.id || item.key);
                const locationHeading = activeCity ? `${activeRoot.title} · ${activeCity.title}` : activeRoot?.title || '地點管理';
                const goBackInLocationAdmin = () => {
                  if (activeCity) setLocationAdminPath([activeRoot.key]);
                  else if (activeRoot) setLocationAdminPath([]);
                };
                const openLocationItem = (item) => {
                  if (item.kind === 'group') return openLocationGroup(item.key);
                  if (item.kind === 'root' && !item.children.length && item.directGroups.length === 1) return openLocationGroup(item.directGroups[0].key);
                  setLocationAdminPath(item.kind === 'root' ? [item.key] : [activeRoot.key, item.key]);
                  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                };
                if (activeGroup) {
                  return <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
                        <button type="button" onClick={() => setActiveLocationGroupKey(null)} style={{ width: '34px', height: '34px', border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontSize: '18px' }}>←</button>
                        <div><p style={{ margin: '0 0 4px', color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.08em' }}>LOCATION WORKS</p><h3 style={{ margin: 0, fontSize: '22px' }}>📍 {activeGroup.label}</h3></div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}><span style={{ color: theme.textSecondary, fontSize: '13px' }}>{activeGroup.photos.length} 張作品</span><button type="button" onClick={() => openLocationBatchPicker(activeGroup)} style={{ padding: '8px 12px', border: 'none', borderRadius: '980px', background: '#0071e3', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>＋ 批量加入照片</button><button type="button" onClick={() => handleRenameLocation(activeGroup)} style={{ padding: '8px 12px', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', background: 'transparent', color: theme.textPrimary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>整組改名</button></div>
                    </div>
                    <p style={{ margin: '-8px 0 14px', color: theme.textSecondary, fontSize: '12px' }}>用「←／→」自訂這個圖集的前後順序；儲存後前台同一地點圖集也會同步。</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '14px' }}>
                      {activeGroup.photos.map((photo, index) => <article key={photo._id} style={{ overflow: 'hidden', borderRadius: '14px', border: `1px solid ${theme.cardBorder}`, background: theme.cardBg }}><button type="button" onClick={() => openLightbox(index, activeGroup.photos)} style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: theme.inputBg, cursor: 'zoom-in' }}><img src={getPhotoImagePath(photo, 'medium')} alt={getPhotoAlt(photo)} loading="lazy" decoding="async" style={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }} /></button><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 11px' }}><span style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', color: theme.textSecondary, fontSize: '11px' }}>{index + 1} · {formatShootingDate(photo.shootingDate) || '未填拍攝日期'}</span><div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><button type="button" onClick={() => moveLocationPhoto(activeGroup, index, -1)} disabled={index === 0 || isSavingLocationPhotoOrder} aria-label="往前移動照片" title="往前移" style={{ border: 'none', background: 'transparent', color: theme.textSecondary, fontSize: '16px', cursor: index === 0 || isSavingLocationPhotoOrder ? 'not-allowed' : 'pointer', opacity: index === 0 || isSavingLocationPhotoOrder ? .35 : 1 }}>←</button><button type="button" onClick={() => moveLocationPhoto(activeGroup, index, 1)} disabled={index === activeGroup.photos.length - 1 || isSavingLocationPhotoOrder} aria-label="往後移動照片" title="往後移" style={{ border: 'none', background: 'transparent', color: theme.textSecondary, fontSize: '16px', cursor: index === activeGroup.photos.length - 1 || isSavingLocationPhotoOrder ? 'not-allowed' : 'pointer', opacity: index === activeGroup.photos.length - 1 || isSavingLocationPhotoOrder ? .35 : 1 }}>→</button><button type="button" onClick={() => openEditModal(photo)} style={{ border: 'none', background: 'transparent', color: '#0071e3', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>編輯</button></div></div></article>)}
                    </div>
                  </div>;
                }
                return <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>{activeRoot && <button type="button" onClick={goBackInLocationAdmin} style={{ width: '34px', height: '34px', border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontSize: '18px' }}>←</button>}<div><p style={{ margin: '0 0 4px', color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.08em' }}>{activeRoot ? 'LOCATION HIERARCHY' : 'LOCATION MANAGEMENT'}</p><h3 style={{ margin: '0 0 5px', fontSize: '18px', fontWeight: '700' }}>{activeRoot ? `📍 ${locationHeading}` : '地點管理'}</h3><p style={{ margin: 0, color: theme.textSecondary, fontSize: '13px' }}>{activeRoot ? (activeCity ? '選擇具體景點，進入後可批量加入與整理照片。' : '選擇城市，再查看具體景點。') : '先按省份整理；點進省份後才顯示城市與具體景點。'}</p></div></div>
                    {!activeRoot && <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', justifyContent: 'flex-end' }}><button type="button" onClick={() => setIsManagingLocationCollections(value => !value)} style={{ padding: '7px 11px', border: `1px solid ${isManagingLocationCollections ? '#0071e3' : theme.cardBorder}`, borderRadius: '980px', background: isManagingLocationCollections ? (isDarkMode ? 'rgba(0,113,227,.22)' : 'rgba(0,113,227,.08)') : 'transparent', color: isManagingLocationCollections ? '#0071e3' : theme.textPrimary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>{isManagingLocationCollections ? '完成前台三級設定' : '前台折疊設定'}</button><span style={{ padding: '6px 10px', borderRadius: '980px', background: theme.inputBg, color: theme.textSecondary, fontSize: '12px', whiteSpace: 'nowrap' }}>{locationAdminTree.length} 個省份／地區</span></div>}
                  </div>
                  <p style={{ margin: '-9px 0 18px', color: theme.textSecondary, fontSize: '12px' }}>用每張卡片右上角的「←／→」調整前台 Work 選單順序：最外層是省份，其後依序是城市與具體地點。</p>
                  {!activeRoot && isManagingLocationCollections && <section style={{ margin: '-4px 0 22px', padding: '18px', border: `1px solid ${theme.cardBorder}`, borderRadius: '16px', background: isDarkMode ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.018)' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}><div><h4 style={{ margin: 0, fontSize: '15px' }}>前台地點折疊與三級選單</h4><p style={{ margin: '5px 0 0', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>系統會先按省份折疊；滑到省份後右側顯示城市。你也可在這裡手動新增、移除或改用自訂名稱。</p></div><span style={{ color: theme.textSecondary, fontSize: '12px', whiteSpace: 'nowrap' }}>{normalizeLocationCollections(siteSettings.locationCollections).length} 組</span></div><div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, .7fr) minmax(220px, 1fr) auto', gap: '9px', alignItems: 'center' }}><input value={newLocationCollectionTitle} onChange={event => setNewLocationCollectionTitle(event.target.value)} placeholder="前台名稱，例如：廣東·汕頭" style={{ minWidth: 0, padding: '10px 11px', border: `1px solid ${theme.inputBorder}`, borderRadius: '10px', background: theme.inputBg, color: theme.textPrimary, font: 'inherit', fontSize: '13px', outline: 'none' }} /><input value={newLocationCollectionPrefix} onChange={event => setNewLocationCollectionPrefix(event.target.value)} list="location-collection-prefixes" placeholder="歸屬前綴，例如：廣東·汕頭" style={{ minWidth: 0, padding: '10px 11px', border: `1px solid ${theme.inputBorder}`, borderRadius: '10px', background: theme.inputBg, color: theme.textPrimary, font: 'inherit', fontSize: '13px', outline: 'none' }} /><datalist id="location-collection-prefixes">{locationCollectionPrefixOptions.map(prefix => <option key={prefix} value={prefix} />)}</datalist><button type="button" onClick={addLocationCollection} disabled={isSavingLocationCollections} style={{ padding: '10px 13px', border: 'none', borderRadius: '10px', background: '#0071e3', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: isSavingLocationCollections ? 'not-allowed' : 'pointer', opacity: isSavingLocationCollections ? .6 : 1 }}>{isSavingLocationCollections ? '儲存中…' : '＋ 新增'}</button></div><div style={{ display: 'grid', gap: '7px', marginTop: '14px' }}>{normalizeLocationCollections(siteSettings.locationCollections).map(collection => <div key={collection.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 11px', borderRadius: '10px', background: theme.inputBg }}><strong style={{ minWidth: 0, flex: '0 1 180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>{collection.title}</strong><span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.textSecondary, fontSize: '12px' }}>前綴：{collection.prefix}</span><button type="button" onClick={() => removeLocationCollection(collection.id)} disabled={isSavingLocationCollections} style={{ border: 'none', background: 'transparent', color: '#ff3b30', fontSize: '12px', cursor: isSavingLocationCollections ? 'not-allowed' : 'pointer' }}>移除</button></div>)}</div>{!normalizeLocationCollections(siteSettings.locationCollections).length && <p style={{ margin: '13px 0 0', color: theme.textSecondary, fontSize: '12px' }}>尚未手動設定。上方下拉可直接選取已有前綴；自動折疊的群組不必另外建立。</p>}</section>}
                  {locationItems.length > 0 ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
                    {locationItems.map((item, index) => (
                      <article key={item.key} style={{ position: 'relative', minHeight: '218px', overflow: 'hidden', borderRadius: '18px', border: `1px solid ${theme.cardBorder}`, background: theme.inputBg, boxShadow: theme.cardShadow }}>
                        <button type="button" onClick={() => openLocationItem(item)} style={{ position: 'absolute', inset: 0, width: '100%', padding: 0, border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', textAlign: 'left' }}>
                          <img src={getPhotoImagePath(item.photo, 'medium')} alt={`${item.title} 主圖`} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,.48) 0%, rgba(0,0,0,.05) 46%, rgba(0,0,0,.56) 100%)' }} />
                          <strong style={{ position: 'absolute', top: '16px', left: '16px', right: item.kind === 'group' ? '112px' : '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '21px', letterSpacing: '-.03em' }}>{item.title}</strong>
                          <span style={{ position: 'absolute', left: '16px', bottom: '15px', fontSize: '12px', fontWeight: '600' }}>{item.count} 張作品 · {item.kind === 'group' ? '查看作品' : item.kind === 'city' ? '查看具體景點' : '查看城市'} →</span>
                        </button>
                        <div style={{ position: 'absolute', top: '11px', right: '11px', zIndex: 1, display: 'flex', gap: '4px' }}>
                          <button type="button" onClick={() => moveLocationMenuItem(locationMenuOrderKey, locationItems, index, -1)} disabled={index === 0 || isSavingLocationMenuOrder} aria-label={`將 ${item.title} 往前移`} title="在前台選單往前移" style={{ border: '1px solid rgba(255,255,255,.26)', borderRadius: '8px', padding: '6px 8px', background: 'rgba(0,0,0,.3)', color: '#fff', fontSize: '14px', cursor: index === 0 || isSavingLocationMenuOrder ? 'not-allowed' : 'pointer', opacity: index === 0 || isSavingLocationMenuOrder ? .42 : 1 }}>←</button>
                          <button type="button" onClick={() => moveLocationMenuItem(locationMenuOrderKey, locationItems, index, 1)} disabled={index === locationItems.length - 1 || isSavingLocationMenuOrder} aria-label={`將 ${item.title} 往後移`} title="在前台選單往後移" style={{ border: '1px solid rgba(255,255,255,.26)', borderRadius: '8px', padding: '6px 8px', background: 'rgba(0,0,0,.3)', color: '#fff', fontSize: '14px', cursor: index === locationItems.length - 1 || isSavingLocationMenuOrder ? 'not-allowed' : 'pointer', opacity: index === locationItems.length - 1 || isSavingLocationMenuOrder ? .42 : 1 }}>→</button>
                          {item.kind === 'group' && <button type="button" onClick={() => handleRenameLocation(item.group)} aria-label={`修改 ${item.title} 地點名稱`} title="改名" style={{ border: '1px solid rgba(255,255,255,.26)', borderRadius: '8px', padding: '6px 8px', background: 'rgba(0,0,0,.3)', color: '#fff', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>改名</button>}
                        </div>
                      </article>
                    ))}
                  </div> : <div style={{ padding: '80px 20px', textAlign: 'center', color: theme.textSecondary, border: `1px dashed ${theme.cardBorder}`, borderRadius: '20px' }}>尚未有可管理的作品地點</div>}
                </div>;
              })()}

              {/* 子視圖 2: 🗂️ 媒體庫 */}
              {workTab === 'library' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <label style={{ flex: '1 1 260px', maxWidth: '440px', display: 'flex', alignItems: 'center', gap: '9px', padding: '10px 13px', border: `1px solid ${theme.inputBorder}`, borderRadius: '12px', background: theme.inputBg, color: theme.textSecondary }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.8-3.8" /></svg>
                      <input value={librarySearchQuery} onChange={event => setLibrarySearchQuery(event.target.value)} placeholder="搜尋檔名、地點、器材或描述" style={{ minWidth: 0, flex: 1, border: 'none', outline: 'none', background: 'transparent', color: theme.textPrimary, font: 'inherit', fontSize: '13px' }} />
                      {librarySearchQuery && <button type="button" onClick={() => setLibrarySearchQuery('')} style={{ border: 'none', background: 'transparent', color: theme.textSecondary, cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}>×</button>}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.textSecondary, fontSize: '12px' }}>排序<select value={librarySortMode} onChange={event => setLibrarySortMode(event.target.value)} style={{ padding: '8px 9px', border: `1px solid ${theme.inputBorder}`, borderRadius: '9px', background: theme.inputBg, color: theme.textPrimary, font: 'inherit', fontSize: '12px', outline: 'none' }}><option value="shootingDate">拍攝時間</option><option value="createdAt">加入時間</option><option value="needsAttention">待完善優先</option></select></label>
                    <span style={{ color: theme.textSecondary, fontSize: '12px' }}>顯示 {displayPhotos.length} 張</span>
                  </div>
                  <div className="admin-tabs-container" style={{ display: 'flex', gap: '8px', marginBottom: '24px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {dynamicAdminTabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setLibraryFilterTag(tab.id)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '980px',
                          border: `1px solid ${theme.cardBorder}`,
                          background: libraryFilterTag === tab.id ? theme.textPrimary : 'transparent',
                          color: libraryFilterTag === tab.id ? theme.bg : theme.textPrimary,
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {tab.label} <span style={{ opacity: 0.6, fontSize: '10px' }}>({tab.count})</span>
                      </button>
                    ))}
                  </div>

                  {displayPhotos.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
                      <AnimatePresence>
                        {displayPhotos.map((photo, index) => (
                          <motion.div 
                            key={photo._id || index} 
                            layout 
                            initial={{ opacity: 0, scale: 0.9 }} 
                            animate={{ opacity: 1, scale: 1 }} 
                            exit={{ opacity: 0, scale: 0.9 }} 
                            style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '16px', overflow: 'hidden', boxShadow: theme.cardShadow }}
                          >
                            <div style={{ position: 'relative', width: '100%', height: '200px', backgroundColor: isDarkMode ? '#1c1c1e' : '#e5e5ea', cursor: 'zoom-in' }} onClick={() => openLightbox(index)}>
                              <img 
                                src={getPhotoImagePath(photo, 'thumbnail')} 
                                alt={getPhotoAlt(photo)} 
                                loading="lazy" 
                                decoding="async" 
                                className="grid-image-fade" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                              />
                              <span style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', color: '#fff', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                                {TAG_LABELS[photo.subCategory] || photo.subCategory || '未分類'}
                              </span>
                            </div>

                            <div style={{ padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <h4 style={{ margin: '0 0 2px 0', fontSize: '14px', color: theme.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {photo.location || '未知地點'}</h4>
                                <p style={{ margin: 0, fontSize: '11px', color: theme.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📷 {getPhotoEquipmentLabel(photo) || '無器材參數'}</p>
                                {getPhotoShootingInfo(photo) && <p style={{ margin: '3px 0 0', fontSize: '10px', color: theme.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>⚙︎ {getPhotoShootingInfo(photo)}</p>}
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={() => openEditModal(photo)} style={{ padding: '6px 10px', background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', border: 'none', borderRadius: '8px', color: theme.textPrimary, fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>
                                  ✏️
                                </button>
                                <div onMouseEnter={() => setJourneyActionHoverId(String(photo._id))} onMouseLeave={() => setJourneyActionHoverId(null)} style={{ position: 'relative' }}>
                                  {!photo.journeyCandidate && journeyActionHoverId === String(photo._id) && <button type="button" onClick={() => duplicatePhotoToJourney(photo)} title="保留 Work 原件，另存一份到 Journey 照片庫" style={{ position: 'absolute', right: 0, bottom: '100%', zIndex: 4, padding: '6px 9px', border: '1px solid rgba(0,113,227,.35)', borderRadius: '8px', background: isDarkMode ? '#18293e' : '#eef6ff', color: '#0071e3', fontSize: '11px', cursor: 'pointer', fontWeight: '700', whiteSpace: 'nowrap', boxShadow: '0 6px 16px rgba(0,0,0,.18)' }}>＋另存 J</button>}
                                  <button type="button" onClick={() => toggleJourneyCandidate(photo)} title={photo.journeyCandidate ? '從 Journeys 待選區移除' : '移至 Journeys 待選區'} style={{ padding: '6px 9px', background: photo.journeyCandidate ? 'rgba(0,113,227,.16)' : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'), border: 'none', borderRadius: '8px', color: photo.journeyCandidate ? '#0071e3' : theme.textPrimary, fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                                    {photo.journeyCandidate ? '✓ J' : '↗ J'}
                                  </button>
                                </div>
                                <button onClick={() => setPhotoToDelete(photo)} style={{ padding: '6px 10px', background: 'rgba(255,59,48,0.1)', border: 'none', borderRadius: '8px', color: '#ff3b30', fontSize: '12px', cursor: 'pointer' }}>
                                  🗑️
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div style={{ padding: '80px 20px', textAlign: 'center', color: theme.textSecondary, border: `1px dashed ${theme.cardBorder}`, borderRadius: '20px' }}>
                      該分類下暫無作品
                    </div>
                  )}
                </div>
              )}

              {/* 子視圖 3: 精選作品策展 */}
              {workTab === 'featured' && (() => {
                const selectedIds = (siteSettings.featuredPhotoIds || []).map(String);
                const photoMap = new Map(workLibraryPhotos.map(photo => [String(photo._id), photo]));
                const targetCount = Math.max(1, Math.min(36, Number(siteSettings.featuredTargetCount) || 9));
                const allSelectedPhotos = selectedIds.map(id => photoMap.get(id)).filter(Boolean);
                const selectedPhotos = allSelectedPhotos.slice(0, targetCount);
                const candidates = workLibraryPhotos.filter(photo => !selectedIds.includes(String(photo._id)));
                const removeFeatured = id => setSiteSettings(prev => ({ ...prev, featuredPhotoIds: (prev.featuredPhotoIds || []).filter(item => String(item) !== String(id)) }));
                const addFeatured = id => setSiteSettings(prev => ({ ...prev, featuredPhotoIds: [...(prev.featuredPhotoIds || []), id].slice(0, 36) }));
                const placeFeatured = (id, requestedIndex) => setSiteSettings(prev => {
                  const ids = [...(prev.featuredPhotoIds || [])].map(String);
                  const movingId = String(id);
                  const oldIndex = ids.indexOf(movingId);
                  if (oldIndex >= 0) ids.splice(oldIndex, 1);
                  let targetIndex = Math.max(0, Math.min(ids.length, requestedIndex));
                  if (oldIndex >= 0 && oldIndex < requestedIndex) targetIndex = Math.max(0, targetIndex - 1);
                  ids.splice(targetIndex, 0, movingId);
                  return { ...prev, featuredPhotoIds: ids.slice(0, 36) };
                });
                const beginFeaturedDrag = (event, payload, photo) => {
                  setFeaturedDragPayload(payload);
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', JSON.stringify(payload));
                  const dragImage = document.createElement('img');
                  dragImage.src = getPhotoImagePath(photo, 'thumbnail');
                  dragImage.alt = '';
                  dragImage.style.cssText = 'position:fixed;top:-1000px;left:-1000px;width:112px;height:76px;object-fit:cover;border-radius:10px;box-shadow:0 8px 20px rgba(0,0,0,.28);pointer-events:none;';
                  document.body.appendChild(dragImage);
                  event.dataTransfer.setDragImage(dragImage, 56, 38);
                  window.setTimeout(() => dragImage.remove(), 0);
                };
                const readFeaturedDrag = event => {
                  if (featuredDragPayload) return featuredDragPayload;
                  try { return JSON.parse(event.dataTransfer.getData('text/plain')); } catch (_err) { return null; }
                };
                const dropIntoFeatured = (event, requestedIndex) => {
                  event.preventDefault();
                  const payload = readFeaturedDrag(event);
                  if (payload?.id) placeFeatured(payload.id, requestedIndex);
                  setFeaturedDragPayload(null);
                };
                return <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px, .95fr) minmax(320px, 1.05fr)', gap: '34px', alignItems: 'start' }}>
                  <section style={{ position: 'sticky', top: '92px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
                      <div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.08em' }}>首頁即時預覽</p><h3 style={{ margin: '5px 0 0', fontSize: '20px', letterSpacing: '-.03em' }}>精選櫥窗</h3></div>
                      <span style={{ color: theme.textSecondary, fontSize: '12px' }}>{selectedPhotos.length} / {targetCount}</span>
                    </div>
                    <FeaturedHomepagePreview
                      photos={selectedPhotos}
                      theme={theme}
                      isDarkMode={isDarkMode}
                      featuredDragPayload={featuredDragPayload}
                      onBeginDrag={beginFeaturedDrag}
                      onFinishDrag={() => setFeaturedDragPayload(null)}
                      onRemove={removeFeatured}
                      onDrop={dropIntoFeatured}
                    />
                    {selectedPhotos.length > 0 && <p style={{ margin: '12px 4px 2px', textAlign: 'center', color: theme.textSecondary, fontSize: '11px' }}>這是前台桌面版的等比縮放預覽；位置、順序與首頁一致。</p>}
                    {allSelectedPhotos.length > targetCount && <p style={{ margin: '8px 4px 2px', textAlign: 'center', color: theme.textSecondary, fontSize: '11px' }}>另有 {allSelectedPhotos.length - targetCount} 張已入選，但暫不在首頁展示；提高首頁目標數量後即可顯示。</p>}
                  </section>
                  <section><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}><div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.08em' }}>CURATE FEATURED</p><h3 style={{ margin: '6px 0 0', fontSize: '24px', letterSpacing: '-.04em' }}>手動策展與排序</h3><p style={{ margin: '8px 0 0', color: theme.textSecondary, fontSize: '13px', lineHeight: 1.55 }}>拖拽時會顯示小型預覽；左側卡片上、下藍線就是即將插入的位置。</p></div><button type="button" onClick={saveFeaturedSettings} style={{ flex: 'none', border: 'none', borderRadius: '980px', padding: '10px 16px', background: '#0071e3', color: '#fff', fontWeight: '600', cursor: 'pointer' }}>儲存</button></div><label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', color: theme.textSecondary, fontSize: '13px' }}>首頁目標數量<input type="number" min="1" max="36" value={targetCount} onChange={event => setSiteSettings(prev => ({ ...prev, featuredTargetCount: Math.max(1, Math.min(36, Number(event.target.value) || 1)) }))} style={{ width: '58px', padding: '7px 8px', borderRadius: '9px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, font: 'inherit', textAlign: 'center' }} />張</label><div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridAutoRows: '4px', gridAutoFlow: 'row dense', gap: '10px', alignItems: 'start', maxHeight: '58vh', overflowY: 'auto', paddingRight: '4px' }}>{candidates.map(photo => <MasonryCell key={photo._id} gap={10}><button type="button" draggable onDragStart={event => beginFeaturedDrag(event, { type: 'candidate', id: String(photo._id) }, photo)} onDragEnd={() => setFeaturedDragPayload(null)} onClick={() => addFeatured(photo._id)} style={{ position: 'relative', width: '100%', overflow: 'hidden', padding: 0, border: `1px solid ${theme.cardBorder}`, borderRadius: '12px', background: theme.inputBg, cursor: 'grab' }}><img src={getPhotoImagePath(photo, 'thumbnail')} alt="加入精選作品" draggable={false} style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} /><span style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.28)', color: '#fff', fontSize: '24px', opacity: 0, transition: 'opacity .2s' }}>＋</span></button></MasonryCell>)}</div></section>
                </div>;
              })()}

              {/* 子視圖 4: ⚠️ 待整理收件箱 */}
              {workTab === 'inbox' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700' }}>待整理作品收件箱</h3>
                      <p style={{ margin: 0, fontSize: '13px', color: theme.textSecondary }}>{isBatchAiRunning && batchAiProgress ? `${batchAiMode === 'description' ? '正在補描述第' : '正在整理第'} ${Math.min(batchAiProgress.processed + 1, batchAiProgress.total)} / ${batchAiProgress.total} 張；每完成一張會立即歸檔` : batchAiProgress ? `本輪已處理 ${batchAiProgress.processed} / ${batchAiProgress.total} 張${batchAiProgress.failed ? `，${batchAiProgress.failed} 張失敗待重試` : ''}` : '集中處理未加標籤或尚未生成 AI 描述的照片'}</p>
                    </div>
                    {displayPhotos.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => runBatchAiClassification()}
                          disabled={isBatchAiRunning}
                          style={{ padding: '8px 18px', borderRadius: '980px', border: 'none', background: '#0071e3', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: isBatchAiRunning ? 'not-allowed' : 'pointer', opacity: isBatchAiRunning && batchAiMode !== 'full' ? 0.6 : 1 }}
                        >
                          {isBatchAiRunning && batchAiMode === 'full' && batchAiProgress ? `✨ AI 整理中 · ${batchAiProgress.processed}/${batchAiProgress.total}` : '✨ 一鍵呼叫 AI 批量整理'}
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => runBatchAiClassification({ descriptionOnly: true })}
                          disabled={isBatchAiRunning}
                          title="只補上 AI 描述，不會改動目前的分類、標籤或地點"
                          style={{ padding: '8px 15px', borderRadius: '980px', border: '1px solid #0071e3', background: isDarkMode ? 'rgba(0,113,227,.12)' : '#fff', color: '#0071e3', fontSize: '13px', fontWeight: '600', cursor: isBatchAiRunning ? 'not-allowed' : 'pointer', opacity: isBatchAiRunning && batchAiMode !== 'description' ? 0.6 : 1 }}
                        >
                          {isBatchAiRunning && batchAiMode === 'description' && batchAiProgress ? `✦ 補描述中 · ${batchAiProgress.processed}/${batchAiProgress.total}` : '✦ 僅新增描述'}
                        </motion.button>
                      </div>
                    )}
                  </div>

                  {displayPhotos.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
                      {displayPhotos.map((photo, index) => {
                        const isBeingProcessed = aiProcessingId === photo._id;
                        return <div key={photo._id || index} style={{ background: theme.cardBg, border: `1px solid ${isBeingProcessed ? '#0071e3' : theme.cardBorder}`, borderRadius: '16px', overflow: 'hidden', padding: '14px', boxShadow: isBeingProcessed ? '0 0 0 3px rgba(0,113,227,.12)' : 'none', transition: 'border-color .2s, box-shadow .2s' }}>
                          <img src={getPhotoImagePath(photo, 'thumbnail')} alt="inbox item" loading="lazy" decoding="async" style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '10px', marginBottom: '10px' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600' }}>📍 {photo.location || '待填地點'}</div>
                              <div style={{ fontSize: '11px', color: isBeingProcessed ? '#0071e3' : '#ff9500' }}>{isBeingProcessed ? '✦ AI 正在整理，完成後自動歸檔' : '⚠️ 缺少分類或描述'}</div>
                            </div>
                            <button onClick={() => openEditModal(photo)} style={{ padding: '6px 12px', background: '#0071e3', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                              整理
                            </button>
                          </div>
                        </div>
                      })}
                    </div>
                  ) : (
                    <div style={{ padding: '100px 20px', textAlign: 'center', color: theme.textSecondary, border: `1px dashed ${theme.cardBorder}`, borderRadius: '20px' }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
                      <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: theme.textPrimary }}>所有作品已全部歸檔完畢！</h3>
                      <p style={{ margin: 0, fontSize: '13px' }}>上傳新照片後可在此處統一整理</p>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* 【Gear】模組 */}
          {adminModule === 'Gear' && (
            <section style={{ maxWidth: '920px', margin: '0 auto', background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '24px', overflow: 'hidden', boxShadow: theme.cardShadow }}>
              <div style={{ padding: '28px 32px 18px', borderBottom: `1px solid ${theme.cardBorder}` }}><p style={{ margin: 0, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', color: theme.textSecondary }}>GEAR</p><h2 style={{ margin: '8px 0 0', fontSize: '26px', letterSpacing: '-0.04em' }}>攝影與創作工具</h2></div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px', padding: '20px 24px', borderBottom: `1px solid ${theme.cardBorder}` }}>{[['camera', '機身與鏡頭', '真正參與拍攝的器材'], ['tool', '創作工具', 'Mac、包包、儲存與工作流'], ['color', '色彩對比', 'RAW 與 GRADED 的視覺差異']].map(([id, label, description]) => <button key={id} onClick={() => setGearTab(id)} style={{ position: 'relative', overflow: 'hidden', minHeight: '122px', textAlign: 'left', padding: '18px', border: `1px solid ${gearTab === id ? '#0071e3' : theme.cardBorder}`, borderRadius: '18px', background: gearTab === id ? (isDarkMode ? 'rgba(0,113,227,.16)' : 'rgba(0,113,227,.06)') : (isDarkMode ? 'rgba(255,255,255,.025)' : '#fbfbfd'), color: theme.textPrimary, cursor: 'pointer', transition: 'border-color .2s, background .2s' }}><strong style={{ display: 'block', fontSize: '16px', letterSpacing: '-.02em' }}>{label}</strong><span style={{ display: 'block', marginTop: '8px', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>{description}</span><span style={{ position: 'absolute', left: '18px', bottom: '14px', color: gearTab === id ? '#0071e3' : theme.textSecondary, fontSize: '12px', fontWeight: '650' }}>{gearItems.filter(item => item.type === id).length} 項</span>{gearTab === id && <motion.div layoutId="gearWorkspaceIndicator" style={{ position: 'absolute', right: '18px', bottom: '16px', width: '32px', height: '3px', borderRadius: '99px', background: '#0071e3' }} />}</button>)}</div>
              <div style={{ padding: '28px 32px 32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', alignItems: 'flex-start', marginBottom: '20px' }}><p style={{ margin: 0, color: theme.textSecondary, fontSize: '13px', lineHeight: 1.6 }}>{gearTab === 'camera' ? '放入真正參與拍攝的機身與鏡頭。' : gearTab === 'tool' ? '放入 MacBook、相機包、儲存與後期工作流工具。' : '上傳未調色與調色版本的 JPG，前台以 RAW ↔ Graded 滑桿呈現。'}</p><button onClick={() => openGearDraft(gearTab)} style={{ flex: 'none', padding: '9px 16px', border: 'none', borderRadius: '980px', background: '#0071e3', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>新增項目</button></div>
                {(gearItems.filter(item => item.type === gearTab)).map(item => <div key={item._id} style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '14px 0', borderTop: `1px solid ${theme.cardBorder}` }}><div style={{ width: '72px', aspectRatio: '1', overflow: 'hidden', borderRadius: '12px', background: isDarkMode ? '#2c2c2e' : '#e5e5ea', flex: 'none' }}>{(item.cover?.thumbnail || item.gradedImage?.thumbnail) && <img src={item.cover?.thumbnail || item.gradedImage?.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</div><div style={{ minWidth: 0, flex: 1 }}><strong style={{ display: 'block', fontSize: '15px' }}>{item.title}</strong><span style={{ display: 'block', marginTop: '4px', color: theme.textSecondary, fontSize: '12px' }}>{item.subtitle || item.description || '尚未加入說明'}</span></div><button onClick={() => openGearDraft(gearTab, item)} style={{ border: 'none', background: 'transparent', color: '#0071e3', cursor: 'pointer', fontSize: '13px' }}>編輯</button><button onClick={() => deleteGearItem(item)} style={{ border: 'none', background: 'transparent', color: '#d70015', cursor: 'pointer', fontSize: '13px' }}>刪除</button></div>)}
                {!gearItems.some(item => item.type === gearTab) && <div style={{ padding: '52px 12px', textAlign: 'center', color: theme.textSecondary, border: `1px dashed ${theme.cardBorder}`, borderRadius: '16px', fontSize: '13px' }}>尚未建立項目</div>}
              </div>
            </section>
          )}
          {gearDraft && <GearWorkspaceEditorV2 draft={gearDraft} setDraft={setGearDraft} onClose={() => setGearDraft(null)} onSave={saveGearDraft} onGenerateCopy={note => generateDraftCopy('gear', note, gearDraft)} isSaving={isSavingGear} isGeneratingCopy={aiDraftKind === 'gear'} isDarkMode={isDarkMode} theme={theme} publishedItems={gearItems} />}

          {/* 【Journeys】模組 */}
          {adminModule === 'Journeys' && (
            <section style={{ maxWidth: '920px', margin: '0 auto', overflow: 'hidden', borderRadius: '26px', border: `1px solid ${theme.cardBorder}`, background: theme.cardBg, boxShadow: theme.cardShadow }}>
              <div style={{ padding: '28px 32px 18px', borderBottom: `1px solid ${theme.cardBorder}` }}><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: 700, letterSpacing: '.1em' }}>JOURNEYS CONTROL</p><h2 style={{ margin: '8px 0 0', fontSize: '27px', letterSpacing: '-.04em' }}>旅行手帳</h2></div>
              <div style={{ padding: '28px 32px 34px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '18px', marginBottom: '22px' }}><p style={{ maxWidth: '510px', margin: 0, color: theme.textSecondary, fontSize: '13px', lineHeight: 1.65 }}>新增旅程會按日期自動插入時間線；手動修改編號則會移到指定位置，其他序號自動補齊。Journey 照片與 Work 作品庫完全隔離，發佈或刪除旅程後都會留在這裡。</p><div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap', justifyContent: 'flex-end' }}><button type="button" onClick={resequenceJourneysByDate} style={{ flex: 'none', padding: '10px 14px', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', background: 'transparent', color: theme.textPrimary, fontWeight: 600, cursor: 'pointer' }}>按日期整理</button><button onClick={() => openJourneyDraft()} style={{ flex: 'none', padding: '10px 17px', border: 'none', borderRadius: '980px', background: '#0071e3', color: '#fff', fontWeight: 650, cursor: 'pointer' }}>＋ 新增章節</button></div></div>
                <section style={{ marginBottom: '26px', padding: '17px', borderRadius: '18px', border: `1px solid ${theme.cardBorder}`, background: theme.inputBg }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'baseline', marginBottom: '14px' }}>
                    <div><strong style={{ fontSize: '15px' }}>Journey 照片庫</strong><span style={{ marginLeft: '8px', color: theme.textSecondary, fontSize: '12px' }}>{journeyCandidatePhotos.length} 張</span></div>
                    <span style={{ color: theme.textSecondary, fontSize: '11px' }}>不會出現在 Work；已用於旅程的照片會收在下方</span>
                  </div>
                  {unpublishedJourneyCandidatePhotos.length > 0 ? <JourneyPhotoLibraryGrid photos={unpublishedJourneyCandidatePhotos} theme={theme} isDarkMode={isDarkMode} onMoveToWork={toggleJourneyCandidate} onDelete={setPhotoToDelete} /> : <p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px' }}>{journeyCandidatePhotos.length ? '目前沒有尚未發佈的 Journey 照片。' : '暫無 Journey 照片。可在 Work 卡片按「↗ J」手動移入。'}</p>}
                  {publishedJourneyCandidatePhotos.length > 0 && <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: `1px solid ${theme.cardBorder}` }}>
                    <button type="button" onClick={() => setShowPublishedJourneyPhotos(previous => !previous)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', border: 'none', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', textAlign: 'left' }}>
                      <span><strong style={{ fontSize: '13px' }}>已發佈照片</strong><span style={{ marginLeft: '7px', color: theme.textSecondary, fontSize: '11px' }}>{publishedJourneyCandidatePhotos.length} 張</span></span>
                      <span style={{ color: '#0071e3', fontSize: '12px', fontWeight: 600 }}>{showPublishedJourneyPhotos ? '收起' : '展開全部'}</span>
                    </button>
                    {showPublishedJourneyPhotos && <div style={{ marginTop: '12px' }}><JourneyPhotoLibraryGrid photos={publishedJourneyCandidatePhotos} theme={theme} isDarkMode={isDarkMode} onMoveToWork={toggleJourneyCandidate} onDelete={setPhotoToDelete} /></div>}
                  </div>}
                </section>
                {journeyEntries.map(entry => <div key={entry._id} style={{ display: 'grid', gridTemplateColumns: '84px 68px minmax(0,1fr) auto', gap: '14px', alignItems: 'center', padding: '14px 0', borderTop: `1px solid ${theme.cardBorder}` }}><div><strong style={{ display: 'block', fontSize: '15px', fontVariantNumeric: 'tabular-nums' }}>{String(entry.sequenceNumber).padStart(2, '0')}</strong><small style={{ color: theme.textSecondary, fontSize: '10px' }}>{entry.sequenceSuffix || 'MAIN'}</small></div><div style={{ width: '62px', height: '48px', overflow: 'hidden', borderRadius: '10px', background: theme.inputBg }}>{entry.images?.[0]?.thumbnail && <img src={cleanImagePath(entry.images[0].thumbnail)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</div><div style={{ minWidth: 0 }}><strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>{entry.title}</strong><span style={{ display: 'block', marginTop: '4px', color: theme.textSecondary, fontSize: '12px' }}>{[entry.location, entry.dateLabel, entry.showTime !== false && entry.timeLabel, `${entry.images?.length || 0} 張`].filter(Boolean).join(' · ')}</span></div><div style={{ display: 'flex', gap: '10px' }}><button onClick={() => openJourneyDraft(entry)} style={{ border: 'none', background: 'transparent', color: '#0071e3', cursor: 'pointer' }}>編輯</button><button onClick={() => deleteJourneyEntry(entry)} style={{ border: 'none', background: 'transparent', color: '#d70015', cursor: 'pointer' }}>刪除</button></div></div>)}
                {!journeyEntries.length && <div style={{ padding: '54px 16px', textAlign: 'center', border: `1px dashed ${theme.cardBorder}`, borderRadius: '18px', color: theme.textSecondary, fontSize: '13px' }}>尚未建立旅行章節</div>}
              </div>
            </section>
          )}
                {journeyDraft && <JourneyEditor draft={journeyDraft} setDraft={setJourneyDraft} onClose={() => setJourneyDraft(null)} onSave={saveJourneyDraft} onGenerateCopy={(note, currentDraft, selectedCandidates) => generateDraftCopy('journey', note, currentDraft, selectedCandidates)} isSaving={isSavingJourney} isGeneratingCopy={aiDraftKind === 'journey'} candidatePhotos={journeyCandidatePhotos} workPhotos={workLibraryPhotos} publishedCandidatePhotoIds={publishedJourneyPhotoIds} theme={theme} isDarkMode={isDarkMode} />}

          {adminModule === 'Broadcast' && <BroadcastControlPage
            draft={broadcastDraft}
            setDraft={setBroadcastDraft}
            savedMode={siteSettings.broadcast?.mode || 'off'}
            onPreview={() => setBroadcastPreview(true)}
            onSave={() => saveBroadcast()}
            onEmergencyClose={() => saveBroadcast('off')}
            onUploadAudio={uploadBroadcastAudio}
            isSaving={isSavingBroadcast}
            isUploadingAudio={isUploadingBroadcastAudio}
            theme={theme}
          />}

          {/* 【Contact】模組：僅管理前台真正展示的兩個頁面 */}
          {adminModule === 'Contact' && (
            <section style={{ maxWidth: '760px', margin: '0 auto', background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '24px', overflow: 'hidden', boxShadow: theme.cardShadow }}>
              <div style={{ padding: '28px 32px 18px', borderBottom: `1px solid ${theme.cardBorder}` }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', color: theme.textSecondary }}>CONTACT</p>
                <h2 style={{ margin: '8px 0 0', fontSize: '26px', letterSpacing: '-0.04em' }}>個人資訊與社交連結</h2>
              </div>
              <div style={{ display: 'flex', gap: '24px', padding: '0 32px', borderBottom: `1px solid ${theme.cardBorder}` }}>
                {[['about', '個人簡介'], ['socials', '社交動態']].map(([id, label]) => (
                  <button key={id} onClick={() => setContactTab(id)} style={{ padding: '15px 0 12px', border: 'none', borderBottom: contactTab === id ? '2px solid #0071e3' : '2px solid transparent', background: 'transparent', color: contactTab === id ? theme.textPrimary : theme.textSecondary, fontSize: '14px', fontWeight: contactTab === id ? '650' : '500', cursor: 'pointer' }}>{label}</button>
                ))}
              </div>
              <div style={{ padding: '28px 32px 32px' }}>
                {contactTab === 'about' ? <>
                  <p style={{ margin: '0 0 24px', color: theme.textSecondary, fontSize: '13px', lineHeight: 1.6 }}>作者頁採用大型精拍圖，不是圓形頭像。照片會獨立儲存，不會混進作品媒體庫。</p>
                  <input ref={contactPortraitInputRef} type="file" accept="image/*" onChange={handleContactPortraitUpload} style={{ display: 'none' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '18px', alignItems: 'center', padding: '14px', marginBottom: '24px', border: `1px solid ${theme.cardBorder}`, borderRadius: '16px', background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.018)' }}>
                    <div style={{ aspectRatio: '4 / 5', overflow: 'hidden', borderRadius: '10px', background: isDarkMode ? '#2c2c2e' : '#e5e5ea' }}>
                      {siteSettings.contactPortrait?.thumbnail ? <img src={cleanImagePath(siteSettings.contactPortrait.thumbnail)} alt="作者頁照片預覽" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${siteSettings.contactPortrait?.positionX ?? 50}% ${siteSettings.contactPortrait?.positionY ?? 50}%`, transform: `scale(${(siteSettings.contactPortrait?.scale ?? 100) / 100})`, transformOrigin: `${siteSettings.contactPortrait?.positionX ?? 50}% ${siteSettings.contactPortrait?.positionY ?? 50}%`, display: 'block' }} /> : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary, fontSize: '12px', textAlign: 'center', padding: '12px' }}>尚未選擇照片</div>}
                    </div>
                    <div>
                      <h3 style={{ margin: '0 0 6px', fontSize: '15px' }}>作者頁精拍圖</h3>
                      <p style={{ margin: '0 0 14px', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.55 }}>建議直幅或半身構圖。它會成為個人頁的視覺主角。</p>
                      <button onClick={() => contactPortraitInputRef.current?.click()} disabled={isUploadingContactPortrait} style={{ padding: '8px 15px', border: 'none', borderRadius: '980px', background: '#0071e3', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: isUploadingContactPortrait ? 'wait' : 'pointer', opacity: isUploadingContactPortrait ? 0.65 : 1 }}>{isUploadingContactPortrait ? '上傳中...' : '選擇照片'}</button>
                    </div>
                  </div>
                  {siteSettings.contactPortrait?.thumbnail && <div style={{ padding: '16px', margin: '-8px 0 24px', border: `1px solid ${theme.cardBorder}`, borderRadius: '16px', background: isDarkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.012)' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}><div><h3 style={{ margin: 0, fontSize: '14px' }}>畫面焦點</h3><p style={{ margin: '4px 0 0', color: theme.textSecondary, fontSize: '12px' }}>調整橫圖或人物在前台裁切時保留的位置。</p></div><span style={{ color: theme.textSecondary, fontSize: '11px' }}>不會重傳或裁壞原圖</span></div>
                    {[['positionX', '水平位置', 0, 100, 50], ['positionY', '垂直位置', 0, 100, 50], ['scale', '照片縮放', 100, 220, 100]].map(([key, label, min, max, fallback]) => <label key={key} style={{ display: 'grid', gridTemplateColumns: '76px 1fr 42px', gap: '10px', alignItems: 'center', marginTop: '10px', color: theme.textSecondary, fontSize: '12px' }}><span>{label}</span><input type="range" min={min} max={max} value={siteSettings.contactPortrait?.[key] ?? fallback} onChange={e => setSiteSettings(prev => ({ ...prev, contactPortrait: { ...prev.contactPortrait, [key]: Number(e.target.value) } }))} style={{ accentColor: '#0071e3', width: '100%', cursor: 'pointer' }} /><span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{siteSettings.contactPortrait?.[key] ?? fallback}%</span></label>)}
                  </div>}
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '7px' }}>顯示名稱</label>
                  <input value={siteSettings.contactName || ''} onChange={e => setSiteSettings(prev => ({ ...prev, contactName: e.target.value }))} placeholder="Miles" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', marginBottom: '18px', fontSize: '15px' }} />
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '7px' }}>身份短句</label>
                  <input value={siteSettings.contactRole || ''} onChange={e => setSiteSettings(prev => ({ ...prev, contactRole: e.target.value }))} placeholder="例如：街頭與城市攝影" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', marginBottom: '18px', fontSize: '15px' }} />
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '7px' }}>個人簡介</label>
                  <textarea rows="7" value={siteSettings.contactAbout || ''} onChange={e => setSiteSettings(prev => ({ ...prev, contactAbout: e.target.value }))} placeholder="寫下你希望訪客讀到的攝影理念。" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', resize: 'vertical', fontSize: '15px', lineHeight: 1.65 }} />
                </> : <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', marginBottom: '24px' }}>
                    <p style={{ margin: 0, color: theme.textSecondary, fontSize: '13px', lineHeight: 1.6, maxWidth: '420px' }}>前台只會展示已填寫的連結，以文字列表呈現；不使用彩色平台圖標。</p>
                    <button onClick={() => setIsManagingSocialPlatforms(true)} style={{ flex: 'none', padding: '8px 14px', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', background: 'transparent', color: theme.textPrimary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>編輯平台</button>
                  </div>
                  {(siteSettings.contactSocials || []).map((platform) => <div key={platform.id} style={{ display: 'grid', gridTemplateColumns: '116px 1fr', alignItems: 'center', gap: '16px', padding: '13px 0', borderTop: `1px solid ${theme.cardBorder}` }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary }}>{platform.label}</span>
                    <input type={platform.kind === 'email' ? 'email' : 'url'} value={platform.url || ''} onChange={e => setSiteSettings(prev => ({ ...prev, contactSocials: prev.contactSocials.map(item => item.id === platform.id ? { ...item, url: e.target.value } : item) }))} placeholder={platform.kind === 'email' ? 'name@example.com' : 'https://example.com/your-page'} style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, outline: 'none', fontSize: '14px' }} />
                  </div>)}
                  {isManagingSocialPlatforms && <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.34)', display: 'flex', justifyContent: 'flex-end' }} onMouseDown={() => setIsManagingSocialPlatforms(false)}>
                    <aside onMouseDown={e => e.stopPropagation()} style={{ width: 'min(480px, 100vw)', height: '100dvh', overflowY: 'auto', background: theme.cardBg, color: theme.textPrimary, padding: '28px 30px 44px', boxSizing: 'border-box', boxShadow: '-16px 0 48px rgba(0,0,0,0.18)', borderLeft: `1px solid ${theme.cardBorder}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '10px' }}><div><p style={{ margin: 0, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', color: theme.textSecondary }}>SOCIAL PLATFORMS</p><h3 style={{ margin: '7px 0 0', fontSize: '24px', letterSpacing: '-0.035em' }}>管理平台</h3></div><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><button onClick={() => setIsManagingSocialPlatforms(false)} aria-label="關閉管理平台" style={{ width: '34px', height: '34px', border: `1px solid ${theme.cardBorder}`, borderRadius: '50%', background: 'transparent', color: theme.textSecondary, fontSize: '20px', lineHeight: 1, cursor: 'pointer' }}>×</button><button onClick={async () => { if (await handleSaveContact(true)) setIsManagingSocialPlatforms(false); }} style={{ border: 'none', borderRadius: '980px', padding: '9px 15px', background: '#0071e3', color: '#fff', fontWeight: '600', cursor: 'pointer' }}>完成</button></div></div>
                      <p style={{ color: theme.textSecondary, fontSize: '13px', lineHeight: 1.6, margin: '0 0 24px' }}>在這裡決定有哪些平台；回到上一頁再填各平台的連結。</p>
                      {(siteSettings.contactSocials || []).map((platform) => <div key={platform.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'center', padding: '11px 0', borderTop: `1px solid ${theme.cardBorder}` }}><input value={platform.label} onChange={e => setSiteSettings(prev => ({ ...prev, contactSocials: prev.contactSocials.map(item => item.id === platform.id ? { ...item, label: e.target.value } : item) }))} placeholder="平台名稱" style={{ minWidth: 0, padding: '10px 12px', borderRadius: '12px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary, fontSize: '14px' }} /><button onClick={() => setSiteSettings(prev => ({ ...prev, contactSocials: prev.contactSocials.filter(item => item.id !== platform.id) }))} style={{ border: 'none', background: 'transparent', color: '#d70015', cursor: 'pointer', fontSize: '13px' }}>移除</button></div>)}
                      <button onClick={() => setSiteSettings(prev => ({ ...prev, contactSocials: [...(prev.contactSocials || []), { id: `social-${Date.now()}`, label: '新平台', url: '', kind: 'url' }] }))} style={{ marginTop: '18px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, borderRadius: '980px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>＋ 新增平台</button>
                    </aside>
                  </div>}
                </>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '28px' }}>
                  <button onClick={handleSaveContact} disabled={isSavingContact} style={{ padding: '10px 22px', border: 'none', borderRadius: '980px', background: '#0071e3', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: isSavingContact ? 'wait' : 'pointer', opacity: isSavingContact ? 0.65 : 1, boxShadow: '0 1px 2px rgba(0,0,0,0.12)' }}>{isSavingContact ? '儲存中...' : '儲存設定'}</button>
                </div>
              </div>
            </section>
          )}

        </main>
      ) : currentSubPage?.id === 'destinations_index' ? (
        <DestinationWall journeys={latestJourneys} theme={theme} visitedRef={destinationsVisitedRef} onBack={handleSubPageBack} onSelect={journey => handleSelectSubPage({ id: journey.id, type: 'location', title: journey.title, desc: journey.desc, returnTo: { ...currentSubPage, scrollY: window.scrollY } })} />
      ) : currentSubPage?.id === 'journeys_index' ? (
        <JourneyNotebook entries={journeyEntries} photos={photos} theme={theme} isDarkMode={isDarkMode} onBack={handleSubPageBack} onOpenImages={openGearViewer} onOpenRelatedWorks={openJourneyRelatedWorks} />
      ) : currentSubPage?.id === 'contact_about' ? (
        <main className="contact-about-shell" style={{ position: 'relative', zIndex: 1, maxWidth: '1120px', margin: '0 auto', padding: '142px 24px 120px' }}>
          <motion.section className="contact-about-surface" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: 'hidden', background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '32px', boxShadow: theme.cardShadow }}>
            <div className="contact-about-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 0.9fr) minmax(400px, 1.1fr)', minHeight: '620px' }}>
              <div className={`contact-about-portrait ${(siteSettings.contactPortrait?.hero || siteSettings.contactPortrait?.medium) ? 'contact-about-has-photo' : ''}`} style={{ minHeight: '620px', background: isDarkMode ? '#171719' : '#e9e9ed', overflow: 'hidden' }}>
                {siteSettings.contactPortrait?.hero || siteSettings.contactPortrait?.medium ? <img src={cleanImagePath(siteSettings.contactPortrait.hero || siteSettings.contactPortrait.medium)} alt={`${siteSettings.contactName || 'Miles'} 的作者頁照片`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${siteSettings.contactPortrait?.positionX ?? 50}% ${siteSettings.contactPortrait?.positionY ?? 50}%`, transform: `scale(${(siteSettings.contactPortrait?.scale ?? 100) / 100})`, transformOrigin: `${siteSettings.contactPortrait?.positionX ?? 50}% ${siteSettings.contactPortrait?.positionY ?? 50}%`, display: 'block' }} /> : <div className="contact-about-mark" style={{ background: isDarkMode ? 'linear-gradient(145deg, #1c1c1e, #080808)' : 'linear-gradient(145deg, #f4f4f6, #d7d7dc)', color: isDarkMode ? '#f5f5f7' : '#1d1d1f' }}><span className="mark-main">M.</span><span className="mark-echo mark-echo-1">M.</span><span className="mark-echo mark-echo-2">M.</span><span className="mark-echo mark-echo-3">M.</span><span className="mark-echo mark-echo-4">M.</span></div>}
              </div>
              <article className="contact-about-copy" style={{ padding: '64px clamp(34px, 6vw, 80px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <button onClick={handleGoHome} style={{ background: 'transparent', border: `1px solid ${theme.cardBorder}`, color: theme.textSecondary, borderRadius: '980px', padding: '8px 20px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', marginBottom: '54px' }}>← 返回首頁</button>
                  <p style={{ margin: '0 0 14px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.12em', color: theme.textSecondary }}>M. GALLERY / ABOUT</p>
                  <h1 style={{ margin: 0, fontSize: 'clamp(46px, 6vw, 76px)', lineHeight: 0.98, letterSpacing: '-0.032em', fontWeight: '650', color: theme.textPrimary }}>{siteSettings.contactName || 'Miles'}</h1>
                  <p style={{ margin: '16px 0 0', fontSize: '15px', fontWeight: '600', color: theme.textSecondary, letterSpacing: '-0.01em' }}>{siteSettings.contactRole || '街頭與城市攝影'}</p>
                  <div style={{ width: '44px', height: '1px', background: theme.textPrimary, opacity: 0.38, margin: '36px 0' }} />
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '18px', lineHeight: 1.8, letterSpacing: '-0.018em', color: theme.textPrimary }}>{siteSettings.contactAbout || '以街頭、城市與偶遇的光影為線索，記錄生活中不被安排的瞬間。'}</p>
                </div>
                <p style={{ margin: '48px 0 0', fontSize: '12px', color: theme.textSecondary, letterSpacing: '0.06em' }}>M. GALLERY · PHOTOGRAPHY</p>
              </article>
            </div>
          </motion.section>
        </main>
      ) : currentSubPage?.id === 'contact_socials' ? (
        <main style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto', padding: '142px 24px 120px' }}>
          <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} style={{ maxWidth: '680px', margin: '0 auto' }}>
            <button onClick={handleGoHome} style={{ background: 'transparent', border: `1px solid ${theme.cardBorder}`, color: theme.textSecondary, borderRadius: '980px', padding: '8px 20px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', marginBottom: '54px' }}>← 返回首頁</button>
            <p style={{ margin: '0 0 14px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.12em', color: theme.textSecondary }}>M. GALLERY / CONTACT</p>
            <h1 style={{ margin: '0 0 44px', fontSize: 'clamp(42px, 8vw, 76px)', lineHeight: 1, letterSpacing: '-0.032em', fontWeight: '650', color: theme.textPrimary }}>保持聯絡。</h1>
            <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
              {(() => {
                const configured = (siteSettings.contactSocials || []).filter(platform => platform.url);
                const email = configured.find(platform => platform.kind === 'email');
                const external = configured.filter(platform => platform.kind !== 'email').map(platform => ({ ...platform, href: normalizeExternalUrl(platform.url) })).filter(platform => platform.href);
                return <>
                  {external.map(platform => <a key={platform.id} href={platform.href} target="_blank" rel="noreferrer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', padding: '22px 0', borderBottom: `1px solid ${theme.cardBorder}`, textDecoration: 'none', color: theme.textPrimary }}><span style={{ fontSize: '18px', fontWeight: '600', letterSpacing: '-0.02em' }}>{platform.label}</span><span style={{ color: theme.textSecondary, fontSize: '18px' }}>↗</span></a>)}
                  {email && <div style={{ padding: '26px 0 0', marginTop: external.length ? '22px' : 0, borderTop: external.length ? `1px solid ${theme.cardBorder}` : 'none' }}><p style={{ margin: '0 0 9px', color: theme.textSecondary, fontSize: '12px', fontWeight: '700', letterSpacing: '0.1em' }}>EMAIL</p><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}><span style={{ fontSize: 'clamp(16px, 2.5vw, 21px)', fontWeight: '600', letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>{email.url}</span><button onClick={async () => { try { await navigator.clipboard.writeText(email.url); alert('Email 已複製'); } catch (err) { alert('無法自動複製，請手動複製 Email。'); } }} style={{ flex: 'none', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', padding: '8px 14px', background: 'transparent', color: theme.textPrimary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>複製</button></div></div>}
                </>;
              })()}
              {!(siteSettings.contactSocials || []).some(platform => platform.url) && <p style={{ margin: '28px 0', color: theme.textSecondary, lineHeight: 1.6 }}>聯絡方式即將更新。</p>}
            </div>
          </motion.section>
        </main>
      ) : currentSubPage && ['gear_cameras', 'gear_edc'].includes(currentSubPage.id) ? (
        <main className="gear-page-shell" style={{ position: 'relative', zIndex: 1, maxWidth: '1360px', margin: '0 auto', padding: '118px 24px 100px' }}>
          {(() => {
            const isCamera = currentSubPage.id === 'gear_cameras';
            const normalizedGearQuery = normalizeSearchValue(gearSearchQuery);
            const currentItems = gearItems
              .filter(item => item.type === (isCamera ? 'camera' : 'tool') && item.isVisible)
              .filter(item => !normalizedGearQuery || normalizeSearchValue([item.title, item.subtitle, item.description].filter(Boolean).join(' ')).includes(normalizedGearQuery))
              .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            const selectJourney = journey => handleSelectSubPage({
              id: journey.id,
              type: 'location',
              title: journey.title,
              desc: journey.desc,
              returnTo: {
                id: currentSubPage.id,
                title: currentSubPage.title,
                desc: currentSubPage.desc
              }
            });
            const exploreEdc = isCamera ? () => handleSelectSubPage({
              id: 'gear_edc',
              title: 'EDC 隨身裝備',
              desc: '相機包、儲存設備與創作工具',
              returnTo: { id: currentSubPage.id, title: currentSubPage.title, desc: currentSubPage.desc }
            }) : null;
            return <><div className="gear-content-grid" style={{ display: 'grid', gridTemplateColumns: !isMobileScreen ? 'minmax(0, 720px) 300px' : 'minmax(0, 1fr)', justifyContent: !isMobileScreen ? 'end' : 'center', gap: '42px', alignItems: 'start', width: '100%' }}>
              <section className="gear-feed-column" style={{ minWidth: 0, padding: '0 14px 26px' }}>
                <button className="gear-page-back" onClick={handleGoHome} style={{ background: 'transparent', border: `1px solid ${theme.cardBorder}`, color: theme.textSecondary, borderRadius: '980px', padding: '8px 20px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', marginBottom: '42px' }}>← 返回首頁</button>
                <header className="gear-page-header" style={{ padding: '0 5px 26px', marginBottom: '26px', textAlign: 'left' }}>
                  <p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.1em' }}>M. GALLERY · GEAR</p>
                  <h1 style={{ margin: '9px 0 0', fontSize: 'clamp(34px, 4vw, 50px)', lineHeight: 1, letterSpacing: '-.045em', color: theme.textPrimary }}>{currentSubPage.title}</h1>
                  <p style={{ margin: '13px 0 0', color: theme.textSecondary, fontSize: '16px' }}>{currentSubPage.desc}</p>
                </header>
                <div className="gear-feed-stack" style={{ borderLeft: `1px solid ${theme.cardBorder}`, borderRight: `1px solid ${theme.cardBorder}`, padding: '24px 20px 28px', display: 'grid', gap: 0 }}>
                  {currentItems.map((item, index) => <React.Fragment key={item._id}><GearFeedPost item={{ ...item, images: (item.images || []).map(image => ({ ...image, medium: cleanImagePath(image.medium) })), cover: { ...item.cover, medium: cleanImagePath(item.cover?.medium) } }} theme={theme} isDarkMode={isDarkMode} onImageClick={openGearViewer} />{index < currentItems.length - 1 && <div aria-hidden="true" style={{ width: '100%', height: '1px', margin: '22px 0', background: theme.cardBorder }} />}</React.Fragment>)}
                  {!currentItems.length && <p style={{ margin: '32px 0', color: theme.textSecondary }}>{normalizedGearQuery ? '沒有符合搜尋條件的帖子。' : '內容即將更新。'}</p>}
                </div>
              </section>
              {!isMobileScreen && <GearExploreRail latestJourneys={latestJourneys} theme={theme} isDarkMode={isDarkMode} query={gearSearchQuery} onQueryChange={setGearSearchQuery} onSelectJourney={selectJourney} onExploreEdc={exploreEdc} alignOffset={213} />}
            </div></>;
          })()}
        </main>
      ) : currentSubPage ? (
        <main className={`subpage-shell ${currentSubPage.id === 'gear_luts' ? 'gear-luts-shell' : ''}`} style={{ position: 'relative', zIndex: 1, maxWidth: ['location', 'location-collection'].includes(currentSubPage.type) ? '1180px' : '850px', margin: '0 auto', padding: '120px 24px 100px 24px' }}>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} 
            className={`subpage-surface ${currentSubPage.id === 'gear_luts' ? 'gear-luts-surface' : ''}`}
            style={{ textAlign: 'center', padding: '60px 40px', background: theme.cardBg, borderRadius: '28px', border: `1px solid ${theme.cardBorder}`, backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)', boxShadow: theme.cardShadow }}
          >
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }} 
              onClick={handleSubPageBack} 
              className="subpage-back"
              style={{ display: 'block', background: 'transparent', border: `1px solid ${theme.cardBorder}`, color: theme.textSecondary, borderRadius: '980px', padding: '8px 20px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', margin: '0 0 24px 0' }}
            >
              ← 返回{currentSubPage.returnTo?.title || '首頁'}
            </motion.button>

            <h1 className={`subpage-heading ${currentSubPage.id === 'gear_luts' ? 'gear-luts-heading' : ''}`} style={{ fontSize: '42px', fontWeight: '700', letterSpacing: '-1px', margin: '0 0 16px 0', color: theme.textPrimary }}>
              {currentSubPage.title}
            </h1>
            <p className={`subpage-description ${currentSubPage.id === 'gear_luts' ? 'gear-luts-description' : ''}`} style={{ fontSize: '18px', color: theme.textSecondary, margin: '0 0 40px 0', fontWeight: '400' }}>
              {currentSubPage.desc}
            </p>

            {currentSubPage.id === 'gear_luts' && <div className="gear-luts-list" style={{ display: 'grid', gap: '42px' }}>{gearItems.filter(item => item.type === 'color' && item.isVisible).map(item => <BeforeAfterSlider key={item._id} item={{ ...item, originalImage: { ...item.originalImage, medium: cleanImagePath(item.originalImage?.medium) }, gradedImage: { ...item.gradedImage, medium: cleanImagePath(item.gradedImage?.medium) } }} theme={theme} />)}{!gearItems.some(item => item.type === 'color' && item.isVisible) && <p style={{ color: theme.textSecondary }}>色彩對比即將更新。</p>}</div>}

            {!['gear_luts', 'gear_cameras', 'gear_edc'].includes(currentSubPage.id) && <><div style={{ display: isPhotoLayoutAvailable && !isMobileScreen ? 'flex' : 'none', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '20px', paddingBottom: '14px', borderBottom: `1px solid ${theme.cardBorder}` }}><span style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: '600' }}>瀏覽排列</span><div style={{ display: 'inline-flex', padding: '3px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: theme.inputBg }}><button type="button" onClick={() => setLocationPhotoLayout('single')} aria-pressed={locationPhotoLayout === 'single'} style={{ padding: '7px 11px', border: 'none', borderRadius: '980px', background: locationPhotoLayout === 'single' ? theme.cardBg : 'transparent', color: locationPhotoLayout === 'single' ? theme.textPrimary : theme.textSecondary, fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: locationPhotoLayout === 'single' ? '0 1px 3px rgba(0,0,0,.12)' : 'none' }}>單列大圖</button><button type="button" onClick={() => setLocationPhotoLayout('double')} aria-pressed={locationPhotoLayout === 'double'} style={{ padding: '7px 11px', border: 'none', borderRadius: '980px', background: locationPhotoLayout === 'double' ? theme.cardBg : 'transparent', color: locationPhotoLayout === 'double' ? theme.textPrimary : theme.textSecondary, fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: locationPhotoLayout === 'double' ? '0 1px 3px rgba(0,0,0,.12)' : 'none' }}>兩張一行</button></div></div><div className="subpage-photo-stack" style={{ display: 'grid', gridTemplateColumns: isDoublePhotoLayout ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: isDoublePhotoLayout ? '20px' : '28px', textAlign: 'left', marginTop: '20px' }}>
              {displayPhotos.length > 0 ? (
                displayPhotos.slice(0, visibleSubpagePhotoCount).map((photo, index) => (
                  <div id={`work-photo-${photo._id}`} className="subpage-photo-card" key={photo._id || index} style={{ background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: '20px', overflow: 'hidden', border: `1px solid ${theme.cardBorder}` }}>
                    <div style={{ position: 'relative', width: '100%', minHeight: '200px', backgroundColor: isDarkMode ? '#1c1c1e' : '#e5e5ea', ...(isDoublePhotoLayout ? { aspectRatio: '4 / 3', minHeight: 0, overflow: 'hidden' } : {}) }}>
                      <img 
                        src={getPhotoImagePath(photo, 'medium')} 
                        srcSet={getPhotoImageSrcSet(photo)}
                        sizes={isDoublePhotoLayout ? '(max-width: 768px) 100vw, 560px' : '(max-width: 768px) 100vw, 1080px'}
                        alt={getPhotoAlt(photo)} 
                        loading="lazy" 
                        decoding="async" 
                        className="grid-image-fade" 
                        style={{ width: '100%', height: isDoublePhotoLayout ? '100%' : 'auto', objectFit: isDoublePhotoLayout ? 'cover' : undefined, display: 'block', cursor: 'zoom-in' }} 
                        onClick={() => openLightbox(index)} 
                      />
                    </div>
                    <div className="subpage-photo-meta" style={{ padding: isDoublePhotoLayout ? '14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '18px' }}>
                      <div className="subpage-photo-meta-main">
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: '600' }}>📍 {photo.location || '未知地點'}</h3>
                        <p style={{ margin: 0, fontSize: '13px', color: theme.textSecondary }}>📷 {getPhotoEquipmentLabel(photo) || '無器材參數'}</p>
                        {getPhotoShootingInfo(photo) && <p style={{ margin: '5px 0 0', fontSize: '12px', color: theme.textSecondary }}>⚙︎ {getPhotoShootingInfo(photo)}</p>}
                      </div>
                      <div className="subpage-photo-meta-aside" style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '9px' }}><span style={{ fontSize: '12px', background: '#0071e3', color: '#fff', padding: '4px 12px', borderRadius: '12px', textTransform: 'capitalize' }}>
                        {TAG_LABELS[photo.subCategory] || photo.subCategory || 'uncategorized'}
                      </span>{formatShootingDate(photo.shootingDate) && <span style={{ color: theme.textSecondary, fontSize: '11px', fontWeight: '600', letterSpacing: '.04em', fontVariantNumeric: 'tabular-nums' }}>{formatShootingDate(photo.shootingDate)}</span>}</div>
                    </div>
                    {photo.aiDescription && !isDoublePhotoLayout && (
                      <div style={{ padding: '0 20px 20px 20px', fontSize: '14px', color: theme.textSecondary, fontStyle: 'italic' }}>
                        "{photo.aiDescription}"
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ padding: '60px 20px', textAlign: 'center', border: `1px dashed ${theme.cardBorder}`, borderRadius: '20px', color: theme.textSecondary, fontSize: '14px' }}>
                  該分類下暫無照片 ✨
                </div>
              )}
            </div>{displayPhotos.length > visibleSubpagePhotoCount && <button type="button" onClick={() => setVisibleSubpagePhotoCount(count => count + 24)} style={{ marginTop: '26px', padding: '10px 18px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: theme.cardBg, color: theme.textPrimary, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>載入更多（尚有 {displayPhotos.length - visibleSubpagePhotoCount} 張）</button>}</>}
          </motion.div>
        </main>
      ) : (
        <>
          <HeroSection 
            theme={theme}
            isDarkMode={isDarkMode}
            isMobileScreen={isMobileScreen}
            isAndroid={isAndroid}
            isLoading={isLoading}
            currentHeroPhoto={currentHeroPhoto}
            heroIndex={heroIndex}
            heroImage={heroImage}
            isHeroPaused={isHeroPaused}
            setIsHeroPaused={setIsHeroPaused}
            heroPhotos={heroPhotos}
            heroTextColor={heroTextColor}
            heroSubColor={heroSubColor}
            heroTextShadow={heroTextShadow}
            heroLines={siteSettings.heroLines}
          />

          <main style={{ 
            position: 'relative', 
            zIndex: 50, 
            marginTop: '-12vh', 
            backgroundColor: theme.bg,
            borderTopLeftRadius: '36px',
            borderTopRightRadius: '36px',
            boxShadow: isDarkMode ? '0 -30px 60px rgba(0,0,0,0.5)' : '0 -30px 60px rgba(0,0,0,0.08)',
            padding: '70px 0 100px 0',
            width: '100%',
          }}>
            <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
              <div>
              <div style={{ minWidth: 0 }}>
              
              {/* 美學宣言 */}
              <div id="section-philosophy">
                <motion.div 
                  initial={{ opacity: 0, y: 30 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true, amount: 0.3 }} 
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} 
                  style={{ padding: '60px 0 140px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <h2 style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: '700', color: theme.textPrimary, letterSpacing: '-1.5px', marginBottom: '24px', lineHeight: '1.2', whiteSpace: 'pre-line' }}>
                    {siteSettings.philosophyTitle || '記錄真實，\n重塑秩序。'}
                  </h2>
                  <p style={{ fontSize: 'clamp(17px, 2vw, 21px)', color: theme.textSecondary, lineHeight: '1.6', maxWidth: '680px', fontWeight: '400', margin: 0 }}>
                    {siteSettings.philosophyBody || '攝影對我而言，是一個做減法的過程。摒棄繁雜的器材依賴與過度的修飾，專注於街頭的光影、建築的幾何與最真實的瞬間。以 Clean Fit 的視覺標準，褪去畫面中的無序，只保留最純粹的肌理與溫度。'}
                  </p>
                </motion.div>
              </div>

              {/* 最新旅程 */}
              <div id="section-journeys" style={{ marginBottom: '80px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 6px 0', color: theme.textPrimary, letterSpacing: '-0.5px' }}>最新旅程</h2>
                    <p style={{ color: theme.textSecondary, margin: 0, fontSize: '15px' }}>按拍攝日期排序的目的地圖集</p>
                  </div>
                  {latestJourneys.length > 0 && (
                    <button type="button" className="destination-entry" style={{ color: theme.textSecondary, borderColor: theme.cardBorder }} onClick={() => handleSelectSubPage({ id: 'destinations_index', title: '全部目的地' })}>
                      全部目的地 {latestJourneys.length} ↗
                    </button>
                  )}
                </div>
                
                <div className="horizontal-scroll-container" style={{ display: 'flex', overflowX: 'auto', gap: '18px', paddingBottom: '20px', scrollSnapType: 'x mandatory' }}>
                  {latestJourneys.length > 0 ? latestJourneys.map((journey) => (
                    <motion.div 
                      key={journey.id} 
                      whileHover={{ y: -6, transition: { duration: 0.25 } }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectSubPage({
                        id: journey.id,
                        type: 'location',
                        title: journey.title,
                        desc: journey.desc
                      })}
                      style={{ 
                        flex: '0 0 clamp(240px, 72vw, 320px)', 
                        height: 'clamp(165px, 48vw, 210px)', 
                        scrollSnapAlign: 'start', 
                        borderRadius: '20px', 
                        overflow: 'hidden', 
                        cursor: 'pointer', 
                        position: 'relative', 
                        boxShadow: theme.cardShadow, 
                        border: `1px solid ${theme.cardBorder}`,
                        backgroundColor: isDarkMode ? '#1c1c1e' : '#e5e5ea',
                        userSelect: 'none'
                      }}
                    >
                      <img 
                        src={getPhotoImagePath(journey.coverPhoto, 'medium')} 
                        srcSet={getPhotoImageSrcSet(journey.coverPhoto)}
                        sizes="(max-width: 720px) 72vw, 320px"
                        decoding="async" 
                        loading="lazy" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        alt={getPhotoAlt(journey.coverPhoto, `${journey.title}攝影作品`)} 
                      />

                      <div style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        background: 'rgba(0, 0, 0, 0.55)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        color: '#ffffff',
                        padding: '4px 10px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '600',
                        border: '1px solid rgba(255, 255, 255, 0.15)'
                      }}>
                        {journey.count} 張作品
                      </div>

                      <div style={{ 
                        position: 'absolute', 
                        bottom: 0, 
                        left: 0, 
                        right: 0, 
                        padding: '36px 16px 16px 16px', 
                        background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)', 
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end'
                      }}>
                        <div>
                          <div style={{ fontSize: '17px', fontWeight: '700', letterSpacing: '-0.3px', marginBottom: '2px' }}>
                            📍 {journey.title}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )) : <div style={{ color: theme.textSecondary, padding: '20px 0' }}>暫無目的地圖集</div>}
                </div>
              </div>

              {/* 精選櫥窗 */}
              <div id="section-featured" style={{ marginBottom: '40px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: theme.textPrimary, letterSpacing: '-0.5px' }}>精選櫥窗</h2>
                <p style={{ color: theme.textSecondary, marginBottom: '24px', fontSize: '15px' }}>最滿意的光影瞬間</p>
                
                <div className="featured-showcase-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridAutoRows: '4px', gridAutoFlow: 'row', gap: '20px', alignItems: 'start' }}>
                  {featuredPhotos.length > 0 ? featuredPhotos.map((photo, i) => (
                    <MasonryCell key={photo._id || i} gap={20}>
                      <div 
                        onClick={() => openLightbox(i, featuredPhotos)} 
                        style={{ 
                          borderRadius: '16px', 
                          overflow: 'hidden', 
                          cursor: 'zoom-in', 
                          position: 'relative', 
                          boxShadow: theme.cardShadow, 
                          border: `1px solid ${theme.cardBorder}` 
                        }}
                      >
                        <img 
                          src={getPhotoImagePath(photo, 'medium')} 
                          srcSet={getPhotoImageSrcSet(photo)}
                          sizes="(max-width: 720px) calc(100vw - 48px), (max-width: 1280px) calc((100vw - 88px) / 3), 394px"
                          decoding="async" 
                          loading="lazy" 
                          style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} 
                          alt={getPhotoAlt(photo, '精選攝影作品')} 
                        />
                      </div>
                    </MasonryCell>
                  )) : <div style={{ color: theme.textSecondary }}>暫無作品</div>}
                </div>
              </div>
              </div>
              </div>
            </div>
          </main>
        </>
      )}

      {/* ========================================== */}
      {/* Lightbox 預覽 */}
      {/* ========================================== */}
      <AnimatePresence>
        {lightboxIndex >= 0 && currentPhotoData && (
          <motion.div
            key="lightbox-overlay" 
            ref={lightboxShellRef}
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={() => { setLightboxIndex(-1); resetZoomAndPan(); }}
            onWheel={handleWheel}
            style={{
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw', 
              height: '100dvh', 
              backgroundColor: 'rgba(0, 0, 0, 0.96)', 
              zIndex: 10000, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              perspective: '1200px', 
              overflow: 'hidden', 
              cursor: 'zoom-out', 
              userSelect: 'none',
              touchAction: 'none',
              overscrollBehavior: 'none'
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(-1);
                resetZoomAndPan();
              }}
              style={{
                position: 'absolute',
                top: 'max(20px, env(safe-area-inset-top, 20px))',
                right: 'max(20px, env(safe-area-inset-right, 20px))',
                zIndex: 10002,
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                outline: 'none'
              }}
            >
              ✕
            </button>

            <AnimatePresence custom={lightboxDirection} mode="popLayout">
              <motion.div
                key={currentPhotoData._id || lightboxIndex} 
                custom={lightboxDirection} 
                variants={photoViewerVariants} 
                initial={isFirstOpen ? "initialFromHome" : "enterFromSide"} 
                animate="center" 
                exit="exitRotate"
                drag={isZoomed ? false : "x"} 
                dragConstraints={{ left: 0, right: 0 }} 
                dragElastic={0.7}
                dragMomentum={false}
                onDragStart={beginLightboxDrag}
                onDragEnd={(e, { offset, velocity }) => {
                  finishLightboxDrag();
                  if (!isZoomed) {
                    if (isAnimatingRef.current) return;
                    const currentListLength = activeLightboxPhotos.length;
                    const swipeThreshold = 35;
                    const velocityThreshold = 250;

                    const isSwipeLeft = offset.x < -swipeThreshold || velocity.x < -velocityThreshold;
                    const isSwipeRight = offset.x > swipeThreshold || velocity.x > velocityThreshold;

                    if (isSwipeLeft && lightboxIndex < currentListLength - 1) {
                      isAnimatingRef.current = true;
                      setIsFirstOpen(false);
                      setLightboxDirection(1);
                      setLightboxIndex(prev => prev + 1);
                      resetZoomAndPan();
                      setTimeout(() => {
                        isAnimatingRef.current = false;
                      }, 300);
                    } else if (isSwipeRight && lightboxIndex > 0) {
                      isAnimatingRef.current = true;
                      setIsFirstOpen(false);
                      setLightboxDirection(-1);
                      setLightboxIndex(prev => prev - 1);
                      resetZoomAndPan();
                      setTimeout(() => {
                        isAnimatingRef.current = false;
                      }, 300);
                    }
                  }
                }}
                onClick={handleSmartPhotoClick}
                onDoubleClick={handlePhotoDoubleClick}
                style={{ 
                  position: 'relative', 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  transformOrigin: 'center center', 
                  transform: 'translateZ(0)',
                  cursor: isZoomed ? 'grab' : 'zoom-in' 
                }}
              >
                <motion.div 
                  onPointerDown={beginLightboxPan}
                  onPointerMove={moveLightboxPan}
                  onPointerUp={endLightboxPan}
                  onPointerCancel={endLightboxPan}
                  animate={{ scale: isZoomed ? 2.8 : 1 }} 
                  transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }} 
                  style={{ x: lightboxPanX, y: lightboxPanY, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', transformOrigin: 'center center', width: '100%', height: '100%', cursor: isZoomed ? 'grab' : 'inherit', touchAction: 'none' }}
                >
                  <img 
                    ref={lightboxImageRef}
                    src={getPhotoImagePath(currentPhotoData, 'hero')} 
                    alt={getPhotoAlt(currentPhotoData)} 
                    decoding="async"
                    style={{ maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain', display: 'block', borderRadius: isZoomed ? '2px' : '18px', boxShadow: '0 35px 100px rgba(0,0,0,0.95)', pointerEvents: 'none' }} 
                  />
                  {isSettled && isZoomed && currentPhotoData.imagePath && getPhotoImagePath(currentPhotoData, 'original') !== getPhotoImagePath(currentPhotoData, 'hero') && (
                    <motion.img 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      transition={{ duration: 0.4 }} 
                      decoding="async" 
                      src={getPhotoImagePath(currentPhotoData, 'original')} 
                      alt={getPhotoAlt(currentPhotoData)} 
                      style={{ position: 'absolute', maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain', display: 'block', borderRadius: isZoomed ? '2px' : '18px', pointerEvents: 'none' }} 
                    />
                  )}
                </motion.div>
              </motion.div>
            </AnimatePresence>

            {!isZoomed && lightboxIndex > 0 && (
              <button 
                className="lightbox-nav-btn" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (isAnimatingRef.current) return; 
                  isAnimatingRef.current = true; 
                  setIsFirstOpen(false); 
                  setLightboxDirection(-1); 
                  setLightboxIndex(prev => prev - 1); 
                  resetZoomAndPan(); 
                  setTimeout(() => { isAnimatingRef.current = false; }, 300); 
                }} 
                style={{ position: 'absolute', left: '30px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: '50%', width: '54px', height: '54px', cursor: 'pointer', fontSize: '24px', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(0,0,0,0.4)', zIndex: 10001 }}
              >
                ‹
              </button>
            )}
            {!isZoomed && lightboxIndex < activeLightboxPhotos.length - 1 && (
              <button 
                className="lightbox-nav-btn" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (isAnimatingRef.current) return; 
                  isAnimatingRef.current = true; 
                  setIsFirstOpen(false); 
                  setLightboxDirection(1); 
                  setLightboxIndex(prev => prev + 1); 
                  resetZoomAndPan(); 
                  setTimeout(() => { isAnimatingRef.current = false; }, 300); 
                }} 
                style={{ position: 'absolute', right: '30px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: '50%', width: '54px', height: '54px', cursor: 'pointer', fontSize: '24px', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(0,0,0,0.4)', zIndex: 10001 }}
              >
                ›
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal 彈窗組件 */}
      <AnimatePresence>
        {photoToDelete && (
          <motion.div 
            variants={modalBackdropVariants} 
            initial="hidden" 
            animate="visible" 
            exit="exit" 
            onClick={() => setPhotoToDelete(null)} 
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100dvh', backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(20px)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <motion.div 
              variants={modalContentVariants} 
              initial="hidden" 
              animate="visible" 
              exit="exit" 
              onClick={e => e.stopPropagation()} 
              style={{ background: theme.modalBg, border: `1px solid ${theme.cardBorder}`, padding: '28px', borderRadius: '24px', width: '320px', textAlign: 'center', boxShadow: '0 30px 70px rgba(0, 0, 0, 0.5)' }}
            >
              {photoToDelete.imagePath && (
                <img 
                  src={getPhotoImagePath(photoToDelete, 'thumbnail')} 
                  alt="preview" 
                  style={{ width: '72px', height: '72px', borderRadius: '16px', objectFit: 'cover', margin: '0 auto 16px', display: 'block', boxShadow: '0 6px 20px rgba(0,0,0,0.3)' }} 
                />
              )}
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: theme.textPrimary }}>確定刪除此照片？</h3>
              <p style={{ margin: '0 0 24px 0', fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5' }}>此操作將徹底移除該檔案，無法復原。</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={confirmDeletePhoto} style={{ width: '100%', padding: '12px', background: '#ff3b30', color: '#fff', border: 'none', borderRadius: '980px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>徹底刪除</button>
                <button onClick={() => setPhotoToDelete(null)} style={{ width: '100%', padding: '12px', background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', color: theme.textPrimary, border: 'none', borderRadius: '980px', fontSize: '15px', fontWeight: '500', cursor: 'pointer' }}>取消</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editingPhoto && (
          <motion.div 
            variants={modalBackdropVariants} 
            initial="hidden" 
            animate="visible" 
            exit="exit" 
            onDoubleClick={() => setEditingPhoto(null)}
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100dvh', backgroundColor: 'rgba(0, 0, 0, 0.52)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end', zIndex: 10000 }}
          >
            <motion.div 
              variants={modalContentVariants} 
            initial="hidden" 
            animate="visible" 
            exit="exit" 
            onClick={event => event.stopPropagation()}
            onDoubleClick={event => event.stopPropagation()}
            style={{ background: theme.modalBg, borderLeft: `1px solid ${theme.cardBorder}`, padding: '28px', borderRadius: 0, width: 'min(460px, 100vw)', display: 'flex', flexDirection: 'column', height: '100dvh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '-18px 0 50px rgba(0,0,0,0.16)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: theme.textPrimary }}>照片資訊</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button type="button" onClick={closePhotoEditor} style={{ background: 'transparent', border: 'none', color: theme.textSecondary, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>取消</button>
                  <button type="button" onClick={handleSaveAndSelectBatchTargets} style={{ background: 'transparent', border: 'none', color: theme.textSecondary, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>儲存並批量同步</button>
                  <button type="button" onClick={handleSaveParameters} style={{ background: 'transparent', border: 'none', color: '#0071e3', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>儲存</button>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', color: '#86868b', fontWeight: '500' }}>照片分類（點選切換）</label>
                  <button onClick={() => setIsEditingTags(!isEditingTags)} style={{ background: 'transparent', border: 'none', color: theme.textSecondary, fontSize: '12px', cursor: 'pointer' }}>
                    {isEditingTags ? '完成' : '管理／排序分類'}
                  </button>
                </div>
                
                <div className="pill-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingBottom: '8px' }}>
                  {availableCategories.map(cat => {
                    const isSystemTag = cat === 'uncategorized';
                    return (
                      <div key={cat} style={{ position: 'relative', display: 'inline-block' }}>
                        <button 
                          type="button"
                          onClick={(e) => { 
                            e.stopPropagation();
                            if (!isEditingTags) {
                              setActiveCategory(cat);
                            }
                          }}
                          style={{ 
                            padding: '6px 14px', 
                            borderRadius: '980px', 
                            background: activeCategory === cat ? '#0071e3' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'), 
                            color: activeCategory === cat ? '#fff' : theme.textPrimary, 
                            border: `1px solid ${activeCategory === cat ? '#0071e3' : theme.cardBorder}`, 
                            fontSize: '13px', 
                            fontWeight: '500', 
                            cursor: isEditingTags ? 'default' : 'pointer', 
                            whiteSpace: 'nowrap', 
                            transition: 'all 0.15s ease',
                            opacity: 1
                          }}
                        >
                          {TAG_LABELS[cat] || cat}
                        </button>
                        {isEditingTags && <span style={{ display: 'inline-flex', gap: '2px', marginLeft: '3px', verticalAlign: 'middle' }}><button type="button" onClick={() => moveWorkCategory(cat, -1)} disabled={availableCategories.indexOf(cat) === 0} aria-label={`將 ${TAG_LABELS[cat] || cat} 往前移`} title="往前移" style={{ border: 'none', borderRadius: '6px', padding: '3px 5px', background: theme.inputBg, color: theme.textSecondary, cursor: availableCategories.indexOf(cat) === 0 ? 'not-allowed' : 'pointer', opacity: availableCategories.indexOf(cat) === 0 ? .35 : 1 }}>‹</button><button type="button" onClick={() => moveWorkCategory(cat, 1)} disabled={availableCategories.indexOf(cat) === availableCategories.length - 1} aria-label={`將 ${TAG_LABELS[cat] || cat} 往後移`} title="往後移" style={{ border: 'none', borderRadius: '6px', padding: '3px 5px', background: theme.inputBg, color: theme.textSecondary, cursor: availableCategories.indexOf(cat) === availableCategories.length - 1 ? 'not-allowed' : 'pointer', opacity: availableCategories.indexOf(cat) === availableCategories.length - 1 ? .35 : 1 }}>›</button>{!isSystemTag && <button type="button" onClick={() => handleRemoveTag(cat)} aria-label={`刪除 ${TAG_LABELS[cat] || cat}`} title="刪除" style={{ border: 'none', borderRadius: '6px', padding: '3px 5px', background: 'rgba(255,59,48,.1)', color: '#ff3b30', cursor: 'pointer' }}>×</button>}</span>}
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input 
                      type="text" 
                      value={newTagInput} 
                      onChange={e => setNewTagInput(e.target.value)} 
                      onCompositionStart={() => { tagCompositionRef.current = true; }}
                      onCompositionEnd={e => { tagCompositionRef.current = false; tagCompositionEndedAtRef.current = Date.now(); setNewTagInput(e.target.value); }}
                      onKeyDown={e => {
                        if (e.key !== 'Enter') return;
                        if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229 || tagCompositionRef.current || Date.now() - tagCompositionEndedAtRef.current < 180) return;
                        e.preventDefault();
                        handleAddNewTag();
                      }} 
                      placeholder="+" 
                      style={{ 
                        width: '40px', 
                        padding: '6px', 
                        borderRadius: '980px', 
                        border: `1px dashed ${theme.textSecondary}`, 
                        background: 'transparent', 
                        color: theme.textPrimary, 
                        fontSize: '13px', 
                        textAlign: 'center', 
                        outline: 'none', 
                        transition: 'width 0.2s', 
                        ...(newTagInput && { width: '80px' }) 
                      }} 
                    />
                    {newTagInput && (
                      <button 
                        onClick={handleAddNewTag} 
                        style={{ background: '#34c759', border: 'none', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      >
                        ✓
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ overflowY: 'auto', paddingRight: '8px', flex: 1 }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#86868b', marginBottom: '6px' }}>地點</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'center', gap: '8px' }}>
                    <input type="text" value={editLocationParts.province} onChange={event => updateEditLocationParts(event.target.value, editLocationParts.city, editLocationParts.detail)} placeholder="省份，例如：廣東" aria-label="省份" style={{ width: '100%', padding: '12px 13px', borderRadius: '12px', border: '1px solid rgba(134, 134, 139, 0.3)', backgroundColor: theme.inputBg, color: theme.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                    <span aria-hidden="true" style={{ color: theme.textSecondary, fontSize: '20px', lineHeight: 1 }}>·</span>
                    <input type="text" value={editLocationParts.city} onChange={event => updateEditLocationParts(editLocationParts.province, event.target.value, editLocationParts.detail)} placeholder="城市，例如：汕頭" aria-label="城市" style={{ width: '100%', padding: '12px 13px', borderRadius: '12px', border: '1px solid rgba(134, 134, 139, 0.3)', backgroundColor: theme.inputBg, color: theme.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                    <span aria-hidden="true" style={{ color: theme.textSecondary, fontSize: '20px', lineHeight: 1 }}>·</span>
                    <input type="text" value={editLocationParts.detail} onChange={event => updateEditLocationParts(editLocationParts.province, editLocationParts.city, event.target.value)} placeholder="區縣／景點（可選）" aria-label="區縣或景點" style={{ width: '100%', padding: '12px 13px', borderRadius: '12px', border: '1px solid rgba(134, 134, 139, 0.3)', backgroundColor: theme.inputBg, color: theme.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <p style={{ margin: '7px 0 0', color: theme.textSecondary, fontSize: '11px', lineHeight: 1.45 }}>例如「廣東 · 汕頭 · 澄海」；第三格留空時，儲存與前台只顯示「廣東 · 汕頭」，不會留下多餘的點。</p>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                    <label style={{ fontSize: '13px', color: '#86868b' }}>相機 / 器材</label>
                    <button type="button" onClick={() => setIsManagingEquipmentPresets(value => !value)} style={{ border: 'none', background: 'transparent', padding: 0, color: '#0071e3', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>{isManagingEquipmentPresets ? '完成' : '＋ 常用設備'}</button>
                  </div>
                  <input type="text" value={editEquipment} onChange={e => setEditEquipment(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: '980px', border: '1px solid rgba(134, 134, 139, 0.3)', backgroundColor: theme.inputBg, color: theme.textPrimary, fontSize: '15px', outline: 'none', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', padding: '9px 11px', borderRadius: '10px', background: isDarkMode ? 'rgba(255,255,255,.045)' : 'rgba(0,0,0,.025)', color: theme.textSecondary, fontSize: '12px' }}>
                    <span>⚙︎ 拍攝資訊</span>
                    <strong style={{ color: getPhotoShootingInfo(editingPhoto) ? theme.textPrimary : theme.textSecondary, fontWeight: '600' }}>{getPhotoShootingInfo(editingPhoto) || '暫無快門、光圈、ISO；重新上傳原始檔可回填'}</strong>
                  </div>
                  {(siteSettings.equipmentPresets || []).length > 0 && <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', marginTop: '10px' }}>{siteSettings.equipmentPresets.map(preset => <button key={preset} type="button" onClick={() => setEditEquipment(preset)} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '6px 10px', border: `1px solid ${editEquipment === preset ? '#0071e3' : theme.cardBorder}`, borderRadius: '980px', background: editEquipment === preset ? (isDarkMode ? 'rgba(0,113,227,.24)' : 'rgba(0,113,227,.08)') : 'transparent', color: editEquipment === preset ? '#0071e3' : theme.textSecondary, fontSize: '11px', cursor: 'pointer' }}>{preset}</button>)}</div>}
                  {isManagingEquipmentPresets && <div style={{ marginTop: '12px', padding: '12px', border: `1px solid ${theme.cardBorder}`, borderRadius: '13px', background: isDarkMode ? 'rgba(255,255,255,.035)' : 'rgba(0,0,0,.018)' }}>
                    <div style={{ display: 'flex', gap: '8px' }}><input type="text" value={newEquipmentPreset} onChange={event => setNewEquipmentPreset(event.target.value)} onCompositionStart={() => { equipmentPresetCompositionRef.current = true; }} onCompositionEnd={event => { equipmentPresetCompositionRef.current = false; equipmentPresetCompositionEndedAtRef.current = Date.now(); setNewEquipmentPreset(event.target.value); }} onKeyDown={event => { if (event.key !== 'Enter') return; if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229 || equipmentPresetCompositionRef.current || Date.now() - equipmentPresetCompositionEndedAtRef.current < 180) return; event.preventDefault(); addEquipmentPreset(); }} placeholder="例如：Nikon Z8 + NIKKOR Z 24-120mm f/4" style={{ minWidth: 0, flex: 1, padding: '9px 11px', border: `1px solid ${theme.inputBorder}`, borderRadius: '9px', background: theme.inputBg, color: theme.textPrimary, font: 'inherit', fontSize: '12px', outline: 'none' }} /><button type="button" onClick={addEquipmentPreset} disabled={!newEquipmentPreset.trim()} style={{ flex: 'none', border: 'none', borderRadius: '9px', padding: '0 12px', background: '#0071e3', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: newEquipmentPreset.trim() ? 'pointer' : 'not-allowed', opacity: newEquipmentPreset.trim() ? 1 : .45 }}>新增</button></div>
                    <div style={{ display: 'grid', gap: '6px', marginTop: '10px' }}>{(siteSettings.equipmentPresets || []).map(preset => <div key={preset} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 8px', borderRadius: '8px', background: theme.inputBg }}><button type="button" onClick={() => setEditEquipment(preset)} style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: 0, border: 'none', background: 'transparent', color: theme.textPrimary, textAlign: 'left', fontSize: '12px', cursor: 'pointer' }}>{preset}</button><button type="button" onClick={() => removeEquipmentPreset(preset)} aria-label={`刪除 ${preset}`} style={{ flex: 'none', padding: '0 3px', border: 'none', background: 'transparent', color: '#ff3b30', fontSize: '17px', lineHeight: 1, cursor: 'pointer' }}>×</button></div>)}</div>
                    {!(siteSettings.equipmentPresets || []).length && <p style={{ margin: '10px 0 0', color: theme.textSecondary, fontSize: '12px' }}>先新增你最常用的機身與鏡頭組合。</p>}
                  </div>}
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#86868b', marginBottom: '6px' }}>拍攝時間</label>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme.inputBg, borderRadius: '980px', zIndex: 1, border: `1px solid ${theme.inputBorder}` }} />
                    <input 
                      type="date" 
                      value={editShootingDate} 
                      onChange={e => setEditShootingDate(e.target.value)} 
                      style={{ 
                        width: '100%', 
                        padding: '12px 16px', 
                        borderRadius: '980px', 
                        border: 'none', 
                        background: 'transparent', 
                        color: theme.textPrimary, 
                        colorScheme: isDarkMode ? 'dark' : 'light', 
                        fontSize: '15px', 
                        outline: 'none', 
                        boxSizing: 'border-box', 
                        cursor: 'pointer', 
                        paddingRight: '40px', 
                        position: 'relative', 
                        zIndex: 2 
                      }} 
                    />
                    <svg style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 3 }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  </div>
                </div>

                <div style={{ marginBottom: '24px', padding: '14px', borderRadius: '14px', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(98, 88, 205, .10)' : 'rgba(88, 86, 214, .055)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div>
                      <div style={{ color: '#5856d6', fontSize: '11px', fontWeight: '700', letterSpacing: '.5px' }}>PRIMARY · 聯網核實地點{editingPhotoResearch?.status === 'success' ? ' · 已核實' : ''}</div>
                      <p style={{ margin: '5px 0 0', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>
                        {editingPhotoResearch?.status === 'loading'
                          ? '這張照片正在背景核實中；你可先關閉並編輯其他照片，完成後再回來查看結果。'
                          : editLocation.trim() && !/^未知/.test(editLocation.trim())
                          ? `會優先核實「${editLocation.trim()}」附近是否有符合畫面的地點，仍會以畫面線索比對，不會直接當成答案。`
                          : '可先在上方輸入城市、區域或記得的一小段名稱，再開始核實；本機 Qwen 只提取畫面線索，網頁搜尋只接收文字查詢。'}
                      </p>
                    </div>
                    <button type="button" onClick={researchPhotoLocation} disabled={isLocationResearching} style={{ flex: 'none', border: 'none', borderRadius: '9px', padding: '8px 10px', background: '#5856d6', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: isLocationResearching ? 'not-allowed' : 'pointer', opacity: isLocationResearching ? .6 : 1 }}>
                      {editingPhotoResearch?.status === 'loading' ? '核實中…' : isLocationResearching ? '另一張核實中…' : editingPhotoResearch?.status === 'success' ? '🌐 再次核實' : '🌐 開始核實'}
                    </button>
                  </div>
                  {locationResearchError && <p style={{ margin: '10px 0 0', color: '#ff3b30', fontSize: '12px', lineHeight: 1.5 }}>{locationResearchError}</p>}
                  {locationResearch && <div style={{ marginTop: '12px', display: 'grid', gap: '9px' }}>
                    <p style={{ margin: 0, color: theme.textPrimary, fontSize: '12px', lineHeight: 1.55 }}>{locationResearch.summary}</p>
                    {locationResearch.visualClues?.length > 0 && <p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', lineHeight: 1.5 }}>畫面線索：{locationResearch.visualClues.join(' · ')}</p>}
                    {locationResearch.candidates?.map((candidate, index) => {
                      const confidenceLabel = { high: '較高', medium: '中等', low: '較低' }[candidate.confidence] || '較低';
                      const sourceLinks = (candidate.sourceNumbers || []).map(number => locationResearch.sources?.find(source => source.number === number)).filter(Boolean);
                      return <div key={`${candidate.location}-${index}`} style={{ padding: '11px', borderRadius: '10px', background: theme.inputBg, border: `1px solid ${theme.cardBorder}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}><strong style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.textPrimary, fontSize: '13px' }}>📍 {candidate.location}</strong><button type="button" onClick={() => applyResearchSuggestedLocation(candidate.location)} style={{ flex: 'none', border: 'none', borderRadius: '8px', padding: '6px 8px', background: 'rgba(0,113,227,.10)', color: '#0071e3', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>帶入地點</button></div>
                        <p style={{ margin: '6px 0 0', color: theme.textSecondary, fontSize: '11px', lineHeight: 1.5 }}>置信度：{confidenceLabel} · {candidate.reason || '沒有提供可核對的理由'}</p>
                        {sourceLinks.length > 0 && <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '7px' }}>{sourceLinks.map(source => <a key={source.number} href={source.url} target="_blank" rel="noreferrer" style={{ color: '#5856d6', fontSize: '11px', textDecoration: 'none' }}>來源 {source.number} ↗</a>)}</div>}
                      </div>;
                    })}
                    {locationResearch.candidates?.length === 0 && <p style={{ margin: 0, color: theme.textSecondary, fontSize: '12px' }}>這張照片目前沒有足夠證據，建議保留「未知地點」。</p>}
                  </div>}
                </div>

                {(aiDescription || aiProcessingId === editingPhoto._id) && (
                  <div style={{ marginBottom: '24px', padding: '16px', background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: '16px', border: `1px solid ${theme.cardBorder}`, position: 'relative', overflow: 'hidden' }}>
                    <AnimatePresence>
                      {aiProcessingId === editingPhoto._id && (
                        <motion.div initial={{ width: '0%', opacity: 1 }} animate={{ width: '100%', opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }} transition={{ duration: 12, ease: "linear" }} style={{ position: 'absolute', top: 0, left: 0, height: '3px', background: '#0071e3', zIndex: 10, boxShadow: '0 0 8px rgba(0,113,227,0.8)' }} />
                      )}
                    </AnimatePresence>
                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#0071e3', marginBottom: '8px', letterSpacing: '0.5px' }}>✦ AI DESCRIPTION</div>
                      <p className={aiProcessingId === editingPhoto._id ? "typing-effect" : ""} style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textPrimary, margin: 0, fontStyle: 'italic' }}>
                        {aiProcessingId === editingPhoto._id ? '正在分析畫面特徵與光影細節...' : aiDescription}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={moveEditingPhotoToJourney}
                disabled={Boolean(editingPhoto.journeyCandidate)}
                style={{ width: '100%', padding: '11px', marginTop: '16px', background: isDarkMode ? 'rgba(0,113,227,.14)' : 'rgba(0,113,227,.06)', border: '1px solid rgba(0,113,227,.32)', borderRadius: '980px', color: '#0071e3', fontSize: '13px', fontWeight: '600', cursor: editingPhoto.journeyCandidate ? 'not-allowed' : 'pointer', opacity: editingPhoto.journeyCandidate ? .55 : 1 }}
              >
                {editingPhoto.journeyCandidate ? '已移入 Journey' : '↗ 移入 Journey'}
              </button>
              <button 
                type="button" 
                onClick={() => triggerSingleAiReclassify(editingPhoto._id)} 
                disabled={aiProcessingId === editingPhoto._id} 
                style={{ width: '100%', padding: '12px', marginTop: '16px', background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: `1px solid ${theme.cardBorder}`, borderRadius: '980px', color: '#0071e3', fontSize: '14px', fontWeight: '600', cursor: aiProcessingId === editingPhoto._id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'transform 0.1s' }}
              >
                {aiProcessingId === editingPhoto._id ? (
                  <>
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} style={{ width: '16px', height: '16px', border: '2.5px solid rgba(0,113,227,0.2)', borderTopColor: '#0071e3', borderRadius: '50%', boxSizing: 'border-box' }} />
                    <span>分析中...</span>
                  </>
                ) : '✨ 呼叫 Qwen 重新歸類與描述'}
              </button>
              <button type="button" onClick={() => { setPhotoToDelete(editingPhoto); setEditingPhoto(null); }} style={{ width: '100%', padding: '11px', marginTop: '10px', border: '1px solid rgba(255,59,48,.34)', borderRadius: '980px', background: 'transparent', color: '#ff3b30', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>刪除這張照片</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {locationResearchOverlay && (
          <motion.div
            className="location-research-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="status"
            aria-live="polite"
          >
            <motion.div
              className={`location-research-status-card is-${locationResearchOverlay}`}
              title={locationResearchOverlay === 'success' ? '地點核實完成' : locationResearchOverlay === 'error' ? '這次核實沒有完成' : `正在核實地點，已用 ${Math.floor(locationResearchElapsed)} 秒`}
              initial={{ opacity: 0, scale: 0.9, y: 14 }}
              animate={{ opacity: 1, scale: locationResearchOverlay === 'success' ? 1.08 : 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ type: 'spring', stiffness: 360, damping: 28 }}
            >
              <div className="location-research-orb" aria-hidden="true">
                <svg className="location-research-ring" viewBox="0 0 120 120">
                  <circle className="location-research-ring-track" cx="60" cy="60" r="52" />
                  <motion.circle
                    className="location-research-ring-progress"
                    cx="60"
                    cy="60"
                    r="52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: locationResearchOverlay === 'loading' ? Math.min(0.9, (locationResearchElapsed / 60) * 0.9) : 1 }}
                    transition={{ duration: locationResearchOverlay === 'loading' ? 0.28 : 0.42, ease: 'easeOut' }}
                  />
                </svg>
                {locationResearchOverlay === 'loading' && <motion.span className="location-research-radar" animate={{ rotate: 360 }} transition={{ duration: 1.65, repeat: Infinity, ease: 'linear' }} />}
                <AnimatePresence mode="wait">
                  {locationResearchOverlay === 'success' ? (
                    <motion.svg key="success" className="location-research-result-icon" viewBox="0 0 48 48" initial={{ scale: 0.55, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 430, damping: 22 }}>
                      <motion.path d="M13 25.5 20.7 33 36 16" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.35, delay: 0.08 }} />
                    </motion.svg>
                  ) : locationResearchOverlay === 'error' ? (
                    <motion.span key="error" className="location-research-result-icon location-research-error-icon" initial={{ scale: 0.55, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 430, damping: 22 }}>!</motion.span>
                  ) : (
                    <motion.span key="progress" className="location-research-progress-number" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      {locationResearchElapsed > 0 ? `${Math.floor(locationResearchElapsed)}s` : '…'}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {batchSyncSource && (
          <motion.div
            variants={modalBackdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseDown={event => {
              if (event.target === event.currentTarget && !isBatchSyncing) closeBatchSyncPicker();
            }}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(15px)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          >
            <motion.div
              variants={modalContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onMouseDown={event => event.stopPropagation()}
              style={{ width: 'min(980px, 100%)', height: 'min(760px, calc(100dvh - 40px))', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: theme.modalBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '24px', padding: '24px', boxSizing: 'border-box' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ margin: '0 0 6px', fontSize: '18px', color: theme.textPrimary }}>選擇要同步的照片</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary, lineHeight: '1.5' }}>
                    只會套用你勾選的項目；分類與 AI 描述不會被覆蓋。
                  </p>
                </div>
                <button type="button" onClick={closeBatchSyncPicker} style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>✕</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', marginBottom: '16px', padding: '10px 12px', borderRadius: '12px', border: `1px solid ${theme.cardBorder}`, background: theme.inputBg }}>
                <strong style={{ color: theme.textPrimary, fontSize: '12px', marginRight: '2px' }}>同步項目</strong>
                {[
                  { id: 'location', label: '地點' },
                  { id: 'equipment', label: '器材' },
                  { id: 'shootingDate', label: '拍攝日期' }
                ].map(field => (
                  <label key={field.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 8px', borderRadius: '8px', color: theme.textPrimary, fontSize: '12px', cursor: 'pointer', background: batchSyncFields[field.id] ? (isDarkMode ? 'rgba(0,113,227,.18)' : 'rgba(0,113,227,.09)') : 'transparent' }}>
                    <input type="checkbox" checked={batchSyncFields[field.id]} onChange={() => toggleBatchSyncField(field.id)} style={{ accentColor: '#0071e3', margin: 0 }} />
                    {field.label}
                  </label>
                ))}
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: '2px 2px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}><strong style={{ color: theme.textPrimary, fontSize: '14px' }}>待補地點</strong><span style={{ color: theme.textSecondary, fontSize: '12px' }}>{batchSyncCandidates.missing.length} 張</span></div>
                <p style={{ margin: '0 0 13px', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>尚未填地點的照片集中顯示在這裡，方便直接同步目前照片的資料。</p>
                {batchSyncCandidates.missing.length > 0 ? <div style={{ display: 'grid', alignContent: 'start', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '16px' }}>
                {batchSyncCandidates.missing.map(photo => {
                  const isSelected = selectedBatchPhotoIds.includes(photo._id);
                  return (
                    <button
                      key={photo._id}
                      type="button"
                      onClick={() => toggleBatchPhotoSelection(photo._id)}
                      aria-pressed={isSelected}
                      style={{ position: 'relative', display: 'block', width: '100%', minWidth: 0, minHeight: '190px', padding: 0, overflow: 'hidden', textAlign: 'left', borderRadius: '12px', border: `2px solid ${isSelected ? '#0071e3' : theme.cardBorder}`, background: theme.cardBg, cursor: 'pointer', boxShadow: isSelected ? '0 0 0 3px rgba(0,113,227,0.16)' : 'none' }}
                    >
                      <img src={getPhotoImagePath(photo, 'thumbnail')} alt={photo.title || '目標照片'} loading="lazy" decoding="async" style={{ display: 'block', width: '100%', height: '150px', objectFit: 'cover', background: theme.inputBg }} />
                      <div style={{ padding: '9px 10px', color: theme.textPrimary, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {photo.location || '未知地點'}</div>
                      <span style={{ position: 'absolute', top: '7px', right: '7px', width: '22px', height: '22px', borderRadius: '50%', background: isSelected ? '#0071e3' : 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.65)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>{isSelected ? '✓' : ''}</span>
                    </button>
                  );
                })}</div> : <div style={{ padding: '26px 12px', border: `1px dashed ${theme.cardBorder}`, borderRadius: '13px', color: theme.textSecondary, fontSize: '13px', textAlign: 'center' }}>沒有待補地點的照片。</div>}
                <div style={{ marginTop: '22px', paddingTop: '17px', borderTop: `1px solid ${theme.cardBorder}` }}>
                  <button type="button" onClick={() => setShowExistingBatchSyncPhotos(value => !value)} aria-expanded={showExistingBatchSyncPhotos} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: 0, border: 'none', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', textAlign: 'left' }}><span><strong style={{ display: 'block', fontSize: '14px' }}>已有地點的其他照片</strong><small style={{ display: 'block', marginTop: '4px', color: theme.textSecondary, fontSize: '12px' }}>需要覆蓋既有地點時再展開。</small></span><span style={{ color: '#0071e3', fontSize: '12px', fontWeight: '600' }}>{showExistingBatchSyncPhotos ? '收起 ↑' : `查看全部 ${batchSyncCandidates.existing.length} 張 ↓`}</span></button>
                  {showExistingBatchSyncPhotos && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '16px', marginTop: '16px' }}>{batchSyncCandidates.existing.map(photo => {
                    const isSelected = selectedBatchPhotoIds.includes(photo._id);
                    return <button key={photo._id} type="button" onClick={() => toggleBatchPhotoSelection(photo._id)} aria-pressed={isSelected} style={{ position: 'relative', display: 'block', width: '100%', minWidth: 0, minHeight: '190px', padding: 0, overflow: 'hidden', textAlign: 'left', borderRadius: '12px', border: `2px solid ${isSelected ? '#ff9500' : theme.cardBorder}`, background: theme.cardBg, cursor: 'pointer', boxShadow: isSelected ? '0 0 0 3px rgba(255,149,0,0.16)' : 'none' }}><img src={getPhotoImagePath(photo, 'thumbnail')} alt={photo.title || '已有地點照片'} loading="lazy" decoding="async" style={{ display: 'block', width: '100%', height: '150px', objectFit: 'cover', background: theme.inputBg }} /><div style={{ padding: '9px 10px', color: theme.textPrimary, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {photo.location}</div><span style={{ position: 'absolute', top: '7px', right: '7px', width: '22px', height: '22px', borderRadius: '50%', background: isSelected ? '#ff9500' : 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.65)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>{isSelected ? '✓' : ''}</span></button>;
                  })}</div>}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingTop: '16px', borderTop: `1px solid ${theme.cardBorder}` }}>
                <span style={{ fontSize: '13px', color: theme.textSecondary }}>已選擇 {selectedBatchPhotoIds.length} 張</span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={closeBatchSyncPicker} disabled={isBatchSyncing} style={{ padding: '9px 15px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontSize: '13px' }}>取消</button>
                  <button type="button" onClick={handleApplyBatchSync} disabled={isBatchSyncing || selectedBatchPhotoIds.length === 0 || !Object.values(batchSyncFields).some(Boolean)} style={{ padding: '9px 16px', borderRadius: '980px', border: 'none', background: selectedBatchPhotoIds.length > 0 && Object.values(batchSyncFields).some(Boolean) ? '#0071e3' : theme.inputBg, color: selectedBatchPhotoIds.length > 0 && Object.values(batchSyncFields).some(Boolean) ? '#fff' : theme.textSecondary, cursor: selectedBatchPhotoIds.length > 0 && Object.values(batchSyncFields).some(Boolean) && !isBatchSyncing ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600' }}>{isBatchSyncing ? '同步中...' : `同步到 ${selectedBatchPhotoIds.length} 張`}</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {locationBatchTarget && (
          <motion.div variants={modalBackdropVariants} initial="hidden" animate="visible" exit="exit" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(15px)', zIndex: 11001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <motion.div variants={modalContentVariants} initial="hidden" animate="visible" exit="exit" style={{ width: 'min(980px, 100%)', height: 'min(760px, calc(100dvh - 40px))', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: theme.modalBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '24px', padding: '24px', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '18px' }}>
                <div><h3 style={{ margin: '0 0 6px', fontSize: '18px', color: theme.textPrimary }}>批量加入「{locationBatchTarget.label}」</h3><p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary, lineHeight: '1.5' }}>只會修改照片地點；既有分類、器材、拍攝時間與 AI 描述都不會變動。</p></div>
                <button type="button" onClick={() => { setLocationBatchTarget(null); setSelectedLocationPhotoIds([]); }} aria-label="關閉批量加入照片" style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: '2px 2px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}><strong style={{ color: theme.textPrimary, fontSize: '14px' }}>待補地點</strong><span style={{ color: theme.textSecondary, fontSize: '12px' }}>{locationBatchCandidates.missing.length} 張</span></div>
                <p style={{ margin: '0 0 13px', color: theme.textSecondary, fontSize: '12px', lineHeight: 1.5 }}>預設只顯示尚未填地點的照片，分類、器材和拍攝資料不會受影響。</p>
                {locationBatchCandidates.missing.length > 0 ? <div style={{ display: 'grid', alignContent: 'start', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '16px' }}>
                  {locationBatchCandidates.missing.map(photo => {
                    const isSelected = selectedLocationPhotoIds.includes(photo._id);
                    return <button key={photo._id} type="button" onClick={() => toggleLocationPhotoSelection(photo._id)} aria-pressed={isSelected} style={{ position: 'relative', display: 'block', width: '100%', minWidth: 0, minHeight: '190px', padding: 0, overflow: 'hidden', textAlign: 'left', borderRadius: '12px', border: `2px solid ${isSelected ? '#0071e3' : theme.cardBorder}`, background: theme.cardBg, cursor: 'pointer', boxShadow: isSelected ? '0 0 0 3px rgba(0,113,227,0.16)' : 'none' }}><img src={getPhotoImagePath(photo, 'thumbnail')} alt={photo.title || '待加入照片'} loading="lazy" decoding="async" style={{ display: 'block', width: '100%', height: '150px', objectFit: 'cover', background: theme.inputBg }} /><div style={{ padding: '9px 10px', color: theme.textPrimary, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 待填地點</div><span style={{ position: 'absolute', top: '7px', right: '7px', width: '22px', height: '22px', borderRadius: '50%', background: isSelected ? '#0071e3' : 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.65)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>{isSelected ? '✓' : ''}</span></button>;
                  })}
                </div> : <div style={{ padding: '26px 12px', border: `1px dashed ${theme.cardBorder}`, borderRadius: '13px', color: theme.textSecondary, fontSize: '13px', textAlign: 'center' }}>沒有待補地點的照片。</div>}
                <div style={{ marginTop: '22px', paddingTop: '17px', borderTop: `1px solid ${theme.cardBorder}` }}>
                  <button type="button" onClick={() => setShowExistingLocationBatchPhotos(value => !value)} aria-expanded={showExistingLocationBatchPhotos} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: 0, border: 'none', background: 'transparent', color: theme.textPrimary, cursor: 'pointer', textAlign: 'left' }}><span><strong style={{ display: 'block', fontSize: '14px' }}>已有地點的其他照片</strong><small style={{ display: 'block', marginTop: '4px', color: theme.textSecondary, fontSize: '12px' }}>需要重新分配時再展開；不會顯示已屬於目前地點的照片。</small></span><span style={{ color: '#0071e3', fontSize: '12px', fontWeight: '600' }}>{showExistingLocationBatchPhotos ? '收起 ↑' : `查看全部 ${locationBatchCandidates.existing.length} 張 ↓`}</span></button>
                  {showExistingLocationBatchPhotos && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '16px', marginTop: '16px' }}>{locationBatchCandidates.existing.map(photo => {
                    const isSelected = selectedLocationPhotoIds.includes(photo._id);
                    return <button key={photo._id} type="button" onClick={() => toggleLocationPhotoSelection(photo._id)} aria-pressed={isSelected} style={{ position: 'relative', display: 'block', width: '100%', minWidth: 0, minHeight: '190px', padding: 0, overflow: 'hidden', textAlign: 'left', borderRadius: '12px', border: `2px solid ${isSelected ? '#ff9500' : theme.cardBorder}`, background: theme.cardBg, cursor: 'pointer', boxShadow: isSelected ? '0 0 0 3px rgba(255,149,0,0.16)' : 'none' }}><img src={getPhotoImagePath(photo, 'thumbnail')} alt={photo.title || '重新分配地點'} loading="lazy" decoding="async" style={{ display: 'block', width: '100%', height: '150px', objectFit: 'cover', background: theme.inputBg }} /><div style={{ padding: '9px 10px', color: theme.textPrimary, fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {photo.location}</div><span style={{ position: 'absolute', top: '7px', right: '7px', width: '22px', height: '22px', borderRadius: '50%', background: isSelected ? '#ff9500' : 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.65)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>{isSelected ? '✓' : ''}</span></button>;
                  })}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingTop: '16px', borderTop: `1px solid ${theme.cardBorder}` }}><span style={{ fontSize: '13px', color: theme.textSecondary }}>已選擇 {selectedLocationPhotoIds.length} 張</span><div style={{ display: 'flex', gap: '10px' }}><button type="button" onClick={() => { setLocationBatchTarget(null); setSelectedLocationPhotoIds([]); }} disabled={isLocationBatchSaving} style={{ padding: '9px 15px', borderRadius: '980px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textPrimary, cursor: 'pointer', fontSize: '13px' }}>取消</button><button type="button" onClick={handleApplyLocationBatch} disabled={isLocationBatchSaving || selectedLocationPhotoIds.length === 0} style={{ padding: '9px 16px', borderRadius: '980px', border: 'none', background: selectedLocationPhotoIds.length > 0 ? '#0071e3' : theme.inputBg, color: selectedLocationPhotoIds.length > 0 ? '#fff' : theme.textSecondary, cursor: selectedLocationPhotoIds.length > 0 && !isLocationBatchSaving ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600' }}>{isLocationBatchSaving ? '加入中...' : `加入 ${selectedLocationPhotoIds.length} 張`}</button></div></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showStorageOverview && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onMouseDown={() => setShowStorageOverview(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 12500, padding: '20px', display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.42)', backdropFilter: 'blur(14px)' }}
          >
            <motion.section
              initial={{ opacity: 0, y: 12, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: .98 }}
              onMouseDown={event => event.stopPropagation()}
              style={{ width: 'min(560px, 100%)', boxSizing: 'border-box', padding: '28px', borderRadius: '26px', background: isDarkMode ? '#1d1d1f' : 'rgba(255,255,255,.96)', color: theme.textPrimary, border: `1px solid ${theme.cardBorder}`, boxShadow: '0 22px 70px rgba(0,0,0,.28)' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '18px' }}>
                <div><p style={{ margin: 0, color: theme.textSecondary, fontSize: '11px', fontWeight: '700', letterSpacing: '.1em' }}>SERVER STORAGE</p><h2 style={{ margin: '7px 0 0', fontSize: '25px', letterSpacing: '-.03em' }}>儲存空間</h2></div>
                <button type="button" onClick={() => setShowStorageOverview(false)} aria-label="關閉儲存空間分析" style={{ width: '32px', height: '32px', borderRadius: '50%', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '19px', cursor: 'pointer' }}>×</button>
              </div>

              {isLoadingStorageOverview && <div style={{ minHeight: '250px', display: 'grid', placeItems: 'center', color: theme.textSecondary, fontSize: '14px' }}>正在向伺服器分析空間…</div>}

              {!isLoadingStorageOverview && storageOverview?.error && <div style={{ minHeight: '180px', display: 'grid', placeItems: 'center', textAlign: 'center', color: theme.textSecondary, fontSize: '14px' }}>{storageOverview.error}</div>}

              {!isLoadingStorageOverview && storageOverview?.breakdown && (() => {
                const { totalBytes, freeBytes, usedBytes, breakdown } = storageOverview;
                const items = [
                  { key: 'imagesBytes', label: '圖片與作品媒體', detail: '作品、Hero、個人頁與 Gear 圖片', color: '#ff453a' },
                  { key: 'frontendBytes', label: '前端網站', detail: '已部署的靜態頁面與資源', color: '#0a84ff' },
                  { key: 'backendBytes', label: '後端服務', detail: '程式、依賴與資料處理環境', color: '#8e8e93' },
                  { key: 'otherBytes', label: '系統與其他服務', detail: 'Ubuntu、Swap、系統套件與服務資料', color: '#636366' }
                ];
                return <>
                  <div style={{ marginTop: '28px', padding: '20px', borderRadius: '20px', background: isDarkMode ? 'rgba(255,255,255,.055)' : '#f5f5f7' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}><strong style={{ fontSize: '34px', letterSpacing: '-.05em' }}>{formatStorageBytes(freeBytes)}</strong><span style={{ color: theme.textSecondary, fontSize: '14px' }}>可用</span></div>
                    <p style={{ margin: '6px 0 0', color: theme.textSecondary, fontSize: '13px' }}>共 {formatStorageBytes(totalBytes)} · 已使用 {formatStorageBytes(usedBytes)}</p>
                    <div style={{ display: 'flex', overflow: 'hidden', height: '10px', marginTop: '18px', borderRadius: '99px', background: isDarkMode ? 'rgba(255,255,255,.16)' : '#dedee3' }}>
                      {items.map(item => <span key={item.key} title={`${item.label} ${formatStorageBytes(breakdown[item.key])}`} style={{ width: `${Math.max(0, (breakdown[item.key] / totalBytes) * 100)}%`, background: item.color, minWidth: breakdown[item.key] > 0 ? '2px' : 0 }} />)}
                    </div>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    {items.map(item => <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 2px', borderBottom: `1px solid ${theme.cardBorder}` }}><span style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color, flex: '0 0 auto' }} /><div style={{ minWidth: 0, flex: 1 }}><div style={{ fontSize: '14px', fontWeight: '600' }}>{item.label}</div><div style={{ marginTop: '3px', color: theme.textSecondary, fontSize: '11px' }}>{item.detail}</div></div><strong style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{formatStorageBytes(breakdown[item.key])}</strong></div>)}
                  </div>
                  <p style={{ margin: '16px 0 0', color: theme.textSecondary, fontSize: '11px', lineHeight: 1.5 }}>數據由伺服器在你開啟此面板時即時統計；「系統與其他服務」包含 Swap，因此不等同於網站檔案。</p>
                </>;
              })()}
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {gearViewer && <motion.div ref={gearViewerShellRef} className="gear-viewer-shell" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onWheel={handleGearViewerWheel} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100dvh', zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: '1200px', overflow: 'hidden', background: 'rgba(0,0,0,.96)', cursor: 'zoom-out', userSelect: 'none', touchAction: 'none', overscrollBehavior: 'none' }}>
          <AnimatePresence custom={gearViewerDirection} mode="popLayout"><motion.div key={gearViewer.images[gearViewer.index]} custom={gearViewerDirection} variants={photoViewerVariants} initial={isGearViewerFirstOpen ? 'initialFromHome' : 'enterFromSide'} animate="center" exit="exitRotate" drag={isGearViewerZoomed ? false : 'x'} dragConstraints={{ left: 0, right: 0 }} dragElastic={0.7} dragMomentum={false} onDragStart={beginGearViewerDrag} onDragEnd={handleGearViewerDragEnd} onClick={handleGearViewerClick} onDoubleClick={handleGearViewerDoubleClick} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', transformOrigin: 'center center', transform: 'translateZ(0)', cursor: isGearViewerZoomed ? 'grab' : 'zoom-in' }}><motion.div onPointerDown={beginGearViewerPan} onPointerMove={moveGearViewerPan} onPointerUp={endGearViewerPan} onPointerCancel={endGearViewerPan} animate={{ scale: isGearViewerZoomed ? 2.8 : 1 }} transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }} style={{ x: gearViewerPanX, y: gearViewerPanY, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', transformOrigin: 'center center', width: '100%', height: '100%', cursor: isGearViewerZoomed ? 'grab' : 'inherit', touchAction: 'none' }}><img ref={gearViewerImageRef} src={gearViewer.images[gearViewer.index]} alt="Gear 大圖" style={{ maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain', display: 'block', borderRadius: isGearViewerZoomed ? '2px' : '18px', boxShadow: '0 35px 100px rgba(0,0,0,0.95)', pointerEvents: 'none' }} /></motion.div></motion.div></AnimatePresence>
          {gearViewer.index > 0 && <button className="gear-viewer-nav" onClick={event => { event.stopPropagation(); navigateGearViewer(-1); }} aria-label="上一張圖片" style={{ position: 'fixed', left: 0, top: '50%', width: 'clamp(76px, 10vw, 132px)', height: '72vh', transform: 'translateY(-50%)', display: 'grid', placeItems: 'center', border: 'none', color: '#fff', background: 'linear-gradient(90deg, rgba(0,0,0,.24), transparent)', cursor: 'pointer' }}><span><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></span></button>}
          {gearViewer.index < gearViewer.images.length - 1 && <button className="gear-viewer-nav" onClick={event => { event.stopPropagation(); navigateGearViewer(1); }} aria-label="下一張圖片" style={{ position: 'fixed', right: 0, top: '50%', width: 'clamp(76px, 10vw, 132px)', height: '72vh', transform: 'translateY(-50%)', display: 'grid', placeItems: 'center', border: 'none', color: '#fff', background: 'linear-gradient(270deg, rgba(0,0,0,.24), transparent)', cursor: 'pointer' }}><span><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></span></button>}
          {gearViewer.images.length > 1 && <span style={{ position: 'fixed', bottom: '28px', color: 'rgba(255,255,255,.76)', fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{gearViewer.index + 1} / {gearViewer.images.length}</span>}
          <button onClick={closeGearViewer} aria-label="關閉圖片檢視器" style={{ position: 'fixed', top: '24px', right: '24px', width: '38px', height: '38px', display: 'grid', placeItems: 'center', padding: 0, borderRadius: '50%', border: '1px solid rgba(255,255,255,.35)', color: '#fff', background: 'rgba(0,0,0,.35)', fontSize: '21px', lineHeight: 1, cursor: 'pointer' }}>×</button>
        </motion.div>}
      </AnimatePresence>

      {lightboxIndex < 0 && !gearViewer && showScrollTop && <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="回到頁面頂端" title="回到頂端" style={{ position: 'fixed', right: '28px', bottom: '28px', zIndex: 950, width: '46px', height: '46px', display: 'grid', placeItems: 'center', padding: 0, borderRadius: '50%', border: `1px solid ${theme.cardBorder}`, background: isDarkMode ? 'rgba(28,28,30,.92)' : 'rgba(255,255,255,.94)', color: theme.textPrimary, boxShadow: '0 10px 28px rgba(0,0,0,.16)', cursor: 'pointer', backdropFilter: 'blur(16px)' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5" /><path d="m6.5 10.5 5.5-5.5 5.5 5.5" /></svg></button>}

    </div>
  );
}

export default App;
